// Type definitions
export type MarketSummary = { name: string; value: number; changePct: number };
export type Mover = {
  ticker: string;
  name: string;
  /** Price the row's "Current"/main column should display. For AH/PM
   *  endpoints this equals regularClose (today's RTH close); for the
   *  daily endpoints it's the live quote. */
  price: number;
  /** % change the row's main "Change %" column should display. For
   *  AH/PM endpoints this is the extended-hours-specific change; for
   *  daily endpoints it's the day's change. */
  changePct: number;
  volume: number;
  marketCap: number;
  exchange?: string;
  sector?: string;
  /** GICS industry (one level below sector). Surfaced on the Movers page
   *  as a sortable column + filter dropdown that cascades off the parent
   *  sector. Empty / undefined for unclassified rows. */
  industry?: string;
  analystRating?: string | null;
  /** Yesterday's regular-session close. */
  prevClose?: number | null;
  /** Today's regular-session close (4:00 PM ET print). Same as `price`
   *  on AH/PM endpoints. */
  regularClose?: number | null;
  /** Current extended-hours quote (AH 4–8pm or PM 4–9:30am). */
  ahPrice?: number | null;
  /** Today's regular-session % change vs prev_close. */
  dayChangePct?: number | null;
  /** Extended-hours % move from the regular-session close to the
   *  current extended-hours quote. */
  ahChangePct?: number | null;
  /** 10-day average daily volume. */
  avgVolume?: number | null;
  /** Volume traded in the current extended-hours session. */
  ahVolume?: number | null;
  /** Which extended session this row's quote came from. Set by the
   *  AH/PM endpoints; absent on the daily-mover endpoints. */
  sessionLabel?: 'pre-market' | 'after-hours';
};
export type StockDetail = {
  ticker: string; name: string; price: number; change: number; changePct: number;
  volume: number; marketCap: number; pe: number; high52: number; low52: number;
  exchange: string;
  /** Asset type from tickers.type — drives canonical URL routing. When an
   *  ETF lands on /stocks/X (which happens any time TickerLink-style
   *  navigation routes there without knowing the asset type), the stock
   *  detail page client-redirects to /etf/X. Optional because the field
   *  was added later; older snapshots / fixtures don't carry it. */
  type?: 'stock' | 'etf';
  description: string; sector: string; industry: string;
  eps: number; beta: number; avgVolume: number; sharesOutstanding: number;
  dividendYield: number; analystRating: string; analystCount: number; priceTarget: number;
  analystBreakdown?: { strongBuy: number; buy: number; hold: number; sell: number; strongSell: number } | null;
  priceTargetHigh?: number; priceTargetLow?: number;
  extendedHoursPrice?: number;
  extendedHoursChange?: number;
  extendedHoursChangePct?: number;
  extendedHoursVolume?: number;
  marketSession?: 'regular' | 'pre-market' | 'after-hours' | 'closed';
  /** ISO timestamp of the last priceSync write to tickers.price for this
   *  symbol. The stock-detail header renders "Updated Xs ago" against
   *  THIS timestamp, not the client poll time — so the user sees how
   *  fresh the underlying data is, not how recently their browser
   *  refetched the cached row. */
  priceUpdatedAt?: string | null;
  /** TTM revenue in raw USD; null when no income statement is cached. */
  revenue?: number | null;
  /** Price-to-sales = marketCap / revenue; null when either is missing. */
  priceToSales?: number | null;
  /** Debt-to-equity = (longTermDebt + shortLongTermDebt) / totalEquity.
   *  Null when equity is missing or non-positive. */
  debtToEquity?: number | null;
  /** Issue #193 — IBD-style relative strength rating. 1–99 percentile
   *  of trailing-year price action vs. the rest of the active ticker
   *  universe. Null when the ticker has < 252 trading days of cached
   *  daily history (recent IPOs, sparsely-covered names) — UI shows a
   *  dash. Updated nightly post-close by relativeStrengthService. */
  rsRating?: number | null;
  rsRatingUpdatedAt?: string | null;
  /** Wilder's 14-period RSI on daily closes — momentum oscillator
   *  (0–100). Computed on demand from cached daily bars. Distinct from
   *  rsRating: RSI is per-ticker overbought/oversold, rsRating is
   *  universe-relative ranking. */
  rsi14?: number | null;
  /** S&P 500 membership (true when tickers.heatmap is set by
   *  /admin/tickers/sync-sp500-heatmap). The stock-detail header
   *  surfaces this ahead of Russell 1000; NDX100 is shown independently. */
  sp500?: boolean;
  /** Russell 1000 membership (tickers.russell_1000, synced from
   *  iShares IWB). */
  russell1000?: boolean;
  /** Nasdaq-100 membership (tickers.ndx_100, synced from Nasdaq). */
  ndx100?: boolean;
  /** Combined Buy/Hold/Sell trend signal (plan #363) — rendered as a pill in
   *  the detail header. Null until the nightly sweep rates this ticker. */
  signal?: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  signalReason?: string | null;
  /** SIQ Score — how many of 8 stage-2 trend criteria the stock passes
   *  (0–8), written nightly by the RS-rating sweep; `siqScoreReason` names
   *  the misses. Null until rated (needs ~221 daily bars). Third tile in
   *  the detail page's at-a-glance row. */
  siqScore?: number | null;
  siqScoreReason?: string | null;
};
export type OHLCV = { date: string; open: number; high: number; low: number; close: number; volume: number };
export type ETFDetail = {
  ticker: string; name: string; price: number; change: number; changePct: number;
  /** Asset type. Always 'etf' when this payload is the response from
   *  GET /etf/:ticker, but typed as optional union so the symmetric
   *  redirect on /etf/[ticker] (when a stock symbol gets routed there)
   *  has a way to detect the mismatch. */
  type?: 'stock' | 'etf';
  nav: number; expenseRatio: number; aum: number; holdings: number; dividendYield: number;
  inceptionDate: string; provider: string; benchmark: string; ytdReturn: number; oneYearReturn: number;
  topHoldings?: Array<{ symbol: string; name: string; weight: number }>;
  volume?: number;
  avgVolume?: number;
  high52?: number;
  low52?: number;
  beta?: number;
  description?: string;
  /** ISO timestamp of the last priceSync write to tickers.price. ETF
   *  page header surfaces this as "Updated Xs ago". */
  priceUpdatedAt?: string | null;
  category?: string;
  threeYearReturn?: number;
  fiveYearReturn?: number;
  /** PM / AH snapshot — same fields the stock detail page already
   *  consumes. Surfaced on the ETF header inline next to the RTH
   *  price (matches the stock page treatment). */
  extendedHoursPrice?: number;
  extendedHoursChange?: number;
  extendedHoursChangePct?: number;
  /** Combined Buy/Hold/Sell trend signal (plan #363) — pill in the header. */
  signal?: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  signalReason?: string | null;
};
export type IPO = {
  id: string; company: string; ticker: string; exchange: string; ipoDate: string;
  status: string; priceRangeLow: number; priceRangeHigh: number; currentPrice: number;
  returnSinceIpo: number; sector: string; size: number;
};
export type NewsArticle = {
  id: string; headline: string; summary: string; source: string;
  url: string; publishedAt: string; tickers: string[]; imageUrl?: string;
};
export type User = {
  id: string; username: string; email: string; firstName: string; lastName: string; displayName: string;
  role: 'user' | 'staff' | 'admin';
  subscriptionTier: 'free' | 'pro' | 'full' | 'vip';
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  emailVerified: boolean; isBanned: boolean; isDemo: boolean; mustChangePassword: boolean; createdAt: string;
  avatarUrl: string | null;
  lastLoginAt: string | null;
  timezone: string;
  /** Issue #203 — pending users wait for admin approval before the
   *  verification email fires. 'active' for everyone else. */
  accountStatus?: 'active' | 'pending_prescreen';
};
export type AuthPayload2 = AuthPayload | { pendingApproval: true; email: string; accessToken?: undefined };
export type AuthPayload = { user: User; accessToken: string; showTrialPopup?: boolean; trialPopupTitle?: string; trialPopupMessage?: string };
export type AdSlot = { id: string; slotName: string; imageUrl: string; targetUrl: string; altText: string; isActive: boolean; excludedPages: string[] };
export type SearchResult = { ticker: string; name: string; exchange: string; type?: 'stock' | 'etf'; price?: number; changePct?: number };

// In the browser, always use the relative /api path — Next.js rewrites proxy
// it to the real API server, keeping cookies same-origin on any domain.
// On the server (SSR) we need an absolute URL, so fall back to the env var.
const BASE_URL =
  typeof window !== 'undefined'
    ? '/api'
    : (process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4000/api');

// Module-level token storage (set by AuthContext)
let _accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  _accessToken = token;
}

export function getHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (_accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  return headers;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      ...getHeaders(),
      ...(options?.headers ?? {}),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    let message = `Request failed: ${response.status}`;
    let errorData: Record<string, unknown> = {};
    try {
      const body = await response.json();
      errorData = body.error ?? {};
      message = body.message ?? body.error?.message ?? body.error ?? message;
    } catch {
      // ignore parse error
    }
    const err = new Error(message);
    // Attach extra error fields (code, deviceToken, etc.) for callers to inspect
    Object.assign(err, errorData);
    // Attach the HTTP status so callers can branch on it. Set AFTER the
    // Object.assign so a server-returned `status` field in the body
    // can't shadow the real HTTP status. Without this AuthContext can't
    // tell a real 401 ("no session") from a transient network/5xx
    // error — its `(err as {status?:number})?.status === 401` check
    // would always be `undefined === 401` = false, defeating the
    // distinction issue #263 was meant to introduce.
    (err as { status?: number }).status = response.status;
    throw err;
  }

  const text = await response.text();
  if (!text) return undefined as T;
  const json = JSON.parse(text);
  return (json?.data !== undefined ? json.data : json) as T;
}

// Sector performance
export interface SectorPerformance {
  sector: string;
  changePct: number;
  marketCap: number;
  tickerCount: number;
  topGainer: { ticker: string; changePct: number } | null;
  topLoser: { ticker: string; changePct: number } | null;
}
export async function getSectorPerformance(): Promise<SectorPerformance[]> {
  return request<SectorPerformance[]>('/sectors/performance');
}

// Sector ETF view — the Sector Performance tile's second view. One row per
// sector ETF (what the fund actually printed) rather than a market-cap rollup
// of a sector's constituents.
export type SectorEtfGroup = 'sector' | 'index';
export interface SectorEtfMeta {
  symbol: string;
  /** Full fund name — the row tooltip. */
  name: string;
  /** Short sector/index label the row displays beside the ticker. */
  label: string;
  group: SectorEtfGroup;
}
export interface SectorEtfRow extends SectorEtfMeta {
  price: number | null;
  change: number | null;
  /** Day move, or the 5-session move when `period` is 'weekly'. */
  changePct: number | null;
  /** Daily closes, oldest → newest, for the row sparkline. */
  closes: number[];
  priceUpdatedAt: string | null;
}
export interface SectorEtfsResponse {
  rows: SectorEtfRow[];
  /** Every ETF the picker can offer — sent with the data so the settings
   *  panel can render before the user has chosen anything. */
  catalog: SectorEtfMeta[];
  /** What the tile shows when nothing is configured. */
  defaults: string[];
  period: 'daily' | 'weekly';
}
/** Pass `tickers: null` (or an empty array) to get the server defaults. */
export async function getSectorEtfs(
  tickers: string[] | null,
  period: 'daily' | 'weekly' = 'daily',
): Promise<SectorEtfsResponse> {
  const qs = new URLSearchParams({ period });
  if (tickers && tickers.length > 0) qs.set('tickers', tickers.join(','));
  return request<SectorEtfsResponse>(`/sectors/etfs?${qs.toString()}`);
}

// Sector performance table (/sectors) — one row per sector SPDR plus SPY,
// each with trailing returns over nine periods.
export type SectorReturnPeriod = 'today' | 'yesterday' | 'd5' | 'm1' | 'm3' | 'ytd' | 'y1' | 'y5' | 'all';
/** Percent returns; null when the fund is too young for the period or its
 *  history hasn't loaded. */
export type SectorPeriodReturns = Record<SectorReturnPeriod, number | null>;
/** Date (YYYY-MM-DD) of the close each period is measured from. */
export type SectorPeriodBases = Partial<Record<Exclude<SectorReturnPeriod, 'today'>, string>>;
export interface SectorEtfPerformanceRow {
  symbol: string;
  name: string;
  label: string;
  longLabel: string;
  group: SectorEtfGroup;
  price: number | null;
  /** Session this row's `today` describes. */
  asOf: string | null;
  previousSession: string | null;
  /** Price return — dividends excluded. */
  returns: SectorPeriodReturns;
  /** Total return — dividends reinvested. */
  totalReturns: SectorPeriodReturns;
  bases: SectorPeriodBases;
}
export interface SectorEtfPerformance {
  rows: SectorEtfPerformanceRow[];
  benchmark: string;
  /** Session the `today` column describes. */
  asOf: string | null;
  /** Session the `yesterday` column describes. */
  previousSession: string | null;
  /** Start of each period, from the benchmark row. */
  bases: SectorPeriodBases;
  quoteUpdatedAt: string | null;
  builtAt: string;
  warming: boolean;
}
export async function getSectorEtfPerformance(): Promise<SectorEtfPerformance> {
  return request<SectorEtfPerformance>('/sectors/etf-performance');
}

// Market data
export async function getMarketSummary(): Promise<MarketSummary[]> {
  return request<MarketSummary[]>('/markets/summary');
}
export type CommodityPrice = {
  symbol: string; name: string; unit: string;
  price: number; change: number; changePct: number;
  high: number | null; low: number | null;
  high52: number | null; low52: number | null;
};
export async function getCommodityPrices(): Promise<CommodityPrice[]> {
  return request<CommodityPrice[]>('/markets/commodities');
}

// Movers consolidation (issue #244): the deleted /api/markets/{type}
// endpoints are now served by /api/screener with presets. This wrapper
// keeps existing callers working without forcing every caller to know
// the screener preset shape; new callers should reach for getScreenerPage
// directly to also get pagination + universe filtering.
export async function getMovers(
  type: 'gainers' | 'losers' | 'active' | 'premarket' | 'afterhours',
  opts: { universe?: MoversUniverse; limit?: number; offset?: number } = {},
): Promise<Mover[]> {
  const filters = moversFiltersForType(type, opts);
  const results = await getScreenerResults(filters);
  return results.map(screenerResultToMover);
}

/** Resolve a Movers tab name into the underlying screener filter set.
 *  Single source of truth — used by both the dashboard tiles (via
 *  getMovers above) and the /markets/[type] page (via getScreenerPage). */
export function moversFiltersForType(
  type: 'gainers' | 'losers' | 'active' | 'premarket' | 'afterhours',
  opts: { universe?: MoversUniverse; limit?: number; offset?: number } = {},
): ScreenerFilters {
  const base: ScreenerFilters = {
    universe: opts.universe ?? 'r1000',
    limit: opts.limit,
    offset: opts.offset,
  };
  switch (type) {
    case 'gainers':
      return { ...base, sort: 'changePct.desc', minChangePct: 0.01 };
    case 'losers':
      return { ...base, sort: 'changePct.asc', maxChangePct: -0.01 };
    case 'active':
      return { ...base, sort: 'volume.desc' };
    case 'premarket':
      return { ...base, sort: 'extendedChangePct.desc', session: 'pre-market' };
    case 'afterhours':
      return { ...base, sort: 'extendedChangePct.desc', session: 'after-hours' };
  }
}

/** Map a screener row to the legacy Mover shape consumed by MoversTable +
 *  the dashboard tiles. Picks the right `price` and `changePct` based on
 *  whether the screener returned extended-hours fields. */
export function screenerResultToMover(r: ScreenerResult): Mover {
  const hasExt = r.extendedSession != null && r.extendedChangePct != null;
  return {
    ticker: r.ticker,
    name: r.name,
    price: r.price,
    changePct: hasExt && r.extendedChangePct != null ? r.extendedChangePct : r.changePct,
    volume: r.volume,
    marketCap: r.marketCap,
    exchange: r.exchange,
    sector: r.sector,
    industry: r.industry,
    analystRating: r.analystRating === 'Strong Buy' || r.analystRating === 'Buy' || r.analystRating === 'Hold' || r.analystRating === 'Sell' || r.analystRating === 'Strong Sell' ? r.analystRating : null,
    prevClose: r.prevClose ?? null,
    regularClose: r.price,
    ahPrice: r.extendedPrice ?? null,
    dayChangePct: r.changePct,
    ahChangePct: r.extendedChangePct ?? null,
    avgVolume: r.avgVolume ?? null,
    ahVolume: r.extendedVolume ?? null,
    sessionLabel: r.extendedSession ?? undefined,
  };
}

// ── Smart Leaders (composite-score top-N) ───────────────────────────────────
export type SmartLeader = {
  ticker: string;
  name: string;
  sector: string | null;
  exchange: string | null;
  price: number;
  changePct: number;
  marketCap: number;
  volume: number;
  avgVolume: number;
  rsRating: number | null;
  rsi14: number | null;
  rsDelta7d: number | null;
  rsDelta30d: number | null;
  rsiDelta7d: number | null;
  rsiDelta30d: number | null;
  analystRating: string | null;
  score: number;
  subScores: {
    momentum: number;
    absRs: number;
    rsiZone: number;
    rvol: number;
    analyst: number;
  };
};

export type SmartLeadersResponse = {
  items: SmartLeader[];
  generatedAt: string;
  weightsUsed: { momentum: number; absRs: number; rsiZone: number; rvol: number; analyst: number };
  universe: 'russell' | 'all';
  period: '7d' | '30d';
};

export type SmartLeadersOpts = {
  count?: number;
  period?: '7d' | '30d';
  universe?: 'russell' | 'all';
  weights?: Partial<{ momentum: number; absRs: number; rsiZone: number; rvol: number; analyst: number }>;
};

// ── Week Ahead ──────────────────────────────────────────────────────────────
export type WeekAheadEarningsDay = {
  date: string;
  weekday: string;
  totalCount: number;
  notable: Array<{
    ticker: string;
    name: string;
    marketCap: number;
    sector: string;
    epsEstimate: number | null;
    time: string;
  }>;
};

export type WeekAheadMacroEvent = {
  date: string;
  weekday: string;
  time: string;
  event: string;
  country: string;
  impact: 'High' | 'Medium' | 'Low';
};

export type WeekAheadRsMover = {
  ticker: string;
  name: string | null;
  sector: string | null;
  rsRating: number | null;
  rsDelta7d: number | null;
  rsi14: number | null;
};

export type WeekAhead = {
  weekStart: string;
  earnings: WeekAheadEarningsDay[];
  macroEvents: WeekAheadMacroEvent[];
  leaders: WeekAheadRsMover[];
  laggards: WeekAheadRsMover[];
  generatedAt: string;
};

export async function getWeekAhead(): Promise<WeekAhead> {
  return request<WeekAhead>('/markets/week-ahead');
}

// ── Pre-Market AI Digest (personal) ─────────────────────────────────────────
export type DigestItem = {
  ticker?: string;
  headline: string;
  source: string;
  url: string;
  materiality: 1 | 2 | 3 | 4 | 5;
  why: string;
};

export type PreMarketDigestData = {
  userId: string;
  etDate: string;
  summary: string;
  items: DigestItem[];
  model: string;
  inputCount: number;
  latencyMs: number;
  generatedAt: string;
};

export type PreMarketDigestResponse = {
  digest: PreMarketDigestData | null;
  stale: boolean;
  canGenerate: boolean;
  gateReason: string;
  etDate: string;
};

export async function getPreMarketDigest(): Promise<PreMarketDigestResponse> {
  return request<PreMarketDigestResponse>('/pre-market-digest');
}

export async function generatePreMarketDigest(): Promise<{ digest: PreMarketDigestData; fromCache: boolean }> {
  return request<{ digest: PreMarketDigestData; fromCache: boolean }>('/pre-market-digest/generate', { method: 'POST' });
}

// ── Trade Plans ─────────────────────────────────────────────────────────────
export type PlanDirection = 'long' | 'short';
export type PlanStatus = 'planned' | 'triggered' | 'closed' | 'cancelled';
export type PlanOutcome = 'stopped' | 'targeted' | 'manual';

export type TradePlan = {
  id: string;
  userId: string;
  ticker: string;
  direction: PlanDirection;
  entryPrice: number;
  stopPrice: number;
  targetPrice: number;
  positionSize: number | null;
  thesis: string;
  catalystDate: string | null;
  status: PlanStatus;
  outcome: PlanOutcome | null;
  entryAlertId: string | null;
  stopAlertId: string | null;
  targetAlertId: string | null;
  triggeredAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  currentPrice: number | null;
  changePct: number | null;
};

export type CreateTradePlanInput = {
  ticker: string;
  direction: PlanDirection;
  entryPrice: number;
  stopPrice: number;
  targetPrice: number;
  positionSize?: number | null;
  thesis?: string;
  catalystDate?: string | null;
};

export async function listTradePlans(activeOnly = false): Promise<TradePlan[]> {
  const qs = activeOnly ? '?activeOnly=1' : '';
  return request<TradePlan[]>(`/trade-plans${qs}`);
}

export async function createTradePlan(input: CreateTradePlanInput): Promise<TradePlan> {
  return request<TradePlan>('/trade-plans', { method: 'POST', body: JSON.stringify(input) });
}

export type UpdateTradePlanInput = {
  thesis?: string;
  catalystDate?: string | null;
  positionSize?: number | null;
  direction?: PlanDirection;
  entryPrice?: number;
  stopPrice?: number;
  targetPrice?: number;
};

export async function updateTradePlan(id: string, input: UpdateTradePlanInput): Promise<TradePlan> {
  return request<TradePlan>(`/trade-plans/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function closeTradePlan(id: string): Promise<TradePlan> {
  return request<TradePlan>(`/trade-plans/${id}/close`, { method: 'POST' });
}

export async function cancelTradePlan(id: string): Promise<TradePlan> {
  return request<TradePlan>(`/trade-plans/${id}/cancel`, { method: 'POST' });
}

export async function deleteTradePlan(id: string): Promise<void> {
  await request<{ ok: boolean }>(`/trade-plans/${id}`, { method: 'DELETE' });
}

// ── Pre-Market Dashboard ────────────────────────────────────────────────────
/** Recent value path for the card's sparkline, oldest → newest. Optional
 *  because it only exists once `pre_market_quote_history` has accumulated
 *  ticks — a fresh deploy serves `[]` until the first sync lands, and
 *  `TrendSparkline` renders a dash for anything under two points. */
type WithSpark = { spark?: number[] };

export type PreMarketQuote = WithSpark & {
  symbol: string;
  name: string;
  value: number;
  change: number;
  changePct: number;
  prevValue: number | null;
  marketStatus: string | null;
};

export type PreMarketCommodity = WithSpark & {
  symbol: string;
  name: string;
  unit: string;
  value: number;
  change: number;
  changePct: number;
};

export type PreMarketBriefData = {
  summary: string;
  model: string;
  generatedAt: string;
  etDate: string;
};

export type PreMarketDashboard = {
  futures: PreMarketQuote[];
  rates: PreMarketQuote[];
  fx: PreMarketQuote[];
  commodities: PreMarketCommodity[];
  global: PreMarketQuote[];
  vix: PreMarketQuote | null;
  brief: PreMarketBriefData | null;
  asOf: string | null;
};

export async function getPreMarketDashboard(): Promise<PreMarketDashboard> {
  return request<PreMarketDashboard>('/markets/pre-market');
}

// ── Next Day Focus ───────────────────────────────────────────────────────

export type WatchlistCatalyst = 'earnings' | 'm&a' | 'news' | 'momentum' | 'sector' | 'unknown';

export interface WatchlistPickSourceData {
  closePx?: number;
  ahPct?: number | null;
  ahVolume?: number | null;
  peakRvol?: number | null;
  closeOffHighPct?: number | null;
  mcap?: number | null;
  russell1000?: boolean;
  sector?: string | null;
}

export interface WatchlistPick {
  ticker: string;
  rank: number;
  catalyst: WatchlistCatalyst;
  setup: string;
  reasoning: string;
  risk: string;
  sourceData: WatchlistPickSourceData;
}

export interface WatchlistPickOutcome {
  ticker: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  prevClose: number | null;
  openGapPct: number | null;
  closeReturnPct: number | null;
  maxFavorablePct: number | null;
  maxAdversePct: number | null;
  hit: boolean | null;
}

export interface WatchlistRow {
  targetDate: string;
  sourceDate: string;
  generatedAt: string;
  marketTone: string | null;
  picks: WatchlistPick[];
  model: string;
  sourceKind: 'ai' | 'fallback';
  inputScanCount: number | null;
  outcomes: WatchlistPickOutcome[] | null;
  outcomesRecordedAt: string | null;
}

export async function getPostMarketWatchlist(date?: string): Promise<WatchlistRow | null> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  return request<WatchlistRow | null>(`/ai/post-market-watchlist${qs}`);
}

export async function getPostMarketWatchlistHistory(limit = 30): Promise<WatchlistRow[]> {
  return request<WatchlistRow[]>(`/ai/post-market-watchlist/history?limit=${limit}`);
}

export async function runPostMarketWatchlistNow(opts: { targetDate?: string; sourceDate?: string } = {}): Promise<WatchlistRow> {
  return request<WatchlistRow>('/ai/post-market-watchlist/run-now', {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

// ── Hot News (issue #362) ───────────────────────────────────────────────────

export interface HotNewsItem {
  headline: string;
  url: string;
  source: string;
  publishedAt: string | null;
  /** US-listed ticker(s) the story is primarily about. */
  tickers: string[];
  why: string;
  /** 0-100 expected near-term price impact. */
  hotScore: number;
}

export interface HotNewsRow {
  asOf: string | null;
  items: HotNewsItem[];
}

export async function getHotNews(): Promise<HotNewsRow> {
  return request<HotNewsRow>('/hot-news');
}

export interface HotNewsSettings {
  enabled: boolean;
  intervalMinutes: number;
  windowStartHourEt: number;
  windowEndHourEt: number;
  webSearchEnabled: boolean;
  lastRunAt: string | null;
  lastError: string | null;
}

export async function adminGetHotNewsSettings(): Promise<HotNewsSettings> {
  return request<HotNewsSettings>('/admin/settings/hot-news');
}

export async function adminSaveHotNewsSettings(data: Partial<HotNewsSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/hot-news', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminRunHotNewsNow(): Promise<HotNewsRow> {
  return request<HotNewsRow>('/hot-news/run-now', { method: 'POST' });
}

// ── Live News (News Room) ───────────────────────────────────────────────────

/** Which ingestion feed a News Room item came from (derived server-side from the
 *  synthetic id band). The News Room tile filters on this. */
export type LiveNewsProvider = 'benzinga' | 'alphavantage' | 'globenewswire' | 'sec' | 'prnewswire' | 'ai';

export interface LiveNewsItem {
  id: number;
  headline: string;
  source: string;
  author: string;
  summary: string;
  url: string;
  symbols: string[];
  createdAt: string;
  /** AI News Room source only: expected near-term price impact, 0-100. */
  hotScore?: number;
  provider?: LiveNewsProvider;
}

export async function getLiveNews(opts: { sinceId?: number; symbols?: string[]; limit?: number; providers?: LiveNewsProvider[] } = {}): Promise<{ items: LiveNewsItem[]; latestId: number }> {
  const qs = new URLSearchParams();
  if (opts.sinceId != null) qs.set('sinceId', String(opts.sinceId));
  if (opts.symbols && opts.symbols.length) qs.set('symbols', opts.symbols.join(','));
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  if (opts.providers && opts.providers.length) qs.set('providers', opts.providers.join(','));
  const q = qs.toString();
  return request<{ items: LiveNewsItem[]; latestId: number }>(`/live-news${q ? `?${q}` : ''}`);
}

export interface LiveNewsSettings {
  enabled: boolean;
  pollSeconds: number;
  windowStartHourEt: number;
  windowEndHourEt: number;
  lastPollAt: string | null;
  lastError: string | null;
  lastCount: number;
  /** AlphaVantage NEWS_SENTIMENT as a second News Room source. */
  avEnabled: boolean;
  avPollSeconds: number;
  avTopics?: string;
  avLastPollAt: string | null;
  avLastError: string | null;
  avLastCount: number;
  /** GlobeNewswire RSS as a third News Room source (primary press-release wire). */
  gnwEnabled: boolean;
  gnwPollSeconds: number;
  gnwLastPollAt: string | null;
  gnwLastError: string | null;
  gnwLastCount: number;
  /** SEC EDGAR 8-K as a fourth source (primary material-events filings). */
  secEnabled: boolean;
  secPollSeconds: number;
  secLastPollAt: string | null;
  secLastError: string | null;
  secLastCount: number;
  /** PR Newswire (financial feed) as a fifth source. */
  prnEnabled: boolean;
  prnPollSeconds: number;
  prnLastPollAt: string | null;
  prnLastError: string | null;
  prnLastCount: number;
}

export async function adminGetLiveNewsSettings(): Promise<LiveNewsSettings> {
  return request<LiveNewsSettings>('/admin/settings/live-news');
}

export async function adminSaveLiveNewsSettings(data: Partial<LiveNewsSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/live-news', { method: 'POST', body: JSON.stringify(data) });
}

export interface LiveNewsTestResult {
  fetched: number;
  fresh: number;
  av?: { fetched: number; fresh: number };
  gnw?: { fetched: number; fresh: number };
  sec?: { fetched: number; fresh: number };
  prn?: { fetched: number; fresh: number };
  ringCount: number;
  lastError: string | null;
  avLastError?: string | null;
  gnwLastError?: string | null;
  secLastError?: string | null;
  prnLastError?: string | null;
  sample: LiveNewsItem[];
}

export async function adminTestLiveNews(): Promise<LiveNewsTestResult> {
  return request<LiveNewsTestResult>('/admin/settings/live-news/test', { method: 'POST' });
}

// ── Scanner "News" badge ────────────────────────────────────────────────────

export interface ScannerNewsHit {
  headline: string;
  url: string;
  source: string;
  publishedAt: string;
}

/** Which of these scanner tickers have related news on `date` (ET). Live feed
 *  for today, cached AV lookup for past dates. Returns { ticker → hits }
 *  (tickers with no news are omitted). Admin-gated. */
export async function adminGetScannerTickerNews(tickers: string[], date: string): Promise<Record<string, ScannerNewsHit[]>> {
  return request<Record<string, ScannerNewsHit[]>>('/admin/scanner/ticker-news', {
    method: 'POST',
    body: JSON.stringify({ tickers, date }),
  });
}

// ── Options Plan (issue #246, v1 directional-only) ──────────────────────────

export type OptionsDirection = 'calls' | 'puts' | 'neutral';
export type OptionsBucket = 'hot' | 'warm' | 'cold';

export interface OptionsPlanBody {
  direction: OptionsDirection;
  strikeRange: { low: number; high: number; spotAtSuggest: number } | null;
  expirationRange: { minDTE: number; maxDTE: number };
  entry: {
    trigger: string;
    stockPrice: number | null;
    rvolMin: number;
  };
  exit: {
    profitTargetPct: number;
    stopLossPct: number;
    timeStopDTE: number;
    stockStopPrice: number | null;
    stockTargetPrice: number | null;
  };
  riskNotes: string[];
}

export interface OptionsPlanComponents {
  trendAlignment: number;
  rsLeadership: number;
  rsiZone: number;
  volumeConviction: number;
  vsSpy: number;
  atrLiquidity: number;
  earningsProximity: number;
}

export interface OptionsPlanSignals {
  trendDirection: 'up' | 'down' | 'flat';
  rsRating: number | null;
  rsi14: number | null;
  vsSpyPct: number | null;
  earningsToday: boolean;
  daysToEarnings: number | null;
  atr14Pct: number | null;
  scannerHitDays5: number;
  rvolToday: number | null;
  sector: string | null;
  currentPrice: number | null;
}

export interface OptionsPlan {
  ticker: string;
  asOf: string;
  direction: OptionsDirection;
  bucket: OptionsBucket;
  score: number;
  thesis: string;
  components: OptionsPlanComponents;
  plan: OptionsPlanBody;
  signals: OptionsPlanSignals;
}

export async function getOptionsPlan(ticker: string): Promise<OptionsPlan | null> {
  return request<OptionsPlan | null>(`/options-plan/${encodeURIComponent(ticker)}`);
}

export async function getTopOptionsPlans(opts: { limit?: number; direction?: 'calls' | 'puts' } = {}): Promise<OptionsPlan[]> {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.direction) params.set('direction', opts.direction);
  const qs = params.toString();
  return request<OptionsPlan[]>(`/options-plan/top${qs ? `?${qs}` : ''}`);
}

// ── Paper Options Trading (issue #247) ──────────────────────────────────────

export type PaperOptionSide = 'call' | 'put';

export interface PaperChainRow {
  contractId: string;
  ticker: string;
  side: PaperOptionSide;
  strike: number;
  expiration: string;
  snapshotDate: string;
  bid: number | null;
  ask: number | null;
  mark: number | null;
  lastTraded: number | null;
  volume: number | null;
  openInterest: number | null;
  impliedVolatility: number | null;
  delta: number | null;
  gamma: number | null;
  theta: number | null;
  vega: number | null;
  rho: number | null;
  fetchedAt: string;
}

export interface PaperChain {
  ticker: string;
  snapshotDate: string | null;
  rows: PaperChainRow[];
  /** Present when the on-demand chain sync failed and the cache is empty.
   *  UI should surface the message instead of a silent empty state. */
  syncError?: string;
}

export interface PaperPosition {
  id: string;
  userId: string;
  portfolioId: string | null;
  contractId: string;
  ticker: string;
  side: PaperOptionSide;
  strike: number;
  expiration: string;
  contracts: number;
  entryPremium: number;
  entryIv: number;
  entryUnderlyingPrice: number;
  entryDelta: number | null;
  entryGamma: number | null;
  entryTheta: number | null;
  entryVega: number | null;
  entryAt: string;
  currentPremium: number | null;
  currentPnl: number | null;
  currentPnlPct: number | null;
  currentUnderlyingPrice: number | null;
  lastPricedAt: string | null;
  stopLossPct: number | null;
  profitTargetPct: number | null;
  exitPremium: number | null;
  exitAt: string | null;
  exitReason: 'user_close' | 'expired_itm' | 'expired_otm' | 'stop_loss' | 'profit_target' | null;
  exitUnderlyingPrice: number | null;
  sourcePlanDate: string | null;
}

export async function getPaperOptionsChain(ticker: string, opts: { expirationFrom?: string; expirationTo?: string } = {}): Promise<PaperChain> {
  const params = new URLSearchParams();
  if (opts.expirationFrom) params.set('expirationFrom', opts.expirationFrom);
  if (opts.expirationTo) params.set('expirationTo', opts.expirationTo);
  const qs = params.toString();
  return request<PaperChain>(`/paper-options/chain/${encodeURIComponent(ticker)}${qs ? `?${qs}` : ''}`);
}

export async function getPaperPositions(status: 'open' | 'closed' | 'all' = 'all', limit = 100): Promise<PaperPosition[]> {
  return request<PaperPosition[]>(`/paper-options/positions?status=${status}&limit=${limit}`);
}

export async function getPaperPosition(id: string): Promise<PaperPosition> {
  return request<PaperPosition>(`/paper-options/positions/${encodeURIComponent(id)}`);
}

export async function openPaperPosition(opts: {
  portfolioId?: string;
  ticker?: string;
  contractId: string;
  contracts: number;
  stopLossPct?: number;
  profitTargetPct?: number;
  sourcePlanDate?: string;
}): Promise<PaperPosition> {
  return request<PaperPosition>('/paper-options/positions', {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

export async function closePaperPosition(id: string): Promise<PaperPosition> {
  return request<PaperPosition>(`/paper-options/positions/${encodeURIComponent(id)}/close`, { method: 'POST' });
}

export async function getSmartLeaders(opts: SmartLeadersOpts = {}): Promise<SmartLeadersResponse> {
  const qs = new URLSearchParams();
  if (opts.count != null)    qs.set('count', String(opts.count));
  if (opts.period)           qs.set('period', opts.period);
  if (opts.universe)         qs.set('universe', opts.universe);
  if (opts.weights?.momentum != null) qs.set('wMomentum', String(opts.weights.momentum));
  if (opts.weights?.absRs    != null) qs.set('wAbsRs',    String(opts.weights.absRs));
  if (opts.weights?.rsiZone  != null) qs.set('wRsiZone',  String(opts.weights.rsiZone));
  if (opts.weights?.rvol     != null) qs.set('wRvol',     String(opts.weights.rvol));
  if (opts.weights?.analyst  != null) qs.set('wAnalyst',  String(opts.weights.analyst));
  const q = qs.toString();
  return request<SmartLeadersResponse>(`/markets/smart-leaders${q ? `?${q}` : ''}`);
}

export type DeclineTrendItem = {
  ticker: string; name: string; sector: string; price: number;
  streak: number; streakDropPct: number; todayChangePct: number;
  low52: number | null; vsLow52Pct: number | null;
};

export async function getDeclineTrend(opts: { minStreak?: number; limit?: number } = {}): Promise<DeclineTrendItem[]> {
  const qs = new URLSearchParams();
  if (opts.minStreak != null) qs.set('minStreak', String(opts.minStreak));
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  const q = qs.toString();
  return request<DeclineTrendItem[]>(`/markets/decline-trend${q ? `?${q}` : ''}`);
}

// ── Pro Terminal opportunity scanner (plan #291) ────────────────────────────
export type OpportunitySeverity = 'elevated' | 'high' | 'extreme';

export interface OpportunityCandidate {
  ticker: string;
  name: string | null;
  sector: string | null;
  price: number;
  changePct: number;
  peakChangePct: number;
  currentRvol: number;
  peakRvol: number;
  dollarVolume: number;
  severity: OpportunitySeverity;
  reason: string;
  consecutivePasses: number;
  rsRating: number | null;
  rsi14: number | null;
  edgeWinRate1d: number | null;
  edgeWinRate3d: number | null;
  edgeSampleSize: number | null;
  russell1000: boolean;
  detectedAt: string;
  score: number;
  subScores: { conviction: number; volume: number; edge: number; strength: number; vwap: number };
}

export interface IntradayOpportunitiesResponse {
  /** 'open' = inside today's live 9:15-10:15 ET window; 'recap' = last session. */
  window: 'open' | 'recap';
  sessionDate: string | null;
  universe: 'r1000' | 'all' | 'watchlist';
  count: number;
  candidates: OpportunityCandidate[];
}

export interface IntradayOpportunitiesOpts {
  /** 'r1000' (default, Russell 1000 only) or 'all' (expand to every bucket). */
  scope?: 'r1000' | 'all';
  /** Optional ticker filter (watchlist / holdings); overrides scope. */
  tickers?: string[];
  /** Rows to return (default 10, max 25). */
  limit?: number;
  /** Time window: 'open' = the 9:15-10:15 ET scored open window (default);
   *  'last30' = a live rolling feed of events detected in the last 30 minutes. */
  window?: 'open' | 'last30';
}

export async function getIntradayOpportunities(
  opts: IntradayOpportunitiesOpts = {},
): Promise<IntradayOpportunitiesResponse> {
  const qs = new URLSearchParams();
  if (opts.scope) qs.set('scope', opts.scope);
  if (opts.window) qs.set('window', opts.window);
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  if (opts.tickers && opts.tickers.length > 0) qs.set('tickers', opts.tickers.join(','));
  const q = qs.toString();
  return request<IntradayOpportunitiesResponse>(`/opportunities/intraday${q ? `?${q}` : ''}`);
}

// ── Global AI Opportunity Feed ──────────────────────────────────────────────
export interface AiOpportunityFeedPick {
  ticker: string;
  stance: 'enter' | 'watch';
  setup?: 'momentum' | 'buy_the_dip' | 'pullback';
  confidence: number;
  thesis: string;
  referencePrice: number;
  changePct: number;
  currentRvol: number;
  qualificationReason: string;
}

export interface AiOpportunityFeedEntry {
  id: string;
  generatedAt: string;
  status: 'ready' | 'no_candidates';
  marketTone: 'constructive' | 'mixed' | 'risk_off' | null;
  sp500Direction: 'up' | 'flat' | 'down' | null;
  summary: string;
  opportunities: AiOpportunityFeedPick[];
  model: string | null;
  route: string | null;
  scannerEventCount: number;
  market: {
    spyChangePct: number | null;
    qqqChangePct: number | null;
    vix: number | null;
    r1000BreadthPct: number | null;
  };
}

export type AiOpportunityFeedUniverse = 'any' | 'sp500' | 'ndx100' | 'russell1000';

export interface AiOpportunityFeedResponse {
  enabled: boolean;
  cadenceMinutes: number;
  startTimeEt: string;
  endTimeEt: string;
  universe: AiOpportunityFeedUniverse;
  nextRunAt: string | null;
  entries: AiOpportunityFeedEntry[];
  meta: { limit: number; offset: number; hasMore: boolean };
}

export async function getAiOpportunityFeed(limit = 1, offset = 0, cacheBust?: number): Promise<AiOpportunityFeedResponse> {
  const safeLimit = Math.max(1, Math.min(100, Math.trunc(limit)));
  const safeOffset = Math.max(0, Math.min(5_000, Math.trunc(offset)));
  const refreshParam = cacheBust == null ? '' : `&_refresh=${Math.trunc(cacheBust)}`;
  return request<AiOpportunityFeedResponse>(`/opportunities/ai-feed?limit=${safeLimit}&offset=${safeOffset}${refreshParam}`);
}

export interface AiOpportunityFeedRefreshResult {
  fired: boolean;
  status: 'ready' | 'no_candidates' | 'error' | 'skipped';
  candidateCount: number;
  opportunityCount: number;
  skipReason?: string;
}

export async function refreshAiOpportunityFeed(): Promise<AiOpportunityFeedRefreshResult> {
  return request<AiOpportunityFeedRefreshResult>('/opportunities/ai-feed/refresh', { method: 'POST' });
}

/** A row in the live top-volume ranking (GET /api/markets/top-volume). */
export interface TopVolumeRow {
  ticker: string;
  name: string;
  sector: string;
  price: number;
  changePct: number;
  volume: number;
  avgVolume: number | null;
  marketCap: number | null;
  dollarVolume: number;
}

/** Live top-N by share or dollar volume for the current session. */
export async function getTopVolume(
  opts: { n?: number; metric?: 'dollars' | 'shares'; scope?: 'r1000' | 'all' } = {},
): Promise<TopVolumeRow[]> {
  const qs = new URLSearchParams();
  if (opts.n != null) qs.set('n', String(opts.n));
  if (opts.metric) qs.set('metric', opts.metric);
  if (opts.scope) qs.set('scope', opts.scope);
  const q = qs.toString();
  return request<TopVolumeRow[]>(`/markets/top-volume${q ? `?${q}` : ''}`);
}

/** A row in the public RELATIVE-volume ranking (GET /api/markets/top-rvol). */
export interface TopRvolRow {
  ticker: string;
  name: string;
  sector: string;
  price: number;
  changePct: number;
  volume: number;
  avgVolume: number | null;
  marketCap: number | null;
  dollarVolume: number;
  /** Relative volume multiple. today = session volume / avg daily volume;
   *  5m = recent-rate vs baseline (mirrors recentRateRvol). */
  rvol: number | null;
  /** 5m window only — minutes since the last universe sync, and shares printed
   *  in that interval. */
  intervalMinutes?: number | null;
  intervalVolume?: number | null;
}

/** Public top-N by RELATIVE volume. window='today' = session vs avg daily;
 *  window='5m' = recent-rate RVOL since the last full sync (~5 min in RTH). */
export async function getTopRvol(
  opts: { window?: 'today' | '5m'; n?: number; scope?: MoversUniverse } = {},
): Promise<TopRvolRow[]> {
  const qs = new URLSearchParams();
  qs.set('window', opts.window ?? 'today');
  if (opts.n != null) qs.set('n', String(opts.n));
  if (opts.scope) qs.set('scope', opts.scope);
  return request<TopRvolRow[]>(`/markets/top-rvol?${qs.toString()}`);
}

/** Public list of stocks whose session % change falls in [minChangePct,
 *  maxChangePct] (min default 4, max optional = no cap), ranked by change.
 *  `sort` picks the direction ('desc' = biggest first, 'asc' = closest to the
 *  min first). Same row shape + universe scope + liquidity floors as
 *  getTopRvol. Powers the configurable Gainers dashboard tile. */
export async function getTopGainers(
  opts: { n?: number; minChangePct?: number; maxChangePct?: number; sort?: 'asc' | 'desc'; scope?: MoversUniverse } = {},
): Promise<TopRvolRow[]> {
  const qs = new URLSearchParams();
  if (opts.n != null) qs.set('n', String(opts.n));
  if (opts.minChangePct != null) qs.set('minChangePct', String(opts.minChangePct));
  if (opts.maxChangePct != null) qs.set('maxChangePct', String(opts.maxChangePct));
  if (opts.sort) qs.set('sort', opts.sort);
  if (opts.scope) qs.set('scope', opts.scope);
  const q = qs.toString();
  return request<TopRvolRow[]>(`/markets/top-gainers${q ? `?${q}` : ''}`);
}

/** A row in the Opening Drive feed (GET /api/markets/opening-drive) —
 *  tickers the scanner tagged 'opening_drive' (driving up off the 09:30
 *  opening print on heavy opening volume). Reason-scoped, not RVOL-ranked, so
 *  there's no rvol column; price/changePct are the live ticker values,
 *  detectedAt is when the scanner flagged it. */
export interface OpeningDriveRow {
  ticker: string;
  name: string;
  sector: string | null;
  price: number;
  changePct: number;
  detectedAt: string;
}

/** Public Opening Drive feed. `sinceMin` windows back from the latest
 *  opening_drive detection (default 360) rather than from now, so the
 *  morning's names stay visible through the afternoon — the detector only
 *  fires in a 15-minute window, so anchoring to NOW() would blank the tile
 *  every day by mid-session. */
export async function getOpeningDrive(
  opts: { n?: number; sinceMin?: number } = {},
): Promise<OpeningDriveRow[]> {
  const qs = new URLSearchParams();
  if (opts.n != null) qs.set('n', String(opts.n));
  if (opts.sinceMin != null) qs.set('sinceMin', String(opts.sinceMin));
  const q = qs.toString();
  return request<OpeningDriveRow[]>(`/markets/opening-drive${q ? `?${q}` : ''}`);
}

export async function getStocks(): Promise<StockDetail[]> {
  return request<StockDetail[]>('/stocks');
}

export async function getStockDetail(ticker: string, opts: { fresh?: boolean } = {}): Promise<StockDetail> {
  // `fresh=1` forces the server to hit REALTIME_BULK_QUOTES regardless of
  // its DB staleness threshold — only effective for VIP / admin / staff
  // callers (server-side tier gate). Used by the stock-detail auto-
  // refresh tick so admins watching large-caps see live price movement
  // between 5-min priceSync writes.
  const qs = opts.fresh ? '?fresh=1' : '';
  return request<StockDetail>(`/stocks/${ticker}${qs}`);
}

export type SpikeSeverity = 'elevated' | 'high' | 'extreme';
export type BatchStockResult = {
  ticker: string;
  data: StockDetail | null;
  /** Active volume-spike severity from the scanner (null when no live spike). */
  spikeSeverity?: SpikeSeverity | null;
};
export async function getStocksBatch(tickers: string[]): Promise<BatchStockResult[]> {
  if (!tickers.length) return [];
  // Server caps each call at 20 tickers (api/src/routes/stocks.ts:1152) and
  // silently slices anything past — so a 25-ticker watchlist would leave the
  // last 5 tickers stuck in `undefined` quote state forever (loading-pulse
  // dots). Chunk client-side and merge so the contract from the caller's POV
  // is "all your tickers come back."
  const BATCH_SIZE = 20;
  if (tickers.length <= BATCH_SIZE) {
    return request<BatchStockResult[]>(`/stocks/batch?tickers=${tickers.join(',')}`);
  }
  const chunks: string[][] = [];
  for (let i = 0; i < tickers.length; i += BATCH_SIZE) {
    chunks.push(tickers.slice(i, i + BATCH_SIZE));
  }
  const results = await Promise.all(
    chunks.map((chunk) => request<BatchStockResult[]>(`/stocks/batch?tickers=${chunk.join(',')}`)),
  );
  return results.flat();
}

export interface TimelineEvent {
  type: 'earnings' | 'news' | 'congress' | 'insider' | 'split' | 'dividend';
  date: string;
  headline: string;
  detail?: string;
  impact?: { pct: number; dollar: number; nextDate: string } | null;
}

export async function getStockTimeline(ticker: string, days = 365): Promise<{ ohlcv: OHLCV[]; events: TimelineEvent[] }> {
  return request<{ ohlcv: OHLCV[]; events: TimelineEvent[] }>(`/stocks/${ticker}/timeline?days=${days}`);
}

export async function getFinancialStatements(ticker: string): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(`/stocks/${ticker}/financials`);
}

export async function getStockHistory(
  ticker: string,
  days = 365,
  resolution?: 'intraday',
  // Plan #144 — opt in to extended-hours (pre-market + after-hours) bars.
  // Only valid with resolution='intraday'. Stock-detail chart passes true
  // so 1D / 1W views can color-code pre/AH bars in gray.
  extendedHours = false,
  // Plan #144.4 — bar granularity for intraday requests. '1min' is
  // VIP-only (server-side enforced); others get silently downgraded
  // to '5min'. Default '5min' preserves existing behavior.
  bars: '1min' | '5min' = '5min',
  // Drill-down date (YYYY-MM-DD ET) — when set with resolution='intraday',
  // server fetches THAT specific day's bars and falls back to AV's
  // month=YYYY-MM path for dates older than the ~30-day trailing window.
  // Without this the chart's click-to-drill on old dates returned [].
  date?: string,
): Promise<OHLCV[]> {
  const params = resolution === 'intraday'
    ? `resolution=intraday&days=${Math.max(1, days)}${extendedHours ? '&extended=1' : ''}${bars === '1min' ? '&bars=1min' : ''}${date ? `&date=${encodeURIComponent(date)}` : ''}`
    : `days=${days}`;
  return request<OHLCV[]>(`/stocks/${ticker}/history?${params}`);
}

/** Metadata the history endpoint returns alongside `data`. `bars` is the
 *  ACTUAL served resolution (after any server-side downgrade), which the
 *  terminal's data-tier badge reads to tell the truth about what's on
 *  screen. */
export interface StockHistoryMeta {
  resolution?: string;
  days?: number;
  extendedHours?: boolean;
  bars?: '1min' | '5min';
  barGapMin?: number;
  fallbackFromDaily?: boolean;
}

/** Like getStockHistory but preserves the response `meta`. The plain
 *  getStockHistory unwraps `data` and discards `meta` (so callers can't see
 *  the served resolution); the Pro Terminal fetches through here so its
 *  data-tier badge reflects whether 1-min or 5-min bars actually came back. */
export async function getStockHistoryWithMeta(
  ticker: string,
  opts: {
    days?: number;
    resolution?: 'intraday';
    extendedHours?: boolean;
    bars?: '1min' | '5min';
    date?: string;
  } = {},
): Promise<{ data: OHLCV[]; meta: StockHistoryMeta }> {
  const { days = 365, resolution, extendedHours = false, bars = '5min', date } = opts;
  const params = resolution === 'intraday'
    ? `resolution=intraday&days=${Math.max(1, days)}${extendedHours ? '&extended=1' : ''}${bars === '1min' ? '&bars=1min' : ''}${date ? `&date=${encodeURIComponent(date)}` : ''}`
    : `days=${days}`;
  const response = await fetch(`${BASE_URL}/stocks/${ticker}/history?${params}`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  const text = await response.text();
  if (!text) return { data: [], meta: {} };
  const json = JSON.parse(text);
  if (Array.isArray(json)) return { data: json as OHLCV[], meta: {} };
  return { data: (json?.data ?? []) as OHLCV[], meta: (json?.meta ?? {}) as StockHistoryMeta };
}

// Issue #148 — Ask AI (VIP-only stock analysis)
/** Error shape thrown by askAiPrompts() — exposes the server error code +
 *  retry hint so callers can render cooldown countdowns or "try again" text. */
export class AskAiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly retryAfterSec?: number,
  ) {
    super(message);
    this.name = 'AskAiClientError';
  }
}

export interface AskAiPromptsPayload {
  side: 'buy' | 'sell';
  signal:   { systemPrompt: string; userPrompt: string };
  /** stage and longterm are buy-side only; null when side='sell'. */
  stage:    { systemPrompt: string; userPrompt: string } | null;
  longterm: { systemPrompt: string; userPrompt: string } | null;
}

/** Fetch the AI prompts for this ticker without running the AI. Buy
 *  side returns {signal, stage, longterm}; sell side returns {signal}
 *  only (stage and longterm are buy-only by contract). Lets users
 *  review + copy what's about to be sent to Claude before consuming a
 *  quota slot. VIP-gated; no rate limit. */
export async function askAiPrompts(ticker: string, side: 'buy' | 'sell' = 'buy'): Promise<AskAiPromptsPayload> {
  const response = await fetch(`${BASE_URL}/stocks/${ticker}/ask-ai/prompts?side=${side}`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    let code = 'UNKNOWN';
    let message = `Failed to load prompts (HTTP ${response.status})`;
    try {
      const body = await response.json();
      code = body?.error?.code ?? code;
      message = body?.error?.message ?? message;
    } catch { /* ignore */ }
    throw new AskAiClientError(code, message, response.status);
  }
  const body = await response.json();
  return body.data as AskAiPromptsPayload;
}

// Plan #138 — chart event markers (earnings / dividend / split)
export interface StockChartEvent {
  date: string;                 // YYYY-MM-DD
  /** 'signal', 'golden_cross' and 'death_cross' are client-derived (the
   *  trend-signal flip feed; the chart's own daily bars), not served by
   *  /stocks/:ticker/events — they're folded into the same array so they render
   *  through the identical badge + popover path. */
  type: 'earnings' | 'dividend' | 'split' | 'news' | 'signal' | 'golden_cross' | 'death_cross';
  /** Text drawn inside the circle. Empty for 'signal' and 'death_cross', which
   *  draw a glyph. */
  label: 'E' | 'D' | 'S' | 'N' | 'G' | '';
  details: {
    // golden / death cross — both averages on the crossing bar (its close is `price`)
    sma50?: number | null;
    sma200?: number | null;
    /** When the averages crossed back the other way (the next opposite cross),
     *  if they have. */
    crossedBackOn?: string | null;
    /** That opposite cross is on today's still-trading bar — it can undo too. */
    crossedBackLive?: boolean;
    /** Crossed on today's still-trading session — can undo before the close. */
    live?: boolean;
    // news (per-day headlines for the chart "N" badge)
    headlines?: { headline: string; url: string; source: string; publishedAt: string }[];
    // signal flip (plan #363) — the transition and the bar it scored from
    signal?: SignalTier | null;
    prevSignal?: SignalTier | null;
    reason?: string | null;
    price?: number | null;
    changePct?: number | null;
    rsi?: number | null;
    // earnings
    time?: string | null;
    epsEstimate?: number | null;
    fiscalQuarter?: string | null;
    // dividend
    amount?: number | null;
    frequency?: string | null;
    annualDividend?: number | null;
    paymentDate?: string | null;
    /** The issuer's cadence carried past its last published month — a
     *  forecast, not an announced date (VistaShares funds). */
    projected?: boolean;
    // split
    fromFactor?: number;
    toFactor?: number;
    ratio?: string | null;
  };
}

/** Multi-event feed (earnings + dividends + splits) for the StockChart's
 *  inline E/D/S markers. DB-only — no provider fallback, so a sparse
 *  earnings_calendar gives an empty list. Use this only when missing
 *  events are tolerable (markers just don't render). For an
 *  earnings-only view that should always populate (calendar page,
 *  EarningsHistoryCard), use getEarnings() instead — same underlying
 *  table but with a live-provider fallback. */
export async function getStockEvents(ticker: string, start?: string, end?: string): Promise<StockChartEvent[]> {
  const p = new URLSearchParams();
  if (start) p.set('start', start);
  if (end) p.set('end', end);
  const qs = p.toString();
  const res = await request<{ events: StockChartEvent[] }>(`/stocks/${ticker}/events${qs ? `?${qs}` : ''}`);
  return res.events;
}

/** Per-day NEWS events for the chart's "N" badges (news room + AV back-fill,
 *  bucketed by ET day). Same shape as getStockEvents so they render through the
 *  StockChart's existing badge + popover path. */
export async function getStockNewsEvents(ticker: string, days = 120): Promise<StockChartEvent[]> {
  const res = await request<{ events: StockChartEvent[] }>(`/stocks/${ticker}/news-events?days=${days}`);
  return res.events;
}

export interface ScannerEvent {
  /** ISO timestamp the qualification fired. */
  detectedAt: string;
  /** Qualification reason string — keys map to REASON_COLOR / REASON_LABEL
   *  in lib/chart/scannerMarkers. Reasons not in the color map (e.g. internal
   *  diagnostic types) render as a fallback gray dot. */
  reason: string;
  severity: string | null;
  phase: string | null;
  /** AI-validation badge, live_signal_buy/sell rows only (null elsewhere).
   *  'approved' upgrades the chart marker circle → triangle (arrowUp/Down);
   *  every other value (pending/rejected/timeout/error/null) stays a circle. */
  aiValidation?: 'pending' | 'approved' | 'rejected' | 'timeout' | 'error' | string | null;
}

export async function getScannerEvents(ticker: string, days = 1, date?: string): Promise<ScannerEvent[]> {
  // When a YYYY-MM-DD ET date is supplied, the server returns events
  // for that specific session (used by the chart's historical date
  // picker). Without `date`, falls back to the rolling N-day window.
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return request<ScannerEvent[]>(`/stocks/${ticker}/scanner-events?date=${date}`);
  }
  return request<ScannerEvent[]>(`/stocks/${ticker}/scanner-events?days=${Math.max(1, Math.min(30, days | 0))}`);
}

export interface ChartLevel {
  /** Volume-weighted (or arithmetic) mean price of the level cluster. */
  price: number;
  /** How many pivots fall in this cluster — a "touch" count. */
  touches: number;
  /** Composite score (touches × recency × max-rejection). Higher is
   *  stronger. Used by the chart to size line thickness. */
  score: number;
  /** Bar.date of the most recent touch — opaque string passed through
   *  from the server (5-min, 30-min, or daily depending on range). */
  lastTouchedAt: string;
  /** Largest single % rejection seen across the cluster's touches. */
  maxRejectionPct: number;
}

/** Latest trading session's aggregated OHLC, for the watchlist S/R view's
 *  day-range band. Only populated by the batched /watchlists/levels endpoint;
 *  the single-ticker /stocks/:ticker/levels leaves it undefined. */
export interface ChartLevelDayBar {
  /** Session date "YYYY-MM-DD" ET. */
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface ChartLevelsResponse {
  resistance: ChartLevel[];
  support: ChartLevel[];
  /** ATR(14) at the last bar — chart can show "cluster width = ATR ×
   *  multiplier" diagnostic. NaN when too few bars. */
  atrAtLast: number;
  /** Most-recent session's high/low/open/close (watchlist batch only). */
  day?: ChartLevelDayBar | null;
}

export async function getChartLevels(
  ticker: string,
  range: '1d' | '1w' | '1m' | '3m' | '6m' | '1y',
): Promise<ChartLevelsResponse> {
  return request<ChartLevelsResponse>(`/stocks/${ticker}/levels?range=${range}`);
}

/** One detected chart pattern. The API resolves bar indices to
 *  bar dates server-side so the chart layer can map directly to
 *  Lightweight Charts time values without needing to share an
 *  index-to-bar alignment with the detector. */
export interface ChartPatternPoint {
  date: string;
  price: number;
  label?: string;
}
export interface ChartPatternLine {
  a: { date: string; price: number };
  b: { date: string; price: number };
  style: 'neckline' | 'resistance' | 'support' | 'channel' | 'pole' | 'trendline';
}
/** Not currently emitted by the API — the chart layer has no rect-
 *  overlay primitive in lightweight-charts. Type kept so a future
 *  candle-shading path can opt in without breaking changes. */
export interface ChartPatternShading {
  startDate: string;
  endDate: string;
  color: 'green' | 'red' | 'purple' | 'neutral';
}
export type ChartPatternKind =
  | 'double_top' | 'double_bottom' | 'triple_top' | 'triple_bottom'
  | 'head_and_shoulders' | 'inverse_head_and_shoulders'
  | 'rising_wedge' | 'falling_wedge'
  | 'ascending_triangle' | 'descending_triangle' | 'symmetric_triangle'
  | 'bull_flag' | 'bear_flag'
  | 'cup_and_handle';
export interface ChartPattern {
  kind: ChartPatternKind;
  confidence: number;
  active?: boolean;
  startDate: string;
  endDate: string;
  points: ChartPatternPoint[];
  lines?: ChartPatternLine[];
  shading?: ChartPatternShading;
}
export interface ChartPatternsResponse {
  patterns: ChartPattern[];
  byKind: Record<string, number>;
  barCount: number;
}

export async function getChartPatterns(
  ticker: string,
  range: '1d' | '1w' | '1m' | '3m' | '6m' | '1y',
  minConfidence: number = 0.7,
): Promise<ChartPatternsResponse> {
  return request<ChartPatternsResponse>(
    `/stocks/${ticker}/chart-patterns?range=${range}&minConfidence=${minConfidence}`,
  );
}

export async function getETFs(): Promise<ETFDetail[]> {
  return request<ETFDetail[]>('/etf');
}

export async function getETF(ticker: string, opts: { fresh?: boolean } = {}): Promise<ETFDetail> {
  // `fresh=1` forces the server to hit the provider directly regardless
  // of the priceSync schedule — only effective for VIP / admin / staff
  // callers (server-side tier gate at routes/etf.ts). Mirrors
  // getStockDetail's opt-in so the ETF detail page's auto-refresh tick
  // can pull live quotes between the 5-min priceSync writes.
  const qs = opts.fresh ? '?fresh=1' : '';
  return request<ETFDetail>(`/etf/${ticker}${qs}`);
}

export async function getIPOs(): Promise<IPO[]> {
  return request<IPO[]>('/ipos');
}

/** A live bulk quote from the shared terminal-quote cache (GET /stocks/quotes).
 *  The server coalesces all clients/symbols into ~1 provider call per 20s. */
export interface LiveQuote {
  symbol: string;
  price: number;
  change: number;
  changePct: number;
  prevClose: number;
  /** Cumulative regular-session volume so far today, or null when the provider
   *  has none (pre-market on AV). Absent entirely on the closed-session branch
   *  of /stocks/quotes, hence optional. Consumers derive the in-progress bar
   *  volume by subtracting the completed bars total from this. */
  volume?: number | null;
}

export async function getLiveQuotes(symbols: string[]): Promise<LiveQuote[]> {
  const syms = [...new Set(symbols.map((s) => s.trim().toUpperCase()).filter(Boolean))];
  if (!syms.length) return [];
  return request<LiveQuote[]>(`/stocks/quotes?syms=${encodeURIComponent(syms.join(','))}`);
}

export async function getNews(ticker?: string, feedIds?: string[]): Promise<NewsArticle[]> {
  const params = new URLSearchParams();
  if (ticker) params.set('ticker', ticker);
  if (feedIds && feedIds.length > 0) params.set('feedIds', feedIds.join(','));
  const qs = params.toString();
  return request<NewsArticle[]>(`/news${qs ? `?${qs}` : ''}`);
}

// ── Public: AI-generated daily news summary (issue #150) ────────────────

export interface PublicNewsPick {
  rank: number;
  headline: string;
  source: string;
  url: string;
  tickers: string[];
  note: string;
}

export interface PublicNewsDailySummary {
  date: string;
  digest: string;
  picks: PublicNewsPick[];
  marketTone: 'bullish' | 'neutral' | 'bearish' | null;
  createdAt: string;
}

export async function getDailyNewsSummary(): Promise<PublicNewsDailySummary | null> {
  const res = await request<{ summary: PublicNewsDailySummary | null }>(`/news/daily-summary`);
  return res.summary;
}

export type NewsFeedPublic = { id: string; name: string; enabled: boolean };

export async function getNewsFeeds(): Promise<NewsFeedPublic[]> {
  return request<NewsFeedPublic[]>('/news/feeds');
}

export async function getNewsFeedPreferences(): Promise<{ feedIds: string[] | null }> {
  return request<{ feedIds: string[] | null }>('/news/preferences');
}

export async function saveNewsFeedPreferences(feedIds: string[]): Promise<{ feedIds: string[] }> {
  return request<{ feedIds: string[] }>('/news/preferences', {
    method: 'PUT',
    body: JSON.stringify({ feedIds }),
  });
}

export async function searchStocks(query: string): Promise<SearchResult[]> {
  return request<SearchResult[]>(`/stocks/search?q=${encodeURIComponent(query)}`);
}

export async function getAdSlots(): Promise<AdSlot[]> {
  return request<AdSlot[]>('/admin/public/ads');
}

// Auth
export async function login(username: string, password: string): Promise<AuthPayload> {
  return request<AuthPayload>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export interface DeviceVerificationRequired {
  code: 'DEVICE_VERIFICATION_REQUIRED';
  message: string;
  deviceToken: string;
  email: string;
}

export async function verifyDevice(deviceToken: string, code: string): Promise<AuthPayload> {
  return request<AuthPayload>('/auth/verify-device', {
    method: 'POST',
    body: JSON.stringify({ deviceToken, code }),
  });
}

export interface TrustedDevice {
  id: string;
  label: string;
  ip: string;
  last_used_at: string;
  created_at: string;
}

export async function getTrustedDevices(): Promise<TrustedDevice[]> {
  return request<TrustedDevice[]>('/auth/trusted-devices');
}

export async function deleteTrustedDevice(id: string): Promise<void> {
  await request(`/auth/trusted-devices/${id}`, { method: 'DELETE' });
}

// ── Trending ────────────────────────────────────────────────────────────────
export interface TrendingItem {
  rank: number;
  ticker: string;
  name: string;
  price: number;
  changePct: number;
  sector: string;
  viewCount: number;
}

export async function getTrending(period: 'daily' | 'weekly' = 'daily'): Promise<{ period: string; items: TrendingItem[] }> {
  return request<{ period: string; items: TrendingItem[] }>(`/analytics/trending?period=${period}`);
}

export async function googleLogin(credential: string): Promise<AuthPayload> {
  return request<AuthPayload>('/auth/google', {
    method: 'POST',
    body: JSON.stringify({ credential }),
  });
}

export async function register(username: string, email: string, password: string, firstName?: string, lastName?: string, displayName?: string): Promise<AuthPayload> {
  return request<AuthPayload>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ username, email, password, firstName, lastName, displayName }),
  });
}

export async function forgotUsername(email: string): Promise<{ message: string }> {
  return request<{ message: string }>('/auth/forgot-username', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function checkUsernameAvailable(username: string): Promise<{ available: boolean; reason?: string }> {
  return request<{ available: boolean; reason?: string }>(`/auth/check-username/${encodeURIComponent(username)}`);
}

export async function requestLoginCode(email: string): Promise<{ message: string }> {
  return request<{ message: string }>('/auth/login-code/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyLoginCode(email: string, code: string): Promise<AuthPayload> {
  return request<AuthPayload>('/auth/login-code/verify', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
}

export async function forgotPassword(email: string): Promise<{ message: string }> {
  return request<{ message: string }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token: string, password: string): Promise<AuthPayload> {
  return request<AuthPayload>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export async function logout(): Promise<void> {
  return request<void>('/auth/logout', { method: 'POST' });
}

export async function getMe(): Promise<User> {
  return request<User>('/auth/me');
}

export async function updateMe(data: { email?: string; password?: string; currentPassword?: string }): Promise<User> {
  return request<User>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function updateProfile(data: { firstName?: string; lastName?: string; displayName?: string; timezone?: string }): Promise<User> {
  return request<User>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function updateAvatar(avatarUrl: string | null): Promise<{ avatarUrl: string | null }> {
  return request<{ avatarUrl: string | null }>('/auth/avatar', {
    method: 'PATCH',
    body: JSON.stringify({ avatarUrl }),
  });
}

export async function createCheckoutSession(): Promise<string> {
  const res = await request<{ url: string }>('/stripe/checkout', { method: 'POST' });
  return res.url;
}

export async function createPortalSession(): Promise<string> {
  const res = await request<{ url: string }>('/stripe/portal', { method: 'POST' });
  return res.url;
}

export async function cancelSubscription(): Promise<{ message: string; periodEnd: string }> {
  return request<{ message: string; periodEnd: string }>('/stripe/cancel', { method: 'POST' });
}

export async function refreshToken(): Promise<AuthPayload> {
  return request<AuthPayload>('/auth/refresh', { method: 'POST' });
}

export async function verifyEmail(token: string): Promise<AuthPayload> {
  return request<AuthPayload>('/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) });
}

export async function resendVerification(): Promise<{ message: string }> {
  return request<{ message: string }>('/auth/resend-verification', { method: 'POST' });
}

export async function resendVerificationByEmail(email: string): Promise<{ message: string }> {
  return request<{ message: string }>('/auth/resend-verification-email', { method: 'POST', body: JSON.stringify({ email }) });
}

export async function dismissTrialPopup(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/auth/dismiss-trial-popup', { method: 'POST' });
}

export async function deleteAccount(): Promise<{ message: string }> {
  return request<{ message: string }>('/auth/delete-account', { method: 'POST' });
}

// Admin
export type AdminStats = {
  totalUsers: number; newUsersThisWeek: number;
  proSubscribers: number; newProThisWeek: number;
  activeAds: number; totalComments: number; newCommentsToday: number;
  onlineUsers: number; newsletterSubscribers: number;
  totalPortfolios: number; totalAlerts: number;
  recentUsers: Array<{ id: string; email: string; subscriptionTier: string; role: string; createdAt: string }>;
};
export async function adminGetStats(): Promise<AdminStats> {
  return request<AdminStats>('/admin/stats');
}

export interface SyncStatusOverview {
  globalSyncRules: { pauseAll: boolean; weekdaysOnly: boolean; marketHoursOnly: boolean };
  priceSync: { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null };
  ipoSync: { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null; intervalHours?: number };
  indexSync?: { enabled: boolean; status: 'idle' | 'running'; lastError: string | null; lastBackfillAt: string | null; lastTopUpAt: string | null; nextScheduledAt: string | null };
  dividendSync: { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null; intervalHours?: number };
  splitSync: { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null; intervalHours?: number };
  insiderSync: { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null; intervalHours?: number };
  congressSync: { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null; intervalHours?: number };
  companyProfileSync?: { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null; intervalHours?: number };
  fundamentalsSync?:   { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null; intervalHours?: number };
  commoditySync?:      { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null; intervalMinutes?: number };
  earningsSync?:       { enabled: boolean; status: string; lastError: string | null; lastSyncAt: string | null; intervalHours?: number };
  financialSync?: {
    enabled: boolean;
    status: string;
    lastError: string | null;
    lastSyncAt: string | null;
    intervalHours?: number;
    scheduledEnabled?: boolean;
    scope?: 'sp500' | 'russell1000';
    frequency?: 'weekly' | 'monthly';
    scheduledDay?: number;
    scheduledTime?: string;
    nextScheduledAt?: string | null;
    syncTotal?: number;
    syncDone?: number;
    runScope?: 'sp500' | 'russell1000' | null;
  };
  intradayWarm?: {
    enabled: boolean;
    status: 'idle' | 'running';
    lastRunAt: string | null;
    lastRunCount: number;
    lastRunDurationMs: number;
    lastRunErrors: number;
    lastError: string | null;
    nextScheduledAt: string | null;
  };
  /** Daily 4:15 PM ET fill of `ohlcv_cache_intraday` for every ticker
   *  that fired scanner_events that day. Powers the open/close columns
   *  on /portfolio/scanner Captures + Streaks tabs without burning
   *  provider quota during market hours. Surfaced on the admin sync
   *  dashboard so a missed run is visible at a glance. */
  scannerOhlcSync?: {
    enabled: boolean;
    status: 'idle' | 'running';
    lastRunAt: string | null;
    lastError: string | null;
    fireTimeEt?: string;
    lastRunCount?: number;
    lastRunErrors?: number;
    lastRunDurationMs?: number;
    lastRunDate?: string;
    nextScheduledAt: string | null;
  };
  heatmap: { enabled: boolean; refreshMinutes: number; lastRefreshedAt: string | null };
  tickerSync: {
    enabled: boolean;
    status: string;
    lastSyncAt: string | null;
    syncIntervalDays?: number;
    /** Per-ticker tally from the most recently completed run. Both 0
     *  until the first run lands. */
    lastSyncedCount?: number;
    lastFailedCount?: number;
  };
  /** Plan #265 — discovery sync schedule (daily/weekly + ET clock time).
   *  Surfaced on the admin dashboard as a tile so the operator can see
   *  the next scheduled fire and last-run counts without leaving the
   *  page. */
  tickerDiscovery?: {
    enabled: boolean;
    status: 'idle' | 'running' | 'error';
    cadence?: 'daily' | 'weekly';
    dayOfWeekEt?: number;
    fireTimeEt?: string;
    lastRunAt: string | null;
    lastNewCount: number;
    lastDelistedCount: number;
    lastError: string | null;
    nextScheduledAt: string | null;
  };
  nasdaqDir: { enabled: boolean; status: string; lastSyncAt: string | null; syncIntervalDays?: number };
  rsRating?: {
    enabled: boolean;
    /** 'idle' = nothing in flight; 'running' = backfilling OHLCV; 'ranking' = computing the cross-section. */
    status: 'idle' | 'running' | 'ranking';
    scheduleHourEt: number;
    lastSyncAt: string | null;
    lastError: string | null;
    nextScheduledAt: string | null;
    progress: {
      processed: number;
      total: number;
      backfilled: number;
      triggeredBy: 'cron' | 'force' | 'force-ticker';
    } | null;
  };
}

export async function adminGetSyncStatus(): Promise<SyncStatusOverview> {
  return request<SyncStatusOverview>('/admin/sync-status');
}

export async function adminGetUsers(): Promise<User[]> {
  return request<User[]>('/admin/users');
}

export async function adminUpdateUser(id: string, data: { role?: string; subscriptionTier?: string; isBanned?: boolean }): Promise<User> {
  return request<User>(`/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function adminCreateUser(data: {
  username?: string; email: string; firstName?: string; lastName?: string; role?: string; subscriptionTier?: string; password?: string;
}): Promise<User & { tempPassword?: string }> {
  return request('/admin/users', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminDeleteUser(id: string): Promise<void> {
  return request<void>(`/admin/users/${id}`, { method: 'DELETE' });
}
export async function adminResendVerification(email: string): Promise<void> {
  await request('/auth/resend-verification-email', { method: 'POST', body: JSON.stringify({ email }) });
}

// Issue #203 — admin approves a prescreen account, which flips status to
// 'active' and fires the regular verification email.
export async function adminApprovePrescreen(id: string): Promise<{ message: string; already: boolean }> {
  return request<{ message: string; already: boolean }>(`/admin/users/${id}/approve-prescreen`, { method: 'POST' });
}

export type PrescreenSettings = { enabled: boolean };
export async function adminGetPrescreenSettings(): Promise<PrescreenSettings> {
  return request<PrescreenSettings>('/admin/settings/prescreen');
}
export async function adminSavePrescreenSettings(settings: Partial<PrescreenSettings>): Promise<{ prescreen: PrescreenSettings }> {
  return request<{ prescreen: PrescreenSettings }>('/admin/settings/prescreen', { method: 'PUT', body: JSON.stringify(settings) });
}

// Admin Settings
export type SmtpSettings = {
  host: string; port: number; username: string; password: string;
  fromEmail: string; fromName: string; encryption: 'none' | 'tls' | 'ssl'; enabled: boolean;
  emailProvider: 'smtp' | 'resend'; resendApiKey: string;
};
export type TierAccess = { free: boolean; pro: boolean; full: boolean };
export type FeatureFlags = {
  proSubscriptions: TierAccess; advancedCharts: TierAccess; stockScreener: TierAccess;
  earningsCalendar: TierAccess; portfolioTracker: TierAccess; priceAlerts: TierAccess;
  apiAccess: TierAccess; exportData: TierAccess; intradayChart: TierAccess;
  financials: TierAccess; financialHealthSummary: TierAccess;
};
export type NewsFeed = {
  id: string; name: string; url: string;
  type: 'rss' | 'newsapi' | 'custom'; apiKey: string; enabled: boolean;
};
export type EmailSettings = {
  contactEmail: string;
  supportEmail: string;
};
export type SiteSettings = { smtp: SmtpSettings; features: FeatureFlags; newsFeeds: NewsFeed[]; emails: EmailSettings };

export async function adminGetSettings(): Promise<SiteSettings> {
  return request<SiteSettings>('/admin/settings');
}
export async function adminSaveSmtp(data: Partial<SmtpSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/smtp', { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminTestSmtp(to: string): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/smtp/test', { method: 'POST', body: JSON.stringify({ to }) });
}
export async function adminSaveFeatures(data: Partial<FeatureFlags>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/features', { method: 'PUT', body: JSON.stringify(data) });
}

export interface DebugSettings {
  sqlEnabled: boolean;
}

export async function adminGetDebugSettings(): Promise<DebugSettings> {
  return request<DebugSettings>('/admin/settings/debug');
}

export async function adminSaveDebugSettings(data: Partial<DebugSettings>): Promise<{ message: string; debug: DebugSettings }> {
  return request<{ message: string; debug: DebugSettings }>('/admin/settings/debug', { method: 'PUT', body: JSON.stringify(data) });
}

/** TEMPORARY admin debug — wipes holdings, transactions, open orders, and
 *  auto-trade events for every portfolio belonging to the calling admin,
 *  then resets each portfolio's cash to $100k. Auto-trade rules are
 *  preserved (they're configuration). Server requires `confirm: 'RESET'`
 *  in the body to proceed. */
export interface AdminResetAllPortfoliosResult {
  portfoliosReset: number;
  transactionsDeleted: number;
  positionsDeleted: number;
  openOrdersDeleted: number;
  eventsDeleted: number;
}
export async function adminResetAllPortfolios(): Promise<AdminResetAllPortfoliosResult> {
  return request<AdminResetAllPortfoliosResult>('/admin/portfolio-variants/reset-all-portfolios', {
    method: 'POST',
    body: JSON.stringify({ confirm: 'RESET' }),
  });
}

/** Debug helper — generates the matrix of AutoGenerated portfolios on a
 *  clean baseline. Each starts at $100k with category-tuned exits and the
 *  current recommended gates pre-applied.
 *  Re-runs are idempotent on the `[A<N>]` number prefix: a variant whose
 *  position N already has a portfolio gets renamed in place when its
 *  description changes; only genuinely-new positions INSERT. Pass
 *  `reasons` to scope the generate to specific qualification signals
 *  (e.g. only `recent_rvol` + `orb_breakout`). */
export interface AdminGenerateVariationPortfoliosResult {
  created: Array<{ portfolioId: string; name: string; severity: string; category: string; label: string }>;
  skipped: Array<{ name: string; severity: string; category: string; label: string }>;
  renamed: Array<{ portfolioId: string; oldName: string; newName: string; severity: string; category: string; label: string }>;
}
export async function adminGenerateVariationPortfolios(reasons?: string[]): Promise<AdminGenerateVariationPortfoliosResult> {
  return request<AdminGenerateVariationPortfoliosResult>('/admin/portfolio-variants/generate', {
    method: 'POST',
    body: JSON.stringify({
      confirm: 'GENERATE',
      ...(Array.isArray(reasons) && reasons.length > 0 ? { reasons } : {}),
    }),
  });
}

/** Debug helper — applies the latest generator's category-tuning defaults
 *  (stop_loss_pct / take_profit_giveback_pct) to every existing
 *  AutoGenerated portfolio in place, without touching positions or
 *  transactions. Variants 10-12 (GB ladder) keep their giveback
 *  overrides; GB90 (variant 13, when present) is disabled. */
export interface AdminApplyAutogeneratedTuningsResult {
  portfoliosUpdated: number;
  stopLossUpdates: number;
  givebackUpdates: number;
  disabled: number;
}
export async function adminApplyAutogeneratedTunings(): Promise<AdminApplyAutogeneratedTuningsResult> {
  return request<AdminApplyAutogeneratedTuningsResult>('/admin/portfolio-variants/apply-tunings', {
    method: 'POST',
    body: JSON.stringify({ confirm: 'APPLY' }),
  });
}

/** Debug helper — fully deletes every AutoGenerated portfolio (source =
 *  'auto-generated') owned by the caller, including all child rows
 *  (rules, transactions, positions, open orders, auto-trade events).
 *  Manually-created portfolios are never touched. Intended sister to
 *  adminGenerateVariationPortfolios — wipe the matrix to start fresh. */
export interface AdminDeleteAutogeneratedPortfoliosResult {
  portfoliosDeleted: number;
  transactionsDeleted: number;
  positionsDeleted: number;
  openOrdersDeleted: number;
  eventsDeleted: number;
  rulesDeleted: number;
}
export async function adminDeleteAutogeneratedPortfolios(): Promise<AdminDeleteAutogeneratedPortfoliosResult> {
  return request<AdminDeleteAutogeneratedPortfoliosResult>('/admin/portfolio-variants/delete-autogenerated', {
    method: 'POST',
    body: JSON.stringify({ confirm: 'DELETE' }),
  });
}

/** Read-only enumeration of the variant matrix served by
 *  api/src/routes/admin/portfolioVariants.ts. The new admin Portfolios
 *  Generator page uses this to render its mirror table without
 *  duplicating the static data. */
export interface PortfolioVariantRow {
  n: number;
  label: string;
  severity: 'elevated' | 'high' | 'extreme';
  category: 'penny' | 'midrange' | 'quality';
  acceptedReasons: string[];
  stopLossPct: number;
  takeProfitGivebackPct: number;
  minFakeoutLossPct: number;
  maxMinutesSinceLastHigh: number;
  maxPctBelowPremarketHigh: number;
  maxPctOffSessionHigh: number;
  requireAboveSma20: boolean;
  requireUptrendRegime: boolean;
  requireHtfTrendAgreement: boolean;
  takeProfitPct: number | null;
  tickerAllowlist: string[];
  enrolledCategories: string[];
  minPeakRvol: number;
  confirmationPasses: number;
  maxConcurrent: number;
  maxTradesPerDay: number;
}
export async function adminListPortfolioVariants(): Promise<{ total: number; variants: PortfolioVariantRow[] }> {
  return request<{ total: number; variants: PortfolioVariantRow[] }>('/admin/portfolio-variants/list');
}

export async function adminGetNewsFeeds(): Promise<NewsFeed[]> {
  return request<NewsFeed[]>('/admin/settings/newsfeeds');
}
export async function adminSaveNewsFeeds(feeds: NewsFeed[]): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/newsfeeds', { method: 'PUT', body: JSON.stringify(feeds) });
}
export interface FeedPreviewResult {
  count: number;
  articles: { headline: string; source: string; publishedAt: string; url: string }[];
  error?: string;
}
export async function adminPreviewNewsFeed(url: string): Promise<FeedPreviewResult> {
  return request<FeedPreviewResult>('/admin/settings/newsfeeds/preview', { method: 'POST', body: JSON.stringify({ url }) });
}
export async function adminTestDataProvider(params: { provider: string; apiKey?: string }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/dataprovider/test', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function adminGetEmailSettings(): Promise<EmailSettings> {
  return request<EmailSettings>('/admin/settings/emails');
}
export async function adminSaveEmailSettings(data: Partial<EmailSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/emails', { method: 'PUT', body: JSON.stringify(data) });
}

// Analytics
export async function trackPageView(path: string, userId?: string): Promise<void> {
  try {
    await request<void>('/analytics/pageview', {
      method: 'POST',
      body: JSON.stringify({ path, userId }),
    });
  } catch {
    // never surface analytics errors
  }
}

export type PageViewRecord = { id: string; visitor: string; path: string; created_at: string };

export async function adminGetPageViews(): Promise<PageViewRecord[]> {
  return request<PageViewRecord[]>('/analytics/pageviews?limit=10000');
}

export async function adminDeletePageViews(ids: string[]): Promise<{ deleted: number }> {
  return request<{ deleted: number }>('/analytics/pageviews', {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
}

// Newsletter
export type NewsletterSubscriber = {
  id: string; email: string; status: 'active' | 'unsubscribed';
  subscribed_at: string; unsubscribed_at: string | null; user_email: string | null;
};

export async function subscribeNewsletter(email: string, userId?: string): Promise<{ message: string }> {
  return request<{ message: string }>('/newsletter/subscribe', {
    method: 'POST',
    body: JSON.stringify({ email, userId }),
  });
}

export async function adminGetNewsletterSubscribers(): Promise<NewsletterSubscriber[]> {
  return request<NewsletterSubscriber[]>('/newsletter/admin/subscribers');
}

export async function adminUpdateNewsletterSubscriber(id: string, status: 'active' | 'unsubscribed'): Promise<{ message: string }> {
  return request<{ message: string }>(`/newsletter/admin/subscribers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function adminDeleteNewsletterSubscriber(id: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/newsletter/admin/subscribers/${id}`, { method: 'DELETE' });
}

export async function adminSendNewsletter(data: { subject?: string; customContent?: string }): Promise<{ message: string; sent: number; errors: string[] }> {
  return request<{ message: string; sent: number; errors: string[] }>('/newsletter/admin/send', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Public form submissions
export async function submitContactForm(data: { name: string; email: string; subject: string; message: string }): Promise<{ message: string }> {
  return request<{ message: string }>('/forms/contact', { method: 'POST', body: JSON.stringify(data) });
}
export async function submitSupportForm(data: { name: string; email: string; subject: string; category: string; message: string }): Promise<{ message: string }> {
  return request<{ message: string }>('/forms/support', { method: 'POST', body: JSON.stringify(data) });
}
// Advertise form removed — ad inquiries now go through internal chat messaging

// ── Discussions ───────────────────────────────────────────────────────────────

export type Comment = {
  id: string; ticker: string; userId: string; userEmail: string; userUsername: string; userDisplayName: string;
  userAvatarUrl: string | null; userCommentCount: number; parentId: string | null; body: string;
  isHidden: boolean; hideReason: string | null; reportCount: number;
  alreadyReported: boolean; canEdit: boolean;
  replies: Comment[]; createdAt: string; updatedAt: string;
};
export type DiscussionPage = { comments: Comment[]; total: number; newToday: number; page: number; pages: number };
export type DiscussionSettings = {
  enabled: boolean; maxReportsToHide: number; allowEditing: boolean;
  editWindowMinutes: number; minCommentLength: number; maxCommentLength: number;
  commentCooldownSeconds: number;
};
export type DiscussionBan = { userId: string; userEmail: string; bannedByEmail: string | null; reason: string | null; createdAt: string };
export type UserProfile = { id: string; username: string; email: string; createdAt: string; commentCount: number; comments: Partial<Comment>[]; page: number; pages: number };

export async function getDiscussions(ticker: string, page = 1, sort: 'newest' | 'oldest' = 'newest'): Promise<DiscussionPage> {
  return request<DiscussionPage>(`/discussions/${ticker}?page=${page}&sort=${sort}`);
}
export async function postComment(ticker: string, body: string): Promise<Comment> {
  return request<Comment>(`/discussions/${ticker}`, { method: 'POST', body: JSON.stringify({ body }) });
}
export async function postReply(ticker: string, commentId: string, body: string): Promise<Comment> {
  return request<Comment>(`/discussions/${ticker}/${commentId}/reply`, { method: 'POST', body: JSON.stringify({ body }) });
}
export async function reportComment(commentId: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/discussions/comments/${commentId}/report`, { method: 'POST' });
}
export async function editComment(commentId: string, body: string): Promise<Comment> {
  return request<Comment>(`/discussions/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify({ body }) });
}
export async function deleteComment(commentId: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/discussions/comments/${commentId}`, { method: 'DELETE' });
}
export async function getUserProfile(userId: string, page = 1): Promise<UserProfile> {
  return request<UserProfile>(`/users/${userId}/profile?page=${page}`);
}

// Admin discussions
export async function adminGetDiscussionSettings(): Promise<DiscussionSettings> {
  return request<DiscussionSettings>('/admin/settings/discussions');
}
export async function adminSaveDiscussionSettings(data: Partial<DiscussionSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/discussions', { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminGetDiscussionComments(page = 1, hidden = false): Promise<{ comments: Comment[]; total: number; page: number; pages: number }> {
  return request(`/admin/discussions/comments?page=${page}&hidden=${hidden}`);
}
export async function adminModerateComment(id: string, action: 'hide' | 'restore'): Promise<{ message: string }> {
  return request(`/admin/discussions/comments/${id}`, { method: 'PATCH', body: JSON.stringify({ action }) });
}
export async function adminDeleteComment(id: string): Promise<{ message: string }> {
  return request(`/admin/discussions/comments/${id}`, { method: 'DELETE' });
}
export async function adminGetBans(): Promise<DiscussionBan[]> {
  return request<DiscussionBan[]>('/admin/discussions/bans');
}
export async function adminBanUser(userId: string, reason?: string): Promise<{ message: string }> {
  return request('/admin/discussions/bans', { method: 'POST', body: JSON.stringify({ userId, reason }) });
}
export async function adminUnbanUser(userId: string): Promise<{ message: string }> {
  return request(`/admin/discussions/bans/${userId}`, { method: 'DELETE' });
}

// ── Donation Settings ─────────────────────────────────────────────────────────

export type DonationSettings = { enabled: boolean; paypalLink: string; zelleEmail: string; venmoHandle: string; message: string };
export type StripeAdminSettings = { enabled: boolean; secretKey: string; webhookSecret: string; priceIdPro: string; priceIdProYearly: string };

export async function getPublicDonationSettings(): Promise<DonationSettings> {
  return request<DonationSettings>('/admin/public/donations');
}
export async function adminGetDonationSettings(): Promise<DonationSettings> {
  return request<DonationSettings>('/admin/settings/donations');
}
export async function adminSaveDonationSettings(data: Partial<DonationSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/donations', { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminGetStripeSettings(): Promise<StripeAdminSettings> {
  return request<StripeAdminSettings>('/admin/settings/stripe');
}
export async function adminSaveStripeSettings(data: Partial<StripeAdminSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/stripe', { method: 'PUT', body: JSON.stringify(data) });
}

export type SessionSettings = { timeoutMinutes: number; refreshTokenDays: number; maxLoginAttempts: number; lockoutDurationMinutes: number; trustedDeviceVerification: boolean };
export async function adminGetSessionSettings(): Promise<SessionSettings> {
  return request<SessionSettings>('/admin/settings/session');
}
export async function adminSaveSessionSettings(data: Partial<SessionSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/session', { method: 'PUT', body: JSON.stringify(data) });
}

export interface AnalyticsSettings {
  pageViewRetentionDays: number;
}
export async function adminGetAnalyticsSettings(): Promise<AnalyticsSettings> {
  return request<AnalyticsSettings>('/admin/settings/analytics');
}
export async function adminSaveAnalyticsSettings(data: Partial<AnalyticsSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/analytics', { method: 'PUT', body: JSON.stringify(data) });
}

export type PricingSettings = {
  proMonthlyPrice: number;
  proMonthlyOriginalPrice: number;
  proYearlyPrice: number;
  proYearlyOriginalPrice: number;
};

export type WidgetVisibility = 'public' | 'registered' | 'pro' | 'admin';

export type DashboardWidget = {
  id: string;
  enabled: boolean;
  colSpan: 1 | 2 | 3;
  order: number;
  x?: number;
  y?: number;
  /** Legacy: `false` means registered-only. Prefer `visibility` for new code. */
  anonymousVisible?: boolean;
  /** 'public' = everyone, 'registered' = logged-in users, 'pro' = pro/admin only,
   *  'admin' = admin role only (operator widgets like Rate Limiter telemetry).
   *  When unset, falls back to `anonymousVisible` (false => 'registered'). */
  visibility?: WidgetVisibility;
  halfHeight?: boolean;
  tileHeight?: number; // 1=200px, 2=400px, 3=600px, 4=800px. Overrides halfHeight.
  pinned?: boolean;
};

/** Resolve a widget's effective visibility, honoring the legacy anonymousVisible flag. */
export function resolveWidgetVisibility(w: Pick<DashboardWidget, 'visibility' | 'anonymousVisible'>): WidgetVisibility {
  if (w.visibility) return w.visibility;
  return w.anonymousVisible === false ? 'registered' : 'public';
}
export type DashboardSettings = {
  widgets: DashboardWidget[]; maxLayoutsFree?: number; maxLayoutsPro?: number;
  /** How anonymous visitors see the dashboard widget grid. Default 'preview'.
   *  - 'full':    widgets interactive
   *  - 'preview': widgets visible but blocked by a 50% overlay + sign-in CTA
   *  - 'hidden':  grid not rendered; marketing carousel still shown */
  anonWidgetMode?: 'full' | 'preview' | 'hidden';
  /** Plan #265 — unified live-signal session window in HH:MM ET form.
   *  Drives the chart's BACKTEST view's bar filter so it matches the
   *  scanner's evaluation window. Defaults to '07:00' / '16:00' when
   *  unset (chart applies its own fallback). */
  signalSessionStartEt?: string;
  signalSessionEndEt?: string;
  /** Momentum window (HH:MM ET) for the chart signal's trend / reversal /
   *  SELL paths — the early-reversal candlestick path ignores it. Defaults
   *  to 09:30 / 11:00 when unset. See lib/intradaySignal MOMENTUM_WINDOW_START. */
  signalMomentumStartEt?: string;
  signalMomentumEndEt?: string;
  /** Live BUY/SELL momentum gates (global, admin-tuned). When on, the chart's
   *  BACKTEST arrows apply the same MACD/Eixom/RSI filters the API's scanner +
   *  auto-trade path uses, so the dots match what actually fires. */
  signalRequireMacd?: boolean;
  signalRequireEixom?: boolean;
  signalRsiOverbought?: number;
  signalRsiOversold?: number;
  /** Higher-timeframe (15m + 30m) agreement gate. When on, the chart's
   *  BACKTEST arrows apply the same HTF filter the API applies, so the dots
   *  keep matching what actually fires. */
  signalRequireHtfAgreement?: boolean;
  /** Early-reversal candlestick BUY path (default ON). The chart reads it so
   *  its tentative BUY? / confirmed BUY ✓ arrows match what the API fires. */
  signalCandleReversal?: boolean;
};

export type WidgetLayout = {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  visible: boolean;
  pinned?: boolean;
};

export type LatestComment = {
  id: string;
  ticker: string;
  body: string;
  createdAt: string;
  userEmail: string;
  userDisplayName: string;
  userAvatarUrl: string | null;
  userCommentCount: number;
  replyCount: number;
};

export async function getDashboardSettings(): Promise<DashboardSettings> {
  return request<DashboardSettings>('/admin/public/dashboard');
}

// ── V2 platform-default layouts (plan #168) ────────────────────────────────

export type V2DefaultScope = 'anon' | 'new_user';

export interface V2DefaultLayout {
  scope: V2DefaultScope;
  items: Array<{ id: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number }>;
  /** Plan #170 — independent 1-column layout for viewports < 1024px. */
  mobileItems: Array<{ id: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number }>;
  widgetSettings: Record<string, Record<string, unknown>>;
  collapsed: { ids: string[]; saved: Record<string, { x: number; y: number; w: number; h: number }> };
  /** Per-viewport auto-fit defaults. Always present in GET responses
   *  (server seeds desktop=false, mobile=true for any row that hasn't
   *  been explicitly toggled). Client mirrors these into the user's
   *  localStorage on first-time mount AND on Reset, so the admin's
   *  default propagates without the user having to touch the toggle. */
  desktopAutoFit: boolean;
  mobileAutoFit: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** PUT body shape. `mobileItems` is optional — omission means "leave the
 *  stored mobile column alone". Lets a desktop-only edit save without
 *  clobbering a previously-saved mobile arrangement. Same pattern for
 *  the per-viewport auto-fit booleans: editing the desktop default
 *  shouldn't reset the mobile-auto-fit toggle and vice versa. */
export type V2DefaultPayload = Pick<V2DefaultLayout, 'items' | 'widgetSettings' | 'collapsed'> & {
  mobileItems?: V2DefaultLayout['mobileItems'];
  desktopAutoFit?: boolean;
  mobileAutoFit?: boolean;
};

export function getV2DefaultLayout(scope: V2DefaultScope): Promise<V2DefaultLayout> {
  return request<V2DefaultLayout>(`/dashboard-defaults-v2/${scope}`);
}

export function putV2DefaultLayout(scope: V2DefaultScope, body: V2DefaultPayload): Promise<V2DefaultLayout> {
  return request<V2DefaultLayout>(`/dashboard-defaults-v2/${scope}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// ── Personal V2 layout sync — server-side persistence so customizations
//    propagate across a user's devices. Companion to V2DefaultLayout
//    (admin defaults).
//
//    Desktop and mobile share one canonical widget catalog. Each V2Layout
//    retains independent `items` and `mobileItems` coordinates; `device`
//    tells the API which coordinate array is authoritative for a save.

export interface UserLayoutsV2Layout {
  id: string;
  name: string;
  items: Array<{ id: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number; noResize?: boolean }>;
  mobileItems: Array<{ id: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number; noResize?: boolean }>;
  widgetSettings: Record<string, Record<string, unknown>>;
  collapsed: { ids: string[]; saved: Record<string, { x: number; y: number; w: number; h: number }> };
  pinned?: boolean;
}

export interface UserLayoutsV2DeviceBundle {
  layouts: UserLayoutsV2Layout[];
  activeId: string;
}

export interface UserLayoutsV2Bundle {
  desktop: UserLayoutsV2DeviceBundle;
  mobile: UserLayoutsV2DeviceBundle;
  /** ISO timestamp of the most-recent server-side write (either bundle).
   *  Null when the user has no row yet — client falls through to
   *  localStorage + admin defaults until the first save lands. */
  updatedAt: string | null;
}

export type UserLayoutsV2Device = 'desktop' | 'mobile';

export interface UserLayoutsV2Payload extends UserLayoutsV2DeviceBundle {
  device: UserLayoutsV2Device;
  /** Optimistic-concurrency token from the latest GET/PUT. Null means the
   * client expects that no server row exists yet. */
  baseUpdatedAt: string | null;
}

export function getUserLayoutsV2(): Promise<UserLayoutsV2Bundle> {
  return request<UserLayoutsV2Bundle>('/dashboard-layouts-v2');
}

export function putUserLayoutsV2(body: UserLayoutsV2Payload): Promise<UserLayoutsV2Bundle> {
  return request<UserLayoutsV2Bundle>('/dashboard-layouts-v2', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}
export async function getLatestComments(): Promise<LatestComment[]> {
  return request<LatestComment[]>('/admin/public/comments/latest');
}
export async function adminGetDashboardSettings(): Promise<DashboardSettings> {
  return request<DashboardSettings>('/admin/settings/dashboard');
}
export async function adminSaveDashboardSettings(data: DashboardSettings): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/dashboard', { method: 'PUT', body: JSON.stringify(data) });
}

// ── User Dashboard Layout ────────────────────────────────────────────────────
// Plan #169 — V1 dashboard removed. The V1 per-user `/dashboard/layout` API
// and its associated types (WidgetLayout, DashboardLayoutSummary,
// DashboardLayoutFull, WidgetSettings, etc.) are gone. V2 stores per-user
// layouts in browser localStorage; the platform anon / new_user defaults
// live in `platform_dashboard_defaults_v2` (see getV2DefaultLayout above).

export async function getPricingSettings(): Promise<PricingSettings> {
  return request<PricingSettings>('/admin/public/pricing');
}
export async function adminGetPricingSettings(): Promise<PricingSettings> {
  return request<PricingSettings>('/admin/settings/pricing');
}
export async function adminSavePricingSettings(data: Partial<PricingSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/pricing', { method: 'PUT', body: JSON.stringify(data) });
}

export async function adminGetAdSlots(): Promise<AdSlot[]> {
  return request<AdSlot[]>('/admin/ads');
}

export async function adminUpdateAdSlot(id: string, data: Partial<AdSlot>): Promise<AdSlot> {
  return request<AdSlot>(`/admin/ads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export interface AdsConfig { globalExcludedPages: string[]; }

export async function adminGetAdsSettings(): Promise<AdsConfig> {
  return request<AdsConfig>('/admin/settings/ads');
}
export async function adminSaveAdsSettings(data: AdsConfig): Promise<{ message: string; globalExcludedPages: string[] }> {
  return request<{ message: string; globalExcludedPages: string[] }>('/admin/settings/ads', { method: 'PUT', body: JSON.stringify(data) });
}
export async function getAdsConfig(): Promise<AdsConfig> {
  return request<AdsConfig>('/admin/public/ads-config');
}

// ── Stock Comparison ──────────────────────────────────────────────────────────

export type CompareResult = { ticker: string; data: OHLCV[] };

export async function getCompareData(tickers: string[], days: number): Promise<CompareResult[]> {
  return request<CompareResult[]>(`/stocks/compare?tickers=${encodeURIComponent(tickers.join(','))}&days=${days}`);
}

// ── Earnings Calendar ─────────────────────────────────────────────────────────

export type EarningsEntry = {
  id: string;
  ticker: string;
  companyName: string;
  date: string;
  time: 'before_open' | 'after_close' | 'unknown';
  marketCap: number;
  revenueEstimate: number | null;
  revenueEstimateChange: number | null;
  epsEstimate: number | null;
  epsEstimateChange: number | null;
  fiscalQuarter: string;
  sector: string;
  // Index membership + live snapshot from `tickers` (DB path only).
  isSp500?: boolean;
  isRussell1000?: boolean;
  isNdx100?: boolean;
  currentPrice?: number | null;
  currentChangePct?: number | null;
};

/** Earnings calendar across ALL tickers in [from, to]. Backed by
 *  earnings_calendar with a live-provider fallback when the cache is
 *  sparse. Used by the Earnings Calendar page. NOT a good source for
 *  past quarters — earnings_calendar is forward-looking only and ages
 *  out historical rows. For per-ticker historical earnings (4+ past
 *  quarters), use getEarningsHistory() instead. */
export async function getEarnings(from: string, to: string): Promise<EarningsEntry[]> {
  return request<EarningsEntry[]>(`/earnings?from=${from}&to=${to}`);
}

// ── Pre-Earnings Setups scanner (issue #210) ────────────────────────────
// Composite score combining quality + oversold + not-extended + last-Q
// reaction bias. Auth-gated; surfaces top 50 candidates for a date.
export type EarningsSetup = {
  id: string;
  ticker: string;
  companyName: string;
  date: string;
  time: 'before_open' | 'after_close' | 'unknown';
  marketCap: number;
  sector: string;
  isSp500: boolean;
  isRussell1000: boolean;
  currentPrice: number | null;
  changeTodayPct: number | null;
  pct5d: number | null;
  pct30d: number | null;
  rsi: number | null;
  rsRating: number | null;
  pe: number | null;
  beta: number | null;
  lastEarningsMove: number | null;
  setupScore: number;
  scoreBreakdown: {
    qualityWeight: number;
    oversoldSignal: number;
    notExtendedSignal: number;
    earningsBiasMultiplier: number;
    coilScore: number;
    momentumScore: number;
    chosenPath: 'coil' | 'momentum';
  };
};

export async function getEarningsSetups(
  date: string,
  time: 'after_close' | 'before_open' | 'all' = 'all',
  minMcapB = 1,
): Promise<EarningsSetup[]> {
  const qs = new URLSearchParams({ date, time, minMcapB: String(minMcapB) }).toString();
  return request<EarningsSetup[]>(`/earnings/setups?${qs}`);
}

// ── Admin scanner debug-replay (issue #211, v2 in #216) ────────────────
export interface ScannerReplayDiagnostics {
  bars5Count: number;
  bars1Count: number;
  warmupCount: number;
  totalScanTicks: number;
  qualifiedScanTicks: number;
  firstTickTs: string | null;
  lastTickTs: string | null;
  skippedNonTradablePhase: number;
  skippedNoReason: number;
  skippedBadTs: number;
  skippedPersistFailed: number;
}

/** Per-event snapshot returned alongside count summaries so the operator
 *  can diff the pre-delete and post-insert event sets — same count can hide
 *  different timestamps / prices when the gate logic or cached bars shift
 *  between runs. Sorted by detectedAt ascending. */
export interface ScannerReplayEventSnapshot {
  detectedAt: string;
  reason: string;
  severity: string;
  phase: string;
  price: number;
  changePct: number;
  rvol: number;
  peakRvol: number;
  peakChangePct: number;
  passes: number;
  bucket: string | null;
}

export interface ScannerReplayPerTicker {
  ticker: string;
  preCount: number;
  deleted: number;
  inserted: number;
  postCount: number;
  delta: number;
  reasons: Record<string, number>;
  skipped?: 'no_bars';
  // Empty when overwrite=false (nothing was deleted).
  preEvents: ScannerReplayEventSnapshot[];
  postEvents: ScannerReplayEventSnapshot[];
  diagnostics?: ScannerReplayDiagnostics;
}

export interface ScannerReplayResult {
  runAt: string;
  dates: string[];
  includePremarket: boolean;
  includeAfterMarket: boolean;
  overwrite: boolean;
  totalDeleted: number;
  totalInserted: number;
  byDate: Array<{
    date: string;
    byTicker: ScannerReplayPerTicker[];
  }>;
}

export interface ScannerReplayCount {
  byPair: Array<{ ticker: string; date: string; count: number }>;
  total: number;
}

// ── AI Chart Signals admin settings (model / min-role / temperature) ──

export interface AiChartSignalsAdminSettings {
  enabled: boolean;
  model: 'claude-haiku-4-5-20251001' | 'claude-sonnet-4-6' | 'claude-opus-4-8';
  minRoleToEnable: 'admin' | 'vip' | 'pro';
  temperature: number;
  maxTickersPerSession: number;
  maxTickersPerUser: number;
}

export async function adminGetAiChartSignalsSettings(): Promise<AiChartSignalsAdminSettings> {
  return request<AiChartSignalsAdminSettings>('/admin/ai-setup/chart-signals');
}

export async function adminSaveAiChartSignalsSettings(patch: Partial<AiChartSignalsAdminSettings>): Promise<AiChartSignalsAdminSettings> {
  return request<AiChartSignalsAdminSettings>('/admin/ai-setup/chart-signals', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

// ── AI feature routing (#372): per-feature transport ──
// 'api'   raw Anthropic Messages API (metered, logs ai_usage)
// 'sdk'   Claude Code via the Agent SDK (Claude subscription, unmetered)
// 'codex' Codex CLI subprocess (ChatGPT subscription, unmetered)
export type AiRouteId = 'api' | 'sdk' | 'codex';
export type AiProviderId = 'anthropic' | 'openai';

export interface AiFeatureRoute {
  key: string;
  name: string;
  /** A = toggleable (text-out), B = API-only (tool-using), C = tool-using
   *  subscription transports (SDK or Codex). */
  group: 'A' | 'B' | 'C';
  /** Group A: whether the caller actually honors the toggle yet. */
  migrated: boolean;
  note: string | null;
  /** True when there is nothing to choose (a single allowed route). */
  locked: boolean;
  /** Routes this feature may use. Web-search features exclude 'codex'. */
  allowedRoutes: AiRouteId[];
  route: AiRouteId;
  /** Per-feature model override id, or null = use the feature's default. */
  model: string | null;
  /** Per-feature reasoning-effort override, or null. */
  effort: string | null;
  /** Whether effort takes effect for this row (both subscription routes). */
  showEffort: boolean;
}
export interface AiModelOption {
  id: string;
  label: string;
  supportsEffort: boolean;
  /** Which transports can run this model — Anthropic ids can't run on Codex. */
  provider: AiProviderId;
}
export interface AiFeatureRoutingData {
  features: AiFeatureRoute[];
  models: AiModelOption[];
  /** @deprecated use effortLevelsByRoute — the vendors use different scales. */
  effortLevels: string[];
  effortLevelsByRoute: Record<AiRouteId, string[]>;
  /** Display names for the transports (wire values stay api/sdk/codex). */
  routeLabels: Record<AiRouteId, string>;
  codexConfigured: boolean;
  sdkConfigured: boolean;
}
export async function adminGetAiFeatureRouting(): Promise<AiFeatureRoutingData> {
  return request<AiFeatureRoutingData>('/admin/ai-setup/feature-routing');
}
/** Switching vendors clears an incompatible pinned model/effort server-side;
 *  the cleared values come back so the UI can say so. */
export async function adminSaveAiFeatureRoute(key: string, route: AiRouteId): Promise<{
  key: string; route: AiRouteId; clearedModel: string | null; clearedEffort: string | null;
}> {
  return request('/admin/ai-setup/feature-routing', {
    method: 'PUT',
    body: JSON.stringify({ key, route }),
  });
}
export async function adminSaveAiFeatureModel(key: string, patch: { model?: string | null; effort?: string | null }): Promise<{ key: string; model: string | null; effort: string | null }> {
  return request('/admin/ai-setup/feature-model', { method: 'PUT', body: JSON.stringify({ key, ...patch }) });
}
// ── Model catalog (built-ins + admin-added / API-synced) ──────────────────
export interface AiCatalogModel {
  id: string;
  label: string;
  provider: AiProviderId;
  inputPer1M: number;
  outputPer1M: number;
  supportsEffort: boolean;
  /** 'builtin' ships with the code; 'custom' was added or synced in. */
  source?: 'builtin' | 'custom';
  /** Anthropic model with no rates — the Models API doesn't return pricing,
   *  so a synced model logs $0 cost until this is filled in. */
  needsPricing: boolean;
}
export interface AiCatalogData {
  models: AiCatalogModel[];
  providers: AiProviderId[];
  /** Providers that can be auto-synced. Codex has no model enumeration. */
  syncable: AiProviderId[];
}
export async function adminGetAiModels(): Promise<AiCatalogData> {
  return request<AiCatalogData>('/admin/ai-setup/models');
}
export async function adminSaveAiModel(model: Omit<AiCatalogModel, 'needsPricing' | 'source'>): Promise<{ model: AiCatalogModel }> {
  return request('/admin/ai-setup/models', { method: 'POST', body: JSON.stringify(model) });
}
export async function adminDeleteAiModel(id: string): Promise<{ removed: string }> {
  return request(`/admin/ai-setup/models/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export interface AiModelSyncResult {
  ok: boolean;
  error?: string;
  discovered?: number;
  added?: string[];
  needsPricing?: string[];
  /** Catalog ids the Models API no longer lists — reported, never auto-removed. */
  stale?: string[];
  note?: string;
}
export async function adminSyncAiModels(): Promise<AiModelSyncResult> {
  return request<AiModelSyncResult>('/admin/ai-setup/models/sync', { method: 'POST' });
}

/**
 * Apply a transport and/or model/effort to every feature at once.
 *
 * `route` is applied BEFORE the model is validated — otherwise a Codex model
 * would be checked against each feature's current (Claude) transport and
 * rejected everywhere. Features that can't take the requested combination are
 * skipped rather than broken, and reported back.
 */
export async function adminApplyAiModelToAll(patch: {
  route?: AiRouteId;
  model?: string | null;
  effort?: string | null;
}): Promise<{
  applied: boolean;
  appliedCount: number;
  route: AiRouteId | null;
  skipped: Array<{ key: string; route: AiRouteId; reason: string }>;
}> {
  return request('/admin/ai-setup/feature-model/apply-all', { method: 'PUT', body: JSON.stringify(patch) });
}

// ── Technical AI Tick — global master switch (plan #236) ──────────────
// When enabled, the 5-min scanner-driven tick ranker runs for every
// portfolio with technical_ai_enabled. The burst scanner then cedes ENTRIES
// for those portfolios to the ranker (exits are unaffected).

export interface TechnicalAiTickSettings {
  enabled: boolean;
  // Global pick-pass config (issue #358).
  model: string | null;                 // null → Haiku default
  globalQualReasons: string[];          // [] → accept all reasons
  globalMinSeverity: 'elevated' | 'high' | 'extreme';
  globalConfirmationPasses: number;
  lastRunAt: string | null;
  lastBuysExecuted: number;
  lastSellsExecuted: number;
  lastPortfoliosEvaluated: number;
  lastUniverse: number;
}

export type TechnicalAiTickSettingsPatch = Partial<Pick<TechnicalAiTickSettings,
  'enabled' | 'model' | 'globalQualReasons' | 'globalMinSeverity' | 'globalConfirmationPasses'>>;

export async function adminGetTechnicalAiTickSettings(): Promise<TechnicalAiTickSettings> {
  return request<TechnicalAiTickSettings>('/admin/settings/technical-ai-tick');
}

export async function adminSaveTechnicalAiTickSettings(patch: TechnicalAiTickSettingsPatch): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/technical-ai-tick', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

/** Force a single Technical-AI tick now (bypasses the enabled flag + 5-min
 *  boundary). Lets an admin smoke-test the ranker without waiting / flipping
 *  the global switch. Returns the run summary. */
export async function adminRunTechnicalAiTickOnce(): Promise<{
  fired: boolean;
  portfoliosEvaluated?: number;
  candidateUniverse?: number;
  buysExecuted?: number;
  sellsExecuted?: number;
  skipReason?: string;
}> {
  return request('/admin/technical-ai-tick/run', { method: 'POST' });
}

export type CandidatePoolStatus = 'observing' | 'eligible' | 'entered' | 'dropped';

export interface CandidatePoolEntry {
  // Global Technical-AI pool (issue #358) — portfolio-agnostic; no portfolio
  // columns (the pool is shared across all enrolled portfolios).
  id: string;
  ticker: string;
  status: CandidatePoolStatus;
  severity: 'elevated' | 'high' | 'extreme';
  qualificationReason: string | null;
  sector: string | null;
  peakRvolAtQualify: number | null;
  firstPrice: number | null;
  barsObserved: number;
  supportPrice: number | null;
  entryLow: number | null;
  entryHigh: number | null;
  lastHealthScore: number | null;
  lastBarAt: string | null;
  droppedReason: string | null;
  firstSeenAt: string;
  enteredAt: string | null;
  updatedAt: string;
}

export interface CandidatePoolResult {
  rows: CandidatePoolEntry[];
  counts: Record<CandidatePoolStatus, number>;
}

/** Read-only Technical-AI candidate-pool funnel (observing/eligible/entered/
 *  dropped + drop reasons). filters.hours bounds the lookback window. */
export async function adminGetTechnicalAiPool(filters: {
  status?: CandidatePoolStatus | '';
  ticker?: string;
  hours?: number;
  limit?: number;
} = {}): Promise<CandidatePoolResult> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== null) qs.set(k, String(v));
  }
  const q = qs.toString();
  return request<CandidatePoolResult>(`/admin/technical-ai-tick/pool${q ? `?${q}` : ''}`);
}

// ── AI Chart Signals (per-stock buy/sell/hold AI overlay) ──────────────
// View is public; control is admin-gated server-side.

export interface AiChartSignalSub {
  ticker: string;
  enabledAt: string;
  lastSignalAt: string | null;
}

export interface AiChartSignalRun {
  id: string;
  requestedAt: string;
  signal: 'buy' | 'sell' | 'hold' | null;
  confidence: number | null;
  reasoning: string | null;
  model: string;
  durationMs: number | null;
  error: string | null;
}

export async function aiChartSignalsListSubs(): Promise<{ sessionDate: string; tickers: AiChartSignalSub[] }> {
  return request<{ sessionDate: string; tickers: AiChartSignalSub[] }>('/ai-chart-signals/subscriptions');
}

export async function aiChartSignalsEnable(ticker: string): Promise<AiChartSignalSub & { sessionDate: string }> {
  return request<AiChartSignalSub & { sessionDate: string }>('/ai-chart-signals/subscriptions', {
    method: 'POST',
    body: JSON.stringify({ ticker: ticker.toUpperCase() }),
  });
}

export async function aiChartSignalsDisable(ticker: string): Promise<{ ticker: string; deleted: number }> {
  return request<{ ticker: string; deleted: number }>(`/ai-chart-signals/subscriptions/${encodeURIComponent(ticker.toUpperCase())}`, {
    method: 'DELETE',
  });
}

export async function aiChartSignalsListRuns(ticker: string, sinceIso?: string): Promise<{ ticker: string; since: string; runs: AiChartSignalRun[] }> {
  const qs = sinceIso ? `?since=${encodeURIComponent(sinceIso)}` : '';
  return request<{ ticker: string; since: string; runs: AiChartSignalRun[] }>(`/ai-chart-signals/${encodeURIComponent(ticker.toUpperCase())}${qs}`);
}

/** Trigger an on-demand AI chart-signal evaluation (writes a real run +
 *  step-log entry). Omit `asOf` for a LIVE evaluation of the current session
 *  (fired per fresh 1-min bar in live mode); pass `asOf` (ET
 *  "YYYY-MM-DD HH:MM[:SS]") to evaluate AS-OF a paused replay bar. Admin/
 *  min-role gated; the server still enforces the daily cap + circuit breaker. */
export async function aiChartSignalsEvaluate(ticker: string, asOf?: string): Promise<AiChartSignalRun> {
  return request<AiChartSignalRun>(`/ai-chart-signals/${encodeURIComponent(ticker.toUpperCase())}/evaluate`, {
    method: 'POST',
    body: JSON.stringify(asOf ? { asOf } : {}),
  });
}

/** A single chart-signal run with the FULL persisted detail — the feature
 *  bundle the model saw (requestPayload) + the parsed/raw model output
 *  (responsePayload) — for the step-log side panel's per-step trace. */
export interface AiChartSignalRunDetail extends AiChartSignalRun {
  etDate: string;
  /** ChartSignalPromptInput: { ticker, bars1m, bars5m, scannerEvents, priorSignals, current }. null on pre-call gate skips. */
  requestPayload: unknown;
  /** { parsed, rawText } on success, { raw } on parse-fail, or null on gates. */
  responsePayload: unknown;
  requestTokens: number | null;
  responseTokens: number | null;
}

export async function aiChartSignalDetail(ticker: string, runId: string): Promise<AiChartSignalRunDetail> {
  return request<AiChartSignalRunDetail>(`/ai-chart-signals/${encodeURIComponent(ticker.toUpperCase())}/${encodeURIComponent(runId)}`);
}

/** A real auto-trader execution for the current user on a ticker — the ACTUAL
 *  decisions the AI auto-trader (Technical-AI / Longterm-AI) acted on, distinct
 *  from the advisory AI chart-signal arrows (a separate engine that never
 *  trades). Scoped server-side to the caller's own portfolios. */
export interface AiTraderDecision {
  id: string;
  engine: 'technical_ai' | 'longterm_ai';
  eventType: 'entry' | 'exit';
  occurredAt: string;        // UTC ISO
  price: number | null;
  qty: number | null;
  pnl: number | null;        // exits only
  exitReason: string | null; // exits only
}

export async function getAiTraderDecisions(ticker: string, sinceIso?: string): Promise<AiTraderDecision[]> {
  const qs = sinceIso ? `?since=${encodeURIComponent(sinceIso)}` : '';
  const data = await request<{ decisions: AiTraderDecision[] }>(`/stocks/${encodeURIComponent(ticker.toUpperCase())}/ai-trader-decisions${qs}`);
  return data.decisions;
}

export async function adminScannerReplayCount(tickers: string[], dates: string[]): Promise<ScannerReplayCount> {
  const qs = new URLSearchParams({ tickers: tickers.join(','), dates: dates.join(',') }).toString();
  return request<ScannerReplayCount>(`/admin/scanner/debug-replay/count?${qs}`);
}

export async function adminScannerReplay(
  tickers: string[],
  dates: string[],
  includePremarket = false,
  overwrite = false,
  includeAfterMarket = false,
): Promise<ScannerReplayResult> {
  return request<ScannerReplayResult>('/admin/scanner/debug-replay', {
    method: 'POST',
    body: JSON.stringify({ tickers, dates, includePremarket, includeAfterMarket, overwrite }),
  });
}

// ── Admin per-portfolio trade replay ────────────────────────────────────
export interface PortfolioReplayByDate {
  date: string;
  entries: number;
  exits: number;
  totalPnl: number;
  cashChange: number;
  byTicker: Array<{
    ticker: string;
    entryTs: string | null;
    exitTs: string | null;
    shares: number;
    entryPrice: number;
    exitPrice: number | null;
    pnl: number;
    exitReason: string | null;
  }>;
  skippedCandidates: Array<{ ticker: string; reason: string }>;
  error?: string;
}

export interface PortfolioReplayResult {
  portfolioId: string;
  date: string;
  /** End of inclusive range when run in range mode; null in single-day mode.
   *  Older API deploys may omit this field — treat undefined as null. */
  endDate?: string | null;
  entries: number;
  exits: number;
  totalPnl: number;
  cashChange: number;
  byTicker: Array<{
    ticker: string;
    entryTs: string | null;
    exitTs: string | null;
    shares: number;
    entryPrice: number;
    exitPrice: number | null;
    pnl: number;
    exitReason: string | null;
  }>;
  /** Optional — older API deploys (pre-2026-05-07) didn't include this. */
  skippedCandidates?: Array<{ ticker: string; reason: string }>;
  /** Populated in range mode (endDate set). One entry per replayed weekday. */
  byDate?: PortfolioReplayByDate[];
}

export interface PortfolioReplayCount {
  portfolioId: string;
  date: string;
  events: number;
  transactions: number;
  openPositions: number;
}

export interface PortfolioReplayCleanupResult {
  portfolioId: string;
  date: string;
  deletedEvents: number;
  deletedAudit: number;
  deletedTransactions: number;
  deletedPositions: number;
  cashReversed: number;
}

export async function adminPortfolioReplayCount(portfolioId: string, date: string): Promise<PortfolioReplayCount> {
  const qs = new URLSearchParams({ date }).toString();
  return request<PortfolioReplayCount>(`/admin/portfolios/${portfolioId}/replay-trades/count?${qs}`);
}

export interface AdminPortfolioReplayOptions {
  /** Inclusive end date for range mode. Omit for single-day. */
  endDate?: string;
  /** Silently clean up existing auto-trade activity for each replayed day
   *  before running. Defaults to false — caller passes true when wiring
   *  cleanup into the Run button (so re-runs don't require a manual click). */
  forceCleanup?: boolean;
}

export async function adminPortfolioReplay(
  portfolioId: string,
  date: string,
  includePremarket = false,
  tickers?: string[],
  bypassScanner = false,
  options: AdminPortfolioReplayOptions = {},
): Promise<PortfolioReplayResult> {
  // Replay route uses chunked-keepalive streaming — response is ALWAYS
  // 200 with body shaped as { data } on success or { error } on failure.
  // The keepalive prefix is a sequence of space bytes (RFC 8259 ignored
  // whitespace) followed by the final JSON. Standard fetch+text() reads
  // the full response; JSON.parse tolerates the leading whitespace.
  const normalizedTickers = Array.isArray(tickers)
    ? Array.from(new Set(
        tickers
          .map((t) => (typeof t === 'string' ? t.trim().toUpperCase() : ''))
          .filter((t) => t.length > 0),
      ))
    : [];
  const endDate = typeof options.endDate === 'string' && options.endDate.trim() !== ''
    ? options.endDate.trim()
    : undefined;
  const response = await fetch(`${BASE_URL}/admin/portfolios/${portfolioId}/replay-trades`, {
    method: 'POST',
    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      date,
      includePremarket,
      ...(normalizedTickers.length > 0 ? { tickers: normalizedTickers } : {}),
      ...(bypassScanner ? { bypassScanner: true } : {}),
      ...(endDate ? { endDate } : {}),
      ...(options.forceCleanup ? { forceCleanup: true } : {}),
    }),
  });
  const text = await response.text();
  let body: { data?: PortfolioReplayResult; error?: { code?: string; message?: string; details?: unknown } } = {};
  try {
    body = JSON.parse(text);
  } catch {
    throw new Error(`Replay returned unparseable response (status ${response.status})`);
  }
  if (body.error) {
    const err = new Error(body.error.message ?? 'Replay failed');
    Object.assign(err, body.error);
    throw err;
  }
  if (!body.data) {
    throw new Error('Replay returned no data');
  }
  return body.data;
}

export async function adminPortfolioReplayCleanup(
  portfolioId: string,
  date: string,
): Promise<PortfolioReplayCleanupResult> {
  return request<PortfolioReplayCleanupResult>(`/admin/portfolios/${portfolioId}/replay-trades`, {
    method: 'DELETE',
    body: JSON.stringify({ date }),
  });
}

export interface EarningsHistoryQuarter {
  fiscalDateEnding: string;       // YYYY-MM-DD
  reportedDate: string;           // YYYY-MM-DD — actual announcement day
  reportedEPS: number | null;
  estimatedEPS: number | null;
  surprise: number | null;
  surprisePercentage: number | null;
  /** Optional fiscal quarter label (e.g. "Q2 2026") — present on
   *  upcoming-earnings rows sourced from earnings_calendar so the row
   *  label uses the company's real fiscal calendar instead of the
   *  calendar-month heuristic. Past rows from getEarningsHistory don't
   *  carry it (AV doesn't return it); they fall back to the heuristic. */
  fiscalQuarter?: string | null;
  /** True when reportedDate was rewritten by the AV provider's
   *  date-only-placeholder corrector — AV occasionally serves
   *  reportedDate=fiscalDateEnding alongside real EPS data; the value
   *  here is the company's historical-cadence median estimate. UI
   *  should append "(est.)" so users know it's not authoritative. */
  reportedDateEstimated?: boolean;
}

/** Per-ticker quarterly earnings history. ON-DEMAND, NOT SYNCED — hits
 *  the configured card-call provider (Admin → Data & Sync → Earnings Sync
 *  → "On-Demand Card Calls"). Cached 1h at route + 24h in-provider since
 *  past quarters are immutable except on restatements. Used by
 *  EarningsHistoryCard. */
export async function getEarningsHistory(ticker: string): Promise<{
  quarters: EarningsHistoryQuarter[];
  provider: string;
  supported: boolean;
  /** Most-recent KNOWN report time-of-day for this ticker, sourced from
   *  earnings_calendar.time. Drives the D0/D+1 anchor decision in the
   *  earnings price-action grid: AMC reports' actual reaction lands on
   *  the next trading day. Null when we have no signal — the chart then
   *  falls back to a per-quarter price-action heuristic. */
  tickerReportTime: 'after_close' | 'before_open' | null;
}> {
  return request(`/stocks/${ticker}/earnings-history`);
}

// ── Earnings Track Record (summary stats over past quarters) ────────────────
export type BeatMetric = 'beat' | 'miss' | 'inline' | 'unknown';

export type QuarterTrackRecord = {
  reportedDate: string;
  fiscalDateEnding: string;
  reportedEPS: number | null;
  estimatedEPS: number | null;
  surprisePct: number | null;
  beatMetric: BeatMetric;
  closeReportDay: number | null;
  closeNextDay: number | null;
  reactionPct: number | null;
};

export type EarningsTrackRecord = {
  ticker: string;
  quartersAvailable: number;
  beatsCount: number;
  missesCount: number;
  inlineCount: number;
  unknownCount: number;
  beatRate: number | null;
  avgReactionPct: number | null;
  biggestUpReactionPct: number | null;
  biggestUpDate: string | null;
  biggestDownReactionPct: number | null;
  biggestDownDate: string | null;
  divergenceCount: number;
  quarters: QuarterTrackRecord[];
};

export async function getEarningsTrackRecord(ticker: string): Promise<EarningsTrackRecord | null> {
  return request<EarningsTrackRecord | null>(`/stocks/${ticker}/earnings-track-record`);
}

// ── Stock Screener ─────────────────────────────────────────────────────────────

export type ScreenerResult = {
  ticker: string;
  name: string;
  price: number;
  changePct: number;
  volume: number;
  marketCap: number;
  revenue: number;
  netIncome: number;
  pe: number | null;
  analystRating: string;
  sector: string;
  /** GICS sub-industry / industry classification (one level below sector).
   *  Available on every screener row that has the parent sector populated.
   *  Empty string when the ticker hasn't been classified yet. */
  industry: string;
  exchange: string;
  /** IBD-style relative strength rank, 1-99. Null if the nightly RS sweep
   *  hasn't rated this ticker yet (insufficient daily history). */
  rsRating: number | null;
  /** Wilder's RSI(14) of daily close, 0-100 (rounded). */
  rsi14: number | null;
  /** Direction-of-travel deltas vs the rs_rating_history snapshot from
   *  ~7 / ~30 calendar days ago. Null when no history row exists for
   *  that snapshot date (e.g., ticker added recently, holiday gap).
   *  Positive = improving (rank climbing / RSI rising). */
  rsDelta7d: number | null;
  rsiDelta7d: number | null;
  rsDelta30d: number | null;
  rsiDelta30d: number | null;
  /** Index membership flags. Driven by `tickers.heatmap` (S&P 500) and
   *  `tickers.russell_1000`. Provider-fallback rows default to false. */
  inSp500: boolean;
  inRussell1000: boolean;
  /** Extended-hours fields. Populated only when the screener call asked
   *  for them (sort=extendedChangePct.* or session=pre-market|after-hours). */
  extendedPrice?: number | null;
  extendedChangePct?: number | null;
  extendedVolume?: number | null;
  extendedSession?: 'pre-market' | 'after-hours' | null;
  /** Day % derived prev close (back-derived from change_pct since the
   *  stored prev_close column goes stale on splits). */
  prevClose?: number | null;
  /** 10-day average daily volume, for Most Active comparisons. */
  avgVolume?: number | null;
};

// ── Movers consolidation (issue #244) ─────────────────────────────────────
// The 5 /api/markets/{gainers,losers,active,premarket,afterhours} endpoints
// were replaced by /api/screener with preset params. The mapping is:
//   gainers     → { sort: 'changePct.desc', minChangePct: 0.01 }
//   losers      → { sort: 'changePct.asc',  maxChangePct: -0.01 }
//   active      → { sort: 'volume.desc' }
//   premarket   → { sort: 'extendedChangePct.desc', session: 'pre-market' }
//   afterhours  → { sort: 'extendedChangePct.desc', session: 'after-hours' }
// Plus the new shared params: universe (all/sp500/r1000), limit, offset.

export type ScreenerSort =
  | 'marketCap.desc'
  | 'marketCap.asc'
  | 'changePct.desc'
  | 'changePct.asc'
  | 'volume.desc'
  | 'volume.asc'
  | 'extendedChangePct.desc'
  | 'extendedChangePct.asc';

export type MoversUniverse = 'all' | 'sp500' | 'r1000';

export type ScreenerFilters = {
  sector?: string;
  exchange?: string;
  minMarketCap?: number;
  maxMarketCap?: number;
  minPE?: number;
  maxPE?: number;
  analystRating?: string;
  minRsRating?: number;
  maxRsRating?: number;
  minRsi14?: number;
  maxRsi14?: number;
  /** Trend signal filter (plan #363): 'buy' | 'hold' | 'sell'. */
  signal?: string;
  // Movers consolidation:
  sort?: ScreenerSort;
  universe?: MoversUniverse;
  limit?: number;
  offset?: number;
  minChangePct?: number;
  maxChangePct?: number;
  session?: 'pre-market' | 'after-hours';
};

export interface ScreenerPage {
  rows: ScreenerResult[];
  total: number;
  limit: number;
  offset: number;
}

function buildScreenerQS(filters?: ScreenerFilters): string {
  const params = new URLSearchParams();
  if (filters?.sector) params.set('sector', filters.sector);
  if (filters?.exchange) params.set('exchange', filters.exchange);
  if (filters?.minMarketCap !== undefined) params.set('minMarketCap', String(filters.minMarketCap));
  if (filters?.maxMarketCap !== undefined) params.set('maxMarketCap', String(filters.maxMarketCap));
  if (filters?.minPE !== undefined) params.set('minPE', String(filters.minPE));
  if (filters?.maxPE !== undefined) params.set('maxPE', String(filters.maxPE));
  if (filters?.analystRating) params.set('analystRating', filters.analystRating);
  if (filters?.minRsRating !== undefined) params.set('minRsRating', String(filters.minRsRating));
  if (filters?.maxRsRating !== undefined) params.set('maxRsRating', String(filters.maxRsRating));
  if (filters?.minRsi14 !== undefined) params.set('minRsi14', String(filters.minRsi14));
  if (filters?.maxRsi14 !== undefined) params.set('maxRsi14', String(filters.maxRsi14));
  if (filters?.signal) params.set('signal', filters.signal);
  if (filters?.sort) params.set('sort', filters.sort);
  if (filters?.universe) params.set('universe', filters.universe);
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
  if (filters?.minChangePct !== undefined) params.set('minChangePct', String(filters.minChangePct));
  if (filters?.maxChangePct !== undefined) params.set('maxChangePct', String(filters.maxChangePct));
  if (filters?.session) params.set('session', filters.session);
  return params.toString();
}

export async function getScreenerResults(filters?: ScreenerFilters): Promise<ScreenerResult[]> {
  const qs = buildScreenerQS(filters);
  return request<ScreenerResult[]>(`/screener${qs ? `?${qs}` : ''}`);
}

/** Paginated variant — returns `{ rows, total, limit, offset }` so the
 *  Movers UI can render "Showing X-Y of N". Reads from the response's
 *  `meta` block. Falls back to `data.length` when `meta` is absent (older
 *  prod build). */
export async function getScreenerPage(filters?: ScreenerFilters): Promise<ScreenerPage> {
  const qs = buildScreenerQS(filters);
  // request() unwraps `{ data }` automatically — we need the raw envelope
  // here to also access `meta`. Use fetch directly with the same auth.
  const url = `/api/screener${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`screener page ${res.status}`);
  }
  const body = (await res.json()) as { data: ScreenerResult[]; meta?: { total: number; limit: number; offset: number } };
  const rows = Array.isArray(body?.data) ? body.data : [];
  const meta = body?.meta;
  return {
    rows,
    total: meta?.total ?? rows.length,
    limit: meta?.limit ?? rows.length,
    offset: meta?.offset ?? 0,
  };
}

// ── Compact Screener (Speculator-IO-style dense layout) ──────────────────────
// Adds bar-derived columns (YTD %, 1Y %, RS Rank 1M, SMA flags, % off
// 52w high) to the standard ScreenerResult. Filter language matches
// /api/screener, so the same filter sidebar can drive either endpoint.

export type CompactScreenerRow = {
  ticker: string;
  name: string;
  price: number;
  changePct: number;
  volume: number;
  avgVolume: number | null;
  marketCap: number;
  pe: number | null;
  analystRating: string;
  sector: string;
  industry: string;
  exchange: string;
  prevClose: number | null;
  rsRating: number | null;
  rsi14: number | null;
  /** Combined Buy/Hold/Sell trend signal (plan #363) — weekly-gated daily.
   *  Null until the nightly sweep has rated this ticker (needs >= 200 daily
   *  bars). */
  signal: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  /** Human-readable reason for the combined signal. */
  signalReason: string | null;
  /** Per-timeframe components of `signal` (plan #363 phase 2). */
  signalDaily: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  signalWeekly: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  /** SIQ Score — how many of the 8 stage-2 uptrend criteria the stock
   *  passes (0–8). Null until the nightly sweep has rated it (needs ~221
   *  daily bars). `siqScoreReason` names what it misses. */
  siqScore: number | null;
  siqScoreReason: string | null;
  /** Δ vs ~7d / ~30d-ago rs_rating_history snapshots. Positive =
   *  improving (rank climbing / RSI rising). Null when no snapshot
   *  exists for that date. */
  rsDelta7d: number | null;
  rsiDelta7d: number | null;
  rsDelta30d: number | null;
  rsiDelta30d: number | null;
  /** Forward dividend yield in percent (e.g. 1.42 = 1.42%). Sourced
   *  from tickers.dividend_yield, populated by fundamentals sync.
   *  Null for non-dividend-paying stocks. */
  dividendYield: number | null;
  inSp500: boolean;
  inRussell1000: boolean;
  high52: number | null;
  low52: number | null;
  // Bar-derived columns.
  /** Calendar-YTD price change. Null when no Jan 2 anchor bar exists. */
  ytdChangePct: number | null;
  /** Trailing 252-day price change. Null when daily history is short. */
  oneYearChangePct: number | null;
  /** (price - high_52) / high_52, expressed as %. Always ≤ 0 in
   *  practice. Null when high_52 is missing/zero. */
  pctOff52High: number | null;
  /** 0-100 percentile rank of the ticker's 1-month return against the
   *  S&P 500 1M distribution. S&P members get a direct rank; non-members
   *  are placed against the same distro via binary search. */
  rsRank1M: number | null;
  /** Price > SMA20 / SMA50 / SMA200. Null when the SMA can't be
   *  computed yet (insufficient history). */
  sma20Above: boolean | null;
  sma50Above: boolean | null;
  sma200Above: boolean | null;
  /** Signed % distance of the live price from the 200-day SMA:
   *  (price - sma200) / sma200 * 100. Negative = below the line
   *  ("approaching from below" zone), positive = above. Null when
   *  the SMA200 can't be computed (insufficient history). */
  distToSma200Pct: number | null;
  /** DIRECTION flag: true when price is below the 200-day SMA AND
   *  rising into it — the gap narrowed over the trailing ~5 closed
   *  bars with price climbing. This carries NO proximity: a stock 40%
   *  below its SMA that's ticking up is still `true`. Combine with
   *  `distToSma200Pct` and a proximity band to get the actual
   *  "approaching a reclaim" signal (the UI does this). Null when
   *  there's insufficient history to judge. */
  sma200RisingFromBelow: boolean | null;
  /** Price-to-sales (market_cap / TTM revenue). Null when revenue
   *  isn't cached or market cap is zero. */
  priceToSales: number | null;
  /** TTM revenue (raw USD). Sourced from financial_statements
   *  cache — latest annual income statement when available,
   *  else sum of last 4 quarterlies. Null when no statement is
   *  cached. */
  revenue: number | null;
  /** TTM net income (raw USD; can be negative). Same source as
   *  revenue. */
  netIncome: number | null;
  /** ~52-point weekly close series covering the trailing year,
   *  oldest → newest. Drives the inline sparkline column. Optional
   *  because the compact route omits this field by default to keep
   *  the initial payload small; the table fetches sparklines for
   *  just the visible page via `getCompactScreenerSparklines`. */
  closes1Y?: number[] | null;
};

export async function getCompactScreenerResults(filters?: ScreenerFilters): Promise<CompactScreenerRow[]> {
  const qs = buildScreenerQS(filters);
  return request<CompactScreenerRow[]>(`/screener/compact${qs ? `?${qs}` : ''}`);
}

// ── Trend-signal flips (plan #363, Phase 3) ──────────────────────────────────

export type SignalTimeframe = 'combined' | 'daily' | 'weekly';

/** One recorded signal flip — a ticker whose signal changed vs its prior
 *  snapshot for a given timeframe. Served by GET /api/signals/flips. */
export type SignalFlip = {
  ticker: string;
  name: string;
  /** Close + day move of the bar the signal was SCORED from — the price when
   *  this flip fired, not today's quote. Null on flips recorded before these
   *  were captured (change_pct is forward-only, so older flips show "—"). */
  price: number | null;
  changePct: number | null;
  /** RSI(14) on that same scored bar — the momentum reading behind the flip,
   *  not today's. Null for flips recorded before it was captured. */
  rsi?: number | null;
  /** The ticker's live quote now — for "where is it today" context (e.g. the
   *  support/resistance marker), never for the flip's own price cell. */
  currentPrice?: number | null;
  currentChangePct?: number | null;
  /** Date of the bar the signal was scored from. Trails etDate by at least one
   *  session — the sweep stamps wall-clock today but scores the last cached bar. */
  barDate?: string | null;
  sector: string;
  /** ET date of the flip, 'YYYY-MM-DD'. */
  etDate: string;
  timeframe: SignalTimeframe;
  signal: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  /** The signal the ticker flipped FROM (null if first record). */
  prevSignal: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  reason: string | null;
  score: number | null;
  signalDaily: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  signalWeekly: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  /** The ticker's live SIQ Score (0–8) + what it misses — today's re-rating,
   *  like currentPrice, not a snapshot at the flip. Null when unrated. */
  siqScore?: number | null;
  siqScoreReason?: string | null;
};

export type SignalFlipsParams = {
  timeframe?: SignalTimeframe;
  /** Keep only flips INTO this signal. */
  signal?: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  limit?: number;
};

export async function getSignalFlips(params?: SignalFlipsParams): Promise<SignalFlip[]> {
  const qs = new URLSearchParams();
  if (params?.timeframe) qs.set('timeframe', params.timeframe);
  if (params?.signal) qs.set('signal', params.signal);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return request<SignalFlip[]>(`/signals/flips${s ? `?${s}` : ''}`);
}

export type SignalFlipUniverse = 'sp500' | 'r1000' | 'watchlist' | 'all';

/** 50/200 SMA cross state. 'golden' = the 50 is above the 200. */
export type MaCrossKind = 'golden' | 'death';

export interface MaCrossRow {
  ticker: string;
  name: string;
  sector: string;
  price: number | null;
  changePct: number | null;
  sma50: number | null;
  sma200: number | null;
  /** The 50's distance past the 200 as a percent of the 200. Near zero means
   *  the averages are still braided and the cross hasn't separated yet. */
  spreadPct: number | null;
  cross: MaCrossKind;
  /** Bar date the pair crossed (YYYY-MM-DD). */
  crossDate: string;
  /** Calendar days from the cross to today. */
  daysSince: number | null;
  rsRating: number | null;
  signal: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
}

export interface MaCrossMeta {
  total: number;
  cross: MaCrossKind | 'any';
  universe: SignalFlipUniverse;
  withinDays: number | null;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface MaCrossParams {
  cross?: MaCrossKind | 'any';
  universe?: SignalFlipUniverse;
  /** Explicit allowlist — how universe='watchlist' is served, since the public
   *  endpoint can't resolve the caller's watchlist server-side. */
  tickers?: string[];
  /** Only crosses this recent. Omit or pass 0 for no bound. */
  withinDays?: number;
  limit?: number;
  offset?: number;
}

/** Recent 50/200 SMA crosses, newest first. Backed by columns the nightly
 *  rs-rating sweep writes, so this is an indexed read, not bar math. */
export async function getMaCrosses(
  params: MaCrossParams = {},
): Promise<{ data: MaCrossRow[]; meta: MaCrossMeta }> {
  const qs = new URLSearchParams();
  if (params.cross) qs.set('cross', params.cross);
  if (params.universe) qs.set('universe', params.universe);
  if (params.tickers && params.tickers.length) qs.set('tickers', params.tickers.join(','));
  if (params.withinDays !== undefined) qs.set('withinDays', String(params.withinDays));
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const s = qs.toString();
  const fallback: MaCrossMeta = {
    total: 0,
    cross: params.cross ?? 'golden',
    universe: params.universe ?? 'all',
    withinDays: params.withinDays ?? null,
    limit: params.limit ?? 100,
    offset: params.offset ?? 0,
    hasMore: false,
  };
  const response = await fetch(`${BASE_URL}/signals/ma-cross${s ? `?${s}` : ''}`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  const text = await response.text();
  if (!text) return { data: [], meta: fallback };
  const json = JSON.parse(text);
  return { data: (json?.data ?? []) as MaCrossRow[], meta: (json?.meta ?? fallback) as MaCrossMeta };
}

// ── 50/200 cross backtest (admin) ───────────────────────────────────────────

/** Universes these admin endpoints can scope to. Deliberately no 'all': the
 *  backtest walk needs a 201-bar window per candidate bar, and the sync issues
 *  a row update per ticker — both far too costly across ~12k symbols. */
export type MaCrossScope = 'watchlist' | 'sp500' | 'r1000';

export interface MaCrossHorizonStats {
  /** Trading days forward. */
  horizon: number;
  /** Hits that have aged into this horizon. */
  n: number;
  winRate: number;
  avgReturn: number;
  medianReturn: number;
  avgBenchmarkReturn?: number | null;
  /** avgReturn - avgBenchmarkReturn. The number that actually answers
   *  "is there edge here" for a long-biased signal. */
  avgExcessReturn?: number | null;
  /** Median of (hit - benchmark). Skew-resistant, so this is the honest
   *  centre; the average is inflated by single-stock right-skew. */
  medianExcessReturn?: number | null;
  beatBenchmarkRate?: number | null;
}

export interface MaCrossBacktestHit {
  date: string;
  ticker: string;
  spikeRvol: number;
  changePct: number;
  spikePrice: number;
  forwardReturns: Array<{ horizon: number; returnPct: number | null }>;
  benchmarkReturns?: Array<{ horizon: number; returnPct: number | null }>;
  details?: Record<string, number | string | null>;
}

export interface MaCrossBacktest {
  cfg: { fast: number; slow: number; direction: MaCrossKind };
  universe: string;
  universeSize: number | null;
  since: string;
  until: string;
  horizons: number[];
  benchmark: string;
  totalHits: number;
  distinctTickers: number;
  byHorizon: MaCrossHorizonStats[];
  hits: MaCrossBacktestHit[];
  capped: number | null;
}

export interface MaCrossBacktestParams {
  /** 'watchlist' (default) resolves the calling admin's own lists server-side. */
  universe?: MaCrossScope;
  /** Drill down to one symbol; overrides `universe`. */
  ticker?: string;
  direction?: MaCrossKind;
  fast?: number;
  slow?: number;
  since?: string;
  until?: string;
  /** Trading-day forward horizons. Defaults to 21/63/126/252. */
  horizons?: number[];
}

/** Replay the 50/200 cross over historical daily bars. Admin-only. */
export async function getMaCrossBacktest(params: MaCrossBacktestParams = {}): Promise<MaCrossBacktest> {
  const qs = new URLSearchParams();
  if (params.universe) qs.set('universe', params.universe);
  if (params.ticker) qs.set('ticker', params.ticker);
  if (params.direction) qs.set('direction', params.direction);
  if (params.fast !== undefined) qs.set('fast', String(params.fast));
  if (params.slow !== undefined) qs.set('slow', String(params.slow));
  if (params.since) qs.set('since', params.since);
  if (params.until) qs.set('until', params.until);
  if (params.horizons?.length) qs.set('horizons', params.horizons.join(','));
  const s = qs.toString();
  return request<MaCrossBacktest>(`/admin/ma-cross/backtest${s ? `?${s}` : ''}`);
}

export interface SignalFlipsMeta {
  /** Full filtered count (not the page size). */
  total: number;
  timeframe: SignalTimeframe;
  limit: number;
  offset: number;
  hasMore: boolean;
  /** MIN(et_date) of all flips for the timeframe — the lower bound of available
   *  history (first page only; null on later pages / empty). */
  historyBegins?: string | null;
  /** Present only on the groupBy=ticker (by-ticker rollup) response. */
  groupBy?: 'ticker';
}

export interface SignalFlipsHistoryParams {
  timeframe?: SignalTimeframe;
  signal?: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  universe?: SignalFlipUniverse;
  /** Explicit ticker allowlist — the caller resolves it for universe='watchlist'
   *  (the public endpoint can't read the user's watchlist server-side). */
  tickers?: string[];
  /** Single-symbol search — flips for ONE ticker; overrides `universe`. */
  ticker?: string;
  /** Inclusive flip-date bounds (YYYY-MM-DD). */
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

/** Like getSignalFlips but paginated + universe-filtered, and preserves `meta`
 *  (total / hasMore / offset) so the page can render "N–M of TOTAL" + Prev/Next.
 *  The plain getSignalFlips unwraps `.data` and drops meta. */
export async function getSignalFlipsHistory(
  params: SignalFlipsHistoryParams = {},
): Promise<{ data: SignalFlip[]; meta: SignalFlipsMeta }> {
  const qs = new URLSearchParams();
  if (params.timeframe) qs.set('timeframe', params.timeframe);
  if (params.signal) qs.set('signal', params.signal);
  if (params.universe) qs.set('universe', params.universe);
  if (params.tickers && params.tickers.length) qs.set('tickers', params.tickers.join(','));
  if (params.ticker) qs.set('ticker', params.ticker);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const s = qs.toString();
  const fallback: SignalFlipsMeta = {
    total: 0, timeframe: params.timeframe ?? 'combined',
    limit: params.limit ?? 100, offset: params.offset ?? 0, hasMore: false, historyBegins: null,
  };
  const response = await fetch(`${BASE_URL}/signals/flips${s ? `?${s}` : ''}`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  const text = await response.text();
  if (!text) return { data: [], meta: fallback };
  const json = JSON.parse(text);
  return { data: (json?.data ?? []) as SignalFlip[], meta: (json?.meta ?? fallback) as SignalFlipsMeta };
}

export interface SignalFlipTickerRow {
  ticker: string;
  name: string;
  price: number | null;
  changePct: number | null;
  /** The ticker's LIVE RSI(14) — this row's price/day-move are the live quote
   *  too, so the reading matches them rather than the last flip's bar. */
  rsi?: number | null;
  sector: string;
  /** How many times this ticker flipped within the current filters. */
  flipCount: number;
  /** Date of the most recent flip (YYYY-MM-DD). */
  lastDate: string;
  timeframe: SignalTimeframe;
  /** The latest flip's transition (from = prevSignal, to = signal). */
  signal: SignalTier | null;
  prevSignal: SignalTier | null;
  reason: string | null;
  /** The ticker's live current signal. */
  currentSignal: SignalTier | null;
  signalDaily: SignalTier | null;
  signalWeekly: SignalTier | null;
}

/** By-ticker rollup of the flips feed (groupBy=ticker): one row per UNIQUE
 *  ticker with its flip count + latest flip + current signal. Same filters as
 *  getSignalFlipsHistory; paginated by distinct ticker. */
export async function getSignalFlipsByTicker(
  params: SignalFlipsHistoryParams = {},
): Promise<{ data: SignalFlipTickerRow[]; meta: SignalFlipsMeta }> {
  const qs = new URLSearchParams();
  qs.set('groupBy', 'ticker');
  if (params.timeframe) qs.set('timeframe', params.timeframe);
  if (params.signal) qs.set('signal', params.signal);
  if (params.universe) qs.set('universe', params.universe);
  if (params.tickers && params.tickers.length) qs.set('tickers', params.tickers.join(','));
  if (params.ticker) qs.set('ticker', params.ticker);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const fallback: SignalFlipsMeta = {
    total: 0, timeframe: params.timeframe ?? 'combined',
    limit: params.limit ?? 100, offset: params.offset ?? 0, hasMore: false, historyBegins: null,
  };
  const response = await fetch(`${BASE_URL}/signals/flips?${qs.toString()}`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  const text = await response.text();
  if (!text) return { data: [], meta: fallback };
  const json = JSON.parse(text);
  return { data: (json?.data ?? []) as SignalFlipTickerRow[], meta: (json?.meta ?? fallback) as SignalFlipsMeta };
}

/** Batch-fetch sparkline closes for the visible page of tickers.
 *  `1y` returns ~52 weekly-sampled points; `5d` returns the last five daily
 *  closes. Up to 100 tickers per request. */
export async function getCompactScreenerSparklines(
  tickers: string[],
  range: '5d' | '1y' = '1y',
): Promise<Record<string, number[] | null>> {
  if (tickers.length === 0) return {};
  const qs = new URLSearchParams({ tickers: tickers.join(','), range }).toString();
  return request<Record<string, number[] | null>>(`/screener/sparklines?${qs}`);
}

// ── Generic Saved Filters ────────────────────────────────────────────────
// Backs the scanner ("kind=scanner") and the stock screener
// ("kind=screener") today. Each kind has its own namespace inside the
// shared `saved_filters` table (UNIQUE(user_id, kind, name) on the
// server). The `filters` payload is a free JSONB blob — each calling
// page picks what to serialize (filter state, column visibility,
// sort, etc.).

export interface SavedFilter<T = Record<string, unknown>> {
  id: string;
  name: string;
  filters: T;
  createdAt: string;
  updatedAt: string;
}

export interface SavedFilterList<T = Record<string, unknown>> {
  searches: SavedFilter<T>[];
  /** ID of the user's currently "active" / default save for this
   *  kind, or null when nothing is active. Pages typically
   *  auto-apply this on fresh load (when URL params haven't
   *  overridden). */
  activeId: string | null;
}

export async function getSavedFilters<T = Record<string, unknown>>(kind: string): Promise<SavedFilterList<T>> {
  return request<SavedFilterList<T>>(`/saved-filters/${encodeURIComponent(kind)}`);
}

/** Create or upsert-by-name. Saving with an existing name overwrites
 *  that row's filters (same upsert semantics the scanner endpoint
 *  uses today). */
export async function createSavedFilter<T = Record<string, unknown>>(
  kind: string,
  name: string,
  filters: T,
): Promise<SavedFilter<T>> {
  return request<SavedFilter<T>>(`/saved-filters/${encodeURIComponent(kind)}`, {
    method: 'POST',
    body: JSON.stringify({ name, filters }),
  });
}

/** Update one or both of name / filters. Pass only the fields you
 *  want to change; the rest stay as-is on the server. */
export async function updateSavedFilter<T = Record<string, unknown>>(
  kind: string,
  id: string,
  patch: { name?: string; filters?: T },
): Promise<SavedFilter<T>> {
  return request<SavedFilter<T>>(`/saved-filters/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export async function deleteSavedFilter(kind: string, id: string): Promise<{ deleted: string }> {
  return request<{ deleted: string }>(`/saved-filters/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/** Set or clear the active / default pointer for this kind. Pass
 *  null to clear. */
export async function setActiveSavedFilter(kind: string, savedFilterId: string | null): Promise<{ activeId: string | null }> {
  return request<{ activeId: string | null }>(`/saved-filters/${encodeURIComponent(kind)}/active`, {
    method: 'POST',
    body: JSON.stringify({ savedFilterId }),
  });
}

// ── Developer API Tokens ───────────────────────────────────────────────────────

export interface ApiToken {
  id: string;
  userId: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface ApiTokenCreated extends ApiToken {
  token: string; // raw token, shown only once
}

export async function listApiTokens(): Promise<ApiToken[]> {
  return request<ApiToken[]>('/auth/api-tokens');
}

export async function createApiToken(name: string): Promise<ApiTokenCreated> {
  return request<ApiTokenCreated>('/auth/api-tokens', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function revokeApiToken(id: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/auth/api-tokens/${id}`, { method: 'DELETE' });
}

// ── Site Branding ──────────────────────────────────────────────────────────────

export type LogoDesignPreset = {
  font?: string | null;
  useGradient?: boolean;
  gradStart?: string;
  gradEnd?: string;
  solidColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  fontWeight?: number;
};
export type BrandingSettings = {
  siteName: string;
  logoUrl: string | null;
  /** Optional icon-only mobile variant. When set, the navbar swaps to
   *  this on narrow viewports so wordmark + PRO badge don't overlap
   *  with the tab bar buttons on small screens. */
  logoUrlMobile?: string | null;
  tagline: string;
  providerUserAgent?: string;
  /** Round-trips the in-place font picker + text-styling controls so
   *  the operator's last-used choices survive a page reload. */
  logoDesign?: LogoDesignPreset;
};

export async function adminGetBrandingSettings(): Promise<BrandingSettings> {
  return request<BrandingSettings>('/admin/settings/branding');
}

export async function adminSaveBrandingSettings(data: Partial<BrandingSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/branding', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ── Heatmap ───────────────────────────────────────────────────────────────────

export interface HeatmapStock {
  ticker: string;
  name: string;
  price: number;
  changePct: number;
  marketCapB: number;
}

export interface HeatmapGroup {
  sector: string;
  industry: string;
  stocks: HeatmapStock[];
}

export type HeatmapUniverse = 'sp500' | 'russell1000' | 'etfs' | 'all' | 'watchlist';

/** Performance window the heatmap cell colors represent. */
export type HeatmapPeriod = '1d' | '1w' | '1m' | '3m' | 'ytd';

export interface HeatmapData {
  groups: HeatmapGroup[];
  updatedAt: string;
  universe?: HeatmapUniverse;
  period?: HeatmapPeriod;
}

export async function getHeatmap(
  universe?: HeatmapUniverse,
  period?: HeatmapPeriod,
): Promise<HeatmapData | null> {
  try {
    const qs = new URLSearchParams();
    if (universe) qs.set('universe', universe);
    if (period) qs.set('period', period);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request<HeatmapData>(`/heatmap${suffix}`);
  } catch {
    return null;
  }
}

export interface HeatmapSparklines {
  asOf: string;
  tickers: Record<string, number[]>;
}

export async function getHeatmapSparklines(tickers: string[]): Promise<HeatmapSparklines | null> {
  if (tickers.length === 0) return { asOf: new Date().toISOString(), tickers: {} };
  try {
    const qs = encodeURIComponent(tickers.join(','));
    return await request<HeatmapSparklines>(`/heatmap/sparklines?tickers=${qs}`);
  } catch {
    return null;
  }
}

export async function adminGetHeatmapSettings(): Promise<{ enabled: boolean; refreshMinutes: number; lastRefreshedAt: string | null }> {
  return request<{ enabled: boolean; refreshMinutes: number; lastRefreshedAt: string | null }>('/admin/settings/heatmap');
}

export async function adminSaveHeatmapSettings(settings: { enabled: boolean; refreshMinutes: number }): Promise<void> {
  await request('/admin/settings/heatmap', { method: 'POST', body: JSON.stringify(settings) });
}

export async function adminTriggerHeatmapRefresh(): Promise<void> {
  await request('/admin/settings/heatmap/refresh', { method: 'POST' });
}

// ── Polling Settings ─────────────────────────────────────────────────────────

export type PollingSettings = {
  marketsRefreshSeconds: number;
  etfRefreshSeconds: number;
  stocksRefreshSeconds: number;
  dashboardRefreshMinutes: number;
};

export async function getPublicPollingSettings(): Promise<PollingSettings> {
  return request<PollingSettings>('/admin/public/polling');
}

export async function adminGetPollingSettings(): Promise<PollingSettings> {
  return request<PollingSettings>('/admin/settings/polling');
}

export async function adminSavePollingSettings(settings: PollingSettings): Promise<void> {
  await request('/admin/settings/polling', { method: 'POST', body: JSON.stringify(settings) });
}

// ── Ticker Management ─────────────────────────────────────────────────────────

export interface TickerRow {
  symbol: string;
  name: string;
  type: 'stock' | 'etf';
  exchange: string;
  sector: string;
  industry: string;
  marketCap: number | null;
  price: number | null;
  changePct: number | null;
  priceUpdatedAt: string | null;
  heatmap: boolean;
  /** Plan #136 — Russell 1000 membership flag. Set by the iShares IWB sync
   *  (admin tickers → "Sync R1000"). Read-only in the UI. */
  russell1000?: boolean;
  /** Nasdaq-100 membership flag. Set by the Nasdaq constituent sync. */
  ndx100?: boolean;
  lastSyncedAt: string | null;
  createdAt: string;
  /** Plan #157 — provenance + lifecycle. Status='inactive' means the
   *  provider's listActiveSymbols stopped including this symbol;
   *  historical bars + trade rows are preserved. */
  status?: 'active' | 'inactive';
  ipoDate?: string | null;
  priceSyncProvider?: string | null;
  metadataSyncProvider?: string | null;
}

export interface TickerSyncInfo {
  enabled: boolean;
  provider: string;
  syncIntervalDays: number;
  lastSyncAt: string | null;
  status: 'idle' | 'syncing' | 'error';
  total: number;
  done: number;
  lastError: string | null;
  /** Per-ticker tally from the most recently completed run. Both 0 until
   *  the first run lands. */
  lastSyncedCount?: number;
  lastFailedCount?: number;
}

/** Plan #157 — ticker-universe discovery sync. Pulls the provider's full
 *  active+delisted symbol list, inserts new symbols, flags delistings.
 *  Plan #265 — replaced interval-based fire with ET cadence + clock time
 *  (daily or weekly + fireTimeEt). intervalDays is kept for legacy reads. */
export interface TickerDiscoveryInfo {
  enabled: boolean;
  intervalDays: number;
  cadence?: 'daily' | 'weekly';
  /** 0 = Sunday … 6 = Saturday. Only used when cadence='weekly'. */
  dayOfWeekEt?: number;
  fireTimeEt?: string;
  lastRunAt: string | null;
  lastNewCount: number;
  lastDelistedCount: number;
  lastError: string | null;
  state: {
    status: 'idle' | 'running' | 'error';
    runStartedAt: string | null;
    nextScheduledAt: string | null;
  };
}

export async function adminGetTickerDiscovery(): Promise<TickerDiscoveryInfo> {
  return request<TickerDiscoveryInfo>('/admin/settings/ticker-discovery');
}

export async function adminSaveTickerDiscovery(input: {
  enabled?: boolean;
  intervalDays?: number;
  cadence?: 'daily' | 'weekly';
  dayOfWeekEt?: number;
  fireTimeEt?: string;
}): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/ticker-discovery', { method: 'POST', body: JSON.stringify(input) });
}

export async function adminRunTickerDiscovery(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/ticker-discovery/run', { method: 'POST' });
}

export interface TickersPage {
  tickers: TickerRow[];
  total: number;
  page: number;
  pages: number;
}

export async function adminGetTickerSyncSettings(): Promise<TickerSyncInfo> {
  return request<TickerSyncInfo>('/admin/settings/ticker-sync');
}

export async function adminSaveTickerSyncSettings(data: { enabled?: boolean; provider?: string; syncIntervalDays?: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/ticker-sync', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminForceTickerSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/ticker-sync/force', { method: 'POST' });
}

export async function adminGetTickers(params?: { page?: number; search?: string; searchMode?: 'all' | 'symbol'; type?: string; limit?: number; sort?: string; dir?: 'asc' | 'desc'; index?: 'all' | 'sp500' | 'r1000' | 'ndx100' }): Promise<TickersPage> {
  const p = new URLSearchParams();
  if (params?.page) p.set('page', String(params.page));
  if (params?.search) p.set('search', params.search);
  if (params?.searchMode === 'symbol') p.set('searchMode', 'symbol');
  if (params?.type) p.set('type', params.type);
  if (params?.limit) p.set('limit', String(params.limit));
  if (params?.sort) p.set('sort', params.sort);
  if (params?.dir)  p.set('dir', params.dir);
  if (params?.index && params.index !== 'all') p.set('index', params.index);
  const qs = p.toString();
  return request<TickersPage>(`/admin/tickers${qs ? `?${qs}` : ''}`);
}

export async function adminAddTicker(data: { symbol: string; type: 'stock' | 'etf' }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/tickers', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminUpdateTicker(symbol: string, data: { heatmap: boolean }): Promise<{ message: string }> {
  return request<{ message: string }>(`/admin/tickers/${encodeURIComponent(symbol)}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function adminDeleteTicker(symbol: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/admin/tickers/${encodeURIComponent(symbol)}`, { method: 'DELETE' });
}

export async function adminExcludeTicker(symbol: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/admin/tickers/${encodeURIComponent(symbol)}/exclude`, { method: 'POST' });
}

export async function adminUnexcludeTicker(symbol: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/admin/tickers/${encodeURIComponent(symbol)}/unexclude`, { method: 'POST' });
}

export interface CleanupStaleResult {
  dryRun: boolean;
  days: number;
  tickersDeleted: number;
  orphans: Array<{ table: string; count: number }>;
  totalOrphans: number;
}
export async function adminCleanupStaleTickers(days: number, dryRun: boolean): Promise<CleanupStaleResult> {
  const qs = dryRun ? '?dryRun=1' : '';
  return request<CleanupStaleResult>(`/admin/tickers/cleanup-stale${qs}`, {
    method: 'POST',
    body: JSON.stringify({ days }),
  });
}

export async function adminSyncOneTicker(symbol: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/admin/tickers/${encodeURIComponent(symbol)}/sync`, { method: 'POST' });
}

export async function adminGetTickerDetail(symbol: string): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(`/admin/tickers/${encodeURIComponent(symbol)}/detail`);
}

export async function adminImportTickersCSV(rows: { symbol: string; type?: string; name?: string; heatmap?: boolean }[]): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/tickers/import/csv', { method: 'POST', body: JSON.stringify({ rows }) });
}

/** Live-price slice returned by `adminSyncTickerPrice` after the admin POST
 *  has written the freshly-fetched provider quote to `tickers`. Detail-page
 *  state merges these fields in directly so the manual-refresh path costs
 *  one provider call instead of two (the prior shape's `?fresh=1` follow-up
 *  GET re-hit the provider for data we'd just written). Same shape covers
 *  both stocks + ETFs since the relevant columns are identical. */
export interface TickerPriceUpdate {
  price: number;
  change: number;
  changePct: number;
  /** null when the provider had no volume (typical in pre-market on AV) —
   *  caller should preserve its prior value rather than overwrite with null. */
  volume: number | null;
  prevClose: number;
  /** null when the provider had no extended-hours quote (illiquid name
   *  outside RTH, or RTH session where extended is stale) AND no prior
   *  value was on the DB row. */
  extendedHoursPrice: number | null;
  extendedHoursChange: number | null;
  extendedHoursChangePct: number | null;
  /** ISO timestamp of the just-written row. Drives the "Updated Xs ago"
   *  stamp without the client having to read its own clock. */
  priceUpdatedAt: string;
}

export async function adminSyncTickerPrice(symbol: string): Promise<{ message: string; quote: TickerPriceUpdate }> {
  return request<{ message: string; quote: TickerPriceUpdate }>(`/admin/tickers/${encodeURIComponent(symbol)}/sync-price`, { method: 'POST' });
}

export async function adminSyncSP500Heatmap(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/tickers/sync-sp500-heatmap', { method: 'POST' });
}

/** Plan #136 — sync Russell 1000 universe from iShares IWB daily holdings CSV.
 *  Upserts ~1000 tickers and sets russell_1000 = TRUE; clears the flag on
 *  names that dropped out of the index. */
export async function adminSyncRussell1000(): Promise<{ message: string; count: number; newRows: number }> {
  return request<{ message: string; count: number; newRows: number }>('/admin/tickers/sync-russell1000', { method: 'POST' });
}

// ── Global AI Opportunity Feed settings ────────────────────────────────────

export interface AiOpportunityFeedAdminSettings {
  enabled: boolean;
  startTimeEt: string;
  endTimeEt: string;
  intervalMinutes: number;
  universe: AiOpportunityFeedUniverse;
}

export async function adminGetAiOpportunityFeedSettings(): Promise<AiOpportunityFeedAdminSettings> {
  return request<AiOpportunityFeedAdminSettings>('/admin/ai-setup/opportunity-feed');
}

export async function adminSaveAiOpportunityFeedSettings(
  settings: AiOpportunityFeedAdminSettings,
): Promise<AiOpportunityFeedAdminSettings> {
  return request<AiOpportunityFeedAdminSettings>('/admin/ai-setup/opportunity-feed', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

/** Sync Nasdaq-100 membership from Nasdaq's public constituent table. */
export async function adminSyncNdx100(): Promise<{ message: string; count: number; newRows: number; source: string }> {
  return request<{ message: string; count: number; newRows: number; source: string }>('/admin/tickers/sync-ndx100', { method: 'POST' });
}

export interface IndexMembershipSyncOutcome {
  key: 'sp500' | 'r1000' | 'ndx100';
  label: string;
  ok: boolean;
  count: number;
  newRows: number;
  source: string | null;
  error: string | null;
}

export async function adminSyncIndexMemberships(): Promise<{
  message: string;
  results: IndexMembershipSyncOutcome[];
  successCount: number;
  failureCount: number;
}> {
  return request('/admin/tickers/sync-index-memberships', { method: 'POST' });
}

// ── IPO Sync ─────────────────────────────────────────────────────────────────

export interface IpoSyncSettings {
  enabled: boolean;
  provider: string;
  intervalHours: number;
  lastSyncAt: string | null;
  status: 'idle' | 'syncing' | 'error';
  lastError: string | null;
  lastCount: number;
}

export async function adminGetIpoSyncSettings(): Promise<IpoSyncSettings> {
  return request<IpoSyncSettings>('/admin/settings/ipo-sync');
}

export async function adminSaveIpoSyncSettings(data: { enabled: boolean; provider?: string; intervalHours: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/ipo-sync', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminForceIpoSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/ipo-sync/force', { method: 'POST' });
}

// ── Index Sync (issue #290) ──────────────────────────────────────────────────

export interface IndexSyncSettings {
  enabled: boolean;
  provider: string;
  symbols: string[];
  /** Hour-of-day (ET, 0-23) the daily top-up fires. Default 16. */
  runAtEtHour: number;
  /** Minute-of-hour (0-59) the daily top-up fires. Default 30. */
  runAtEtMinute: number;
  /** Skip Sat/Sun ticks. Default true. */
  weekdaysOnly: boolean;
  // live state (from getIndexHistoryState)
  status: 'idle' | 'running';
  lastBackfillAt: string | null;
  lastTopUpAt: string | null;
  lastRowsUpserted: number;
  lastError: string | null;
  nextScheduledAt: string | null;
  // metadata for the UI
  curatedSymbols: { symbol: string; name: string }[];
  baseSymbols: string[];
}

export async function adminGetIndexSyncSettings(): Promise<IndexSyncSettings> {
  return request<IndexSyncSettings>('/admin/settings/index-sync');
}

export async function adminSaveIndexSyncSettings(data: {
  enabled: boolean; provider?: string; symbols: string[];
  runAtEtHour?: number; runAtEtMinute?: number; weekdaysOnly?: boolean;
}): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/index-sync', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminForceIndexSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/index-sync/force', { method: 'POST' });
}

/** Index chart bars (issue #290). Daily from index_history; intraday live from Yahoo. */
export async function getIndexHistory(symbol: string, resolution: 'daily' | 'intraday' = 'daily', days = 90): Promise<OHLCV[]> {
  return request<OHLCV[]>(`/markets/index-history/${encodeURIComponent(symbol)}?resolution=${resolution}&days=${days}`);
}

// ── Dividend Sync ────────────────────────────────────────────────────────────

export interface SyncSectionState {
  enabled: boolean;
  provider: string;
  intervalHours: number;
  lastSyncAt: string | null;
  status: 'idle' | 'syncing' | 'error';
  lastError: string | null;
  lastCount: number;
  lastDurationMs?: number | null;
  syncTotal?: number;
  syncDone?: number;
  // Optional — used only by fundamentals sync today
  staleAfterHours?: number;
  batchSize?: number;
  /** Hour-of-day (ET, 0-23) for the daily fundamentals sweep. Default 18.
   *  Always weekdays-only — weekends are skipped server-side regardless. */
  runAtEtHour?: number;
  // Optional — used only by earnings sync today. Provider for per-ticker
  // on-demand earnings-history calls (Stock Compare card). Independent
  // of the bulk-sync `provider` field above.
  cardProvider?: string;
  // Optional — used only by financial sync today. Scheduled-run config
  // for the universe-scope walk (S&P 500 or Russell 1000) that builds
  // financial_statements coverage for the screener's P/S column.
  scheduledEnabled?: boolean;
  scope?: 'sp500' | 'russell1000';
  frequency?: 'weekly' | 'monthly';
  scheduledDay?: number;
  scheduledTime?: string;
  nextScheduledAt?: string | null;
  runScope?: 'sp500' | 'russell1000' | null;
  syncOk?: number;
  syncFailed?: number;
  lastFailedCount?: number;
}

export async function adminGetDividendSyncSettings(): Promise<SyncSectionState> {
  return request<SyncSectionState>('/admin/settings/dividend-sync');
}
export async function adminSaveDividendSyncSettings(data: { enabled?: boolean; provider?: string; intervalHours?: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/dividend-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceDividendSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/dividend-sync/force', { method: 'POST' });
}

// ── Commodity Sync ───────────────────────────────────────────────────────────
// Yahoo-backed quote refresh for the 5 tracked commodity futures (oil, brent,
// gold, silver, nat gas). Powers the /markets commodities tile. Uses minutes
// (not hours) for the interval — the dashboard expects intra-day refreshes.
export interface CommoditySyncState {
  enabled: boolean;
  intervalMinutes: number;
  lastSyncAt: string | null;
  status: 'idle' | 'syncing' | 'error';
  lastError: string | null;
  lastCount: number;
}
export async function adminGetCommoditySyncSettings(): Promise<CommoditySyncState> {
  return request<CommoditySyncState>('/admin/settings/commodity-sync');
}
export async function adminSaveCommoditySyncSettings(data: { enabled?: boolean; intervalMinutes?: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/commodity-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceCommoditySync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/commodity-sync/force', { method: 'POST' });
}

// ── Options Chain Sync (issue #247) ─────────────────────────────────────────

/** Schedule-driven (not interval-based) nightly options-chain sweep. */
export interface OptionsChainSyncState {
  enabled: boolean;
  runHourEt: number;
  runMinuteEt: number;
  concurrency: number;
  lastSyncAt: string | null;
  lastTickersAttempted: number | null;
  lastTickersSynced: number | null;
  lastRowsTotal: number | null;
  lastFailedCount: number | null;
  status: 'idle' | 'syncing' | 'error';
  lastError: string | null;
}

export async function adminGetOptionsChainSyncSettings(): Promise<OptionsChainSyncState> {
  return request<OptionsChainSyncState>('/admin/settings/options-chain-sync');
}
export async function adminSaveOptionsChainSyncSettings(data: { enabled?: boolean; runHourEt?: number; runMinuteEt?: number; concurrency?: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/options-chain-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceOptionsChainSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/options-chain-sync/force', { method: 'POST' });
}

// ── Company Profile Sync ────────────────────────────────────────────────────

export async function adminGetCompanyProfileSyncSettings(): Promise<SyncSectionState> {
  return request<SyncSectionState>('/admin/settings/company-profile-sync');
}
export async function adminSaveCompanyProfileSyncSettings(data: { enabled?: boolean; provider?: string; intervalHours?: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/company-profile-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceCompanyProfileSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/company-profile-sync/force', { method: 'POST' });
}

// ── Fundamentals Sync (Yahoo quoteSummary) ──────────────────────────────────

export async function adminGetFundamentalsSyncSettings(): Promise<SyncSectionState> {
  return request<SyncSectionState>('/admin/settings/fundamentals-sync');
}
export async function adminSaveFundamentalsSyncSettings(data: {
  enabled?: boolean; provider?: string; intervalHours?: number; runAtEtHour?: number;
  staleAfterHours?: number; batchSize?: number;
}): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/fundamentals-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceFundamentalsSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/fundamentals-sync/force', { method: 'POST' });
}

// ── ETF Metadata Sync (Yahoo quoteSummary → tickers etf_* columns) ──────────

export async function adminGetEtfMetadataSyncSettings(): Promise<SyncSectionState> {
  return request<SyncSectionState>('/admin/settings/etf-metadata-sync');
}
export async function adminSaveEtfMetadataSyncSettings(data: {
  enabled?: boolean; intervalHours?: number; staleAfterHours?: number; batchSize?: number;
}): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/etf-metadata-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceEtfMetadataSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/etf-metadata-sync/force', { method: 'POST' });
}

// ── Company Profile (public) ────────────────────────────────────────────────

export interface CompanyProfile {
  symbol: string;
  companyName: string;
  description: string;
  sector: string;
  industry: string;
  address: string;
  phone: string;
  website: string;
  region: string;
}

export async function getCompanyProfile(ticker: string): Promise<CompanyProfile | null> {
  return request<CompanyProfile | null>(`/stocks/${encodeURIComponent(ticker)}/company-profile`);
}

// ── Split Sync ──────────────────────────────────────────────────────────────

export async function adminGetSplitSyncSettings(): Promise<SyncSectionState> {
  return request<SyncSectionState>('/admin/settings/split-sync');
}
export async function adminSaveSplitSyncSettings(data: { enabled?: boolean; provider?: string; intervalHours?: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/split-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceSplitSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/split-sync/force', { method: 'POST' });
}

// ── Insider Sync ────────────────────────────────────────────────────────────

export async function adminGetInsiderSyncSettings(): Promise<SyncSectionState> {
  return request<SyncSectionState>('/admin/settings/insider-sync');
}
export async function adminSaveInsiderSyncSettings(data: { enabled?: boolean; provider?: string; intervalHours?: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/insider-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceInsiderSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/insider-sync/force', { method: 'POST' });
}
// ── Earnings Sync ────────────────────────────────────────────────────────────
export async function adminGetEarningsSyncSettings(): Promise<SyncSectionState> {
  return request<SyncSectionState>('/admin/settings/earnings-sync');
}
export async function adminSaveEarningsSyncSettings(data: { enabled?: boolean; provider?: string; intervalHours?: number; cardProvider?: string }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/earnings-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceEarningsSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/earnings-sync/force', { method: 'POST' });
}

// ── Data Retention ───────────────────────────────────────────────────────────
export interface DataRetentionState {
  enabled: boolean;
  cleanupHourEt: number;
  insiderMonths: number;
  congressMonths: number;
  earningsMonths: number;
  ohlcvUnusedDays: number;
  pageViewDays: number;
  scannerEventsDays: number;
  /** Purge claude_validations rows older than N days. Default 3.
   *  0 = never purge. */
  claudeValidationsDays: number;
  /** Purge etf_setup_predictions rows whose signal_date is older than
   *  N months. Default 12. 0 = never purge. */
  etfPredictionsMonths: number;
  /** Purge auto_trade_events rows older than N days. Default 90.
   *  0 = never purge. */
  autoTradeEventsDays: number;
  /** Purge auto_trade_audit rows older than N days. Default 30.
   *  Forensic per-trade snapshot sidecar. 0 = never purge. */
  autoTradeAuditDays: number;
  /** Purge broker_order_log rows older than N days. Default 90.
   *  0 = never purge. */
  brokerOrderLogDays: number;
  /** Purge signal_outcomes rows older than N days. Default 365.
   *  Aggregate-stats endpoint queries last 90d; default keeps a 4x
   *  buffer for ad-hoc backtests. 0 = never purge. */
  signalOutcomesDays: number;
  /** Purge scanner_candidate_rejections rows older than N days.
   *  Default 7. Powers PM "why didn't this qualify?" diagnostics —
   *  short window since investigations happen the morning after the
   *  miss. 0 = never purge. */
  scannerCandidateRejectionsDays: number;
  lastCleanupAt: string | null;
  lastCleanupStats: Record<string, number> | null;
  rowCounts?: Record<string, number>;
}
export async function adminGetDataRetention(): Promise<DataRetentionState> {
  return request<DataRetentionState>('/admin/settings/data-retention');
}
export async function adminSaveDataRetention(data: Partial<DataRetentionState>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/data-retention', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminRunCleanup(): Promise<Record<string, number>> {
  return request<Record<string, number>>('/admin/settings/data-retention/cleanup', { method: 'POST' });
}

export async function adminPurgeEtfPredictions(): Promise<{ deleted: number; message: string }> {
  return request<{ deleted: number; message: string }>('/admin/settings/data-retention/etf-predictions/purge', { method: 'POST' });
}
export async function adminPurgeTable(table: string, days: number): Promise<{ deleted: number; days: number }> {
  return request<{ deleted: number; days: number }>('/admin/settings/data-retention/purge', {
    method: 'POST',
    body: JSON.stringify({ table, days }),
  });
}
export async function adminSnapshotPrevClose(): Promise<{ updated: number; message: string }> {
  return request<{ updated: number; message: string }>('/admin/price-sync/snapshot-prev-close', { method: 'POST' });
}

export interface TableStat {
  name: string;
  rows: number;
  bytes: number;
  sizeHuman: string;
  /** Most recent snapshot at least 12h old. null = no qualifying baseline yet. */
  prevRows: number | null;
  prevBytes: number | null;
  prevCapturedAt: string | null;
}
export async function adminGetTableCounts(): Promise<{ fetchedAt: string; tables: TableStat[] }> {
  return request<{ fetchedAt: string; tables: TableStat[] }>('/admin/database/table-counts');
}
export async function adminSnapshotDatabase(): Promise<{ inserted: number; capturedAt: string }> {
  return request<{ inserted: number; capturedAt: string }>('/admin/database/snapshot', { method: 'POST' });
}

// ── Scanner Log ──────────────────────────────────────────────────────────────
export type ScannerSeverity = 'elevated' | 'high' | 'extreme';
export type ScannerPhase = 'regular' | 'premarket' | 'afterhours';
export type ScannerReason =
  | 'projected_rvol' | 'recent_rvol' | 'recent_rvol_confirmed' | 'velocity' | 'pressure'
  | 'gap_and_go' | 'gap_and_go_confirmed' | 'orb_breakout' | 'trend_continuation'
  // Plan #234 — emitted by liveSignalService on verdict transitions.
  // _buy is a trader entry hook; _sell is captures-view visibility only.
  | 'live_signal_buy' | 'live_signal_sell'
  // Plan #242 — VWAP + MA + Momentum + Volume + ATR multi-factor entry.
  | 'confluence_stack'
  // Plan #359 — Eixom (HTF MACD-histogram) crosses ≤0 → positive and rising.
  | 'eixom_cross_up'
  // opening_drive — driving UP off the 09:30 opening print on heavy opening
  // volume, confirmed by a rising 09:30 < 09:35 < 09:40 structure and evaluated
  // from 09:42 ET. Gap-agnostic: measured from day_open, not
  // prev_close, which is the case gap_and_go (needs an up-gap) and orb_breakout
  // (can't fire until the range seals at 09:45) both miss. Fires once per
  // ticker per day; opt-in per portfolio but a real entry hook.
  | 'opening_drive'
  // NOTE: retired reasons (momentum_run, steady_climber) are deliberately
  // ABSENT from this union. The exhaustive Record<ScannerReason, …> label and
  // color maps across the scanner UI would otherwise force every surface to
  // keep a dead entry forever. Historical dots still render correctly: the
  // chart's REASON_COLOR / REASON_LABEL in lib/chart/scannerMarkers are
  // Record<string, string> and retain their entries, and the chart's reason
  // filter only lists reasons actually present in the loaded events — so the
  // chip disappears on its own once the 7-day rows age out.
  // fast_dip — bearish 5-min flush: a >$10 name whose change_pct dropped ≥4%
  // since the prior price-sync tick. Display-only, never auto-tradable.
  | 'fast_dip';

export interface ScannerAutoBuy {
  portfolioId: string;
  portfolioName: string | null;
  qty: number | null;
  price: number | null;
  occurredAt: string;
}
export interface ScannerLogEntry {
  id: string;
  ticker: string;
  name: string | null;
  sector: string | null;
  detectedAt: string;
  severity: ScannerSeverity;
  phase: ScannerPhase;
  qualificationReason: ScannerReason;
  currentPrice: number;
  currentChangePct: number;
  currentRvol: number;
  peakRvol: number;
  peakChangePct: number;
  dollarVolume: number;
  consecutiveQualifiedPasses: number;
  spikeKey: string | null;
  /** Scanner-pass dedupe bucket. `russell1000` when the ticker came in via
   *  the R1000 candidate query (curated large-caps); `main` for the
   *  small/mid-cap pool. UI maps this + currentPrice to the P/M/Q category
   *  (penny/midrange/quality) using the same precedence as
   *  api/portfolioCategories.categoryForTicker. */
  bucket: 'main' | 'russell1000' | null;
  /** Live (current) ticker price + change% from the tickers row, joined at
   *  query time. Lets the scanner log show "qualified at $X / now at $Y"
   *  alongside the row's frozen entry-time snapshot (currentPrice /
   *  currentChangePct above). Null when the ticker row hasn't been
   *  populated yet. */
  livePrice: number | null;
  liveChangePct: number | null;
  /** True when this ticker had no scanner_events in the 7 days before
   *  detectedAt's ET date — "first time in a week" hit. Computed
   *  server-side via NOT EXISTS so the value agrees with the newOnly
   *  filter when applied. */
  isNew: boolean;
  /** True when liveChangePct < -2 — the move that fired the scanner has
   *  since reverted past the qualification floor. Mirrors the same
   *  threshold the excludeStale filter uses, so column + filter agree
   *  per row. */
  isStale: boolean;
  /** Auto-trade entries fired on the same ticker within ±2 min of this
   *  scanner event. Empty if no portfolio acted on this qualification. */
  autoBuys: ScannerAutoBuy[];
}
export interface ScannerLogFilters {
  limit?: number;
  offset?: number;
  since?: string;
  /** Upper bound on detected_at (exclusive). Pair with `since` to scope a
   *  single ET trading day; omit for "everything from `since` onward". */
  until?: string;
  /** Multi-select severity. Sent as comma-separated to the server (single
   *  value still works for back-compat with older callers + bookmarks). */
  severity?: ScannerSeverity | ScannerSeverity[];
  /** Multi-select reason. Sent as comma-separated to the server. */
  reason?: ScannerReason | ScannerReason[];
  /** How the multi-reason filter combines. 'or' (default) returns rows
   *  whose reason is any of the listed values. 'and' returns rows from
   *  tickers that fired EVERY listed reason within the same time window
   *  (rows are still scoped to the listed reasons; tickers without the
   *  full set are dropped). Single-reason selections behave identically
   *  in both modes. */
  reasonMode?: 'or' | 'and';
  phase?: ScannerPhase;
  /** Portfolio-category bucket(s) of the underlying ticker (multi-select).
   *  - penny: price < $5
   *  - quality: price ≥ $5 AND in Russell 1000
   *  - midrange: price ≥ $5 AND not in Russell 1000
   *  Penny wins precedence over quality (a sub-$5 R1000 stock is penny). */
  category?: 'penny' | 'midrange' | 'quality' | Array<'penny' | 'midrange' | 'quality'>;
  ticker?: string;
  /** Filter by consecutive_qualified_passes count.
   *  Stringified positive integer — server interprets as ≥ N
   *  (so passes='5' means "show events with at least 5 consecutive
   *  qualified passes"). Omit / empty string for no filter. */
  passes?: string;
  /** How `passes` compares: 'gte' (default — ≥ N) or 'eq' (exactly N). Use
   *  'eq' with passes='1' to match only the FIRST qualifying pass (e.g. so an
   *  alert fires once per spike instead of on every re-qualification). */
  passesMode?: 'gte' | 'eq';
  /** Per-ticker total-events floor. When set, the server only returns
   *  rows whose ticker has at least N scanner_events in the visible
   *  date window across all reasons combined. Lets the operator
   *  surface only "active" tickers and hide one-off noise. */
  minTotalEvents?: number;
  /** Multi-select session filter. Sent as comma-separated to the server.
   *  - 'premarket'  : se.phase = 'premarket' (4:00-9:30 ET)
   *  - 'market'     : se.phase = 'regular' AND ET hour < 16
   *  - 'afterhours' : se.phase = 'regular' AND ET hour >= 16
   *  Omit / send all three to include every session. */
  sessions?: ('premarket' | 'market' | 'afterhours')[];
  /** When true, narrow results to scanner_events that actually produced an
   *  auto_trade_events entry. Lets the operator see only the qualifications
   *  that resulted in a real purchase, separate from the much larger pool
   *  of scanner noise. Server-side via EXISTS so totals + pagination stay
   *  correct. */
  hasAutoBuy?: boolean;
  /** When true, exclude rows where the underlying ticker's live change pct
   *  is < -2% (morning runners that already faded). Server-side filter so
   *  pagination reflects the visible row count — without this, the FE's
   *  client-side "Hide stale" filter shrinks each page from 50 → 1-or-2
   *  visible rows when sorting by RVOL DESC (the high-RVOL hits are
   *  precisely the ones that ran-and-dumped). Mirrors the FE's
   *  STALE_FLOOR_PCT constant. */
  excludeStale?: boolean;
  /** When true, restrict to tickers with no scanner_events in the 7 days
   *  before each row's detected ET date — surfaces first-time-in-a-week
   *  names vs chronic re-qualifiers. Server-side via NOT EXISTS so totals
   *  + pagination reflect the visible set. Mirrors the same shape the
   *  captures-route filter uses. */
  newOnly?: boolean;
  /** Multi-select status filter — CSV subset of `active`, `stale`,
   *  `new`. Empty / undefined = show all rows. When set, the server
   *  returns the UNION of the selected status buckets:
   *    - "new"   = first qualification in the prior 7 ET days
   *    - "stale" = ticker's live change pct < -2% (move has faded)
   *    - "active" = neither new nor stale (qualified before, still running)
   *  Replaces the old `excludeStale` + `newOnly` checkboxes with one
   *  positive-selection control. The legacy params still work for any
   *  caller (saved searches, bookmarks) that predates the refactor. */
  status?: string;
  /** Server-side sort column. Without this, only the current page reorders
   *  on header click — older rows from later pages can never reach the top. */
  sortBy?: 'detectedAt' | 'ticker' | 'severity' | 'qualificationReason'
    | 'currentPrice' | 'currentChangePct' | 'currentRvol' | 'dollarVolume'
    | 'consecutiveQualifiedPasses';
  sortDir?: 'asc' | 'desc';
}
export interface ScannerLogPage {
  items: ScannerLogEntry[];
  total: number;
  limit: number;
  offset: number;
  /** True when the scanner is currently eligible to produce new rows —
   *  admin enabled, global sync rules allow it, and the ET clock is inside
   *  the scan window. FE uses this to pause auto-refresh when scanning is
   *  paused (no new rows are possible anyway). */
  scannerActive?: boolean;
}
/** URLSearchParams serializer that skips empties and arrays that would
 *  serialize to "". Array values get joined with commas (matches the
 *  backend's CSV-subset parser for severity/reason/category/sessions). */
function appendScannerFilterParams(filters: Record<string, unknown>): URLSearchParams {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      qs.set(k, v.join(','));
    } else {
      qs.set(k, String(v));
    }
  }
  return qs;
}

export async function adminGetScannerLog(filters: ScannerLogFilters = {}): Promise<ScannerLogPage> {
  const qs = appendScannerFilterParams(filters as unknown as Record<string, unknown>);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<ScannerLogPage>(`/admin/scanner-log${suffix}`);
}

// ── Scanner saved searches ───────────────────────────────────────────────
// Per-user saved filter presets for /portfolio/scanner. Persisted server-
// side so the operator's library follows them across devices and browsers.
// `filters` is whatever subset of the filter bar's state we want to
// restore — the SavedSearchFilters type below mirrors the live filter
// state minus `date` and `ticker` (kept session-local on purpose; see
// the route file's comment block for rationale).

/** Shape stored in the `filters` jsonb column. Mirrors live filter state
 *  minus `date` and `ticker`. */
export interface SavedSearchFilters {
  severity?: ScannerSeverity[];
  reason?: ScannerReason[];
  reasonMode?: 'or' | 'and';
  category?: Array<'penny' | 'midrange' | 'quality'>;
  passes?: string;
  /** How `passes` compares: 'gte' (default ≥ N) or 'eq' (exactly N — e.g. =1
   *  to match only the first pass). */
  passesMode?: 'gte' | 'eq';
  /** Stringified positive integer — see ScannerLogFilters.minTotalEvents.
   *  Stored as string in saved searches so the input value round-trips
   *  exactly (saved "" / undefined both = no filter). */
  minTotalEvents?: string;
  hasAutoBuy?: boolean;
  sessions?: Array<'premarket' | 'market' | 'afterhours'>;
  /** @deprecated Use `statuses` instead. Kept so saved searches written
   *  before the multi-select refactor still apply cleanly: showStale=false
   *  + newOnly=false migrates to statuses=['active','new'], etc. */
  showStale?: boolean;
  /** @deprecated Use `statuses` instead — see showStale above. */
  newOnly?: boolean;
  /** Multi-select status filter — empty / undefined = show all rows.
   *  Subset of { 'active', 'stale', 'new' }. Mirrors ScannerLogFilters.status. */
  statuses?: Array<'active' | 'stale' | 'new'>;
  refreshSec?: 30 | 60 | 300;
}
export interface ScannerSavedSearch {
  id: string;
  name: string;
  filters: SavedSearchFilters;
  createdAt: string;
  updatedAt: string;
}
export interface ScannerSavedSearchesList {
  searches: ScannerSavedSearch[];
  activeId: string | null;
}
export async function adminListScannerSavedSearches(): Promise<ScannerSavedSearchesList> {
  return request<ScannerSavedSearchesList>('/admin/scanner-saved-searches');
}
export async function adminCreateScannerSavedSearch(name: string, filters: SavedSearchFilters): Promise<ScannerSavedSearch> {
  return request<ScannerSavedSearch>('/admin/scanner-saved-searches', {
    method: 'POST',
    body: JSON.stringify({ name, filters }),
  });
}
export async function adminUpdateScannerSavedSearch(
  id: string,
  patch: { name?: string; filters?: SavedSearchFilters },
): Promise<ScannerSavedSearch> {
  return request<ScannerSavedSearch>(`/admin/scanner-saved-searches/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}
export async function adminDeleteScannerSavedSearch(id: string): Promise<{ deleted: string }> {
  return request<{ deleted: string }>(`/admin/scanner-saved-searches/${id}`, { method: 'DELETE' });
}
export async function adminSetActiveScannerSavedSearch(searchId: string | null): Promise<{ activeId: string | null }> {
  return request<{ activeId: string | null }>('/admin/scanner-saved-searches/active', {
    method: 'POST',
    body: JSON.stringify({ searchId }),
  });
}

// ── Volume Contraction → Expansion scan (issue #177) ─────────────────────
export interface VcpConfig {
  contractionDays: number;
  contractionThreshold: number;
  spikeMultiplier: number;
  minChangePct: number;
  rangeThreshold: number | null;
}

// Multi-pattern registry surface (issue #178).
export interface PatternFilterField {
  key: string;
  label: string;
  help: string;
  type?: 'number' | 'nullable-number';
  min: number;
  max: number;
  step: number;
  default: number | null;
}
export interface PatternDescriptor {
  id: string;
  label: string;
  description: string;
  defaultCfg: Record<string, number | null>;
  filterSchema: PatternFilterField[];
}
export async function adminGetScannerPatterns(): Promise<PatternDescriptor[]> {
  return request<PatternDescriptor[]>('/admin/scans/patterns');
}
export interface VcpDailyBar {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}
export interface VcpMatch {
  ticker: string;
  name: string | null;
  sector: string | null;
  baselineAvgVolume: number;
  /** Renamed from `contractionBars` to `prefixBars` in the multi-pattern
   *  refactor (#178). The shape is the same; the name generalises so
   *  oversold-bounce / capitulation-reversal results fit the same
   *  envelope. The field still holds the bars BEFORE the spike day
   *  (contraction bars for VCP, downtrend bars for capitulation, etc.). */
  prefixBars: VcpDailyBar[];
  spikeBar: VcpDailyBar;
  spikeRvol: number;
  changePct: number;
  /** Pattern-specific extras. For VCP: `contractionAvgVolume`,
   *  `contractionRangeFraction`. Other patterns add their own keys. */
  details: Record<string, number | string | null>;
}
export interface VcpScanResult {
  matches: VcpMatch[];
  scanned: number;
  cfg: Record<string, number | null>;
  pattern: PatternDescriptor;
}
export async function adminGetVolumeContractionScan(opts: { pattern?: string; cfg?: Record<string, number | null> } = {}): Promise<VcpScanResult> {
  const qs = new URLSearchParams();
  if (opts.pattern) qs.set('pattern', opts.pattern);
  if (opts.cfg) {
    for (const [k, v] of Object.entries(opts.cfg)) {
      if (v === null) qs.set(k, 'null');
      else if (v !== undefined) qs.set(k, String(v));
    }
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<VcpScanResult>(`/admin/scans/volume-contraction${suffix}`);
}

/** A recorded pattern detection — the durable history written nightly by the
 *  rs-rating sweep (patternDetectionSweep), as opposed to the on-demand scan
 *  above which recomputes against the CURRENT trailing window and so can't
 *  answer "what fired last Tuesday". */
export interface PatternDetection {
  pattern: string;
  ticker: string;
  /** Sweep day the detection was recorded. */
  et_date: string;
  /** The bar the pattern actually fired on — can be a few sessions before
   *  et_date (e.g. a breakout within the freshness window). */
  trigger_date: string | null;
  confidence: number | null;
  spike_rvol: number | null;
  change_pct: number | null;
  spike_price: number | null;
  details: Record<string, number | string | null> | null;
  /** Set once the alert dispatcher emailed this row. */
  alerted_at: string | null;
  name: string | null;
  sector: string | null;
  /** tickers.price at read time — for an at-a-glance "how did it do since". */
  current_price: number | null;
}
export interface PatternDetectionsResult {
  detections: PatternDetection[];
  days: number;
  pattern: string | null;
}
export async function adminGetPatternDetections(
  opts: { pattern?: string; sinceDays?: number; limit?: number } = {},
): Promise<PatternDetectionsResult> {
  const qs = new URLSearchParams();
  if (opts.pattern) qs.set('pattern', opts.pattern);
  if (opts.sinceDays != null) qs.set('sinceDays', String(opts.sinceDays));
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<PatternDetectionsResult>(`/admin/scans/detections${suffix}`);
}

// ── Coiled-spring scanner (issue #200) ───────────────────────────────────

export interface CoiledSpringTopNews {
  title: string;
  url: string;
  source: string;
  publishedAt: string;
  sentimentLabel: string;
  sentimentScore: number;
  relevance: number;
}

export interface CoiledSpringBreakdown {
  atrRatio: number;
  rangeTightness: number;
  volumeRatio: number;
  maProximity: number;
  rsi14: number;
  rsRating: number | null;
  rsDelta7d: number | null;
  rsiDelta7d: number | null;
  compressionDays: number;
  daysToEarnings: number | null;
  accumulationRatio: number;
  normalized: { atr: number; range: number; volume: number; ma: number };
  modifiers: {
    rsi: number;
    rsRating: number;
    rsDelta: number;
    rsiDelta: number;
    compressionDays: number;
    earningsProximity: number;
    accumulation: number;
  };
}

export interface CoiledSpringCandidate {
  rank: number;
  ticker: string;
  name: string | null;
  sector: string | null;
  price: number;
  compressionScore: number;
  compressionBreakdown: CoiledSpringBreakdown;
  newsStrength: number;
  newsCount: number;
  topNews: CoiledSpringTopNews | null;
  finalScore: number;
}

export interface CoiledSpringScanResult {
  candidates: CoiledSpringCandidate[];
  context: {
    universeScanned: number;
    qualifyingByCompression: number;
    qualifyingWithNews: number;
    elapsedMs: number;
  };
}

export interface CoiledSpringScanRequest {
  lookbackDays?: number;
  minCompressionScore?: number;
  universe?: 'heatmap' | 'russell1000' | 'all_stocks';
  withNewsOnly?: boolean;
  newsWindowDays?: number;
  minNewsSentiment?: number;
  minNewsRelevance?: number;
  topK?: number;
}

export async function adminGetCoiledSpringScan(opts: CoiledSpringScanRequest = {}): Promise<CoiledSpringScanResult> {
  const qs = new URLSearchParams();
  if (opts.lookbackDays != null) qs.set('lookbackDays', String(opts.lookbackDays));
  if (opts.minCompressionScore != null) qs.set('minCompressionScore', String(opts.minCompressionScore));
  if (opts.universe) qs.set('universe', opts.universe);
  if (opts.withNewsOnly) qs.set('withNewsOnly', '1');
  if (opts.newsWindowDays != null) qs.set('newsWindowDays', String(opts.newsWindowDays));
  if (opts.minNewsSentiment != null) qs.set('minNewsSentiment', String(opts.minNewsSentiment));
  if (opts.minNewsRelevance != null) qs.set('minNewsRelevance', String(opts.minNewsRelevance));
  if (opts.topK != null) qs.set('topK', String(opts.topK));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<CoiledSpringScanResult>(`/admin/scans/coiled-spring${suffix}`);
}

export interface CoiledSpringRerankEntry {
  originalRank: number;
  newRank: number;
  confidence: number;
  rationale: string;
}

export interface CoiledSpringRerankResponse {
  outcome: 'ok' | 'no_key' | 'no_candidates' | 'api_error' | 'parse_error' | 'timeout';
  recommendedRank: number | null;
  rerank: CoiledSpringRerankEntry[];
  model: string;
  latencyMs: number;
  inputTokens: number | null;
  outputTokens: number | null;
  error?: string;
}

export async function adminRerankCoiledSpring(candidates: CoiledSpringCandidate[]): Promise<CoiledSpringRerankResponse> {
  return request<CoiledSpringRerankResponse>(`/admin/scans/coiled-spring/rerank`, {
    method: 'POST',
    body: JSON.stringify({ candidates }),
  });
}

export interface VcpBacktestHit {
  date: string;
  ticker: string;
  spikeRvol: number;
  changePct: number;
  spikePrice: number;
  forwardReturns: Array<{ horizon: number; returnPct: number | null }>;
}
export interface VcpBacktestHorizonStats {
  horizon: number;
  n: number;
  winRate: number;
  avgReturn: number;
  medianReturn: number;
}
export interface VcpBacktestResult {
  cfg: Record<string, number | null>;
  since: string;
  until: string;
  horizons: number[];
  totalHits: number;
  distinctTickers: number;
  byHorizon: VcpBacktestHorizonStats[];
  hits: VcpBacktestHit[];
  capped: number | null;
  pattern: PatternDescriptor;
}
export interface VcpAlertConfig {
  enabled: boolean;
  filters?: Partial<VcpConfig>;
  suppressionDays?: number;
  lastAlerted?: Record<string, string>;
  lastDispatchedDate?: string;
}
export async function adminGetVcpAlertConfig(): Promise<VcpAlertConfig> {
  return request<VcpAlertConfig>('/admin/scans/volume-contraction/alert-config');
}
export async function adminSaveVcpAlertConfig(cfg: Partial<VcpAlertConfig>): Promise<VcpAlertConfig> {
  return request<VcpAlertConfig>('/admin/scans/volume-contraction/alert-config', {
    method: 'PUT', body: JSON.stringify(cfg),
  });
}
export async function adminPreviewVcpDispatch(): Promise<{ candidates: VcpBacktestHit[]; count: number }> {
  return request<{ candidates: VcpBacktestHit[]; count: number }>('/admin/scans/volume-contraction/dispatch-preview', { method: 'POST' });
}

export async function adminBacktestVolumeContractionScan(opts: {
  pattern?: string;
  cfg?: Record<string, number | null>;
  since?: string;
  until?: string;
  horizons?: number[];
} = {}): Promise<VcpBacktestResult> {
  const qs = new URLSearchParams();
  if (opts.pattern) qs.set('pattern', opts.pattern);
  if (opts.since) qs.set('since', opts.since);
  if (opts.until) qs.set('until', opts.until);
  if (opts.horizons && opts.horizons.length > 0) qs.set('horizons', opts.horizons.join(','));
  if (opts.cfg) {
    for (const [k, v] of Object.entries(opts.cfg)) {
      if (v === null) qs.set(k, 'null');
      else if (v !== undefined) qs.set(k, String(v));
    }
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<VcpBacktestResult>(`/admin/scans/volume-contraction/backtest${suffix}`);
}

// ── Signal edge (issue #184) — historical forward-return aggregates ─────
export interface SignalEdgeHorizon {
  horizon: number;
  n: number;
  winRate: number | null;
  avgReturn: number | null;
}
/** Each row's identifying columns depend on the active groupBy. The
 *  default groupBy ('reason_severity_bucket') populates signal /
 *  severity / bucket; alternate groupings populate sector / a subset.
 *  FE switches on groupBy to know which keys are present. */
export interface SignalEdgeRow {
  signal?: string;          // qualification_reason
  severity?: string;
  bucket?: string;
  sector?: string;
  totalN: number;
  byHorizon: SignalEdgeHorizon[];
}
export type SignalEdgeGroupBy = 'reason_severity_bucket' | 'sector' | 'signal_sector' | 'bucket';
export interface SignalEdgeResponse {
  sinceDays: number;
  minN: number;
  groupBy: SignalEdgeGroupBy;
  rows: SignalEdgeRow[];
}
export async function adminGetSignalEdge(opts: {
  signal?: string; severity?: string; bucket?: string; sector?: string;
  minN?: number; sinceDays?: number; groupBy?: SignalEdgeGroupBy;
} = {}): Promise<SignalEdgeResponse> {
  const qs = new URLSearchParams();
  if (opts.signal) qs.set('signal', opts.signal);
  if (opts.severity) qs.set('severity', opts.severity);
  if (opts.bucket) qs.set('bucket', opts.bucket);
  if (opts.sector) qs.set('sector', opts.sector);
  if (opts.minN != null) qs.set('minN', String(opts.minN));
  if (opts.sinceDays != null) qs.set('sinceDays', String(opts.sinceDays));
  if (opts.groupBy) qs.set('groupBy', opts.groupBy);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<SignalEdgeResponse>(`/admin/signal-edge${suffix}`);
}

/** Download the scanner log as CSV. Bypasses the JSON `request` helper since
 *  we need the raw response body and browser download machinery. Passes the
 *  Bearer token in the Authorization header (cookies alone won't work for the
 *  Railway deploy). Respects the same filter params as adminGetScannerLog. */
export async function adminExportScannerLogCsv(filters: Omit<ScannerLogFilters, 'limit' | 'offset'> = {}): Promise<void> {
  const qs = appendScannerFilterParams(filters as unknown as Record<string, unknown>);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await fetch(`${BASE_URL}/admin/scanner-log/export.csv${suffix}`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }
  const blob = await response.blob();
  // Use the server-provided filename when present; fallback to ET-date based.
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] ?? `scanner-log-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Response shape for the distinct-tickers helper powering the "Run Sim on
 *  filtered" button on /portfolio/scanner (Plan #135). `tickers` is sorted
 *  newest-detection-first and capped at `cap`. `total` is the uncapped
 *  distinct count so the UI can show "showing top 50 of 137". */
export interface ScannerLogFilteredTickersResult {
  tickers: string[];
  /** Distinct-ticker count matching the filter (uncapped). */
  total: number;
  /** Raw scanner-event count matching the filter (non-distinct).
   *  A single ticker qualifying 5 times throughout the day counts as 5
   *  hits but 1 in `total`. Exposed so the UI can explain the gap
   *  between the log page's "N hits" and the batch's distinct-ticker
   *  count. */
  totalHits: number;
  capped: boolean;
  cap: number;
}
export async function adminGetScannerLogFilteredTickers(
  filters: Omit<ScannerLogFilters, 'limit' | 'offset'> = {},
): Promise<ScannerLogFilteredTickersResult> {
  const qs = appendScannerFilterParams(filters as unknown as Record<string, unknown>);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<ScannerLogFilteredTickersResult>(`/admin/scanner-log/filtered-tickers${suffix}`);
}

// ── Auto-Trade Portfolio generator (issue #199) ──────────────────────────────

export type AutoTradeRiskTolerance = 'low' | 'medium' | 'high';
export type AutoTradeSeverity = 'elevated' | 'high' | 'extreme';
export type AutoTradePortfolioCategory = 'penny' | 'midrange' | 'quality';

export interface GeneratorRequest {
  categories: AutoTradePortfolioCategory[];
  capital: number;
  riskTolerance: AutoTradeRiskTolerance;
  lookbackDays: number;
  includeAi: boolean;
}

export interface GeneratorRulesShape {
  enabled: boolean;
  minSeverity: AutoTradeSeverity;
  minEntryChangePct: number;
  maxEntryChangePct: number;
  phase: 'regular' | 'premarket' | 'afterhours' | 'both';
  sectorsWhitelist: string[];
  tickerBlacklist: string[];
  /** Empty (default) = no restriction. Non-empty = only listed tickers
   *  qualify (e.g. a TQQQ-only swing portfolio). */
  tickerAllowlist: string[];
  sizingMode: 'percent' | 'fixed';
  positionPct: number;
  positionFixed: number;
  maxConcurrent: number;
  maxTradesPerDay: number;
  stopLossPct: number;
  takeProfitGivebackPct: number;
  minPeakGainForGivebackPct: number;
  minFakeoutLossPct: number;
  volumeCoolingPct: number;
  postExitCooldownMin: number;
  minHoldMinutes: number;
  skipIfBelowOneShare: boolean;
  acceptedQualificationReasons: string[];
  entryCutoffMinutesBeforeClose: number;
  lateSessionStopLossPct: number;
  brokerMode: 'off' | 'paper' | 'live';
  aiValidateBuys: boolean;
  aiValidateSells: boolean;
  enrolledCategories: AutoTradePortfolioCategory[];
  acceptsRussell1000Signals?: boolean;
}

export interface GeneratorCandidate {
  rank: number;
  rules: GeneratorRulesShape;
  metrics: {
    totalPnl: number;
    pnlPct: number;
    winRate: number;
    tradeCount: number;
    maxDrawdown: number;
    daysProfitable: number;
    daysSimulated: number;
    topDayShare: number;
  };
  score: number;
  scoreBreakdown: {
    pnlPctTerm: number;
    winRateTerm: number;
    drawdownTerm: number;
  };
}

export interface GeneratorAiRerankEntry {
  originalRank: number;
  newRank: number;
  confidence: number;
  rationale: string;
}

export interface GeneratorResponse {
  candidates: GeneratorCandidate[];
  /** Per-risk fallback rule set returned alongside candidates so the UI
   *  can spawn a portfolio with sensible defaults when the historical
   *  search produces zero viable candidates. */
  presetRules?: GeneratorRulesShape;
  context: {
    universeTickers: number;
    daysSimulated: number;
    candidatesEvaluated: number;
    qualifyingCandidates: number;
    distinctOutcomes: number;
    daysWithBars: number;
    daysWithoutBars: number;
    totalSimRuns: number;
    elapsedMs: number;
  };
  ai: null | {
    outcome: 'ok' | 'no_key' | 'no_candidates' | 'api_error' | 'parse_error' | 'timeout';
    recommendedRank: number | null;
    rerank: GeneratorAiRerankEntry[];
    model: string;
    latencyMs: number;
    inputTokens: number | null;
    outputTokens: number | null;
    error?: string;
  };
}

export async function adminGenerateAutoTradeConfig(req: GeneratorRequest): Promise<GeneratorResponse> {
  return request<GeneratorResponse>('/admin/auto-trade/generate', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export interface GeneratorCreateRequest {
  name: string;
  capital: number;
  categories: AutoTradePortfolioCategory[];
  riskTolerance: AutoTradeRiskTolerance;
  lookbackDays: number;
  rules: GeneratorRulesShape;
}

export async function createGeneratedAutoTradePortfolio(req: GeneratorCreateRequest): Promise<{ id: string; name: string }> {
  return request<{ id: string; name: string }>('/portfolios/auto-trade/generate-create', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

// ── Scanner Recent (dashboard widget — issue #198) ───────────────────────────

export interface ScannerTopTickerEntry {
  ticker: string;
  detectCount: number;
  lastDetectedAt: string;
  lastSeverity: 'elevated' | 'high' | 'extreme';
  lastReason: string | null;
  lastChangePct: number | null;
  lastRvol: number | null;
  peakChangePct: number | null;
  peakRvol: number | null;
  livePrice: number | null;
  liveChangePct: number | null;
  /** Pre-market / after-hours quote from the priceSync extended-hours
   *  pull. Null when no extended data was captured for this ticker. */
  extendedPrice: number | null;
  extendedChangePct: number | null;
}

export type ScannerSession = 'pre-market' | 'regular' | 'after-hours' | 'closed';

export interface ScannerStateView {
  /** Whether the scanner is expected to be ticking right now. */
  active: boolean;
  /** Which clock band we're in regardless of toggles. */
  currentSession: ScannerSession;
  /** ISO timestamp of the next moment the scanner will become active.
   *  Null if priceSync is disabled outright or no session toggles are on. */
  nextResumeAt: string | null;
  /** Which band the next resume corresponds to. */
  nextResumeSession: Exclude<ScannerSession, 'closed'> | null;
  /** Which session toggles are on (drives copy in the off-hours empty state). */
  enabledSessions: { pre: boolean; rth: boolean; ah: boolean };
}

export interface ScannerTopTickersResponse {
  entries: ScannerTopTickerEntry[];
  windowMin: number;
  windowStartedAt: string;
  scanner?: ScannerStateView;
}

export async function getScannerTopTickers(
  sinceMin = 30,
  limit = 10,
  reasons: string[] = [],
): Promise<ScannerTopTickersResponse> {
  const qs = new URLSearchParams({ sinceMin: String(sinceMin), limit: String(limit) });
  if (reasons.length > 0) qs.set('reason', reasons.join(','));
  return request<ScannerTopTickersResponse>(`/admin/scanner-log/top-tickers?${qs.toString()}`);
}

// ── Auto-Trade Events ────────────────────────────────────────────────────────
export type AutoTradeEventType = 'entry' | 'exit' | 'skip';

export interface AutoTradeEventEntry {
  id: string;
  portfolioId: string;
  portfolioName: string | null;
  ticker: string;
  eventType: AutoTradeEventType;
  exitReason: string | null;
  skipReason: string | null;
  spikeKey: string | null;
  qty: number | null;
  price: number | null;
  proceeds: number | null;
  pnl: number | null;
  /** Per-event context (JSONB on the server). Currently populated for
   *  `volume_cooling` exits with {coolingPeakRvol, coolingCurrentRvol,
   *  coolingCurrentPctOfPeak, coolingThresholdPct} so the activity log
   *  can render the actual ratio that triggered the exit. */
  detail?: Record<string, unknown> | null;
  occurredAt: string;
}
export interface AutoTradeEventsFilters {
  portfolioId?: string;
  ticker?: string;
  eventType?: AutoTradeEventType;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}
export interface AutoTradeEventsPage {
  items: AutoTradeEventEntry[];
  total: number;
  limit: number;
  offset: number;
}
export async function adminGetAutoTradeEvents(filters: AutoTradeEventsFilters = {}): Promise<AutoTradeEventsPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<AutoTradeEventsPage>(`/admin/auto-trade-events${suffix}`);
}

export type ScannerActivityType =
  | 'scan_start' | 'scan_skip' | 'scan_candidates' | 'scan_near_miss'
  | 'scan_qualified' | 'scan_query_error' | 'scan_end';

export interface ScannerActivityEntry {
  ts: number;
  type: ScannerActivityType;
  ticker?: string;
  detail?: string;
}

export async function adminGetScannerActivity(limit = 200): Promise<ScannerActivityEntry[]> {
  return request<ScannerActivityEntry[]>(`/admin/scanner/activity?limit=${limit}`);
}

/** Per-ticker capture-timeline data for the scanner-captures page. One
 *  group per unique ticker for the requested ET date, with every
 *  scanner_events qualification in chronological order. Used to render
 *  a horizontal time strip showing first detection + every requalification
 *  as colored dots without any bar fetch. */
export interface ScannerCaptureRow {
  detectedAt: string;
  reason: ScannerReason;
  severity: ScannerSeverity;
  phase: 'regular' | 'premarket' | 'afterhours';
  changePct: number;
  price: number;
  rvol: number;
}
export interface ScannerCaptureTickerGroup {
  ticker: string;
  /** Company name from tickers.name. Null when the ticker isn't in the
   *  tickers table (rare — usually a stale scanner_events row pointing
   *  at a delisted symbol). UI falls back to showing just the ticker. */
  name: string | null;
  sector: string | null;
  firstDetectedAt: string;
  lastDetectedAt: string;
  captureCount: number;
  captures: ScannerCaptureRow[];
  /** Most-recent ET date BEFORE the requested date that has any
   *  scanner_events for this ticker, YYYY-MM-DD. null when none. */
  prevDate?: string | null;
  /** Next ET date AFTER the requested date that has any scanner_events
   *  for this ticker, YYYY-MM-DD. null when none. */
  nextDate?: string | null;
  /** Session open price (first 5-min bar's open) for the requested
   *  ET date. Null when intraday bars aren't cached for the date —
   *  UI falls back to the first-capture price in that case. */
  dayOpen?: number | null;
  /** Session close price (last 5-min bar's close). Null when bars
   *  aren't cached. For an in-progress live session this is the
   *  most-recent bar's close (effectively "current"). */
  dayClose?: number | null;
  /** After-market close — last 5-min bar's close in the 16:00–20:00 ET
   *  AH window. Null when bars aren't cached or no AH bars exist
   *  (illiquid ticker / extended-hours opt-in cache row missing). */
  dayPmClose?: number | null;
}
export interface ScannerCapturesResponse {
  date: string;
  tickers: ScannerCaptureTickerGroup[];
}
export async function adminGetScannerCaptures(params: {
  date?: string;
  reason?: ScannerReason | ScannerReason[];
  /** How the multi-reason filter combines — see ScannerLogFilters.reasonMode. */
  reasonMode?: 'or' | 'and';
  severity?: ScannerSeverity | ScannerSeverity[];
  /** Portfolio-category filter (multi-select). Same shape as scanner-log's
   *  `category` param — backend joins to tickers and applies the price <
   *  $5 / Russell-1000 partition. */
  category?: 'penny' | 'midrange' | 'quality' | Array<'penny' | 'midrange' | 'quality'>;
  ticker?: string;
  minCaptures?: number;
  sort?: 'first' | 'count' | 'ticker';
  /** Multi-select session phases — same shape as scanner-log's `sessions`
   *  param. Passing all 3 (or omitting) returns every phase; subset filters
   *  server-side. Supersedes the legacy `includeExtended` boolean (still
   *  honored for back-compat). */
  sessions?: Array<'premarket' | 'market' | 'afterhours'>;
  /** When true, server returns events from premarket (04:00–09:30 ET)
   *  + after-market (16:00–20:00 ET) windows in addition to RTH.
   *  Default false = RTH-only. */
  includeExtended?: boolean;
  /** When true, restrict to tickers with no scanner_events in the 7
   *  calendar days before `date` — surfaces first-time-in-a-week hits
   *  rather than chronic re-qualifiers. Default false. */
  newOnly?: boolean;
  /** When true, restrict to tickers that had at least one
   *  auto_trade_events entry on the same ET date — "Bought only" on
   *  the scanner page. Ticker-level coupling since the captures grid
   *  aggregates a ticker's whole session, not per-row. */
  hasAutoBuy?: boolean;
} = {}): Promise<ScannerCapturesResponse> {
  const qs = new URLSearchParams();
  if (params.date) qs.set('date', params.date);
  // reason / severity / category accept scalar (back-compat) or array
  // (multi-select). Array.toString() joins with commas — server handles
  // both as the same CSV-subset shape via parseScannerReasonCsv /
  // parseScannerSeverityCsv / parseScannerCategoryCsv.
  if (params.reason) qs.set('reason', Array.isArray(params.reason) ? params.reason.join(',') : params.reason);
  if (params.reasonMode === 'and') qs.set('reasonMode', 'and');
  if (params.severity) qs.set('severity', Array.isArray(params.severity) ? params.severity.join(',') : params.severity);
  if (params.category) {
    const arr = Array.isArray(params.category) ? params.category : [params.category];
    if (arr.length > 0) qs.set('category', arr.join(','));
  }
  // Ticker prefix filter — trim + uppercase before sending so `aapl `
  // and `AAPL` both work; server enforces the symbol shape.
  if (params.ticker && params.ticker.trim()) qs.set('ticker', params.ticker.trim().toUpperCase());
  if (params.minCaptures != null && params.minCaptures > 1) qs.set('minCaptures', String(params.minCaptures));
  if (params.sort && params.sort !== 'first') qs.set('sort', params.sort);
  if (params.includeExtended) qs.set('includeExtended', 'true');
  if (params.newOnly) qs.set('newOnly', 'true');
  if (params.hasAutoBuy) qs.set('hasAutoBuy', '1');
  // sessions: only send when a strict subset (server treats omit/all-3 as
  // no-filter, matches backend default).
  if (params.sessions && params.sessions.length > 0 && params.sessions.length < 3) {
    qs.set('sessions', params.sessions.join(','));
  }
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<ScannerCapturesResponse>(`/admin/scanner-captures${suffix}`);
}

// ── Volume Timeline ─────────────────────────────────────────────────────────
// Horizontal, per-5-min-interval ranking of the top-20 tickers by the
// dollar-volume measure the scanner records in scanner_events. Sourced
// entirely from the existing scanner_events table (no new table/job): the
// server buckets events by the 5-min ET interval of detected_at, dedups
// (bucket,ticker) to the row with MAX(dollar_volume), then ROW_NUMBER()s
// per bucket by dollar_volume DESC and keeps rank<=20. Scoped to the same
// scanner filters the rest of the page uses.
export interface VolumeTimelineTicker {
  rank: number;
  ticker: string;
  dollarVolume: number | null;
  rvol: number | null;
  price: number | null;
  changePct: number | null;
  severity: string | null;
  reason: string | null;
  sector: string | null;
}
export interface VolumeTimelineInterval {
  /** ET minute-of-day of the 5-min bucket start (e.g. 570 = 09:30). */
  intervalMin: number;
  /** ISO timestamp of the bucket (server-resolved bucket start). */
  takenAt: string;
  phase: 'premarket' | 'regular' | 'afterhours' | null;
  /** Length <= 20, rank 1 = highest dollarVolume. */
  tickers: VolumeTimelineTicker[];
}
export interface VolumeTimelineResponse {
  etDate: string;
  scannerActive: boolean;
  nowEtMinute: number | null;
  /** Ascending by intervalMin. */
  intervals: VolumeTimelineInterval[];
}

/** Fetch the Volume Timeline for an ET date (defaults to the latest ET date
 *  that has scanner_events). All filters are optional and use the same CSV
 *  subset shape as the scanner-log / captures getters. `sessions` is the
 *  contract's `premarket,regular,afterhours` set — the scanner page tracks
 *  the regular phase as `market`, so we normalize that here so both sides
 *  match the API contract exactly. */
export async function getVolumeTimeline(params: {
  date?: string;
  severity?: string[];
  reason?: string[];
  reasonMode?: 'or' | 'and';
  category?: string[];
  sessions?: string[];
  ticker?: string;
} = {}): Promise<VolumeTimelineResponse> {
  const qs = new URLSearchParams();
  if (params.date) qs.set('date', params.date);
  if (params.ticker && params.ticker.trim()) qs.set('ticker', params.ticker.trim().toUpperCase());
  if (params.severity && params.severity.length > 0) qs.set('severity', params.severity.join(','));
  if (params.reason && params.reason.length > 0) qs.set('reason', params.reason.join(','));
  if (params.reasonMode === 'and') qs.set('reasonMode', 'and');
  if (params.category && params.category.length > 0) qs.set('category', params.category.join(','));
  if (params.sessions && params.sessions.length > 0) {
    // Normalize the page's `market` phase label to the contract's `regular`.
    const norm = params.sessions.map((s) => (s === 'market' ? 'regular' : s));
    // Only send when it's a strict subset (omit/all-3 = server default).
    const uniq = Array.from(new Set(norm));
    if (uniq.length > 0 && uniq.length < 3) qs.set('sessions', uniq.join(','));
  }
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<VolumeTimelineResponse>(`/markets/volume-timeline${suffix}`);
}

/** Scanner Streaks (issue #225) — tickers that fired on the scanner on
 *  >=N consecutive trading days AND whose price action over the streak is
 *  positive. Trading-day gaps are derived from the union of all scanner
 *  activity dates so weekends/holidays don't break the index. */
export interface ScannerStreakDay {
  date: string;                 // YYYY-MM-DD ET
  eventCount: number;
  firstReason: ScannerReason;
  maxSeverity: ScannerSeverity;
  open: number | null;
  close: number | null;
  dayChangePct: number | null;
}
export interface ScannerStreakItem {
  ticker: string;
  sector: string | null;
  streakDays: number;
  startDate: string;            // YYYY-MM-DD ET
  endDate: string;              // YYYY-MM-DD ET
  perDay: ScannerStreakDay[];
  /** Net % change from streak-start.open through current price. Null when
   *  no daily bars are cached for the start date. */
  totalChangePct: number | null;
  /** Today's intraday % change since previous close — RTH-only.
   *  Renamed semantic: this no longer prefers extended-hours; the
   *  extended move is exposed separately via `extendedChangePct`. */
  currentChangePct: number;
  /** Extended-hours micro-move (pre-market AM or after-hours PM) on top
   *  of the RTH close. Null when no extended quote is in flight. */
  extendedChangePct: number | null;
  currentPrice: number | null;
  /** Portfolio category (issue #191 rule): price < $5 → penny;
   *  Russell 1000 → quality; otherwise midrange. Null when the cached
   *  ticker row has no price yet — UI should hide the badge. */
  category: 'penny' | 'midrange' | 'quality' | null;
}
export interface ScannerStreaksResponse {
  asOfDate: string;
  /** Canonical name post-2026-05-13. Items have streak EXACTLY this many days. */
  streakDays: number;
  /** @deprecated Use streakDays. Mirrors the same value for back-compat. */
  minStreak: number;
  requirePositive: 'today' | 'net' | 'each' | 'each_intraday' | 'both' | 'off';
  items: ScannerStreakItem[];
}
export async function adminGetScannerStreaks(params: {
  /** Default 1 — exact streak length to filter on. Filter is `=`, not `>=`,
   *  so streakDays=5 returns ONLY 5-day streaks (not 6+). With the
   *  each_intraday default below, "1" means today must be both
   *  scanner-active AND closed positive intraday — and yesterday must NOT
   *  have been on the scanner (otherwise the streak is 2+, not 1). */
  streakDays?: number;
  /** Default 14 — how far back to scan for streaks. */
  lookbackDays?: number;
  /** How "positive price action" is enforced. Default 'each_intraday'
   *  (every day in the streak must have intraday close > open — matches
   *  the green/red chips the UI renders). */
  requirePositive?: 'today' | 'net' | 'each' | 'each_intraday' | 'both' | 'off';
} = {}): Promise<ScannerStreaksResponse> {
  const qs = new URLSearchParams();
  // Default-omit checks mirror the server defaults so the URL stays clean
  // when the caller is using defaults. Update both sides in lockstep.
  if (params.streakDays != null && params.streakDays !== 1) qs.set('streakDays', String(params.streakDays));
  if (params.lookbackDays != null && params.lookbackDays !== 14) qs.set('lookbackDays', String(params.lookbackDays));
  if (params.requirePositive && params.requirePositive !== 'each_intraday') qs.set('requirePositive', params.requirePositive);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<ScannerStreaksResponse>(`/admin/scanner-streaks${suffix}`);
}

/** DB-backed feed of recent qualified hits from scanner_events. Survives API
 *  restarts. Used together with adminGetScannerActivity so the live panel
 *  shows complete qualified history within the requested window even when
 *  the in-memory ring was wiped by a deploy.
 *
 *  ringStartedAt is bundled here (rather than on /scanner/activity) so
 *  changing this endpoint's shape doesn't break clients loaded before this
 *  deploy. UI uses it to render a "ring uptime Nm" badge that tells users
 *  how far back the transient diagnostic events are reliable. */
export interface ScannerQualifiedRecentResponse {
  entries: ScannerActivityEntry[];
  ringStartedAt: number;
}
export async function adminGetScannerQualifiedRecent(sinceMin = 60, limit = 500): Promise<ScannerQualifiedRecentResponse> {
  return request<ScannerQualifiedRecentResponse>(`/admin/scanner/qualified-recent?sinceMin=${sinceMin}&limit=${limit}`);
}

// ── Technical-AI global picks (issue #358) ──────────────────────────────────
// The shared, portfolio-agnostic ranked pick list the Technical-AI pass
// produces each tick. Full-gated; drives the scanner "Technical AI" tab.
export interface TechnicalAiPick {
  ticker: string;
  rank: number;
  approved: boolean;
  confidence: number | null;
  reason: string | null;
  severity: 'elevated' | 'high' | 'extreme';
  qualificationReason: string;
  peakRvol: number;
  currentRvol: number;
  changePct: number | null;
  price: number;
  sector: string | null;
}
export interface TechnicalAiPicksResponse {
  enabled: boolean;
  generatedAt: string | null;
  etDate?: string;
  model?: string | null;
  buyThreshold?: number | null;
  marketTone?: string | null;
  candidateCount: number;
  approvedCount: number;
  picks: TechnicalAiPick[];
}
export async function adminGetTechnicalAiPicks(): Promise<TechnicalAiPicksResponse> {
  return request<TechnicalAiPicksResponse>('/admin/technical-ai-picks');
}

// ── Admin signal historic-change (plan #365) ───────────────────────────────
// Read-only on-the-fly comparison of each ticker's Buy/Hold/Sell signal as of
// a past date vs. today. Writes nothing.

export type SignalTier = 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';

export interface SignalSnapshotChange {
  ticker: string;
  from: SignalTier;
  to: SignalTier;
}

export interface SignalHistoricChange {
  monthsAgo: number;
  targetDate: string;   // ET date the past snapshot was computed against
  latestDate: string;   // ET date of the most recent cached close
  universe: number;     // tickers with enough cached history
  compared: number;     // tickers with a signal at BOTH snapshots
  changed: number;
  transitions: Record<string, number>;  // "buy->sell": count
  changes: SignalSnapshotChange[];
  durationMs: number;
}

/** Admin-only. Pass `date` (YYYY-MM-DD) to snapshot a specific day, or
 *  `monthsAgo` to derive the cutoff N months back. Heavy (~10-15s) — compute
 *  runs over ~9k tickers on the fly and stores nothing. */
export async function adminGetSignalHistoricChange(
  params: { date?: string; monthsAgo?: number } = {},
): Promise<SignalHistoricChange> {
  const q = new URLSearchParams();
  if (params.date) q.set('date', params.date);
  if (params.monthsAgo) q.set('monthsAgo', String(params.monthsAgo));
  const qs = q.toString();
  return request<SignalHistoricChange>(`/admin/signals-debug/historic-change${qs ? `?${qs}` : ''}`);
}

// ── Pre-market / after-hours gappers ───────────────────────────────────────

export type GappersSession = 'pre-market' | 'regular' | 'after-hours' | 'closed';
export type GappersDirection = 'up' | 'down' | 'both';
export type GappersCategory = 'P' | 'M' | 'Q';
export type GappersSortBy = 'gapPct' | 'pmVolume' | 'floatRotation' | 'marketCap';

export interface GappersRow {
  ticker: string;
  name: string | null;
  sector: string | null;
  price: number | null;
  prevClose: number | null;
  open: number | null;
  /** Pre/after-hours quote when the extended session has data. */
  extendedPrice: number | null;
  extendedChangePct: number | null;
  extendedVolume: number | null;
  extendedUpdatedAt: string | null;
  avgVolume: number | null;
  marketCap: number | null;
  /** Shares outstanding — used as a float proxy. True public float
   *  (excludes insider lock-ups + treasury) requires a different data
   *  source and isn't stored. The UI labels the column accordingly. */
  sharesOutstanding: number | null;
  russell1000: boolean;
  gapPct: number;
  gapDollars: number;
  /** extendedVolume / sharesOutstanding * 100 — null when either input
   *  is missing. Above ~50% in pre-market is squeeze-candidate territory. */
  floatRotationPct: number | null;
  /** extendedVolume / avgVolume * 100 — null when either input is missing.
   *  Above ~25% in pre-market suggests genuine institutional interest
   *  rather than a thin-quote artifact. */
  pmVolVsAvgPct: number | null;
  /** ((price - open) / open) × 100. Positive = held above open after
   *  the morning print; negative = faded back. Null pre-market or
   *  when the open print hasn't landed yet. The reliability tell:
   *  for gap-ups, a positive value means the gap-and-go is intact;
   *  for gap-downs, positive means the gap is filling (reversal). */
  pctFromOpen: number | null;
  /** True when current price >= open (regardless of gap direction).
   *  Combined with gap direction: gap-up + held = continuation;
   *  gap-up + faded = morning fade pattern; gap-down + held above
   *  open = oversold bounce; gap-down + still falling = breakdown. */
  heldAboveOpen: boolean | null;
  /** Count of scanner_events rows for this ticker on today's ET date.
   *  Higher = momentum confirmed by the system (multiple qualifying
   *  spike windows during the day). 0 = mechanical gap with no
   *  follow-through volatility — usually fade candidates. */
  scannerEventsToday: number;
}

export interface GappersResponse {
  items: GappersRow[];
  total: number;
  limit: number;
  offset: number;
  /** ET-time market session at the moment of the query. Drives column
   *  labels (PM vs RTH) and the client's auto-refresh cadence. */
  session: GappersSession;
}

export interface GappersFilters {
  direction?: GappersDirection;
  minGapPct?: number;
  minPremarketVolume?: number;
  category?: GappersCategory;
  sector?: string;
  sortBy?: GappersSortBy;
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export async function adminGetScannerGappers(filters: GappersFilters = {}): Promise<GappersResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<GappersResponse>(`/admin/scanner/gappers${suffix}`);
}

export async function adminGetFinancialSyncSettings(): Promise<SyncSectionState> {
  return request<SyncSectionState>('/admin/settings/financial-sync');
}
export async function adminSaveFinancialSyncSettings(data: {
  enabled?: boolean;
  provider?: string;
  intervalHours?: number;
  scheduledEnabled?: boolean;
  scope?: 'sp500' | 'russell1000';
  frequency?: 'weekly' | 'monthly';
  scheduledDay?: number;
  scheduledTime?: string;
}): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/financial-sync', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminForceFinancialSync(ticker: string): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/financial-sync/force', { method: 'POST', body: JSON.stringify({ ticker }) });
}
/** Scope-based force run — kicks off a universe walk for the configured
 *  scope (or the override passed in). Returns immediately; progress
 *  surfaces via the sync-status endpoint that the dashboard tile polls. */
export async function adminForceFinancialSyncScope(scope?: 'sp500' | 'russell1000'): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/financial-sync/force', { method: 'POST', body: JSON.stringify({ scope }) });
}

export interface IntradayWarmSettings {
  enabled: boolean;
  status: 'idle' | 'running';
  lastRunAt: string | null;
  lastRunCount: number;
  lastRunDurationMs: number;
  lastRunErrors: number;
  lastError: string | null;
  nextScheduledAt: string | null;
}
export async function adminGetIntradayWarmSettings(): Promise<IntradayWarmSettings> {
  return request<IntradayWarmSettings>('/admin/settings/intraday-warm');
}
export async function adminSaveIntradayWarmSettings(data: { enabled?: boolean }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/intraday-warm', { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminRunIntradayWarm(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/intraday-warm/run', { method: 'POST' });
}

// ── Scanner OHLC Sync ────────────────────────────────────────────────────────
// Daily post-close fill of intraday-bar cache for tickers that fired
// scanner_events that day. Powers the open/close columns on the
// captures page without burning provider quota during market hours.

/** Plan #234 — live BUY/SELL signal emitter. Walks the scanner-event
 *  ticker universe each 5-min bar boundary, computes deriveLiveSignal
 *  on cached intraday bars, emits scanner_events rows when a verdict
 *  flips. Off by default; admin enables via this panel after the
 *  60-session backtest validates the strategy. */
export type LiveSignalUniverseMode = 'all' | 'russell1000' | 'sp500' | 'none';

export interface LiveSignalEmitSettings {
  enabled: boolean;
  status: 'idle' | 'running';
  /** ISO timestamp of the most recent tick (any verdict, not just emissions). */
  lastRunAt: string | null;
  /** Number of scanner_events rows emitted on the most recent tick. */
  lastEmissionCount: number;
  /** Tickers evaluated on the most recent tick. */
  lastEvaluatedCount: number;
  lastError: string | null;
  /** Base universe the emitter walks each tick. tickerOverride is
   *  ADDED to this base (additive, not replacing). 'none' + empty
   *  custom = legacy scanner_events fallback. */
  universeMode?: LiveSignalUniverseMode;
  /** Custom ticker list ADDED to the base universe. Empty = base-only. */
  tickerOverride?: string[];
  /** Plan #359 — also emit eixom_cross_up from the same loop (off by default). */
  eixomCrossUpEnabled?: boolean;
  /** AI validation badge for live BUY/SELL: fresh emissions get an async
   *  Claude quality check; approved signals draw as triangles on charts.
   *  Annotate-only (never gates the auto-trader). Off by default. */
  aiValidationEnabled?: boolean;
}
export async function adminGetLiveSignalEmitSettings(): Promise<LiveSignalEmitSettings> {
  return request<LiveSignalEmitSettings>('/admin/settings/live-signal-emit');
}
export async function adminSaveLiveSignalEmitSettings(data: { enabled?: boolean; eixomCrossUpEnabled?: boolean; aiValidationEnabled?: boolean; tickerOverride?: string[]; universeMode?: LiveSignalUniverseMode }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/live-signal-emit', { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminRunLiveSignalEmitOnce(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/live-signal-emit/run', { method: 'POST' });
}
/** Backtest input — all optional. sinceDate / untilDate default to last 60
 *  calendar days; takeProfitPct / stopLossPct / maxHoldMinutes default to
 *  5 / 2 / 240 in the backtester. */
export interface LiveSignalBacktestInput {
  sinceDate?: string;
  untilDate?: string;
  takeProfitPct?: number;
  stopLossPct?: number;
  maxHoldMinutes?: number;
}
export async function adminRunLiveSignalBacktest(input: LiveSignalBacktestInput = {}): Promise<unknown> {
  return request<unknown>('/admin/settings/live-signal-emit/backtest', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface ScannerOhlcSyncSettings {
  enabled: boolean;
  fireTimeEt: string;
  status: 'idle' | 'running';
  lastRunAt: string | null;
  lastRunDate: string | null;
  lastRunCount: number;
  lastRunErrors: number;
  lastRunDurationMs: number;
  lastError: string | null;
  nextScheduledAt: string | null;
}
export async function adminGetScannerOhlcSyncSettings(): Promise<ScannerOhlcSyncSettings> {
  return request<ScannerOhlcSyncSettings>('/admin/settings/scanner-ohlc-sync');
}
export async function adminSaveScannerOhlcSyncSettings(data: { enabled?: boolean; fireTimeEt?: string }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/scanner-ohlc-sync', { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminRunScannerOhlcSync(date?: string): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/scanner-ohlc-sync/run', {
    method: 'POST',
    body: JSON.stringify(date ? { date } : {}),
  });
}

/** One-shot recompute of scanner_events.current_change_pct for a single
 *  ET date — uses ohlcv_cache_intraday's prev-day last bar as the
 *  authoritative prev_close instead of whatever stale value AV had at
 *  scan time. Run AFTER the OHLC sync has populated bars for the
 *  date in question (otherwise we have no prev_close source). */
export interface RecomputeChangePctResponse {
  message: string;
  date: string;
  scanned: number;
  updated: number;
  /** Number of rows where current_price changed (subset of `updated`). */
  priceUpdated?: number;
  skippedNoBar?: number;
  skippedSmallDrift?: number;
  samples: Array<{
    ticker: string;
    wasPrice: number; nowPrice: number;
    wasPct: number; nowPct: number;
    prevClose: number;
  }>;
}
export async function adminRecomputeScannerEventsChangePct(date: string): Promise<RecomputeChangePctResponse> {
  return request<RecomputeChangePctResponse>('/admin/scanner-events/recompute-change-pct', {
    method: 'POST',
    body: JSON.stringify({ date }),
  });
}

// ── Congress Sync ────────────────────────────────────────────────────────────

export interface CongressSyncState {
  enabled: boolean;
  provider: string;
  intervalHours: number;
  lookbackDays: number;
  perProviderLimit: number;
  lastSyncAt: string | null;
  status: 'idle' | 'syncing' | 'error';
  newTradesFound: number;
  lastError: string | null;
}

export async function adminGetCongressSyncSettings(): Promise<CongressSyncState> {
  return request<CongressSyncState>('/admin/settings/congress-sync');
}

export async function adminSaveCongressSyncSettings(data: { enabled?: boolean; provider?: string; intervalHours?: number; lookbackDays?: number; perProviderLimit?: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/congress-sync', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminForceCongressSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/congress-sync/force', { method: 'POST' });
}

// ── Price Sync ───────────────────────────────────────────────────────────────

// ── Global Sync Rules ────────────────────────────────────────────────────────

export interface GlobalSyncRules {
  pauseAll: boolean;
  weekdaysOnly: boolean;
  // marketHoursOnly removed (plan #144.7) — now priceSync-specific.
  // Issue #151 — retry + email-on-failure for daily syncs.
  retryAttempts: number;
  retryIntervalMinutes: number;
  failureNotifyEmails: string[];
}

export async function adminGetSyncRules(): Promise<GlobalSyncRules> {
  return request<GlobalSyncRules>('/admin/settings/sync-rules');
}

export async function adminSaveSyncRules(data: Partial<GlobalSyncRules>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/sync-rules', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminTestSyncFailureEmail(to?: string): Promise<{ sent: number; failed: number; sentTo: string[] }> {
  return request<{ sent: number; failed: number; sentTo: string[] }>(
    '/admin/settings/sync-rules/test-email',
    { method: 'POST', body: JSON.stringify(to ? { to } : {}) },
  );
}

export interface ProviderConfigs {
  [key: string]: { apiKey?: string; callsPerMinute: number; reservedForCritical?: number; maxCallsPerSecond?: number };
}

export async function adminGetProviderConfigs(): Promise<ProviderConfigs> {
  return request<ProviderConfigs>('/admin/settings/provider-configs');
}

export async function adminSaveProviderConfigs(data: Record<string, { apiKey?: string; callsPerMinute?: number; reservedForCritical?: number; maxCallsPerSecond?: number }>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/provider-configs', { method: 'POST', body: JSON.stringify(data) });
}

export interface PriceSyncSettings {
  enabled: boolean;
  provider: string;
  weekdaysOnly?: boolean;
  /** Three explicit per-session toggles. RTH (+EOD capture) is gated by
   *  includeMarketHours; pre and after each have their own gate. */
  includePreMarket?: boolean;
  includeMarketHours?: boolean;
  includeAfterHours?: boolean;
  /** @deprecated kept only for safe deserialization of stale records — new
   *  writes use the three explicit flags above. */
  marketHoursOnly?: boolean;
  lastSyncAt: string | null;
  status: 'idle' | 'syncing' | 'error';
  lastError: string | null;
  lastCount: number;
  /** Symbols sent to the provider on the last successful sync. Usually
   *  >= `lastCount` — the difference is how many the provider dropped
   *  (AV silently omits unknown symbols from REALTIME_BULK_QUOTES
   *  responses). Big `lastInputCount - lastCount` = lots of stale rows
   *  in the tickers table. */
  lastInputCount?: number;
  lastDurationMs?: number | null;
  metadataIntervalDays?: number;
  metadataProvider?: string;
  /** Surfaced in the admin Metadata Sync card. Hydrated client-side from
   *  the /admin/settings/ticker-sync GET — not part of the persisted
   *  price-sync record. */
  metadataLastSynced?: number | null;
  metadataLastFailed?: number | null;
  // Hot-universe sub-sync — refreshes the scanner's candidate universe 2:30
  // past each 5-min boundary (issue #140). Effective 2.5-min refresh on
  // scanner candidates. Cadence is fixed at the 5-min grid — no interval
  // override.
  hotUniverseEnabled?: boolean;
  lastHotSyncAt?: string | null;
  /** Plan #306 — restrict the full sweep to the included ETF set. */
  etfSyncRestricted?: boolean;
  /** Plan #306 — admin extra ETF symbols always kept in the sweep. */
  etfAllowlist?: string[];
}

/** Plan #306 — categorized view of which ETFs stay in the full sweep. */
export interface EtfSyncCoverage {
  restricted: boolean;
  system: string[];
  watchlist: string[];
  alerts: string[];
  holdings: string[];
  admin: string[];
  totalIncluded: number;
}

export async function adminGetPriceSyncSettings(): Promise<PriceSyncSettings> {
  return request<PriceSyncSettings>('/admin/settings/price-sync');
}

export async function adminSavePriceSyncSettings(data: {
  enabled: boolean;
  provider?: string;
  includePreMarket?: boolean;
  includeMarketHours?: boolean;
  includeAfterHours?: boolean;
  hotUniverseEnabled?: boolean;
  etfSyncRestricted?: boolean;
  etfAllowlist?: string[];
}): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/price-sync', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminForcePriceSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/price-sync/force', { method: 'POST' });
}

export async function adminGetEtfSyncCoverage(): Promise<EtfSyncCoverage> {
  return request<EtfSyncCoverage>('/admin/settings/price-sync/etf-coverage');
}

// ── RS Rating Sync (issue #193) ──────────────────────────────────────────

export interface RsRatingSyncLastRun {
  startedAt: string;
  durationMs: number;
  updated: number;
  skipped: number;
  backfilledTickers: number;
  triggeredBy: 'cron' | 'force' | 'force-ticker';
  error?: string;
}

export interface RsRatingSyncRunState {
  status: 'idle' | 'running' | 'ranking';
  startedAt: string | null;
  totalCount: number;
  processedCount: number;
  triggeredBy: 'cron' | 'force' | 'force-ticker';
  backfilledTickers: number;
  lastTickAt: string | null;
  error?: string;
}

export interface RsRatingSyncSettings {
  enabled: boolean;
  scheduleHourEt: number;
  retentionDays: number;
  pacePerHour: number;
  lastRun?: RsRatingSyncLastRun | null;
  runState?: RsRatingSyncRunState | null;
}

export async function adminGetRsRatingSync(): Promise<RsRatingSyncSettings> {
  return request<RsRatingSyncSettings>('/admin/settings/rs-rating-sync');
}

export async function adminSaveRsRatingSync(data: {
  enabled?: boolean;
  scheduleHourEt?: number;
  retentionDays?: number;
  pacePerHour?: number;
}): Promise<{ message: string; config: RsRatingSyncSettings }> {
  return request<{ message: string; config: RsRatingSyncSettings }>(
    '/admin/settings/rs-rating-sync',
    { method: 'POST', body: JSON.stringify(data) },
  );
}

export async function adminForceRsRatingSync(tickers?: string[]): Promise<{
  message: string;
  result?: { updated: number; skipped: number; backfilledTickers: number; durationMs: number };
  enqueued?: number;
  runState?: RsRatingSyncRunState;
}> {
  return request<{
    message: string;
    result?: { updated: number; skipped: number; backfilledTickers: number; durationMs: number };
    enqueued?: number;
    runState?: RsRatingSyncRunState;
  }>(
    '/admin/settings/rs-rating-sync/force',
    { method: 'POST', body: JSON.stringify({ tickers: tickers ?? [] }) },
  );
}

export async function adminCancelRsRatingSync(): Promise<{ message: string }> {
  return request<{ message: string }>(
    '/admin/settings/rs-rating-sync/cancel',
    { method: 'POST' },
  );
}

export interface AvRealtimeCheckResult {
  ticker: string;
  isRealtime: boolean;
  skewSeconds: number | null;
  serverTimestamp: string;
  avTimestamp: string | null;
  note: string;
  raw?: unknown;
}

export async function adminAvRealtimeCheck(ticker?: string): Promise<AvRealtimeCheckResult> {
  const qs = ticker ? `?ticker=${encodeURIComponent(ticker)}` : '';
  return request<AvRealtimeCheckResult>(`/admin/provider/av-realtime-check${qs}`);
}

export async function adminForceMetadataSync(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/ticker-sync/force', { method: 'POST' });
}

// ── Spike Scan (Unusual Volume scanner) ──────────────────────────────────────

export interface SpikeScanSettings {
  enabled: boolean;
  intervalMinutes: number;
  lastScanAt: string | null;
  status: 'idle' | 'scanning' | 'error';
  lastError: string | null;
  lastActiveCount: number;
  lastScanDurationMs?: number | null;
  // Universe filters — admin-editable; undefined means server is using defaults
  // (0.5 / 100000 / 5 respectively).
  minPrice?: number;
  minAvgVolume?: number;
  maxMarketCapB?: number;
  /** Extend the scanner window to cover pre-market (04:00–9:30 ET) so
   *  pre-market spikes get qualified. Off by default. Requires a price
   *  provider with extended-hours data AND priceSync.includePreMarket
   *  to be on so `tickers.volume` is fresh during the 04:00 window. */
  includePremarket?: boolean;
  /** Symmetric to includePremarket — extends the scanner window past the
   *  4:00 PM close to 8:00 PM ET so after-hours spikes get qualified.
   *  Off by default. Requires a price provider with extended-hours data. */
  includeAfterMarket?: boolean;
  // Dual-signal qualification thresholds — see volumeSpikeService.scoreCandidate.
  recentRvolThreshold?: number;
  /** Optional stricter directional gate for the recent_rvol path. Default
   *  `-1` = DISABLED (path uses only the `maxShortTermFadePp` baseline).
   *  Set to 0 for strict (no fade), 0.3 for noise tolerance. */
  recentRvolMaxFadePp?: number;
  /** Optional stricter directional gate for the velocity path. Default
   *  `false` = DISABLED. When true, signed Δchange_pct must be > 0. */
  velocityRequirePositiveDirection?: boolean;
  changeVelocityThreshold?: number;
  maxSnapshotAgeMin?: number;
  // Pre-market overrides (only applied during premarket phase).
  premarketRvolMin?: number;
  premarketRecentRvolMin?: number;
  premarketChangeVelocityMin?: number;
  premarketChangePctMin?: number;
  premarketDollarVolumeMin?: number;
  premarketIntervalDollarVolumeMin?: number;
  // Pressure qualifier (plan #124) — sustained bullish buy/sell pressure as
  // a 4th qualification path. Off by default so toggling the admin switch
  // opens the observability tap without changing trading behavior.
  enablePressureQualifier?: boolean;
  pressureWindowSize?: number;
  pressureMinStrongCount?: number;
  pressureMinChangePct?: number;
  pressureMinRecentRvol?: number;
  /** Plan #242 — confluence_stack master switch. Default true (path
   *  has been firing unconditionally since re-add); flip false to
   *  disable globally without touching per-portfolio scanner paths. */
  enableConfluenceStack?: boolean;
  /** Plan #151 — master switch for the trend-continuation path. Off = no
   *  bar fetches for trend-qualification, no trend_continuation entries.
   *  Flip off to cut the dominant scanner:bars traffic source. */
  enableTrendContinuation?: boolean;
  /** Master switch for the bar-fetch window. Off = the gated bar-using
   *  paths (pressure, trend_continuation, gonogo_trend, confluence_stack)
   *  contribute nothing to the bar-fetch union at any hour. Default true. */
  barFetchEnabled?: boolean;
  /** Auto-trade entry window. When enabled, evaluateEntries is gated to
   *  [start, end] ET minutes-of-day; outside the window NO candidates are
   *  bought regardless of severity. evaluateExits is unaffected. Default
   *  enabled, 600 (10:00 AM ET) – 720 (12:00 PM ET). */
  entryWindowEnabled?: boolean;
  entryWindowStartEtMin?: number;
  entryWindowEndEtMin?: number;
  /** Plan #265 — unified live-signal session window (ET minutes-of-day).
   *  Chart + scanner + replay all read this. Defaults 420 (07:00) / 960 (16:00). */
  signalSessionStartEtMin?: number;
  signalSessionEndEtMin?: number;
  /** Momentum window for the trend / reversal / SELL paths (ET minutes-of-day).
   *  Defaults 570 (09:30) / 660 (11:00). */
  signalMomentumStartEtMin?: number;
  signalMomentumEndEtMin?: number;
  /** Live BUY/SELL momentum gates. signalRequireMacd defaults ON (backtest:
   *  lifts per-trade expectancy). Eixom/RSI default OFF (reduced expectancy). */
  signalRequireMacd?: boolean;
  signalRequireEixom?: boolean;
  signalRsiOverbought?: number;
  signalRsiOversold?: number;
  /** Higher-timeframe (15m + 30m) agreement gate. Default OFF. */
  signalRequireHtfAgreement?: boolean;
  /** Early-reversal candlestick BUY path. Default ON. */
  signalCandleReversal?: boolean;
  /** Plan #152 — bar-fetch active window (ET minutes-of-day, inclusive
   *  both ends). Outside [start, end], scanner:bars does zero fetches for
   *  pressure + trendCont. Default 630 (10:30) – 870 (14:30). */
  barFetchActiveStartEtMin?: number;
  barFetchActiveEndEtMin?: number;
  /** Plan #152 — minimum minutes between per-ticker bar fetches. Default 30. */
  barFetchMinIntervalMinutes?: number;
  /** Kill switch for the scanner's ORB range build. When false, the
   *  scanner stops the per-ticker intraday bar fetches that populate
   *  scanner_morning_state — orb_breakout never fires, but the scanner
   *  stops saturating the provider rate-limit queue during the first
   *  15 min of the session. Default true. */
  orbBuildEnabled?: boolean;
  /** Cumulative dollar-volume floor for stocks priced below
   *  lowPriceThreshold ($5). Default $3M. Lowering catches low-priced
   *  plays sooner; raising filters more thin-liquidity false positives. */
  lowPriceDollarVolumeMin?: number;
  /** Rolling-window dollar-volume floor for the recent_rvol burst path.
   *  Default $500K. Lowering catches early bursts on thinly-traded names;
   *  raising blocks fake-liquidity bursts. */
  intervalDollarVolumeMin?: number;
  // Universal qualifier gates (apply to projected_rvol + recent_rvol).
  dollarVolumeMin?: number;
  lowPriceThreshold?: number;
  projectedRvolMin?: number;
  changePctAbsMin?: number;
  // Fade / stall / RVOL-decay guards.
  maxRetraceFromPeak?: number;
  maxShortTermFadePp?: number;
  stallAfterPeakMinutes?: number;
  stallRetracePpMin?: number;
  rvolDecayGuardMinAgeMin?: number;
  rvolDecayGuardMaxFraction?: number;
  rvolDecayFreshExemptRvol?: number;
  rvolDecaySessionPeakPersistEnabled?: boolean;
  premarketRecentRvolEnabled?: boolean;
  // Spike lifecycle.
  quietPassesToResolve?: number;
  warmRestartWindowMin?: number;
  includeHotTicksInPressureRing?: boolean;
  minChangePctFromPrevClose?: number;
  // Gap-and-Go + ORB.
  gapMinPct?: number;
  gapSustainedMinPct?: number;
  gapVolumeRvolMin?: number;
  gapRecentDollarVolumeMin?: number;
  gapRecentRvolMin?: number;
  orbWindowMinutes?: number;
  orbRangeMaxPct?: number;
  orbBreakoutBufferPct?: number;
  orbVolumeConfirmMultiplier?: number;
  // Trend continuation.
  trendHigherLowMinRatio?: number;
  trendNearTopMinFraction?: number;
  trendPressureBuyMinCount?: number;
  trendMinChangePct?: number;
  trendMaxRecentDrawdownPct?: number;
  trendLookbackBars?: number;
  /** Mover threshold (absolute |extended_change_pct|) for the priceSync
   *  per-mover cumulative-volume sweep that runs only during pre-market
   *  and after-hours. Default 4. 0 disables the filter. */
  extVolumeChgThresholdPct?: number;
}

export async function adminGetSpikeScanSettings(): Promise<SpikeScanSettings> {
  return request<SpikeScanSettings>('/admin/settings/spike-scan');
}

export async function adminSaveSpikeScanSettings(data: {
  enabled?: boolean;
  intervalMinutes?: number;
  minPrice?: number;
  minAvgVolume?: number;
  maxMarketCapB?: number;
  includePremarket?: boolean;
  includeAfterMarket?: boolean;
  recentRvolThreshold?: number;
  recentRvolMaxFadePp?: number;
  velocityRequirePositiveDirection?: boolean;
  changeVelocityThreshold?: number;
  maxSnapshotAgeMin?: number;
  premarketRvolMin?: number;
  premarketRecentRvolMin?: number;
  premarketChangeVelocityMin?: number;
  premarketChangePctMin?: number;
  premarketDollarVolumeMin?: number;
  premarketIntervalDollarVolumeMin?: number;
  enablePressureQualifier?: boolean;
  pressureWindowSize?: number;
  pressureMinStrongCount?: number;
  pressureMinChangePct?: number;
  pressureMinRecentRvol?: number;
  enableConfluenceStack?: boolean;
  enableTrendContinuation?: boolean;
  barFetchEnabled?: boolean;
  barFetchActiveStartEtMin?: number;
  barFetchActiveEndEtMin?: number;
  barFetchMinIntervalMinutes?: number;
  entryWindowEnabled?: boolean;
  entryWindowStartEtMin?: number;
  entryWindowEndEtMin?: number;
  /** Plan #265 — unified live-signal session window (ET minutes-of-day).
   *  Chart + scanner + replay all read this. Defaults 420 (07:00) / 960 (16:00). */
  signalSessionStartEtMin?: number;
  signalSessionEndEtMin?: number;
  signalMomentumStartEtMin?: number;
  signalMomentumEndEtMin?: number;
  // Live BUY/SELL momentum gates.
  signalRequireMacd?: boolean;
  signalRequireEixom?: boolean;
  signalRsiOverbought?: number;
  signalRsiOversold?: number;
  signalRequireHtfAgreement?: boolean;
  signalCandleReversal?: boolean;
  orbBuildEnabled?: boolean;
  lowPriceDollarVolumeMin?: number;
  intervalDollarVolumeMin?: number;
  dollarVolumeMin?: number;
  lowPriceThreshold?: number;
  projectedRvolMin?: number;
  changePctAbsMin?: number;
  maxRetraceFromPeak?: number;
  maxShortTermFadePp?: number;
  stallAfterPeakMinutes?: number;
  stallRetracePpMin?: number;
  rvolDecayGuardMinAgeMin?: number;
  rvolDecayGuardMaxFraction?: number;
  rvolDecayFreshExemptRvol?: number;
  rvolDecaySessionPeakPersistEnabled?: boolean;
  premarketRecentRvolEnabled?: boolean;
  quietPassesToResolve?: number;
  warmRestartWindowMin?: number;
  includeHotTicksInPressureRing?: boolean;
  minChangePctFromPrevClose?: number;
  gapMinPct?: number;
  gapSustainedMinPct?: number;
  gapVolumeRvolMin?: number;
  gapRecentDollarVolumeMin?: number;
  gapRecentRvolMin?: number;
  orbWindowMinutes?: number;
  orbRangeMaxPct?: number;
  orbBreakoutBufferPct?: number;
  orbVolumeConfirmMultiplier?: number;
  trendHigherLowMinRatio?: number;
  trendNearTopMinFraction?: number;
  trendPressureBuyMinCount?: number;
  trendMinChangePct?: number;
  trendMaxRecentDrawdownPct?: number;
  trendLookbackBars?: number;
  extVolumeChgThresholdPct?: number;
}): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/spike-scan', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminForceSpikeScan(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/spike-scan/force', { method: 'POST' });
}

// ── Auto-position watch (admin) ──────────────────────────────────────────────
// Targeted price refresh for open auto-trade positions on a faster cadence
// than the global priceSync. Same shape as the user-facing watch-status
// endpoint but unscoped — admin sees activeSymbols across all users.
export interface AdminAutoPositionWatchSettings {
  status: 'idle' | 'watching' | 'error' | 'paused';
  enabled: boolean;
  intervalMinutes: number;
  lastCheckAt: string | null;
  nextCheckAt: string | null;
  lastDurationMs: number | null;
  lastError: string | null;
  activeSymbols: string[];
}

export async function adminGetAutoPositionWatchSettings(): Promise<AdminAutoPositionWatchSettings> {
  return request<AdminAutoPositionWatchSettings>('/admin/settings/auto-position-watch');
}

export async function adminSaveAutoPositionWatchSettings(data: {
  enabled?: boolean;
  intervalMinutes?: number;
}): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/auto-position-watch', { method: 'POST', body: JSON.stringify(data) });
}

export async function adminForceAutoPositionWatch(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/auto-position-watch/force', { method: 'POST' });
}

export interface SpikeScanQuality {
  total: number;
  fakeouts: number;
  sustained: number;
  fakeoutRate: number;
  bySeverity: Record<string, { total: number; fakeouts: number; sustained: number; fakeoutRate: number }>;
  /** Breakdown keyed by qualification_reason (plan #124). Includes a 'unknown'
   *  bucket for legacy rows written before the column existed. */
  byReason?: Record<string, {
    total: number; fakeouts: number; sustained: number; fakeoutRate: number;
    avg1dReturn: number | null; withBars: number;
  }>;
  groupStats: Record<string, { avgRvol: number; avgChange: number }>;
  windowDays: number;
  /** Avg next-session % return on sustained spikes — null when no sustained
   *  spike's ticker has cached daily bars (the source the calc draws from). */
  sustainedAvg1dReturn: number | null;
  /** Sample size for sustainedAvg1dReturn — how many sustained spikes had
   *  usable bars. Pair with sustainedTotal to show coverage. */
  sustainedWithBars: number;
  sustainedTotal: number;
  /** ISO timestamp of the most recent operator-triggered "reset" of the
   *  breakdown averages, or null when no reset is active. While set, the
   *  server filters volume_spikes to first_detected_at > resetAt. UI
   *  surfaces a "since X" hint + a button to clear it. */
  qualityResetAt: string | null;
}

export async function adminGetSpikeScanQuality(): Promise<SpikeScanQuality> {
  return request<SpikeScanQuality>('/admin/settings/spike-scan/quality');
}

/** Set or clear the Scanner Quality breakdown's lower-bound filter.
 *  `mode='reset'` (default) sets it to now; `mode='clear'` returns to
 *  the default 30-day window. Non-destructive — spike rows stay in the
 *  DB, only the displayed averages shift. */
export async function adminResetSpikeScanQuality(mode: 'reset' | 'clear' = 'reset'): Promise<{ qualityResetAt: string | null }> {
  return request<{ qualityResetAt: string | null }>('/admin/settings/spike-scan/quality/reset', {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });
}

// ── Broker connection (Alpaca, plan #118) ────────────────────────────────────
export type BrokerMode = 'paper' | 'live';

export interface BrokerConnection {
  broker: string;
  mode: BrokerMode;
  accountId: string | null;
  accountNumber: string | null;
  status: string | null;
  buyingPower: number | null;
  equity: number | null;
  cash: number | null;
  /** Masked display of the stored API key (first 3 + last 3 chars). Null if
   *  the ciphertext cannot be decrypted (env key rotated / corrupted row). */
  apiKeyMasked: string | null;
  connectedAt: string;
  lastUsedAt: string | null;
  lastError: string | null;
}

export interface BrokerConnectionResponse {
  connection: BrokerConnection | null;
  /** Whether the server has BROKER_CREDS_KEY configured. When false the
   *  admin UI shows a setup banner instead of the creds form. */
  keyConfigured: boolean;
}

export async function adminGetBrokerConnection(): Promise<BrokerConnectionResponse> {
  return request<BrokerConnectionResponse>('/admin/broker/alpaca');
}

export async function adminSaveBrokerConnection(body: {
  apiKey: string; apiSecret: string; mode: BrokerMode;
}): Promise<BrokerConnectionResponse> {
  return request<BrokerConnectionResponse>('/admin/broker/alpaca', {
    method: 'POST', body: JSON.stringify(body),
  });
}

export async function adminTestBrokerConnection(): Promise<BrokerConnectionResponse> {
  return request<BrokerConnectionResponse>('/admin/broker/alpaca/test', { method: 'POST' });
}

export async function adminRevokeBrokerConnection(): Promise<{ revoked: boolean }> {
  return request<{ revoked: boolean }>('/admin/broker/alpaca', { method: 'DELETE' });
}

export interface BrokerOrderLogEntry {
  id: string;
  broker: string;
  mode: 'paper' | 'live';
  ticker: string;
  side: 'buy' | 'sell';
  qty: number | null;
  submittedPrice: number | null;
  filledQty: number | null;
  filledAvgPrice: number | null;
  status: string | null;
  rejectReason: string | null;
  brokerOrderId: string | null;
  clientOrderId: string | null;
  submittedAt: string;
  updatedAt: string;
  portfolioId: string | null;
  portfolioName: string | null;
  autoTradeEventId: string | null;
  /** Signed bps — positive = unfavorable fill (paid more on a buy, got less
   *  on a sell). Null when either submitted or filled price is missing. */
  slippageBps: number | null;
}

export interface BrokerOrderLogPage {
  items: BrokerOrderLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface BrokerOrderLogFilters {
  ticker?: string;
  status?: string;
  side?: 'buy' | 'sell';
  mode?: 'paper' | 'live';
  since?: string;
  limit?: number;
  offset?: number;
}

// ── Broker execution global settings (kill switch + daily cap) ─────────────
export interface BrokerExecutionSettings {
  enabled: boolean;
  maxDailyNotionalUsd: number;
}

export async function adminGetBrokerExecutionSettings(): Promise<BrokerExecutionSettings> {
  return request<BrokerExecutionSettings>('/admin/settings/broker-execution');
}

export async function adminSaveBrokerExecutionSettings(body: Partial<BrokerExecutionSettings>): Promise<BrokerExecutionSettings> {
  return request<BrokerExecutionSettings>('/admin/settings/broker-execution', {
    method: 'POST', body: JSON.stringify(body),
  });
}

// ── Claude AI pre-trade validator (plan #128) ──────────────────────────────
export type ClaudeModel = 'claude-haiku-4-5-20251001' | 'claude-sonnet-4-6' | 'claude-opus-4-8';

export interface ClaudeValidatorSettings {
  enabled: boolean;
  /** EFFECTIVE model the signal validator resolves to (Feature Routing
   *  override if set, else the base fallback). Any catalog id — not just the
   *  legacy 3 — since Feature Routing can pick Fable/Sonnet/etc. */
  model: string;
  /** EFFECTIVE model the stage validator resolves to (may differ from `model`
   *  when the two features are overridden independently). */
  stageModel: string;
  approveOnTimeout: boolean;
  timeoutMs: number;
  dailyRequestCap: number;
  /** When true, the validator is handed a broad-market context block (regime
   *  tilt, SPY/QQQ %, R1000 breadth, VIX, the ticker's sector %) so it weighs
   *  market mood on buys and sells. */
  includeMarketContext: boolean;
  /** Masked display of the stored API key (first 3 + last 3 chars). Null if
   *  unconfigured or ciphertext can't be decrypted (env key rotated). */
  apiKeyMasked: string | null;
  /** Whether the server has BROKER_CREDS_KEY configured. When false the
   *  admin UI shows a setup banner instead of the creds form. */
  keyConfigured: boolean;
  /** Today's usage, bucketed by ET trading day. Diagnostic display only. */
  usageToday: {
    count: number;
    approvedCount: number;
    rejectedCount: number;
  };
}

export async function adminGetClaudeValidator(): Promise<ClaudeValidatorSettings> {
  return request<ClaudeValidatorSettings>('/admin/validator/claude');
}

export async function adminSaveClaudeValidator(body: {
  apiKey?: string;
  enabled?: boolean;
  model?: ClaudeModel;
  approveOnTimeout?: boolean;
  timeoutMs?: number;
  dailyRequestCap?: number;
  includeMarketContext?: boolean;
}): Promise<ClaudeValidatorSettings> {
  return request<ClaudeValidatorSettings>('/admin/validator/claude', {
    method: 'POST', body: JSON.stringify(body),
  });
}

export async function adminTestClaudeValidator(): Promise<{ ok: boolean; reply: string; model: string }> {
  // `model` is the EFFECTIVE model probed (Feature Routing override if set) —
  // any catalog id, so typed string rather than the legacy ClaudeModel union.
  return request<{ ok: boolean; reply: string; model: string }>('/admin/validator/claude/test', { method: 'POST' });
}

export async function adminRevokeClaudeValidator(): Promise<{ revoked: boolean }> {
  return request<{ revoked: boolean }>('/admin/validator/claude', { method: 'DELETE' });
}

// ── Broker waitlist (VIP approval) ────────────────────────────────────────
export interface BrokerWaitlistUser {
  id: string;
  email: string;
  username: string | null;
  role: string;
  approved: boolean;
  hasConnection: boolean;
  createdAt: string;
}

export async function adminGetBrokerWaitlist(filters: { q?: string; approvedOnly?: boolean } = {}): Promise<{ users: BrokerWaitlistUser[] }> {
  const qs = new URLSearchParams();
  if (filters.q) qs.set('q', filters.q);
  if (filters.approvedOnly) qs.set('approved', '1');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<{ users: BrokerWaitlistUser[] }>(`/admin/broker/waitlist${suffix}`);
}

export async function adminSetBrokerWaitlistApproval(userId: string, approved: boolean): Promise<{ userId: string; approved: boolean }> {
  return request<{ userId: string; approved: boolean }>(`/admin/broker/waitlist/${userId}`, {
    method: 'POST', body: JSON.stringify({ approved }),
  });
}

export async function adminGetBrokerOrders(filters: BrokerOrderLogFilters = {}): Promise<BrokerOrderLogPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<BrokerOrderLogPage>(`/admin/broker/orders${suffix}`);
}

// ── Push notifications (web-push / VAPID) ────────────────────────────────────

export async function getPushVapidPublicKey(): Promise<string | null> {
  try {
    const res = await request<{ publicKey: string }>('/push/vapid-public-key');
    return res.publicKey;
  } catch {
    return null;
  }
}

export async function subscribePush(subscription: PushSubscription): Promise<void> {
  await request('/push/subscribe', { method: 'POST', body: JSON.stringify({ subscription }) });
}

export async function unsubscribePush(endpoint: string): Promise<void> {
  await request('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) });
}

export async function sendTestPush(): Promise<{ sent: number; pruned: number }> {
  return request<{ sent: number; pruned: number }>('/push/test', { method: 'POST' });
}

// ── NASDAQ Symbol Directory Sync ─────────────────────────────────────────────

export interface NasdaqDirectoryState {
  enabled: boolean;
  syncIntervalDays: number;
  lastSyncAt: string | null;
  status: 'idle' | 'syncing' | 'error';
  insertedCount: number;
  totalParsed: number;
  lastError: string | null;
}

export async function adminGetNasdaqDirectorySettings(): Promise<NasdaqDirectoryState> {
  return request<NasdaqDirectoryState>('/admin/settings/nasdaq-directory');
}

export async function adminSaveNasdaqDirectorySettings(data: { enabled?: boolean; syncIntervalDays?: number }): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/nasdaq-directory', { method: 'PUT', body: JSON.stringify(data) });
}

export async function adminSyncNasdaqDirectory(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/nasdaq-directory/sync', { method: 'POST' });
}


// ── Google Auth Settings ─────────────────────────────────────────────────────

export interface GoogleAuthSettings {
  clientId: string;
}

export async function adminGetGoogleAuthSettings(): Promise<GoogleAuthSettings> {
  return request<GoogleAuthSettings>('/admin/settings/google-auth');
}

export async function adminSaveGoogleAuthSettings(data: Partial<GoogleAuthSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/google-auth', { method: 'POST', body: JSON.stringify(data) });
}

export async function getPublicGoogleAuth(): Promise<{ clientId: string | null }> {
  return request<{ clientId: string | null }>('/admin/public/google-auth');
}

// ── Maintenance Mode ─────────────────────────────────────────────────────────

export interface MaintenanceSettings {
  enabled: boolean;
  message: string;
  blockAnonymous: boolean;
  errorCode: string;
}

export async function adminGetMaintenanceSettings(): Promise<MaintenanceSettings> {
  return request<MaintenanceSettings>('/admin/settings/maintenance');
}

export async function adminSaveMaintenanceSettings(data: Partial<MaintenanceSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/maintenance', { method: 'POST', body: JSON.stringify(data) });
}

export async function getPublicMaintenance(): Promise<MaintenanceSettings> {
  return request<MaintenanceSettings>('/admin/public/maintenance');
}

// ── Content Preview ─────────────────────────────────────────────────────────

export interface ContentPreviewSettings {
  enabled: boolean;
  previewSeconds: number;
  expiryHours: number;
}

export interface ContentPreviewStatus {
  enabled: boolean;
  previewSeconds: number;
  expiryHours: number;
  canPreview: boolean;
}

export async function getPublicContentPreview(): Promise<ContentPreviewStatus> {
  return request<ContentPreviewStatus>('/admin/public/content-preview');
}

export async function markContentPreviewConsumed(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/admin/public/content-preview/consumed', { method: 'POST' });
}

export async function adminGetContentPreviewSettings(): Promise<ContentPreviewSettings> {
  return request<ContentPreviewSettings>('/admin/settings/content-preview');
}

export async function adminSaveContentPreviewSettings(data: Partial<ContentPreviewSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/content-preview', { method: 'POST', body: JSON.stringify(data) });
}

export interface PublicFeatureFlags {
  proSubscriptions: TierAccess;
  advancedCharts: TierAccess;
  stockScreener: TierAccess;
  earningsCalendar: TierAccess;
  portfolioTracker: TierAccess;
  priceAlerts: TierAccess;
  apiAccess: TierAccess;
  exportData: TierAccess;
  intradayChart: TierAccess;
  financials: TierAccess;
}

export async function getPublicFeatures(): Promise<PublicFeatureFlags> {
  return request<PublicFeatureFlags>('/admin/public/features');
}

// ── Page Access ──────────────────────────────────────────────────────────────

export interface PageAccessRule {
  path: string;
  label: string;
  access: 'public' | 'registered' | 'pro' | 'full' | 'admin';
}

export async function getPublicPageAccess(): Promise<PageAccessRule[]> {
  return request<PageAccessRule[]>('/admin/public/page-access');
}

export async function adminGetPageAccess(): Promise<PageAccessRule[]> {
  return request<PageAccessRule[]>('/admin/settings/page-access');
}

export async function adminSavePageAccess(rules: PageAccessRule[]): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/page-access', { method: 'PUT', body: JSON.stringify(rules) });
}

export interface PageNode {
  path: string;
  label: string;
  isDynamic: boolean;
  exists: boolean;
  access: 'public' | 'registered' | 'pro' | 'full' | 'admin';
  accessSource: 'rule' | 'default';
  children: PageNode[];
}

export interface PageRegistryResponse {
  pages: PageNode[];
  stats: { totalPages: number; totalRules: number; missingRules: number; staleRules: number };
}

export async function adminGetPageRegistry(): Promise<PageRegistryResponse> {
  return request<PageRegistryResponse>('/admin/pages/registry');
}

// ── Changelog ────────────────────────────────────────────────────────────────

export interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  description: string;
  releasedAt: string; // YYYY-MM-DD
}

export function getChangelog(): Promise<ChangelogEntry[]> {
  return request<ChangelogEntry[]>('/changelog');
}

export function adminCreateChangelogEntry(data: Omit<ChangelogEntry, 'id'>): Promise<ChangelogEntry> {
  return request<ChangelogEntry>('/changelog', { method: 'POST', body: JSON.stringify(data) });
}

export function adminUpdateChangelogEntry(id: string, data: Partial<Omit<ChangelogEntry, 'id'>>): Promise<ChangelogEntry> {
  return request<ChangelogEntry>(`/changelog/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function adminDeleteChangelogEntry(id: string): Promise<void> {
  return request<void>(`/changelog/${id}`, { method: 'DELETE' });
}

// ── Watchlists ───────────────────────────────────────────────────────────────

export interface WatchlistDto {
  id: string;
  name: string;
  pinned: boolean;
  /** When true, the list is hidden from the dashboard tile and excluded
   *  from the global isWatched() check on stocks/screener; its tickers
   *  also drop out of the watchlist→tracked_tickers materialization,
   *  silencing volume alerts. Names are stored UPPERCASE server-side. */
  muted: boolean;
  items: { ticker: string; addedAt: number }[];
  sortOrder: number;
  updatedAt: string;
}

export function getWatchlists(): Promise<WatchlistDto[]> {
  return request<WatchlistDto[]>('/watchlists');
}

export function saveWatchlists(watchlists: WatchlistDto[]): Promise<WatchlistDto[]> {
  return request<WatchlistDto[]>('/watchlists', { method: 'PUT', body: JSON.stringify({ watchlists }) });
}

export function createWatchlist(payload: Pick<WatchlistDto, 'id' | 'name'> & Partial<Pick<WatchlistDto, 'pinned' | 'muted' | 'items'>>): Promise<WatchlistDto> {
  return request<WatchlistDto>('/watchlists', { method: 'POST', body: JSON.stringify(payload) });
}

export function patchWatchlist(id: string, patch: Partial<Pick<WatchlistDto, 'name' | 'pinned' | 'muted' | 'items' | 'sortOrder'>>): Promise<WatchlistDto> {
  return request<WatchlistDto>(`/watchlists/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteWatchlist(id: string): Promise<void> {
  return request<void>(`/watchlists/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** 30-minute price-action trend per ticker (issue #234). Backed by the
 *  existing intraday cache — same bars the chart + scanner write to.
 *  Tickers without enough recent data return null and the FE skips the
 *  indicator for that row. */
export interface TrendSummary {
  slope: 'up' | 'down' | 'flat';
  changePct30m: number;
  anchorDate: string;
}
export function getWatchlistTrends(tickers: string[]): Promise<Record<string, TrendSummary | null>> {
  if (tickers.length === 0) return Promise.resolve({});
  const csv = tickers.join(',');
  return request<Record<string, TrendSummary | null>>(
    `/watchlists/trends?tickers=${encodeURIComponent(csv)}`,
  );
}

/** One 15-min window of the session for the watchlist momentum views. */
export interface PulseBucket {
  /** Bucket start as ET wall-clock "HH:MM" (e.g. "09:30"). */
  t: string;
  /** Percent move during this 15-min window (close-over-close vs the
   *  prior bucket; the first bucket is its own open→close). */
  pct: number;
  /** Bucket close price — the price path the sparkline view plots. */
  close: number;
}
/** Short-term intraday EMA posture for the watchlist "↗ SMA" view —
 *  EMA(9)/EMA(20) on the session's 5-min closes. Mirrors the API's
 *  IntradayMA (watchlistTrendService.computeIntradayMA). */
export interface IntradayMA {
  price: number;
  ema9: number | null;
  ema20: number | null;
  /** Signed % of price vs each EMA: (price − ema) / ema * 100. */
  distToEma9Pct: number | null;
  distToEma20Pct: number | null;
  /** bullish = price > ema9 > ema20; bearish = price < ema9 < ema20;
   *  else mixed. */
  posture: 'bullish' | 'bearish' | 'mixed';
  /** Below the slow EMA(20) but reclaimed the fast EMA(9) — climbing
   *  back toward the slow line (the green ↗ cue). */
  reclaiming: boolean;
}
/** One raw 5-min OHLC bar for the watchlist Trend column's candlestick
 *  mode. Mirrors the API's PulseBar (watchlistTrendService.takeRecentBars). */
export interface PulseBar {
  /** Bar start as ET wall-clock "HH:MM" (e.g. "15:55"). */
  t: string;
  open: number;
  high: number;
  low: number;
  close: number;
}
export interface PulseSeries {
  buckets: PulseBucket[];
  /** Session date the buckets belong to, "YYYY-MM-DD" ET. */
  asOf: string;
  /** EMA9/EMA20 posture for the "↗ SMA" view, computed from the same
   *  5-min bars. Optional/nullable — present once the session has ≥9
   *  valid closes. */
  ma?: IntradayMA | null;
  /** Trailing raw 5-min bars of the session (oldest→newest) for the Trend
   *  column's candlestick mode — the last 30 minutes. Built from the same
   *  bars as `buckets`, so both Trend modes ride one request. */
  bars?: PulseBar[];
}
export function getWatchlistPulse(tickers: string[]): Promise<Record<string, PulseSeries | null>> {
  if (tickers.length === 0) return Promise.resolve({});
  const csv = tickers.join(',');
  return request<Record<string, PulseSeries | null>>(
    `/watchlists/pulse?tickers=${encodeURIComponent(csv)}`,
  );
}

/** Batched support/resistance levels for the combined-watchlist "S/R Levels"
 *  view. One entry per ticker (null when illiquid / not enough bars), each a
 *  {resistance, support, atrAtLast} just like the single-ticker
 *  getChartLevels — the same detector + per-range tuning, fanned out
 *  server-side. Default range 6m (daily bars) for structural levels. */
export type WatchlistLevelsRange = '1d' | '1w' | '1m' | '3m' | '6m' | '1y';
export function getWatchlistLevels(
  tickers: string[],
  range: WatchlistLevelsRange = '6m',
): Promise<Record<string, ChartLevelsResponse | null>> {
  if (tickers.length === 0) return Promise.resolve({});
  const csv = tickers.join(',');
  return request<Record<string, ChartLevelsResponse | null>>(
    `/watchlists/levels?tickers=${encodeURIComponent(csv)}&range=${range}`,
  );
}

/** One distinct step of the intraday Eixom staircase (HTF MACD-histogram). */
export interface EixomStep {
  /** Bucket-start as ET wall-clock "HH:MM". */
  t: string;
  /** Eixom value for this step (oscillates around zero). */
  v: number;
}
/** Intraday Eixom for the combined-watchlist Eixom views. Matches the chart's
 *  Eixom pane (same params); steps are ~25-min HTF buckets. */
export interface EixomSeries {
  steps: EixomStep[];
  latest: number | null;
  state: 'positive' | 'negative' | 'zero' | null;
  crossUp: boolean;
  crossUpAt: string | null;
  asOf: string;
}
export function getWatchlistEixom(tickers: string[]): Promise<Record<string, EixomSeries | null>> {
  if (tickers.length === 0) return Promise.resolve({});
  const csv = tickers.join(',');
  return request<Record<string, EixomSeries | null>>(
    `/watchlists/eixom?tickers=${encodeURIComponent(csv)}`,
  );
}

/** Direction flag on the combined-watchlist "1M Candles" view. Computed
 *  server-side (api/src/utils/dailyTrend.ts) from the SAME bars the row
 *  renders, so the badge can never contradict the candles beside it. */
export type DailyTrendDirection = 'uptrend' | 'downtrend' | 'neutral';
export interface DailyTrendVerdict {
  direction: DailyTrendDirection;
  /** -100..+100 composite; sign follows direction, magnitude is conviction. */
  score: number;
  /** Net % move across the window (first close → last close). */
  changePct: number;
  /** Regression slope as % of mean close per day. */
  slopePctPerDay: number;
  /** Signed % distance of the last close from the window's SMA(10). */
  distToSmaPct: number | null;
  /** Fraction (0..1) of closes that finished above the running SMA(10). */
  aboveSmaFrac: number | null;
  structure: 'higher-highs' | 'lower-lows' | 'mixed';
  bars: number;
  /** Plain-English justification, shown as the badge tooltip. */
  reason: string;
}
export interface DailyCandle {
  /** "YYYY-MM-DD". */
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}
export interface WatchlistDailySeries {
  bars: DailyCandle[];
  trend: DailyTrendVerdict | null;
  /** Date of the last bar, "YYYY-MM-DD". */
  asOf: string | null;
}
/** Batched daily candles (~1 trading month) + trend flag for the
 *  combined-watchlist "1M Candles" view. Cache-only server side — tickers
 *  with no warm daily cache come back null. */
export function getWatchlistDaily(
  tickers: string[],
  bars = 22,
): Promise<Record<string, WatchlistDailySeries | null>> {
  if (tickers.length === 0) return Promise.resolve({});
  const csv = tickers.join(',');
  return request<Record<string, WatchlistDailySeries | null>>(
    `/watchlists/daily?tickers=${encodeURIComponent(csv)}&bars=${bars}`,
  );
}

// ── Dividends ──────────────────────────────────────────────────────────────

export interface DividendEntry {
  symbol: string;
  companyName: string;
  exDate: string;
  paymentDate: string | null;
  amount: number | null;
  annualDividend: number | null;
  frequency: string | null;
  yield: number | null;
  /** The issuer's cadence carried past its last published month — a
   *  forecast, not an announced date. */
  projected?: boolean;
}

export async function getDividends(from: string, to: string, symbol?: string): Promise<DividendEntry[]> {
  const params = `from=${from}&to=${to}${symbol ? `&symbol=${encodeURIComponent(symbol)}` : ''}`;
  return request<DividendEntry[]>(`/dividends?${params}`);
}

// ── Stock Splits ──────────────────────────────────────────────────────────

export interface StockSplit {
  symbol: string;
  splitDate: string;
  fromFactor: number;
  toFactor: number;
  ratio: string;
}

export async function getStockSplits(from?: string, to?: string): Promise<StockSplit[]> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return request<StockSplit[]>(`/splits${qs ? `?${qs}` : ''}`);
}

// ── Insider Trading ────────────────────────────────────────────────────────

export interface InsiderTrade {
  id: number;
  symbol: string;
  name: string;
  title: string;
  transactionType: string;
  shares: number | null;
  value: number | null;
  transactionDate: string;
  filedDate: string | null;
}

export async function getInsiderTrades(limit = 50): Promise<InsiderTrade[]> {
  return request<InsiderTrade[]>(`/insiders/recent?limit=${limit}`);
}

export async function getInsiderTradesBySymbol(symbol: string): Promise<InsiderTrade[]> {
  return request<InsiderTrade[]>(`/insiders/${encodeURIComponent(symbol)}`);
}

// ── Dilution detection ───────────────────────────────────────────────────────

/** Shares-outstanding delta (Option A). hasData=false until the nightly
 *  fundamentals sweep has accumulated ≥ 2 snapshots. */
export interface DilutionInfo {
  ticker: string;
  hasData: boolean;
  latestShares: number | null;
  latestDate: string | null;
  baselineShares: number | null;
  baselineDate: string | null;
  windowDays: number;
  rawGrowthPct: number | null;
  splitAdjustedGrowthPct: number | null;
  splitMultiplier: number;
  isDiluting: boolean;
  probableUnrecordedSplit: boolean;
}
export async function getDilutionInfo(ticker: string): Promise<DilutionInfo> {
  return request<DilutionInfo>(`/stocks/${encodeURIComponent(ticker)}/dilution`);
}

/** SEC offering filing (Option B) — a dilutive registration/prospectus. */
export interface OfferingFiling {
  id: string;
  symbol: string;
  form: string;
  filedDate: string;
  accession: string;
  issuerName: string | null;
  secUrl: string | null;
}
export async function getOfferingsBySymbol(symbol: string): Promise<OfferingFiling[]> {
  return request<OfferingFiling[]>(`/offerings/${encodeURIComponent(symbol)}`);
}

export interface RecentOfferingsPage {
  items: OfferingFiling[];
  total: number;
}

/** Paged, filterable cross-universe feed of dilutive offering filings.
 *  `form` is a root-form family ('424b' | 's-1' | 's-3' | 'fwp' | 'other');
 *  `q` matches symbol or issuer name. Returns the page items + total count. */
export async function getRecentOfferings(opts: {
  limit?: number;
  offset?: number;
  form?: string;
  q?: string;
} = {}): Promise<RecentOfferingsPage> {
  const p = new URLSearchParams();
  p.set('limit', String(opts.limit ?? 50));
  p.set('offset', String(opts.offset ?? 0));
  if (opts.form && opts.form !== 'all') p.set('form', opts.form);
  if (opts.q && opts.q.trim()) p.set('q', opts.q.trim());
  return request<RecentOfferingsPage>(`/offerings/recent?${p.toString()}`);
}

/** Distinct symbols with a recent (≤30d) dilutive offering filing — used to
 *  flag movers/watchlist rows with a (D) badge via useDilutingSymbols(). */
export async function getDilutingSymbols(): Promise<string[]> {
  return request<string[]>('/offerings/diluting-symbols');
}

// ── Economic Calendar ──────────────────────────────────────────────────────

export interface EconomicEvent {
  id: string;
  date: string;
  time: string;
  event: string;
  country: string;
  impact: 'High' | 'Medium' | 'Low';
  forecast: string | null;
  previous: string | null;
  actual: string | null;
}

export async function getEconomicCalendar(from: string, to: string): Promise<EconomicEvent[]> {
  return request<EconomicEvent[]>(`/economic-calendar?from=${from}&to=${to}`);
}

// Upcoming US economic releases (CPI / jobs / FOMC / PPI / PCE / GDP / retail)
// for the dashboard Economic Calendar widget. DB-backed seeded schedule.
export type EconReleaseType = 'cpi' | 'ppi' | 'jobs' | 'fomc' | 'gdp' | 'pce' | 'retail';
export interface EconomicReleaseItem {
  date: string;   // YYYY-MM-DD (ET)
  type: EconReleaseType;
  title: string;
  time: string;   // ET HH:MM, '' = TBA
  impact: 'high' | 'medium' | 'low';
}
export async function getUpcomingEconomicReleases(limit = 12): Promise<EconomicReleaseItem[]> {
  return request<EconomicReleaseItem[]>(`/economic-calendar/upcoming?limit=${limit}`);
}

// ── Price Alerts ─────────────────────────────────────────────────────────────

export interface PriceAlert {
  id: string;
  symbol: string;
  condition: 'above' | 'below' | 'pct_change_up' | 'pct_change_down';
  threshold: number;
  active: boolean;
  triggeredAt: string | null;
  createdAt: string;
  currentPrice?: number;
}

export async function getAlerts(): Promise<PriceAlert[]> {
  return request<PriceAlert[]>('/alerts');
}

export async function createAlert(data: { symbol: string; condition: string; threshold: number }): Promise<PriceAlert> {
  return request<PriceAlert>('/alerts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAlert(id: string, data: Partial<{ active: boolean; threshold: number; condition: string }>): Promise<PriceAlert> {
  return request<PriceAlert>(`/alerts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAlert(id: string): Promise<void> {
  return request<void>(`/alerts/${id}`, { method: 'DELETE' });
}

// ── Notification log / Alerts history ─────────────────────────────────────
export type NotificationLogEntryType = 'price' | 'volume_spike';
export type NotificationLogChannel = 'email' | 'push' | 'both' | 'none';

export interface NotificationLogEntry {
  id: string;
  alertType: NotificationLogEntryType;
  symbol: string;
  channel: NotificationLogChannel;
  /** Shape varies by alertType:
   *   price:        { alertId, condition, threshold, price, changePct }
   *   volume_spike: { severity, rvol, changePct, name, firstDetectedAt } */
  payload: Record<string, unknown>;
  firedAt: string; // ISO
}

export async function getAlertsHistory(opts: { limit?: number; before?: string; type?: NotificationLogEntryType } = {}): Promise<{ items: NotificationLogEntry[]; hasMore: boolean }> {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.before) params.set('before', opts.before);
  if (opts.type) params.set('type', opts.type);
  const qs = params.toString();
  return request<{ items: NotificationLogEntry[]; hasMore: boolean }>(`/alerts/history${qs ? `?${qs}` : ''}`);
}

export async function rearmAlertFromHistory(id: string): Promise<PriceAlert> {
  return request<PriceAlert>(`/alerts/history/${id}/rearm`, { method: 'POST' });
}

export async function clearAlertsHistory(opts: { type?: NotificationLogEntryType } = {}): Promise<{ deleted: number }> {
  const qs = opts.type ? `?type=${opts.type}` : '';
  return request<{ deleted: number }>(`/alerts/history${qs}`, { method: 'DELETE' });
}

export async function deleteAlertHistoryEntry(id: string): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(`/alerts/history/${id}`, { method: 'DELETE' });
}

// ── Tracked Tickers (issue #182) — unified per-ticker view aggregating
// watchlist memberships + price alerts behind one row. Powers the
// /account/alerts page.
export interface TrackedTickerWatchlist {
  id: string;
  name: string;
}
export interface TrackedTickerPriceAlert {
  id: string;
  condition: 'above' | 'below' | 'pct_change_up' | 'pct_change_down';
  threshold: number;
  active: boolean;
}
export interface TrackedTicker {
  ticker: string;
  name: string | null;
  currentPrice: number | null;
  volumeAlertsEnabled: boolean;
  inWatchlists: TrackedTickerWatchlist[];
  priceAlerts: TrackedTickerPriceAlert[];
}

export async function getTrackedTickers(): Promise<TrackedTicker[]> {
  return request<TrackedTicker[]>('/account/tracked-tickers');
}

export async function setTrackedTickerVolumeAlerts(ticker: string, enabled: boolean): Promise<{ ticker: string; volumeAlertsEnabled: boolean }> {
  return request(`/account/tracked-tickers/${encodeURIComponent(ticker)}/volume-alerts`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

export async function removeTrackedTicker(ticker: string): Promise<void> {
  return request<void>(`/account/tracked-tickers/${encodeURIComponent(ticker)}`, { method: 'DELETE' });
}

// ── Congress Follows ────────────────────────────────────────────────────────

export interface CongressFollow {
  id: string;
  bioGuideId: string;
  representative: string;
  createdAt: string;
}

export async function getCongressFollows(): Promise<CongressFollow[]> {
  return request<CongressFollow[]>('/congress/follows');
}

export async function followCongressMember(bioGuideId: string, representative: string): Promise<CongressFollow> {
  return request<CongressFollow>('/congress/follows', {
    method: 'POST',
    body: JSON.stringify({ bioGuideId, representative }),
  });
}

export async function unfollowCongressMember(bioGuideId: string): Promise<void> {
  return request<void>(`/congress/follows/${bioGuideId}`, { method: 'DELETE' });
}

// ── Smart Money Signals ─────────────────────────────────────────────────────

export interface SmartMoneySignal {
  ticker: string;
  insiderName: string;
  insiderTitle: string;
  insiderType: 'buy' | 'sell' | null;
  insiderDate: string;
  congressMember: string;
  congressType: 'buy' | 'sell' | null;
  congressDate: string;
  signalType:
    | 'both_buying' | 'both_selling' | 'mixed'
    | 'insider_only_buying' | 'insider_only_selling'
    | 'congress_only_buying' | 'congress_only_selling';
  strength: 'strong' | 'moderate';
}

export async function getSmartMoneySignals(): Promise<SmartMoneySignal[]> {
  return request<SmartMoneySignal[]>('/smart-money/signals');
}

// ── Stock Sentiment Scanner ─────────────────────────────────────────────────

export interface StockSentimentHeadline {
  headline: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  source: string;
  publishedAt: string;
}

export interface StockSentimentData {
  ticker: string;
  score: number;
  label: string;
  totalArticles: number;
  positiveCount: number;
  negativeCount: number;
  recentHeadlines: StockSentimentHeadline[];
}

export async function getStockSentiment(ticker: string): Promise<StockSentimentData> {
  return request<StockSentimentData>(`/stock-sentiment/${encodeURIComponent(ticker)}`);
}

// ── Correlation Finder ──────────────────────────────────────────────────────

export interface CorrelationEntry {
  ticker: string;
  name: string;
  changePct: number;
  sector: string;
  score: number;
}

export interface CorrelationData {
  ticker: string;
  name: string;
  changePct: number;
  sector: string;
  correlations: {
    positive: CorrelationEntry[];
    negative: CorrelationEntry[];
  };
}

export async function getCorrelation(ticker: string): Promise<CorrelationData> {
  return request<CorrelationData>(`/correlation/${encodeURIComponent(ticker)}`);
}

// ── Market Mood ─────────────────────────────────────────────────────────────

export interface MarketMoodData {
  fearGreed: { score: number; label: string };
  breadth: { advancers: number; decliners: number; unchanged: number; ratio: number; newHighs: number; newLows: number };
  sectors: {
    gaining: { sector: string; changePct: number }[];
    losing: { sector: string; changePct: number }[];
  };
  vix: { symbol: string; price: number; changePct: number; label: string } | null;
  trending: { ticker: string; name: string; changePct: number; price: number }[];
  mood: { label: string; emoji: string };
  premarket?: PremarketSentiment;
  /** Daily regime context — descriptive (not predictive) cross-asset
   *  classifications. Built from `index_history` daily bars (refreshed
   *  4:30 PM ET). Same value in both pre-market and RTH views — it's a
   *  point-in-time snapshot of the environment the session is happening
   *  in, not a forecast. */
  regime?: MarketRegimeData | null;
}

export type RegimeState = 'risk-on' | 'neutral' | 'risk-off' | 'unavailable';

export interface MarketRegimeReading {
  key: 'trend' | 'volatility' | 'yield-curve' | 'credit' | 'dollar';
  label: string;
  context: string;
  state: RegimeState;
  value: number | null;
  unit?: string;
}

export interface MarketRegimeData {
  asOf: string;
  forDate: string;
  readings: MarketRegimeReading[];
  composite: {
    riskOn: number;
    riskOff: number;
    neutral: number;
    tilt: 'risk-on' | 'risk-off' | 'mixed';
  };
}

/** Pre-market sentiment composite (plan #226). The widget switches to
 *  this view client-side when getMarketSession() is 'pre-market' or
 *  'closed' (overnight / weekend). futures components are stubbed
 *  off until siteSettings.premarketSentimentFuturesEnabled is true. */
export interface PremarketSentiment {
  asOf: string;
  forDate: string;        // YYYY-MM-DD ET — the session this forecast is for
  session: 'pre-market' | 'regular' | 'after-hours' | 'closed';
  futuresEnabled: boolean;
  score: number;          // 0..100, 50 = neutral
  verdict: string;
  verdictEmoji: string;
  components: {
    spy:            { changePct: number | null; score: number; weight: number; available: boolean };
    mag7:           { changePct: number | null; score: number; weight: number; tickers: string[]; available: boolean };
    sectors:        { score: number; weight: number; alignment: 'risk-on' | 'risk-off' | 'mixed' | 'unavailable'; leading: string[]; lagging: string[]; spread: number | null; available: boolean };
    esFutures:      { changePct: number | null;                              score: number; weight: number; available: boolean; note?: string };
    nqFutures:      { changePct: number | null;                              score: number; weight: number; available: boolean; note?: string };
    vixFutures:     { value: number | null;     changePct: number | null;    score: number; weight: number; available: boolean; note?: string };
    /** 10-year Treasury yield (^TNX). Inverse-scored: rising yields =
     *  bearish equities. `value` is the yield as a percentage. */
    tnx:            { value: number | null;     changePct: number | null;    score: number; weight: number; available: boolean; note?: string };
    oil:            { changePct: number | null;                              score: number; weight: number; available: boolean };
    /** US Dollar Index (DX-Y.NYB). Inverse-scored: strong USD = headwind
     *  for multinationals, commodities, EM. */
    dollar:         { value: number | null;     changePct: number | null;    score: number; weight: number; available: boolean; note?: string };
  };
}

export async function getMarketMood(): Promise<MarketMoodData> {
  return request<MarketMoodData>('/market-mood');
}

// ── User Preferences ────────────────────────────────────────────────────────

// ── Portfolio Tracking ───────────────────────────────────────────────────────

export interface Portfolio {
  id: string; user_id: string; name: string; currency: string; source: string;
  source_meta: Record<string, unknown>; watch_enabled?: boolean;
  created_at: string; updated_at: string;
  last_synced_at: string | null; position_count?: number;
  total_value?: number; total_cost?: number; day_change?: number;
  day_change_pct?: number;
  /** Lifetime realized PnL across all closed trades, FIFO. Server-computed
   *  from transactions; UI adds it to open-position unrealized PnL to show
   *  the true total return. Without this, a portfolio that day-trades
   *  back to zero open positions reads $0 return even after losses. */
  realized_pnl_total?: number;
  /** Realized PnL on sells transacted since today's ET midnight. Combines
   *  with the open positions' day change to show the true day result. */
  realized_pnl_today?: number;
  /** Number of buy+sell transactions since today's ET midnight. Used by
   *  the portfolio list view to surface today's trading activity at a
   *  glance. */
  trades_today?: number;
  best_performer?: { ticker: string; returnPct: number } | null;
  worst_performer?: { ticker: string; returnPct: number } | null;
  dividend_yield?: number;
  cash_balance?: number;
  // True if this portfolio has portfolio_auto_trade_rules.enabled = TRUE.
  // FALSE when no rules row exists. Returned from GET /api/portfolios.
  auto_trade_enabled?: boolean;
  // Claude AI pre-trade validator gates (plan #128). Reflect the
  // portfolio_auto_trade_rules.ai_validate_{buys,sells} columns. Used by
  // the portfolio list card to render a Brain badge when either side is
  // gated — the badge is purely informational; actual gating happens
  // server-side during auto-trade evaluation.
  ai_validate_buys?: boolean;
  ai_validate_sells?: boolean;
  // Plan #236 — Technical AI tick workflow. The Brain badge surfaces this
  // alongside the legacy validator so the portfolio listing communicates
  // "any AI active" at a glance.
  technical_ai_enabled?: boolean;
  // True when the user has opted this portfolio out of aggregate totals
  // (summary page, home dashboard tiles). Still shown on the list/detail
  // pages.
  exclude_from_total?: boolean;
  positions?: Position[];
  /** Fully-closed positions (shares == 0) carrying just ticker + the
   *  preserved avg_cost. Used by the FE's per-sell-row G/L FIFO walk
   *  as a fallback when a sell has no buy lots to match (e.g. position
   *  was added manually and then sold to zero). */
  closed_positions?: Array<{ ticker: string; avg_cost: number }>;
}

export async function convertPortfolioToManual(portfolioId: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/portfolios/${portfolioId}/convert-to-manual`, { method: 'POST' });
}

export interface MergePortfolioResult {
  message: string;
  addedPositions: number;
  mergedOverlappingTickers: number;
  addedTransactions: number;
}

export async function mergePortfolio(sourceId: string, targetId: string): Promise<MergePortfolioResult> {
  return request<MergePortfolioResult>(`/portfolios/${sourceId}/merge-into`, {
    method: 'POST', body: JSON.stringify({ targetId }),
  });
}

export interface CongressWatchAlert {
  id: string;
  ticker: string | null;
  transaction: string;
  transactionDate: string | null;
  actionNeeded: 'member_sold_held' | 'member_bought_new' | 'member_added_to_held';
  representative: string;
  createdAt: string;
  seenAt: string | null;
}

export interface CongressWatchTrade {
  ticker: string | null;
  transaction: string;
  transaction_date: string;
  amount: string;
  range: string;
  representative: string;
  party: string;
  chamber: string;
}

export interface CongressWatchData {
  member: { bioGuideId: string; memberName: string; watchEnabled: boolean };
  alerts: CongressWatchAlert[];
  recentTrades: CongressWatchTrade[];
}

export async function getCongressWatch(portfolioId: string): Promise<CongressWatchData> {
  return request<CongressWatchData>(`/portfolios/${portfolioId}/congress-watch`);
}

export async function markCongressWatchSeen(portfolioId: string, alertIds: string[]): Promise<{ updated: number }> {
  return request<{ updated: number }>(`/portfolios/${portfolioId}/congress-watch/mark-seen`, {
    method: 'POST', body: JSON.stringify({ alertIds }),
  });
}

/** Dismiss (delete) watcher alerts. Pass an empty list or undefined to clear
 *  the whole queue for this portfolio. */
export async function dismissCongressWatch(portfolioId: string, alertIds?: string[]): Promise<{ dismissed: number }> {
  return request<{ dismissed: number }>(`/portfolios/${portfolioId}/congress-watch/dismiss`, {
    method: 'POST', body: JSON.stringify({ alertIds: alertIds ?? [] }),
  });
}

export interface Position {
  id: string; portfolio_id: string; ticker: string; shares: number; avg_cost: number;
  purchase_date: string | null; account_label: string; is_cash: boolean;
  created_at: string; updated_at: string;
  current_price?: number; change_pct?: number; company_name?: string; sector?: string;
  long_shares?: number | null; short_shares?: number | null;
  /** Minute-precise ISO timestamp of the oldest remaining lot (FIFO). Used
   *  by the HELD column. Falls back to created_at when no matching
   *  transactions exist. purchase_date is DATE-only so it reads as midnight
   *  UTC — unusable for intraday auto-trades. */
  first_buy_at?: string | null;
  is_auto_trade?: boolean;
  auto_entry_spike_key?: string | null;
  /** AI engine that opened the position, when applicable. 'technical' = the
   *  Technical AI tick engine (is_auto_trade is FALSE for these by design — the
   *  burst exit path keys on is_auto_trade and must NOT also manage them; their
   *  exits run via the technical exit evaluator on ai_workflow='technical'). The
   *  holdings badge uses this so a Technical AI position isn't mislabeled "M". */
  ai_workflow?: string | null;
  /** Plan #239 — when TRUE, the auto-trader's exit evaluator skips this
   *  position. Set by the sell-review's "Convert to manual hold" action;
   *  toggleable off via the holdings UI. */
  manual_hold?: boolean;
}


export interface PortfolioSummary {
  /** Positions valued at current price + cash across all portfolios. Matches
   *  the "Total Value" stat the user sees on the detail page. */
  totalValue: number;
  /** Sum of `portfolios.cash_balance` across the user's portfolios. Returned
   *  separately so callers can display cash on its own without it inflating
   *  Total Return (which stays positions-only). */
  cashBalance: number;
  totalCost: number; totalGainLoss: number; totalGainLossPct: number;
  dayChange: number; dayChangePct: number; portfolioCount: number; positionCount: number;
}

export interface PortfolioSettings {
  enabled?: boolean; publicPages: boolean; csvImportEnabled: boolean;
  congressionalEnabled: boolean; brokerageSyncEnabled: boolean;
  cryptoEnabled: boolean; cryptoWaitlistEnabled: boolean;
  freeMaxPortfolios: number; freeMaxPositions: number; freeMaxTransactions: number;
  proMaxPortfolios: number; proMaxPositions: number; proMaxTransactions: number;
}

export async function getPortfolios(): Promise<Portfolio[]> {
  return request<Portfolio[]>('/portfolios');
}
export async function createPortfolio(data: { name: string; currency?: string }): Promise<Portfolio> {
  return request<Portfolio>('/portfolios', { method: 'POST', body: JSON.stringify(data) });
}
export async function getPortfolio(id: string): Promise<Portfolio> {
  return request<Portfolio>(`/portfolios/${id}`);
}
export async function updatePortfolio(id: string, data: { name?: string; currency?: string; excludeFromTotal?: boolean }): Promise<Portfolio> {
  return request<Portfolio>(`/portfolios/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function deletePortfolio(id: string): Promise<void> {
  await request(`/portfolios/${id}`, { method: 'DELETE' });
}
export async function clonePortfolio(id: string, name?: string): Promise<{ id: string; name: string }> {
  return request<{ id: string; name: string }>(`/portfolios/${id}/clone`, {
    method: 'POST',
    body: JSON.stringify(name ? { name } : {}),
  });
}

// ── Portfolio cash balance ───────────────────────────────────────────────
export async function depositCash(portfolioId: string, amount: number, note?: string): Promise<{ cashBalance: number }> {
  return request<{ cashBalance: number }>(`/portfolios/${portfolioId}/cash/deposit`, {
    method: 'POST', body: JSON.stringify({ amount, note }),
  });
}
export async function withdrawCash(portfolioId: string, amount: number, note?: string): Promise<{ cashBalance: number }> {
  return request<{ cashBalance: number }>(`/portfolios/${portfolioId}/cash/withdraw`, {
    method: 'POST', body: JSON.stringify({ amount, note }),
  });
}

// ── Auto-trade rules + events ────────────────────────────────────────────
export type QualifyReason = 'projected_rvol' | 'recent_rvol' | 'recent_rvol_confirmed' | 'velocity' | 'pressure' | 'gap_and_go' | 'gap_and_go_confirmed' | 'orb_breakout' | 'trend_continuation';

export interface AutoTradeRules {
  enabled: boolean;
  minSeverity: 'elevated' | 'high' | 'extreme';
  minEntryChangePct: number;
  /** Upper bound on entry chg % — gap-chase guard. Rejects entries at
   *  already-extended prices (e.g. +14% pre-market gap). 0 or undefined
   *  disables the cap. Default 15 in the sim; prod inherits the same
   *  default once the DB column is populated. */
  maxEntryChangePct?: number;
  phase: 'regular' | 'premarket' | 'afterhours' | 'both';
  sectorsWhitelist: string[];
  tickerBlacklist: string[];
  /** Empty (default) = no restriction. Non-empty = portfolio only fires
   *  on listed tickers (e.g. a TQQQ-only swing portfolio). */
  tickerAllowlist: string[];
  sizingMode: 'percent' | 'fixed';
  positionPct: number;
  positionFixed: number;
  maxConcurrent: number;
  maxTradesPerDay: number;
  stopLossPct: number;
  takeProfitGivebackPct: number;
  /** Minimum peak unrealized gain (%) before profit-giveback activates.
   *  Stops tiny peaks from firing the exit on meaningless wiggles. */
  minPeakGainForGivebackPct: number;
  /** Minimum loss (%) before the fakeout exit can fire. Symmetric to
   *  volume_cooling's price-recovery gate (Plan #140). 0 = legacy
   *  (fakeout fires on any spike resolution); default 0.5 = positions
   *  barely below entry ride through to recovery or stop_loss instead
   *  of taking the fakeout exit at the bottom of a small dip. */
  minFakeoutLossPct: number;
  volumeCoolingPct: number;
  /** Minutes to block re-entry on the same ticker after any exit. 0 disables. */
  postExitCooldownMin: number;
  /** Minutes the auto-trader holds a new position before volume_cooling or
   *  profit_giveback can fire. stop_loss and session_close still fire. Also
   *  sets the no-buy cutoff at (15:55 − 2×this). 0 disables. */
  minHoldMinutes: number;
  skipIfBelowOneShare: boolean;
  /** Scanner qualification paths this portfolio acts on. Missing 'pressure'
   *  means the portfolio ignores pressure-qualified spikes (plan #124). */
  acceptedQualificationReasons: QualifyReason[];
  /** Block new entries once within this many minutes of the 15:55 ET close.
   *  Default 60. 0 disables. */
  entryCutoffMinutesBeforeClose: number;
  /** Tightened stop-loss % inside the late-session window. Only tightens
   *  (Math.min with stopLossPct). Default 2. Set >= stopLossPct to disable. */
  lateSessionStopLossPct: number;
  /** Broker mirror mode (plan #118). 'paper' or 'live' enables mirroring
   *  auto-trade entries/exits to the connected Alpaca account. 'off' is
   *  local-only (default). Admin sets this per portfolio. */
  brokerMode: 'off' | 'paper' | 'live';
  /** Claude AI pre-trade validator gates (plan #128). When true, the
   *  corresponding side is routed through Claude before submission. Only
   *  has teeth when the admin has enabled the global validator in
   *  /admin/settings. Visible to all VIP users but inert without the
   *  admin key configured. */
  aiValidateBuys: boolean;
  aiValidateSells: boolean;
  /** Tier 1 post-ship review — confidence floor for the AI Validation
   *  gate. NULL → service-side defaults (0.50 buy / 0.45 sell). When
   *  set, an approval below the floor flips to a soft reject and logs
   *  skipReason='claude_low_confidence_*'. Range [0, 1]. */
  aiValidateBuyMinConfidence?: number | null;
  aiValidateSellMinConfidence?: number | null;
  /** Plan #239 — manual review gate. Routes auto-trade fires through
   *  a popup approval queue before execution. Off by default. */
  manualReviewBuys?: boolean;
  manualReviewSells?: boolean;
  /** Minutes a pending review stays open before the sweeper marks it
   *  expired (= same outcome as a Skip). Default 5. */
  manualReviewExpiryMin?: number;
  /** Per-portfolio buy snooze set by the popup's snooze actions.
   *  ISO timestamp; when in the future, buy evaluation skips this
   *  portfolio entirely. Null when no active snooze. */
  manualSnoozeUntil?: string | null;
  /** Plan #138 — volatility-adjusted stop loss. Opt-in per portfolio.
   *  Widens stopLossPct based on recent intrabar ranges (5-bar avg × 1.5,
   *  capped at 12%) so AKAN-shape parabolic runners with 15-20% intrabar
   *  bars don't whipsaw out on a tight static stop. */
  volatilityStopEnabled?: boolean;
  /** Plan #155 — ATR trailing stop. Opt-in per portfolio. ATR(14)
   *  snapshotted at entry, trail = high_water - multiplier × ATR. Only
   *  ratchets up. Severity 90 — fires above session_close but below the
   *  hard stop_loss. */
  atrTrailingEnabled?: boolean;
  /** ATR period for the entry snapshot. Default 14. */
  atrTrailingPeriods?: number;
  /** Multiplier applied to ATR for the trail offset. Default 2.0. */
  atrTrailingMultiplier?: number;
  /** Issue #285 — hold overnight ("let it ride") vs flush at session close. */
  holdOvernight?: boolean;
  /** Plan #236 — Technical AI rebuilt as a 5-min scanner-driven loop.
   *  technicalAiEnabled opts the portfolio in; concurrency comes from
   *  maxConcurrent + maxTradesPerDay (no separate pick count). */
  technicalAiEnabled?: boolean;
  /** Optional override of `acceptedQualificationReasons` for Technical AI
   *  only. NULL = inherit; non-empty = use this set instead. */
  technicalAiQualificationsOverride?: string[] | null;
  /** Issue #191 — categories the portfolio is enrolled in. The auto-trader
   *  fires only when a scanner_event's resolved category is in this list.
   *  Defaults to `['midrange']` for new portfolios. */
  enrolledCategories?: Array<'penny' | 'midrange' | 'quality'>;
  /** Consecutive qualified scans required before this portfolio's auto-trader
   *  opens a position (formerly siteSettings.spikeScan.minQualifiedPasses).
   *  Higher = fewer fakeouts, slower fills. Range 1–5. Default 2. */
  confirmationPasses?: number;
  /** Max auto-trade entries on the same ticker per ET day for this portfolio
   *  (formerly siteSettings.spikeScan.sameTickerDailyCap). 0 disables. Default 2. */
  sameTickerDailyCap?: number;
  /** Intraday-trend gate A — when true, refuse entry while the intraday
   *  VWAP slope (now vs 5 bars ago) is not strictly rising. Catches the
   *  "broad rollover" pattern where the day's bias is down even though
   *  the scanner's RVOL + change_pct are still hot. Default true. */
  requireRisingVwap?: boolean;
  /** Intraday-trend gate B — refuse entry when current price is more than
   *  X% below the session high. 0 disables (codebase convention). Catches
   *  the "stock peaked and faded" pattern (ADVB 2026-05-04: $8 in pre-
   *  market → $6.41 entry → kept falling). Default 5. */
  maxPctOffSessionHigh?: number;
  /** Intraday-trend gate C — refuse entry when the session high was made
   *  more than this many minutes ago. Replaces the older `maxSpikeAgeMin`
   *  (which blunt-blocked any spike past N min regardless of whether it
   *  was still running). A long-running spike still printing new highs
   *  passes; one that stalled does not. 0 disables. Default 10. */
  maxMinutesSinceLastHigh?: number;
  /** Intraday-trend gate D — refuse entry when current price is more than
   *  X% below the pre-market high. Direct catch for gap-up-then-fade.
   *  0 disables. Default 5. */
  maxPctBelowPremarketHigh?: number;
  /** Daily-trend gate E — require current price to be at or above the
   *  daily 20-bar SMA. Filters counter-trend bounces on tickers in macro
   *  downtrend. Opt-in (false default) since it requires a daily-bars
   *  fetch the auto-trader doesn't otherwise need. */
  requireAboveSma20?: boolean;
  /** Daily-regime gate F (issue #374 phase 1) — require the ticker's
   *  PRIOR daily close to be above its 20-day SMA (day-stable verdict,
   *  cannot flip intraday like gate E). Validated on leveraged-index
   *  ETFs: TQQQ live_signal_buy entries averaged -0.31%/trade in
   *  down-regime vs -0.04% in up-regime over 169 sessions. Opt-in. */
  requireUptrendRegime?: boolean;
  /** Intraday higher-timeframe gate G — require the 15-min AND 30-min views
   *  to agree with a long before entry: last COMPLETED bucket above its
   *  session VWAP, and multi-session MACD histogram > 0. Fills the band
   *  between the 5-min entry signal and the daily gates above, which was
   *  previously unchecked. Opt-in. */
  requireHtfTrendAgreement?: boolean;
  /** Per-portfolio override of the admin's global auto-trade entry
   *  window. When true, this portfolio's entries fire all session long
   *  regardless of the configured window. Default false. */
  ignoreEntryWindow?: boolean;
  /** Resistance guard — when true, refuse entries within
   *  `resistanceGuardPct` % of the nearest top-3 resistance level
   *  detected on the 1D chart range. Pure filter — only blocks trades
   *  that would otherwise fire. Default false (opt-in). */
  useResistanceGuard?: boolean;
  /** Tolerance for the resistance guard, in percent. Skip when nearest
   *  resistance sits within this % above current price. Default 0.5;
   *  range typically 0.05–5. */
  resistanceGuardPct?: number;
  /** Minimum peak RVOL (lifetime max for the spike) required for entry.
   *  Blocks "weak qualifier" spikes — those whose lifetime peak rvol
   *  barely cleared the scanner's projected_rvol floor (~4.5) without
   *  ever printing a strong volume bar. The current bar's rvol can be
   *  lower; what matters is whether real volume showed up *somewhere*
   *  during the spike's lifecycle. 0 disables. Default 6. */
  minPeakRvol?: number;
  /** Strict take-profit at entry × (1 + takeProfitPct/100). Null = off. */
  takeProfitPct?: number | null;
}

export interface AutoTradeEvent {
  id: string;
  ticker: string;
  event_type: 'entry' | 'exit' | 'skip';
  exit_reason: string | null;
  spike_key: string | null;
  qty: number | null;
  price: number | null;
  proceeds: number | null;
  pnl: number | null;
  skip_reason: string | null;
  occurred_at: string;
}

export async function getAutoTradeRules(portfolioId: string): Promise<AutoTradeRules> {
  return request<AutoTradeRules>(`/portfolios/${portfolioId}/auto-trade-rules`);
}
export async function saveAutoTradeRules(portfolioId: string, rules: Partial<AutoTradeRules>): Promise<AutoTradeRules> {
  return request<AutoTradeRules>(`/portfolios/${portfolioId}/auto-trade-rules`, {
    method: 'PUT', body: JSON.stringify(rules),
  });
}

export interface LongtermAiRunResult {
  fired: boolean;
  candidateCount: number;
  buys: Array<{ portfolioId: string; ticker: string; side: 'buy'; ok: boolean; reason?: string }>;
  sells: Array<{ portfolioId: string; ticker: string; side: 'sell'; ok: boolean; reason?: string }>;
  skipReason: string | null;
}

/** Plan #237 removed the manual longterm-ai run-now button — the new
 *  service is pure cron-driven. Kept the type so the Technical AI
 *  manual run can reuse the response shape. */

/** Trigger a Technical AI tick for the caller's portfolio.
 *  Server-side gated to regular market hours; rejects with MARKET_CLOSED
 *  outside the 9:30–16:00 ET window. */
export async function runTechnicalAiNow(portfolioId: string): Promise<LongtermAiRunResult> {
  return request<LongtermAiRunResult>(`/portfolios/${portfolioId}/technical-ai/run`, {
    method: 'POST',
  });
}

/** Per-portfolio Technical AI status — drives the settings-card badge and the
 *  pause/resume controls. currentState is the single derived field the UI
 *  switches on; the rest are supporting detail. */
export interface TechnicalAiStatus {
  currentState: 'running' | 'market_closed' | 'paused' | 'disabled_globally' | 'off';
  globallyEnabled: boolean; technicalAiEnabled: boolean; pausedUntil: string | null;
  marketOpen: boolean; isTradingDay: boolean; lastTickAt: string | null;
  lastEntryAt: string | null; lastSellAt: string | null; todayBuys: number; todaySells: number;
}
export async function getTechnicalAiStatus(portfolioId: string): Promise<TechnicalAiStatus> { return request<TechnicalAiStatus>(`/portfolios/${portfolioId}/technical-ai/status`); }
export async function pauseTechnicalAi(portfolioId: string): Promise<TechnicalAiStatus> { return request<TechnicalAiStatus>(`/portfolios/${portfolioId}/technical-ai/pause`, { method: 'POST' }); }
export async function resumeTechnicalAi(portfolioId: string): Promise<TechnicalAiStatus> { return request<TechnicalAiStatus>(`/portfolios/${portfolioId}/technical-ai/resume`, { method: 'POST' }); }

export async function getAutoTradeEvents(portfolioId: string, limit = 50): Promise<AutoTradeEvent[]> {
  return request<AutoTradeEvent[]>(`/portfolios/${portfolioId}/auto-trade-events?limit=${limit}`);
}

// ── User-scoped auto-trade activity log (cross-portfolio) ───────────────────
//
// Powers the AutoTradeActivityLogModal: a paged + filterable list of every
// auto-trade event the user owns, plus a skip-reason histogram for the
// "blocked entries" chart above the table. One endpoint serves both the
// overview "All activity" view and the per-portfolio drill-down (when
// `portfolioId` is supplied).

export interface AutoTradeActivityRow {
  id: string;
  portfolioId: string;
  portfolioName: string;
  ticker: string;
  eventType: 'entry' | 'exit' | 'skip';
  exitReason: string | null;
  skipReason: string | null;
  spikeKey: string | null;
  /** Scanner qualification_reason that fired this row's spike (joined via
   *  spike_key). Null when the row has no spike_key (legacy entries) or
   *  the matching scanner_events row was pruned by retention. */
  qualificationReason: string | null;
  qty: number | null;
  price: number | null;
  proceeds: number | null;
  pnl: number | null;
  occurredAt: string;
}

export interface AutoTradeActivityFilters {
  portfolioId?: string;
  ticker?: string;
  eventType?: 'entry' | 'exit' | 'skip';
  reason?: string;
  /** ISO date or full ISO timestamp — `>= since` */
  since?: string;
  /** ISO date or full ISO timestamp — `< until` (exclusive upper bound) */
  until?: string;
  limit?: number;
  offset?: number;
}

export interface AutoTradeActivityPage {
  items: AutoTradeActivityRow[];
  total: number;
  limit: number;
  offset: number;
  /** Skip-reason histogram for the active filter set (skip events only) —
   *  drives the collapsible "blocked entries" chart above the table. */
  skipReasonCounts: Array<{ reason: string; count: number }>;
}

export async function getMyAutoTradeActivity(filters: AutoTradeActivityFilters = {}): Promise<AutoTradeActivityPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<AutoTradeActivityPage>(`/portfolios/auto-trade-events${suffix}`);
}

export interface AutoTradeStatus {
  scanner: {
    enabled: boolean;
    lastScanAt: string | null;
    status: 'idle' | 'scanning' | 'error';
    lastError: string | null;
    lastActiveCount: number;
  };
  latestSpike: {
    ticker: string;
    name: string | null;
    rvol: number;
    changePct: number;
    severity: string;
    firstDetectedAt: string;
    resolved: boolean;
  } | null;
}
export async function getAutoTradeStatus(portfolioId: string): Promise<AutoTradeStatus> {
  return request<AutoTradeStatus>(`/portfolios/${portfolioId}/auto-trade-status`);
}

// ── Admin: Auto-Trade Simulator V2 — bar-by-bar pipeline playback ────────

export interface SimV2IntradayBar {
  date: string;
  open: number; high: number; low: number; close: number;
  volume: number;
}

export interface SimV2Event {
  ts: string;
  type: 'scan_tick' | 'spike_confirmed' | 'confirmation_reset' | 'entry_skipped' | 'entry_fired' | 'pressure_snapshot' | 'exit_fired' | 'session_close' | 'morning_state_ready' | 'note';
  [key: string]: unknown;
}

export interface SimV2Trade {
  entryTs: string;
  exitTs: string | null;
  shares: number;
  entryPrice: number;
  exitPrice: number | null;
  pnl: number;
  exitReason: string | null;
  holdMinutes: number | null;
}

/** Plan #129: one recorded decision per would-buy/would-sell when the sim
 *  runs in manual AI validation mode. Admin copy-pastes the prompt into
 *  Claude, pastes the response back as an override, then re-runs with
 *  aiOverrides populated. */
export interface AiDecisionRecord {
  /** Stable hash of `barTs|side|TICKER` (+ `|stage` suffix when
   *  kind='stage') — same inputs always produce the same id so
   *  overrides line up across capture + override runs. */
  decisionId: string;
  /** Which validator this decision is for. `signal` = the pattern
   *  validator. `stage` = the timing / move-stage validator (buy-only).
   *  A single buy produces TWO records, one per kind — both overrides
   *  must approve for the entry to fire (Option A stacking). Sell-side
   *  records only produce kind='signal'. Optional for backwards
   *  compatibility with pre-stage-validator sim runs. */
  kind?: 'signal' | 'stage';
  /** The ET bar timestamp the decision fires on. */
  barTs: string;
  side: 'buy' | 'sell';
  ticker: string;
  /** Buy only — which rule-engine path qualified the candidate. */
  qualificationReason?: string;
  /** Sell only — which exit rule fired. stop_loss + session_close are
   *  NEVER recorded (catastrophic + time-boxed exits always fire). */
  exitReason?: string;
  /** Full prompt text so the admin can paste it into an external Claude
   *  chat. Snapshot is frozen at the decision bar. */
  systemPrompt: string;
  userPrompt: string;
  /** Compact snapshot for UI preview without re-rendering the full prompt. */
  snapshotPreview: {
    price: number;
    sessionHigh: number | null;
    sessionLow: number | null;
    changePctFromPrevClose: number | null;
    barCount: number;
  };
}

/** Plan #129: the shape admin pastes back into the validation queue.
 *  Keyed on AiDecisionRecord.decisionId in the aiOverrides map. */
export interface AiDecisionOverride {
  approve: boolean;
  confidence: number;
  reason: string;
}

/** Effective admin-configured global rules applied to a sim run. Mirrors
 *  the backend's QualifyConfig as a flat record so the frontend can render
 *  the values in the copy-log + Configurations preview without redeclaring
 *  every field by name. Optional for back-compat with older runs. */
export type SimV2QualifyCfg = Record<string, number | boolean | string>;

export interface SimV2Result {
  ticker: string;
  barCount: number;
  baselineAvgVolume: number;
  sector: string | null;
  /** Full bar stream the simulator replayed (5-min ET bars). Included so the
   *  client can render the same bars on a chart alongside entry/exit markers. */
  bars: SimV2IntradayBar[];
  /** Admin-configured global thresholds applied to this run (every field
   *  from QualifyConfig). Surfaced separately from rules so the UI can show
   *  what the live scanner would have used. */
  qualifyCfg?: SimV2QualifyCfg;
  /** Per-ticker daily entry cap from siteSettings.spikeScan. */
  sameTickerDailyCap?: number;
  result: {
    events: SimV2Event[];
    trades: SimV2Trade[];
    summary: {
      totalPnl: number;
      firstQualifiedAt: string | null;
      firstConfirmedAt: string | null;
      entryAt: string | null;
      exitAt: string | null;
      timeFromFirstQualifiedToEntryMin: number | null;
      finalCash: number;
    };
    config: {
      minQualifiedPasses: number;
      includePremarket: boolean;
      ticker: string;
      /** Effective qualifyCfg thresholds for the run — surfaced so the
       *  scan_tick log can render values with their pass/fail thresholds
       *  inline. Optional for back-compat with older sim runs. */
      thresholds?: {
        projectedRvolMin: number;
        recentRvolMin: number;
        changePctVelocityMin: number;
        changePctAbsMin: number;
        dollarVolumeMin: number;
        intervalDollarVolumeMin: number;
      };
    };
    /** Populated only when aiValidationMode === 'manual'. One entry per
     *  decision point the rule engine reached. stop_loss + session_close
     *  are never recorded — they always fire. */
    aiDecisions?: AiDecisionRecord[];
  };
}

export interface SimV2Input {
  ticker?: string;
  date?: string;
  syntheticBars?: SimV2IntradayBar[];
  baselineAvgVolume?: number;
  rules: AutoTradeRules;
  portfolio: { cashBalance: number };
  minQualifiedPasses?: number;
  /** Sim-input override for the same-ticker daily cap. Resolution in the
   *  admin sim route: this body field → `body.rules.sameTickerDailyCap`
   *  → 2 (the schema default). */
  sameTickerDailyCap?: number;
  includePremarket?: boolean;
  /** When `includePremarket` is true: gates whether ENTRIES can fire on
   *  pre-market bars. False (default) = scan + render pre-market bars but
   *  hold entries until 9:30 ET. True = full pre-market trading allowed. */
  allowPremarketEntry?: boolean;
  sector?: string | null;
  requirePressureConfirm?: boolean;
  /** Plan #129: 'off' (default) = normal sim. 'manual' = record full
   *  prompts at every would-validate decision so admin can copy-paste
   *  into an external Claude chat. */
  aiValidationMode?: 'off' | 'manual';
  /** Plan #129: pasted-back overrides keyed on AiDecisionRecord.decisionId.
   *  Only populated on the second ("apply") run. */
  aiOverrides?: Record<string, AiDecisionOverride>;
  /** Plan #131: previous trading day's close. Enables gap_and_go. Null
   *  leaves the path silent; orb_breakout is independent. */
  prevClose?: number | null;
  /** Plan #156: sim-only A/B toggle. When true, exit evaluation + peak
   *  ratchets + spike-state peaks skip on hot-tick mid-bar snapshots so
   *  the run reflects strict closed-bar-only exits. Default false. Prod
   *  auto-trader is unaffected. */
  closedBarExitsOnly?: boolean;
}

export async function simulatorV2Run(input: SimV2Input): Promise<SimV2Result> {
  return request<SimV2Result>('/admin/simulator/v2/run', { method: 'POST', body: JSON.stringify(input) });
}

export interface SimV2TickerRow { ticker: string; latest_bar: string | null; bar_count: number }

export async function simulatorV2Tickers(): Promise<SimV2TickerRow[]> {
  return request<SimV2TickerRow[]>('/admin/simulator/v2/tickers');
}

/** Plan #131: resolve prev-trading-day close for the sim's Prev close
 *  autofill. Returns null for prevClose when the cache doesn't cover
 *  the day before `date` — the UI leaves the field blank silently. */
export interface SimV2PrevCloseResult {
  ticker: string;
  date: string;
  prevDate: string | null;
  prevClose: number | null;
}
export async function simulatorV2PrevClose(ticker: string, date: string): Promise<SimV2PrevCloseResult> {
  const qs = new URLSearchParams({ ticker, date }).toString();
  return request<SimV2PrevCloseResult>(`/admin/simulator/v2/prev-close?${qs}`);
}

// ── Provider log (admin live tail) ───────────────────────────────────────

export type ProviderLogName = 'tiingo' | 'yahoo' | 'finnhub' | 'fmp' | 'twelvedata';
export interface ProviderLogEntry {
  id: number;
  timestamp: number;
  provider: ProviderLogName;
  method: string;
  path: string;
  status: number;
  durationMs: number;
  callerIp?: string;
  callerRoute?: string;
  callerUserId?: string;
  callerUserAgent?: string;
  error?: string;
}
export interface ProviderLogStats {
  total: number;
  maxEntries: number;
  paused: boolean;
  last1m: number;
  last5m: number;
  last1h: number;
  byProvider: Record<string, number>;
}
export interface ProviderLogResponse {
  entries: ProviderLogEntry[];
  stats: ProviderLogStats;
}
export async function getProviderLog(filters: {
  sinceId?: number; limit?: number; provider?: string; ip?: string; path?: string;
} = {}): Promise<ProviderLogResponse> {
  const qs = new URLSearchParams();
  if (filters.sinceId) qs.set('sinceId', String(filters.sinceId));
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.provider) qs.set('provider', filters.provider);
  if (filters.ip) qs.set('ip', filters.ip);
  if (filters.path) qs.set('path', filters.path);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<ProviderLogResponse>(`/admin/provider-log${suffix}`);
}
export async function clearProviderLog(): Promise<void> {
  await request('/admin/provider-log', { method: 'DELETE' });
}
export async function pauseProviderLog(): Promise<void> {
  await request('/admin/provider-log/pause', { method: 'POST' });
}
export async function resumeProviderLog(): Promise<void> {
  await request('/admin/provider-log/resume', { method: 'POST' });
}

// ── Rate-limiter stats (plan #132 telemetry) ────────────────────────────────

export interface RateLimiterCallerStat {
  tag: string;
  tier: 'page' | 'critical' | 'background';
  admitted: number;
  waitMsTotal: number;
  ratePerMin: number;
  waitMsRecent: number;
}
export interface RateLimiterBucket {
  ts: number;
  pageAdmits: number;
  criticalAdmits: number;
  backgroundAdmits: number;
  pageWaitMs: number;
  criticalWaitMs: number;
  backgroundWaitMs: number;
}
export interface RateLimiterProviderStats {
  totalPerMinute: number;
  reservedForCritical: number;
  /** Plan #275 — slots reserved for the page tier. */
  reservedForPage: number;
  totalAdmitted: number;
  pageAdmitted: number;
  criticalAdmitted: number;
  backgroundAdmitted: number;
  pageWaitMsTotal: number;
  criticalWaitMsTotal: number;
  backgroundWaitMsTotal: number;
  pageAdmittedRecent: number;
  criticalAdmittedRecent: number;
  backgroundAdmittedRecent: number;
  pageWaitMsRecent: number;
  criticalWaitMsRecent: number;
  backgroundWaitMsRecent: number;
  pendingPage: number;
  pendingCritical: number;
  pendingBackground: number;
  timeSeries: RateLimiterBucket[];
  callers: RateLimiterCallerStat[];
}
export type RateLimiterStats = Record<string, RateLimiterProviderStats>;

export async function adminGetRateLimiterStats(): Promise<RateLimiterStats> {
  return request<RateLimiterStats>('/admin/rate-limiter/stats');
}

export async function adminResetRateLimiterStats(): Promise<{ providers: string[] }> {
  return request<{ providers: string[] }>('/admin/rate-limiter/reset', { method: 'POST' });
}

// ── Price-sync anomalies (plan #133) ────────────────────────────────────────

export interface PriceSyncAnomaly {
  id: string;
  ticker: string;
  detected_at: string;
  anomaly_type: 'negative_price' | 'negative_volume' | 'price_spike' | 'change_pct_mismatch' | string;
  reason: string;
  prior_value: Record<string, unknown> | null;
  attempted_value: Record<string, unknown> | null;
  provider: string | null;
  action: 'rejected' | 'logged_only';
  count: number;
}
export interface PriceSyncAnomaliesResponse {
  rows: PriceSyncAnomaly[];
  total: number;
}
export async function getPriceSyncAnomalies(filters: {
  ticker?: string; action?: 'rejected' | 'logged_only'; type?: string; limit?: number;
} = {}): Promise<PriceSyncAnomaliesResponse> {
  const qs = new URLSearchParams();
  if (filters.ticker) qs.set('ticker', filters.ticker);
  if (filters.action) qs.set('action', filters.action);
  if (filters.type) qs.set('type', filters.type);
  if (filters.limit) qs.set('limit', String(filters.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<PriceSyncAnomaliesResponse>(`/admin/price-sync-anomalies${suffix}`);
}
export async function clearPriceSyncAnomalies(): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(`/admin/price-sync-anomalies`, { method: 'DELETE' });
}

// ── Debug Providers (data-sync inspector) ───────────────────────────────────

export interface SyncTypeCapability {
  id: string;
  label: string;
  description: string;
  providers: string[];
  readOnly: boolean;
  category: 'quotes' | 'ohlcv' | 'fundamentals' | 'corporate-actions' | 'news' | 'provider-raw';
  typicalSizeKB: number;
}
export async function adminGetDataSyncCapabilities(): Promise<{ syncTypes: SyncTypeCapability[] }> {
  return request<{ syncTypes: SyncTypeCapability[] }>('/admin/data-sync/capabilities');
}

export interface InspectPerProvider {
  provider: string;
  ok: boolean;
  ms: number;
  sizeBytes: number;
  truncated: boolean;
  payload: unknown;
  error: string | null;
}
export interface InspectResponse {
  ticker: string;
  syncType: string;
  primary: string;
  mode: 'preview' | 'push';
  totalMs: number;
  dbBefore: Record<string, unknown> | null;
  perProvider: InspectPerProvider[];
  merged: unknown;
  mergeTrace: Record<string, { value: unknown; from: string }>;
  dbDelta: Array<{ column: string; before: unknown; after: unknown }>;
  dbAfter: Record<string, unknown> | null;
  persisted: boolean;
}
export async function adminDataSyncInspect(payload: {
  ticker: string;
  syncType: string;
  providers: string[];
  primary: string;
  mode: 'preview' | 'push';
  /** Plan #138 — forwarded to sync def's fetch. Only ohlcv-intraday
   *  uses it (5min vs 1min bar toggle). Ignored for other sync types. */
  resolution?: '1min' | '5min';
}): Promise<InspectResponse> {
  return request<InspectResponse>('/admin/data-sync/inspect', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getPortfolioSummary(): Promise<PortfolioSummary> {
  return request<PortfolioSummary>('/portfolios/summary');
}

// ── Auto-trade portfolio comparison ────────────────────────────────────────

/** Full per-portfolio config + perf snapshot for the side-by-side compare
 *  modal. Field naming mirrors `portfolio_auto_trade_rules` columns
 *  (camelCased) so the UI can render any new field that lands by just
 *  adding a row to its descriptor table. */
export interface AutoTradeCompareRules {
  enabled: boolean;
  minSeverity: 'elevated' | 'high' | 'extreme';
  minEntryChangePct: number;
  maxEntryChangePct: number;
  phase: 'regular' | 'premarket' | 'afterhours' | 'both';
  sectorsWhitelist: string[];
  tickerBlacklist: string[];
  /** Empty (default) = no restriction. Non-empty = portfolio only fires
   *  on listed tickers (e.g. TQQQ-only swing portfolio). */
  tickerAllowlist: string[];
  acceptedQualificationReasons: string[];
  acceptsRussell1000Signals: boolean;
  enrolledCategories: string[];
  sizingMode: 'percent' | 'fixed';
  positionPct: number;
  positionFixed: number;
  maxConcurrent: number;
  maxTradesPerDay: number;
  stopLossPct: number;
  takeProfitGivebackPct: number;
  minPeakGainForGivebackPct: number;
  minFakeoutLossPct: number;
  volumeCoolingPct: number;
  postExitCooldownMin: number;
  minHoldMinutes: number;
  entryCutoffMinutesBeforeClose: number;
  lateSessionStopLossPct: number;
  volatilityStopEnabled: boolean;
  atrTrailingEnabled: boolean;
  atrTrailingPeriods: number;
  atrTrailingMultiplier: number;
  aiValidateBuys: boolean;
  aiValidateSells: boolean;
  aiValidateBuyMinConfidence: number | null;
  aiValidateSellMinConfidence: number | null;
  holdOvernight: boolean;
  technicalAiEnabled: boolean;
  technicalAiQualificationsOverride: string[] | null;
  brokerMode: 'off' | 'paper' | 'live';
  skipIfBelowOneShare: boolean;
  confirmationPasses: number;
  sameTickerDailyCap: number;
  requireRisingVwap: boolean;
  maxPctOffSessionHigh: number;
  maxMinutesSinceLastHigh: number;
  maxPctBelowPremarketHigh: number;
  requireAboveSma20: boolean;
  requireUptrendRegime: boolean;
  requireHtfTrendAgreement: boolean;
  ignoreEntryWindow?: boolean;
  minPeakRvol: number;
}

export interface AutoTradeComparePerf {
  totalValue: number;
  totalPnl: number;
  totalPnlPct: number;
  realizedPnl: number;
  upl: number;
  /** Sum of pnl on auto_trade_events.exit rows whose occurred_at falls
   *  on today's ET calendar date. UPL day-change isn't tracked. */
  dailyRealizedPnl: number;
  openCount: number;
  marketValue: number;
  costBasis: number;
  entries: number;
  exits: number;
}

export interface AutoTradeComparePortfolio {
  id: string;
  name: string;
  cash: number;
  updatedAt: string;
  perf: AutoTradeComparePerf;
  rules: AutoTradeCompareRules;
}

export async function getAutoTradeComparison(): Promise<{ portfolios: AutoTradeComparePortfolio[] }> {
  return request<{ portfolios: AutoTradeComparePortfolio[] }>('/portfolios/auto-trade-comparison');
}

// ── Pre-open order queue ────────────────────────────────────────────────────

export type OpenOrderSizingMode = 'shares' | 'dollar';
export type OpenOrderExitMode = 'buy_hold' | 'auto_trade';
export type OpenOrderStatus = 'pending' | 'filled' | 'cancelled' | 'failed';

export interface OpenOrderRow {
  id: string;
  ticker: string;
  sizingMode: OpenOrderSizingMode;
  qtyOrDollar: number;
  exitMode: OpenOrderExitMode;
  scheduledDate: string;          // YYYY-MM-DD (ET)
  status: OpenOrderStatus;
  fillPrice: number | null;
  filledShares: number | null;
  executedAt: string | null;      // ISO
  errorReason: string | null;
  createdAt: string;              // ISO
}

export interface OpenOrderListPayload {
  orders: OpenOrderRow[];
  /** True when portfolio_auto_trade_rules.enabled is true. The frontend
   *  hides the auto_trade exit-mode toggle when this is false. */
  autoTradeEnabled: boolean;
}

export async function getPortfolioOpenOrders(portfolioId: string): Promise<OpenOrderListPayload> {
  return request<OpenOrderListPayload>(`/portfolios/${portfolioId}/open-orders`);
}

export async function createPortfolioOpenOrder(portfolioId: string, body: {
  ticker: string;
  sizingMode: OpenOrderSizingMode;
  qtyOrDollar: number;
  exitMode: OpenOrderExitMode;
  scheduledDate?: string; // optional override; server defaults to today (ET)
}): Promise<{ id: string }> {
  return request<{ id: string }>(`/portfolios/${portfolioId}/open-orders`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function cancelPortfolioOpenOrder(portfolioId: string, orderId: string): Promise<{ cancelled: boolean }> {
  return request<{ cancelled: boolean }>(`/portfolios/${portfolioId}/open-orders/${orderId}`, {
    method: 'DELETE',
  });
}

// ── Cross-portfolio transaction log ─────────────────────────────────────────

export interface MyTransactionRow {
  id: string;
  portfolioId: string;
  portfolioName: string;
  ticker: string;
  action: 'buy' | 'sell';
  shares: number;
  price: number;
  commission: number;
  transactedAt: string;
  /** True when the transaction was fired by the auto-trader (matched
   *  against an auto_trade_events entry/exit row on the same portfolio +
   *  ticker within ±5 seconds). False = manual entry. */
  isAuto: boolean;
  /** Realized P/L in dollars at the moment of the SELL — sell price
   *  minus the weighted-avg cost basis of all prior buys for the same
   *  (portfolio, ticker), times shares. NULL on BUY rows and on SELL
   *  rows with no prior BUY (data inconsistency). */
  realizedPnl: number | null;
  /** Realized P/L as a % of cost basis. NULL on the same rows that
   *  realizedPnl is null. */
  realizedPnlPct: number | null;
  /** Scanner setup that triggered this trade — `gap_and_go`,
   *  `orb_breakout`, `trend_continuation`, `gonogo_trend`,
   *  `confluence_stack`, `projected_rvol`, `recent_rvol`, `velocity`,
   *  or `pressure`. NULL for manual entries and for auto-trades that
   *  pre-date the spike_key audit chain. */
  setupTag: string | null;
  /** Exit trigger for a SELL — `stop_loss`, `profit_giveback`,
   *  `volume_cooling`, `fakeout`, `take_profit`, `session_close`, etc. —
   *  i.e. WHY the position was closed. NULL on BUY rows and on manual sells.
   *  (setupTag is the ENTRY qualification; this is the EXIT reason.) */
  exitReason: string | null;
}
export interface MyTransactionsPage {
  items: MyTransactionRow[];
  total: number;
  limit: number;
  offset: number;
  /** Echoes the date filter the server applied — non-null when a single
   *  ET day is in scope (default: today). Null when allTime is true. */
  date: string | null;
  allTime: boolean;
}
export interface MyTransactionsFilters {
  limit?: number;
  offset?: number;
  action?: 'buy' | 'sell';
  ticker?: string;
  portfolioId?: string;
  /** YYYY-MM-DD in ET. When omitted and `allTime !== true`, the server
   *  defaults to today's ET date. */
  date?: string;
  /** When true, skips the date filter entirely. */
  allTime?: boolean;
}
function txFiltersToQs(filters: MyTransactionsFilters, opts: { skipPagination?: boolean } = {}): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    if (opts.skipPagination && (k === 'limit' || k === 'offset')) continue;
    qs.set(k, typeof v === 'boolean' ? (v ? '1' : '0') : String(v));
  }
  return qs.toString() ? `?${qs.toString()}` : '';
}
export async function getMyAllTransactions(filters: MyTransactionsFilters = {}): Promise<MyTransactionsPage> {
  return request<MyTransactionsPage>(`/portfolios/transactions/all${txFiltersToQs(filters)}`);
}

// ── Trade strategy insight (per-row optimizer + AI analysis) ───────────────

export interface TradeStrategyInsightRecommendation {
  /** Sparse map of fields the recommendation would change. Pass through to
   *  PUT /portfolios/:id/auto-trade-rules to apply. */
  ruleChange: Record<string, number>;
  newPnl: number;
  newPnlPct: number;
  /** newPnl - actualPnl (only meaningful when the row is a closed SELL).
   *  Server returns 0 when the row has no actual exit yet. */
  deltaPnl: number;
  exitReason: 'stop_loss' | 'profit_giveback' | 'session_close' | 'still_open';
  exitPrice: number;
  exitTs: string;
  /** Pre-formatted human-readable summary — renders in the recommendation row. */
  explanation: string;
}

export interface TradeStrategyInsight {
  transaction: {
    id: string; portfolioId: string; portfolioName: string;
    ticker: string; action: 'buy' | 'sell';
    shares: number; price: number; transactedAt: string;
    /** Scanner setup that triggered the entry — same taxonomy as
     *  setup-performance. NULL when manual or pre-spike-key. */
    setupTag: string | null;
  };
  /** Subset of the portfolio's current auto-trade rules — the fields the
   *  optimizer can perturb plus a few that explain the trade. */
  rules: Record<string, number | string | boolean | null>;
  tradeContext: {
    entryPrice: number; entryTs: string;
    exitPrice: number | null; exitTs: string | null;
    actualPnl: number | null;
    peakPrice: number; troughPrice: number;
    postExitHigh: number | null; postExitLow: number | null;
    barsAvailable: number;
  };
  recommendations: TradeStrategyInsightRecommendation[];
  /** Pre-built Claude prompt — shown in the modal so the user can copy or
   *  edit before paying for a Claude call via the AI endpoint. */
  aiPrompt: string;
}

export async function getTradeStrategyInsight(txId: string): Promise<TradeStrategyInsight> {
  return request<TradeStrategyInsight>(`/portfolios/transactions/${txId}/strategy-insight`);
}

export async function runTradeStrategyAi(txId: string, prompt: string): Promise<{ response: string; usage: { input_tokens?: number; output_tokens?: number } | null }> {
  return request(`/portfolios/transactions/${txId}/strategy-insight/ai`, {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  });
}

/** Per-setup performance row. One row per scanner qualification_reason
 *  (gap_and_go, orb_breakout, …) plus a `manual` bucket for hand-entered
 *  trades. R-multiple uses the portfolio's CURRENT stop_loss_pct as the
 *  denominator — see route doc for the approximation caveat. */
export interface SetupPerformanceRow {
  setup: string;
  trades: number;
  wins: number;
  losses: number;
  winRate: number;
  avgR: number;
  totalPnl: number;
  bestR: number;
  worstR: number;
  avgHoldMin: number;
  /** True when trades < 20; UI should de-emphasize the win-rate. */
  lowSample: boolean;
}

export interface SetupPerformancePayload {
  setups: SetupPerformanceRow[];
  stopLossPctUsed: number;
  totalRoundtrips: number;
}

export async function getPortfolioSetupPerformance(portfolioId: string): Promise<SetupPerformancePayload> {
  return request<SetupPerformancePayload>(`/portfolios/${portfolioId}/setup-performance`);
}

/** Apply a single rule-change recommendation. Wraps PUT
 *  /portfolios/:id/auto-trade-rules — the server merges the patch onto
 *  DEFAULT_AUTO_TRADE_RULES, so we only need to send the changed fields
 *  along with portfolioId. */
export async function applyTradeRuleChange(
  portfolioId: string,
  ruleChange: Record<string, number>,
): Promise<unknown> {
  return request(`/portfolios/${portfolioId}/auto-trade-rules`, {
    method: 'PUT',
    body: JSON.stringify({ portfolioId, ...ruleChange }),
  });
}

/** Trigger a CSV download of the transaction log with the given filters.
 *  Bypasses the JSON `request` helper because we need the raw response
 *  body and the browser's download machinery. Pagination params are
 *  stripped — the CSV always exports the full filter match (capped to
 *  5000 rows by the server). */
export async function downloadMyTransactionsCsv(filters: MyTransactionsFilters = {}): Promise<void> {
  const response = await fetch(`${BASE_URL}/portfolios/transactions/all.csv${txFiltersToQs(filters, { skipPagination: true })}`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Export failed: ${response.status}`);
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] ?? `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
export async function getPortfolioPerformance(id: string, range?: string): Promise<{ date: string; value: number; costBasis: number }[]> {
  return request(`/portfolios/${id}/performance${range ? `?range=${range}` : ''}`);
}
export interface PortfolioValueHistory {
  /** Total-value points, ascending by date. `value` = cash + Σ(shares × close) on that day. */
  series: { date: string; value: number }[];
  /** Value at the first point in the requested window (NOT necessarily inception). */
  startValue: number;
  /** Value at the latest point — matches the portfolio's Total Value tile. */
  currentValue: number;
  startDate: string | null;
}
/** True transaction-replay value-growth curve for the portfolio detail chart.
 *  range: 'all' (default) | '1M' | '3M' | '1Y'. */
export async function getPortfolioValueHistory(id: string, range?: string): Promise<PortfolioValueHistory> {
  return request(`/portfolios/${id}/value-history${range ? `?range=${range}` : ''}`);
}
export async function addPosition(portfolioId: string, data: {
  ticker: string; shares: number; avgCost: number; purchaseDate?: string; accountLabel?: string; isCash?: boolean; isAutoTrade?: boolean;
}): Promise<Position> {
  return request<Position>(`/portfolios/${portfolioId}/positions`, { method: 'POST', body: JSON.stringify(data) });
}
export async function updatePosition(portfolioId: string, positionId: string, data: Partial<{
  ticker: string; shares: number; avgCost: number; purchaseDate: string; accountLabel: string; isCash: boolean; isAutoTrade: boolean; manualHold: boolean;
}>): Promise<Position> {
  return request<Position>(`/portfolios/${portfolioId}/positions/${positionId}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function deletePosition(portfolioId: string, positionId: string): Promise<void> {
  await request(`/portfolios/${portfolioId}/positions/${positionId}`, { method: 'DELETE' });
}
export async function deleteAllPositions(portfolioId: string): Promise<void> {
  await request(`/portfolios/${portfolioId}/positions`, { method: 'DELETE' });
}
export async function deleteAllTransactions(portfolioId: string): Promise<void> {
  await request(`/portfolios/${portfolioId}/transactions`, { method: 'DELETE' });
}
export interface Transaction {
  id: string; portfolio_id: string; ticker: string;
  /** Stock trades use 'buy' / 'sell'. Cash movements (ticker='CASH') use
   *  'deposit' / 'withdrawal' — the FE keeps these in the same transaction
   *  list so the audit trail and tile math (deposits vs realized P/L) can
   *  read from one source. */
  action: 'buy' | 'sell' | 'deposit' | 'withdrawal';
  shares: number; price: number; commission: number; transacted_at: string; created_at: string;
}

export async function importCSV(file: File, portfolioId?: string, mode?: string): Promise<{ rows?: { ticker: string; shares: number; avg_cost: number }[]; imported?: number; errors: string[] }> {
  const form = new FormData();
  form.append('file', file);
  if (portfolioId) form.append('portfolioId', portfolioId);
  if (mode) form.append('mode', mode);
  const headers: Record<string, string> = {};
  if (_accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;
  const response = await fetch(`${typeof window !== 'undefined' ? '/api' : (process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4000/api')}/portfolios/import/csv`, {
    method: 'POST', body: form, headers, credentials: 'include',
  });
  if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.error?.message || 'Import failed'); }
  const json = await response.json();
  return json.data;
}

export async function importTransactionsCSV(file: File, portfolioId?: string): Promise<{ rows?: { ticker: string; action: string; shares: number; price: number; commission: number; date: string }[]; imported?: number; errors: string[] }> {
  const form = new FormData();
  form.append('file', file);
  if (portfolioId) form.append('portfolioId', portfolioId);
  const headers: Record<string, string> = {};
  if (_accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;
  const response = await fetch(`${typeof window !== 'undefined' ? '/api' : (process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4000/api')}/portfolios/import/transactions-csv`, {
    method: 'POST', body: form, headers, credentials: 'include',
  });
  if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.error?.message || 'Import failed'); }
  const json = await response.json();
  return json.data;
}

export async function exportPortfolioCSV(portfolioId: string): Promise<void> {
  const headers: Record<string, string> = { Accept: 'text/csv' };
  if (_accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;
  const res = await fetch(`${typeof window !== 'undefined' ? '/api' : BASE_URL}/portfolios/${portfolioId}/export`, { headers, credentials: 'include' });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'portfolio.csv'; a.click();
  URL.revokeObjectURL(url);
}

export async function exportTransactionsCSV(portfolioId: string): Promise<void> {
  const headers: Record<string, string> = { Accept: 'text/csv' };
  if (_accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;
  const res = await fetch(`${typeof window !== 'undefined' ? '/api' : BASE_URL}/portfolios/${portfolioId}/export-transactions`, { headers, credentials: 'include' });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'transactions.csv'; a.click();
  URL.revokeObjectURL(url);
}

export async function getTransactions(portfolioId: string): Promise<Transaction[]> {
  return request<Transaction[]>(`/portfolios/${portfolioId}/transactions`);
}

// ── Auto-trade watch status (user-facing) ────────────────────────────────────
// Live price-watch state for the user's open auto-trade positions. Mirrors
// the admin state object but scopes activeSymbols/history/current to tickers
// the caller actually holds.
export interface AutoTradeWatchStatus {
  status: 'idle' | 'watching' | 'error' | 'paused';
  enabled: boolean;
  intervalMinutes: number;
  lastCheckAt: string | null;
  nextCheckAt: string | null;
  lastDurationMs: number | null;
  lastError: string | null;
  activeSymbols: string[];
  current: Record<string, { price: number; changePct: number; priceUpdatedAt: string | null }>;
  history: Record<string, Array<{ ts: number; price: number }>>;
}

export async function getAutoTradeWatchStatus(): Promise<AutoTradeWatchStatus> {
  return request<AutoTradeWatchStatus>('/portfolios/auto-trade/watch-status');
}

// ── Auto-trade per-ticker timeline ──────────────────────────────────────────
// Event variants used by the per-ticker investigation modal. Named
// AutoTradeTimelineEvent to avoid colliding with the stock-chart
// TimelineEvent exported above.
export interface AutoTradePressure {
  /** Combined buy % (0..100), average of CLV and BVC when both present. */
  buyPct: number;
  /** CLV-only buy %. */
  clvBuyPct: number;
  /** BVC-only buy %, or null when window was insufficient. */
  bvcBuyPct: number | null;
  agreement: 'strong_buy' | 'buy' | 'neutral' | 'sell' | 'strong_sell' | 'conflicting';
  confidence: 'high' | 'moderate' | 'low';
}

export type AutoTradeTimelineEvent =
  | { ts: string; type: 'spike_detected'; rvol: number; changePct: number; severity: 'elevated' | 'high' | 'extreme'; price: number; phase: 'regular' | 'premarket' | 'afterhours' }
  | { ts: string; type: 'spike_peak'; rvol: number; changePct: number }
  | { ts: string; type: 'spike_confirmed'; passes: number; thresholdMinutes: number; synthesized: true }
  | { ts: string; type: 'entry'; shares: number; price: number; total: number; spikeKey: string | null }
  | { ts: string; type: 'price_observation'; price: number; volume: number; rvol: number | null; pressure: AutoTradePressure | null }
  | { ts: string; type: 'exit'; shares: number; price: number; reason: string; pnl: number; total: number }
  | { ts: string; type: 'spike_resolved'; resolution: 'cooled' | 'fakeout' };

export interface AutoTradeTimeline {
  ticker: string;
  cycle: 'latest' | 'all';
  name: string | null;
  currentPrice: number | null;
  currentChangePct: number | null;
  scanIntervalMin: number;
  minQualifiedPasses: number;
  timeline: AutoTradeTimelineEvent[];
}

export async function getAutoTradeTimeline(portfolioId: string, ticker: string, cycle: 'latest' | 'all' = 'latest'): Promise<AutoTradeTimeline> {
  const qs = new URLSearchParams({ ticker, cycle }).toString();
  return request<AutoTradeTimeline>(`/portfolios/${portfolioId}/auto-trade-timeline?${qs}`);
}
export async function addTransaction(portfolioId: string, data: {
  ticker: string; action: string; shares: number; price: number; commission?: number; transactedAt?: string;
}): Promise<Transaction> {
  return request<Transaction>(`/portfolios/${portfolioId}/transactions`, { method: 'POST', body: JSON.stringify(data) });
}
export async function deleteTransaction(portfolioId: string, txnId: string): Promise<void> {
  await request(`/portfolios/${portfolioId}/transactions/${txnId}`, { method: 'DELETE' });
}

// ── Congressional ────────────────────────────────────────────────────────────

export interface CongressMember {
  id: string; name: string; party: string; state: string; chamber: string;
  last_disclosure_date: string | null; ticker_count?: number;
  disclosed_tickers?: string[];
  photo_url?: string;
}

export async function getCongressMembers(params?: { search?: string; party?: string; chamber?: string }): Promise<CongressMember[]> {
  const sp = new URLSearchParams();
  if (params?.search) sp.set('search', params.search);
  if (params?.party) sp.set('party', params.party);
  if (params?.chamber) sp.set('chamber', params.chamber);
  const qs = sp.toString();
  return request<CongressMember[]>(`/congressional/members${qs ? `?${qs}` : ''}`);
}

export async function getCongressMember(id: string): Promise<CongressMember> {
  return request<CongressMember>(`/congressional/members/${id}`);
}

export async function cloneCongressionalPortfolio(data: {
  memberId?: string;
  name?: string;
  tickers?: string[];
  allocations?: { ticker: string; shares: number; avgCost: number }[];
}): Promise<Portfolio> {
  return request<Portfolio>('/congressional/clone', { method: 'POST', body: JSON.stringify(data) });
}

// ── Crypto Waitlist ──────────────────────────────────────────────────────────

// ── Brokerage Sync ───────────────────────────────────────────────────────────

export interface BrokerageConnection {
  id: string; provider: string; institution_name: string; status: string;
  last_synced_at: string | null; last_error: string | null; portfolio_id: string | null; created_at: string;
}

export async function getBrokerageConnections(): Promise<BrokerageConnection[]> {
  return request<BrokerageConnection[]>('/brokerage/connections');
}
export async function connectBrokerage(data: { provider: string; institutionName?: string }): Promise<{ connection: BrokerageConnection; authUrl: string | null; message: string }> {
  return request('/brokerage/connect', { method: 'POST', body: JSON.stringify(data) });
}
export async function syncBrokerage(connectionId: string): Promise<{ synced: number; portfolioId?: string; message?: string }> {
  return request(`/brokerage/sync/${connectionId}`, { method: 'POST' });
}
export async function disconnectBrokerage(connectionId: string): Promise<void> {
  await request(`/brokerage/connections/${connectionId}`, { method: 'DELETE' });
}

// ── Auto-generate Portfolio ──────────────────────────────────────────────────

export interface GeneratedPosition {
  ticker: string; name: string; sector: string; weight: number;
  suggestedShares: number | null; currentPrice: number; rationale: string; score: number;
}

export interface RiskPreview {
  score: number; level: string; beta: number;
  concentration: number; topSectorPct: number;
  sectorWeights: Record<string, number>;
}

export interface GenerateResult {
  style: string; themes: string[]; amount: number | null;
  stockCount: number;
  positions: GeneratedPosition[]; disclaimer: string;
  riskPreview: RiskPreview;
}

export async function generatePortfolio(data: {
  style: 'conservative' | 'balanced' | 'aggressive'; themes: string[]; amount: number | null;
  stockCount?: number;
}): Promise<GenerateResult> {
  return request<GenerateResult>('/portfolios/generate', { method: 'POST', body: JSON.stringify(data) });
}

export async function saveGeneratedPortfolio(data: {
  name: string; style: string; themes: string[]; amount: number | null;
  positions: { ticker: string; shares: number; avgCost: number; weight: number }[];
}): Promise<Portfolio> {
  return request<Portfolio>('/portfolios/generate/save', { method: 'POST', body: JSON.stringify(data) });
}

export async function submitCryptoWaitlist(email: string): Promise<{ message: string }> {
  return request('/crypto/waitlist', { method: 'POST', body: JSON.stringify({ email }) });
}

export async function getPublicPortfolioSettings(): Promise<PortfolioSettings> {
  return request<PortfolioSettings>('/admin/public/portfolio');
}
export async function adminGetPortfolioSettings(): Promise<PortfolioSettings> {
  return request<PortfolioSettings>('/admin/settings/portfolio');
}
export async function adminSavePortfolioSettings(data: Partial<PortfolioSettings>): Promise<void> {
  await request('/admin/settings/portfolio', { method: 'POST', body: JSON.stringify(data) });
}

export async function getPublicBrandingSettings(): Promise<{ siteName: string; logoUrl: string | null; tagline: string; defaultTheme?: string }> {
  return request('/admin/public/branding');
}

export type TickerTapeSource = 'topMovers' | 'pinnedWatchlist' | 'indexFunds' | 'custom';

export type TickerTapeSettings = {
  tickers: string[];
  /** Active sources, in priority order. Empty array OR all-empty buckets
   *  fall back to topMovers in the consumer (TickerTape.tsx) so the
   *  scrolling bar is never blank. Plan #227. */
  sources?: TickerTapeSource[];
  /** @deprecated — single-source picker, superseded by `sources`.
   *  Migrated to `sources` on first load (see PreferencesContext).
   *  Retained one release for back-compat with cached prefs / older
   *  clients that haven't read the new field yet. */
  mode?: 'topMovers' | 'custom';
  scrollBehavior: 'alwaysPlay' | 'alwaysPause' | 'marketHoursOnly';
  /** When the ticker tape itself appears. Defaults to 'marketHours' for
   *  anonymous users and new accounts. */
  visibility?: 'marketHours' | 'always' | 'hidden';
  /** Marquee scroll speed. Maps to px/sec in TickerTape — see
   *  TICKER_TAPE_SPEED_PX_PER_SEC. Defaults to 'normal'. */
  speed?: 'slower' | 'normal' | 'faster';
  /** When true, the tape stays pinned to the top (sticky) as the page
   *  scrolls instead of moving off with the page. Defaults to false. */
  freezeOnScroll?: boolean;
};

/** Mapping for the marquee scroll speed. The 'normal' default (20 px/sec)
 *  is 3× slower than the original hardcoded value (60); 'faster' restores
 *  the original speed for users who liked it. */
export const TICKER_TAPE_SPEED_PX_PER_SEC: Record<NonNullable<TickerTapeSettings['speed']>, number> = {
  slower: 10,
  normal: 20,
  faster: 60,
};

/** Header market-indicator chips (issue #266). Up to 5 user-selected
 *  stock tickers shown in the desktop navbar, plus an optional OIL
 *  (CL=F daily %) chip. Null on the server means "use the consumer
 *  defaults" (SPY + OIL). */
export type HeaderMarketIndicatorsSettings = {
  /** Master on/off for the entire chip group. */
  enabled: boolean;
  /** Up to 5 stock tickers, in render order. Empty array → no stock
   *  chips (the OIL chip can still render if `includeOil`). */
  tickers: string[];
  /** When true, the OIL (CL=F) chip is appended after the stock chips. */
  includeOil: boolean;
};

export const DEFAULT_HEADER_MARKET_INDICATORS: HeaderMarketIndicatorsSettings = {
  enabled: true,
  tickers: ['SPY'],
  includeOil: true,
};

export async function getUserPreferences(): Promise<{
  theme: string;
  largeText: boolean;
  squareTiles: boolean;
  /** Daily pre-trading checklist popup. Optional for back-compat with an
   *  older server that predates the field — the client defaults it ON. */
  showPreTradingChecklist?: boolean;
  tickerTapeSettings?: TickerTapeSettings;
  headerMarketIndicators?: HeaderMarketIndicatorsSettings | null;
}> {
  return request('/users/me/preferences');
}

export async function saveUserPreferences(prefs: {
  theme?: string;
  largeText?: boolean;
  squareTiles?: boolean;
  showPreTradingChecklist?: boolean;
  tickerTapeSettings?: TickerTapeSettings;
  headerMarketIndicators?: HeaderMarketIndicatorsSettings;
}): Promise<void> {
  await request('/users/me/preferences', { method: 'PUT', body: JSON.stringify(prefs) });
}

// ── Chat & Messaging ────────────────────────────────────────────────────────

export interface ChatSettings {
  enabled: boolean;
  online: boolean;
  welcomeMessage: string;
  offlineMessage: string;
}

export interface ChatConversation {
  id: string;
  userId: string | null;
  guestToken: string | null;
  guestName: string;
  guestEmail: string;
  deliveryMode: string;
  status: string;
  subject: string;
  assignedTo: string | null;
  csatRating: number | null;
  csatComment: string | null;
  isBlocked: boolean;
  lastMessageAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Admin extras
  lastMessagePreview?: string;
  unreadCount?: number;
  visitorDisplayName?: string;
  visitorEmail?: string;
  visitorTier?: string | null;
  messages?: ChatMessage[];
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderType: 'visitor' | 'staff' | 'bot' | 'system';
  senderId: string | null;
  body: string;
  attachmentUrl: string | null;
  attachmentName: string | null;
  isInternal: boolean;
  readAt: string | null;
  createdAt: string;
  senderName?: string;
}

function chatHeaders(guestToken?: string | null): HeadersInit {
  const h: Record<string, string> = {};
  if (guestToken) h['X-Guest-Token'] = guestToken;
  return h;
}

// Public
export async function getChatSettingsPublic(): Promise<ChatSettings> {
  return request<ChatSettings>('/chat/settings/public');
}

export async function chatCreateConversation(data: {
  message: string; guestName?: string; guestEmail?: string; subject?: string; deliveryMode?: string;
}, guestToken?: string | null): Promise<ChatConversation> {
  return request<ChatConversation>('/chat/conversations', {
    method: 'POST', body: JSON.stringify(data), headers: chatHeaders(guestToken),
  });
}

export async function chatGetMyConversations(guestToken?: string | null): Promise<ChatConversation[]> {
  return request<ChatConversation[]>('/chat/conversations/mine', { headers: chatHeaders(guestToken) });
}

export async function chatGetConversation(id: string, guestToken?: string | null): Promise<ChatConversation> {
  return request<ChatConversation>(`/chat/conversations/${id}`, { headers: chatHeaders(guestToken) });
}

export async function chatSendMessage(convId: string, body: string, guestToken?: string | null): Promise<ChatMessage> {
  return request<ChatMessage>(`/chat/conversations/${convId}/messages`, {
    method: 'POST', body: JSON.stringify({ body }), headers: chatHeaders(guestToken),
  });
}

export async function chatUpdateGuestInfo(convId: string, data: { guestEmail?: string; guestName?: string }, guestToken?: string | null): Promise<void> {
  await request(`/chat/conversations/${convId}/guest-info`, {
    method: 'PATCH', body: JSON.stringify(data), headers: chatHeaders(guestToken),
  });
}

// Admin
export async function adminChatGetConversations(params?: {
  status?: string; source?: string; search?: string; page?: number; limit?: number;
}): Promise<ChatConversation[]> {
  const sp = new URLSearchParams();
  if (params?.status) sp.set('status', params.status);
  if (params?.source) sp.set('source', params.source);
  if (params?.search) sp.set('search', params.search);
  if (params?.page) sp.set('page', String(params.page));
  if (params?.limit) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return request<ChatConversation[]>(`/chat/admin/conversations${qs ? `?${qs}` : ''}`);
}

export async function adminChatGetConversation(id: string): Promise<ChatConversation> {
  return request<ChatConversation>(`/chat/admin/conversations/${id}`);
}

export async function adminChatReply(convId: string, body: string, isInternal?: boolean): Promise<ChatMessage> {
  return request<ChatMessage>(`/chat/admin/conversations/${convId}/messages`, {
    method: 'POST', body: JSON.stringify({ body, isInternal }),
  });
}

export async function adminChatUpdateConversation(id: string, data: {
  status?: string; assignedTo?: string | null; isBlocked?: boolean;
}): Promise<ChatConversation> {
  return request<ChatConversation>(`/chat/admin/conversations/${id}`, {
    method: 'PATCH', body: JSON.stringify(data),
  });
}

export async function adminChatDeleteConversation(id: string): Promise<void> {
  await request(`/chat/admin/conversations/${id}`, { method: 'DELETE' });
}

export async function adminChatGetStats(): Promise<{
  openCount: number; unassignedCount: number; resolvedCount: number; totalCount: number;
}> {
  return request('/chat/admin/stats');
}

export async function adminChatGetSettings(): Promise<ChatSettings> {
  return request<ChatSettings>('/chat/admin/settings');
}

export async function adminChatSaveSettings(data: Partial<ChatSettings & ChatSettingsExtended>): Promise<void> {
  await request('/chat/admin/settings', { method: 'PUT', body: JSON.stringify(data) });
}

export interface ChatSettingsExtended {
  csatEnabled: boolean;
  turnstileEnabled: boolean;
  turnstileSiteKey: string;
  turnstileSecretKey: string;
  notificationEmails: string;
}

// CSAT
export async function chatSubmitCSAT(convId: string, rating: number, comment?: string, guestToken?: string | null): Promise<void> {
  await request(`/chat/conversations/${convId}/csat`, {
    method: 'POST', body: JSON.stringify({ rating, comment }), headers: chatHeaders(guestToken),
  });
}

// File upload
export async function chatUploadFile(file: File, guestToken?: string | null): Promise<{ url: string; name: string }> {
  const form = new FormData();
  form.append('file', file);
  const headers: Record<string, string> = {};
  if (_accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;
  if (guestToken) headers['X-Guest-Token'] = guestToken;
  const response = await fetch(`${typeof window !== 'undefined' ? '/api' : (process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4000/api')}/chat/upload`, {
    method: 'POST', body: form, headers, credentials: 'include',
  });
  if (!response.ok) throw new Error('Upload failed');
  const json = await response.json();
  return json.data;
}

// Quick reply templates
export async function adminChatGetTemplates(): Promise<{ id: string; title: string; body: string }[]> {
  return request('/chat/admin/templates');
}

export async function adminChatSaveTemplates(templates: { id: string; title: string; body: string }[]): Promise<void> {
  await request('/chat/admin/templates', { method: 'PUT', body: JSON.stringify({ templates }) });
}

// Transcript
export async function adminChatSendTranscript(convId: string): Promise<{ message: string }> {
  return request(`/chat/admin/conversations/${convId}/transcript`, { method: 'POST' });
}

// ── Portfolio Challenge Game ────────────────────────────────────────────────

export interface ChallengeGameSettings {
  enabled: boolean;
  durationWeeks: number;
  startingAmount: number;
  minStocks: number;
  maxStocks: number;
  emailAlerts: boolean;
  digestHour: number;
  digestTimezone: string;
}

export async function adminTestChallengeEmail(): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/challenge-game/test-email', { method: 'POST' });
}

export interface ChallengePosition {
  id: string; ticker: string; name: string; allocationPct: number;
  shares: number; buyPrice: number; buyValue: number;
  currentPrice: number; currentValue: number; gainPct: number;
}

export interface ChallengeSnapshot {
  date: string; portfolioValue: number; benchmarkValue: number;
}

export interface Challenge {
  id: string; name: string; startingAmount: number; durationWeeks: number;
  status: 'active' | 'completed';
  cashBalance: number;
  startDate: string; endDate: string;
  currentValue: number; currentReturnPct: number;
  finalValue: number | null; finalReturnPct: number | null;
  benchmarkReturnPct: number | null; beatMarket: boolean;
  emailAlerts: boolean; createdAt: string;
  daysRemaining?: number;
  positions?: ChallengePosition[];
  snapshots?: ChallengeSnapshot[];
}

export interface ChallengeTrade {
  id: string; ticker: string; action: 'buy' | 'sell';
  order_type: 'market' | 'limit'; shares: number; price: number;
  total: number; limit_price: number | null; status: string;
  created_at: string; filled_at: string | null;
}

export interface LeaderboardEntry {
  rank: number; challengeId: string; challengeName: string;
  userName: string; returnPct: number;
  benchmarkReturnPct: number | null; beatMarket: boolean;
  duration: string; status: string; stockCount: number; tradeCount: number;
  topTicker: string | null; topTickerReturn: number | null;
  startDate: string; endDate: string;
}

export async function getChallengeRandomPicks(): Promise<{ ticker: string; name: string; price: number; allocationPct: number }[]> {
  return request<{ ticker: string; name: string; price: number; allocationPct: number }[]>('/challenges/random-picks');
}

export async function getChallengeSettings(): Promise<ChallengeGameSettings> {
  return request<ChallengeGameSettings>('/challenges/settings');
}

export async function getChallenges(): Promise<Challenge[]> {
  return request<Challenge[]>('/challenges');
}

export async function getChallenge(id: string): Promise<Challenge> {
  return request<Challenge>(`/challenges/${id}`);
}

export async function createChallenge(data: {
  name: string;
  positions: { ticker: string; allocationPct: number }[];
  emailAlerts?: boolean;
}): Promise<{ id: string; message: string }> {
  return request<{ id: string; message: string }>('/challenges', {
    method: 'POST', body: JSON.stringify(data),
  });
}

export async function deleteChallenge(id: string): Promise<void> {
  await request(`/challenges/${id}`, { method: 'DELETE' });
}

export async function updateChallenge(id: string, data: { emailAlerts?: boolean }): Promise<void> {
  await request(`/challenges/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function getChallengeLeaderboard(params?: { status?: string; page?: number }): Promise<{ entries: LeaderboardEntry[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.page) qs.set('page', String(params.page));
  return request<{ entries: LeaderboardEntry[]; total: number }>(`/challenges/board/leaderboard?${qs}`);
}

export async function executeChallengeTrade(challengeId: string, data: {
  action: 'buy' | 'sell'; ticker: string; shares: number;
  orderType?: 'market' | 'limit'; limitPrice?: number;
}): Promise<{ message: string; status: string }> {
  return request<{ message: string; status: string }>(`/challenges/${challengeId}/trade`, {
    method: 'POST', body: JSON.stringify(data),
  });
}

export async function getChallengeTrades(challengeId: string): Promise<ChallengeTrade[]> {
  return request<ChallengeTrade[]>(`/challenges/${challengeId}/trades`);
}

export async function cancelChallengeTrade(challengeId: string, tradeId: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/challenges/${challengeId}/trades/${tradeId}`, { method: 'DELETE' });
}

export async function adminGetChallengeGameSettings(): Promise<ChallengeGameSettings> {
  return request<ChallengeGameSettings>('/admin/settings/challenge-game');
}

export async function adminSaveChallengeGameSettings(data: Partial<ChallengeGameSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/challenge-game', {
    method: 'POST', body: JSON.stringify(data),
  });
}

// ── Free Trial ───────────────────────────────────────────────────────────────

export interface FreeTrialSettings {
  enabled: boolean;
  trialDays: number;
  trialTier: 'pro' | 'full';
  showFirstLoginPopup: boolean;
  warningDaysBeforeExpiry: number;
  warningEmailEnabled: boolean;
  warningInAppEnabled: boolean;
  popupTitle: string;
  popupMessage: string;
}

export async function adminGetFreeTrialSettings(): Promise<FreeTrialSettings> {
  return request<FreeTrialSettings>('/admin/settings/free-trial');
}

export async function adminSaveFreeTrialSettings(data: Partial<FreeTrialSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/free-trial', {
    method: 'POST', body: JSON.stringify(data),
  });
}

// ── Marketing Tile Settings ──────────────────────────────────────────────────

export interface MarketingTileSettings {
  mode: 'original' | 'carousel';
  carouselInterval: number;
}

export async function getPublicMarketingSettings(): Promise<MarketingTileSettings> {
  return request<MarketingTileSettings>('/admin/public/marketing');
}

export async function adminGetMarketingSettings(): Promise<MarketingTileSettings> {
  return request<MarketingTileSettings>('/admin/settings/marketing');
}

export async function adminSaveMarketingSettings(data: Partial<MarketingTileSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/marketing', {
    method: 'POST', body: JSON.stringify(data),
  });
}

// ── Portfolio Risk ────────────────────────────────────────────────────────────

export interface RiskMetrics {
  volatility30d: number | null;
  volatility90d: number | null;
  volatility1y: number | null;
  beta: number | null;
  sharpeRatio: number | null;
  maxDrawdown: number | null;
  var95: number | null;
  concentration: number;
  top3Pct: number;
  sectorWeights: Record<string, number>;
  riskScore: number;
}

export interface RiskHistoryPoint {
  date: string;
  risk_score: number;
  volatility_30d: number | null;
  beta: number | null;
  max_drawdown: number | null;
  concentration: number | null;
}

export interface CorrelationEntry {
  tickerA: string;
  tickerB: string;
  correlation: number;
}

export interface RiskAlert {
  id: string;
  alert_type: string;
  threshold: number;
  active: boolean;
  last_triggered: string | null;
  cooldown_hours: number;
  created_at: string;
}

export async function getPortfolioRisk(portfolioId: string): Promise<{ current: RiskMetrics; history: RiskHistoryPoint[]; riskMonitoring: boolean }> {
  return request(`/portfolios/${portfolioId}/risk`);
}

export async function toggleRiskMonitoring(portfolioId: string, enabled: boolean): Promise<{ riskMonitoring: boolean }> {
  return request(`/portfolios/${portfolioId}/risk/toggle`, { method: 'POST', body: JSON.stringify({ enabled }) });
}

export async function getPortfolioCorrelations(portfolioId: string): Promise<CorrelationEntry[]> {
  return request(`/portfolios/${portfolioId}/risk/correlation`);
}

export async function getRiskAlerts(portfolioId: string): Promise<RiskAlert[]> {
  return request(`/portfolios/${portfolioId}/risk-alerts`);
}

export async function createRiskAlert(portfolioId: string, data: { alertType: string; threshold: number; cooldownHours?: number }): Promise<RiskAlert> {
  return request(`/portfolios/${portfolioId}/risk-alerts`, { method: 'POST', body: JSON.stringify(data) });
}

export async function deleteRiskAlert(portfolioId: string, alertId: string): Promise<{ message: string }> {
  return request(`/portfolios/${portfolioId}/risk-alerts/${alertId}`, { method: 'DELETE' });
}

// ── Announcements ─────────────────────────────────────────────────────────────

export interface Announcement {
  id: string;
  title: string;
  body: string;
  bodyType: 'text' | 'image';
  displayType: 'banner' | 'popup';
  target: 'all' | 'pro' | 'free';
  status: 'draft' | 'active';
  ctaUrl: string;
  ctaLabel: string;
  priority: number;
  remindHours: number;
  startAt: string;
  endAt: string;
  createdAt: string;
  updatedAt: string;
  dismissCount?: number;
}

export function getActiveAnnouncements(): Promise<Announcement[]> {
  return request<Announcement[]>('/announcements');
}

export function dismissAnnouncement(id: string): Promise<void> {
  return request<void>(`/announcements/${id}/dismiss`, { method: 'POST' });
}

export function snoozeAnnouncement(id: string): Promise<void> {
  return request<void>(`/announcements/${id}/snooze`, { method: 'POST' });
}

export function adminGetAnnouncements(): Promise<Announcement[]> {
  return request<Announcement[]>('/announcements/admin');
}

export function adminCreateAnnouncement(data: Partial<Announcement>): Promise<Announcement> {
  return request<Announcement>('/announcements/admin', { method: 'POST', body: JSON.stringify(data) });
}

export function adminUpdateAnnouncement(id: string, data: Partial<Announcement>): Promise<Announcement> {
  return request<Announcement>(`/announcements/admin/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function adminDeleteAnnouncement(id: string): Promise<void> {
  return request<void>(`/announcements/admin/${id}`, { method: 'DELETE' });
}

export function adminResetAnnouncementDismissals(id: string): Promise<void> {
  return request<void>(`/announcements/admin/${id}/reset-dismissals`, { method: 'POST' });
}

// ── Demo Accounts ────────────────────────────────────────────────────────────
export type DemoAccount = {
  id: string; username: string; email: string; displayName: string; tier: string; password: string | null;
  portfolioCount: number; loginCount: number; createdAt: string;
};
export type DemoConfig = {
  emailPrefix: string; emailDomain: string; maxAccounts: number;
  portfolioRange: [number, number]; positionsRange: [number, number];
  maxTransactions: number; autoChallenge: boolean;
  credentialEmailSubject: string; credentialEmailBody: string;
};
export function getDemoAccounts(): Promise<DemoAccount[]> {
  return request<DemoAccount[]>('/admin/demo-accounts');
}
export function createDemoAccount(tier: string): Promise<DemoAccount> {
  return request<DemoAccount>('/admin/demo-accounts', { method: 'POST', body: JSON.stringify({ tier }) });
}
export function bulkCreateDemoAccounts(count: number, tier: string, opts?: { portfolioCount?: number; watchlistCount?: number }): Promise<DemoAccount[]> {
  return request<DemoAccount[]>('/admin/demo-accounts/bulk', { method: 'POST', body: JSON.stringify({ count, tier, ...opts }) });
}
export function bulkDeleteDemoAccounts(ids: string[]): Promise<{ deleted: number }> {
  return request<{ deleted: number }>('/admin/demo-accounts/bulk', { method: 'DELETE', body: JSON.stringify({ ids }) });
}
export function updateDemoTier(id: string, tier: string): Promise<void> {
  return request<void>(`/admin/demo-accounts/${id}/tier`, { method: 'PUT', body: JSON.stringify({ tier }) });
}
export function regenerateDemoData(id: string): Promise<void> {
  return request<void>(`/admin/demo-accounts/${id}/regenerate`, { method: 'POST' });
}
export function resetDemoPassword(id: string): Promise<{ password: string }> {
  return request<{ password: string }>(`/admin/demo-accounts/${id}/reset-password`, { method: 'POST' });
}
export function sendDemoCredentials(id: string, email: string): Promise<{ sent: boolean; to: string }> {
  return request<{ sent: boolean; to: string }>(`/admin/demo-accounts/${id}/send-credentials`, { method: 'POST', body: JSON.stringify({ email }) });
}
export function deleteDemoAccount(id: string): Promise<void> {
  return request<void>(`/admin/demo-accounts/${id}`, { method: 'DELETE' });
}
export function getDemoConfig(): Promise<DemoConfig> {
  return request<DemoConfig>('/admin/demo-accounts/config');
}
export function updateDemoConfig(config: Partial<DemoConfig>): Promise<DemoConfig> {
  return request<DemoConfig>('/admin/demo-accounts/config', { method: 'PUT', body: JSON.stringify(config) });
}

// ── Notification Preferences ─────────────────────────────────────────────────
export type NotificationPreferences = {
  stockAlertsEmail: boolean;
  newsletterEmail: boolean;
  challengeEmail: boolean;
  digestEnabled: boolean;
  digestFrequency: 'daily' | 'weekly';
  digestDay: string;
  digestHour: number;
  volumeSpikeEmail: boolean;
  volumeSpikeMinSeverity: 'elevated' | 'high' | 'extreme';
  volumeSpikePush: boolean;
  /** Browser push for triggered price alerts. Independent of
   *  stockAlertsEmail so a user can opt into push only, email only,
   *  or both. Default false. */
  priceAlertPush: boolean;
  /** AI Watchlist Alerts (#371): opt-in email when a monitored watchlist ticker
   *  approaches support/resistance, drops >4%, or has fresh bullish news. */
  aiWatchlistAlertEmail: boolean;
  /** Which watchlist ids to monitor for AI alerts; empty = the primary list. */
  aiWatchlistAlertListIds: string[];
};
export function getNotificationPreferences(): Promise<NotificationPreferences> {
  return request<NotificationPreferences>('/users/me/notifications');
}
export function updateNotificationPreferences(prefs: Partial<NotificationPreferences>): Promise<void> {
  return request<void>('/users/me/notifications', { method: 'PUT', body: JSON.stringify(prefs) });
}
/** Send a diagnostic test email to the signed-in user's own address. */
export function sendTestEmail(): Promise<{ ok: boolean; to: string }> {
  return request<{ ok: boolean; to: string }>('/users/me/test-email', { method: 'POST' });
}
/** Send an on-demand sample of the AI Watchlist Alert to the signed-in user.
 *  Pass the currently-selected watchlist ids so the test reflects an unsaved
 *  selection (falls back to saved prefs / primary list when empty). */
export function sendWatchlistAlertTest(listIds?: string[]): Promise<{
  ok: boolean; status: string; to: string; candidateCount: number; monitoredCount: number; tickers: string[];
}> {
  return request('/users/me/watchlist-alert-test', { method: 'POST', body: JSON.stringify({ listIds: listIds ?? [] }) });
}

// Per-channel mute (alerts bell footer). Returns the live mute timestamps
// (null when not muted or window has expired).
export type NotificationMuteStatus = {
  emailMutedUntil: string | null;
  pushMutedUntil: string | null;
};
export function getNotificationMuteStatus(): Promise<NotificationMuteStatus> {
  return request<NotificationMuteStatus>('/users/me/notifications/mute');
}
export function muteNotifications(channel: 'email' | 'push', duration: 'today' | 'week'): Promise<NotificationMuteStatus> {
  return request<NotificationMuteStatus>('/users/me/notifications/mute', {
    method: 'POST',
    body: JSON.stringify({ channel, duration }),
  });
}
export function unmuteNotifications(channel: 'email' | 'push'): Promise<NotificationMuteStatus> {
  return request<NotificationMuteStatus>('/users/me/notifications/unmute', {
    method: 'POST',
    body: JSON.stringify({ channel }),
  });
}

// ── Admin Market Holidays ────────────────────────────────────────────────────
export type MarketHoliday = {
  etDate: string;
  name: string;
  source: 'manual' | 'seed' | 'finnhub';
  createdAt: string;
  updatedAt: string;
};
export type MarketHolidayUpsertResult = {
  inserted: number;
  updated: number;
  skipped: Array<{ raw: string; reason: string }>;
};
export function adminListHolidays(): Promise<MarketHoliday[]> {
  return request<MarketHoliday[]>('/admin/holidays');
}
export function adminUpsertHolidays(payload: { entries?: Array<{ etDate: string; name: string }>; bulkText?: string }): Promise<MarketHolidayUpsertResult> {
  return request<MarketHolidayUpsertResult>('/admin/holidays', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
export function adminDeleteHoliday(etDate: string): Promise<{ deleted: string }> {
  return request<{ deleted: string }>(`/admin/holidays/${encodeURIComponent(etDate)}`, { method: 'DELETE' });
}

// ── Admin Email Notification Settings ────────────────────────────────────────
export type EmailNotificationSettings = {
  globalEnabled: boolean;
  challengeDigestHour: number;
  challengeDigestTimezone: string;
  userDigestEnabled: boolean;
  defaultDigestFrequency: 'daily' | 'weekly';
  defaultDigestHour: number;
  defaultDigestDay: string;
  lastDigestRun: string | null;
  lastDigestSentCount: number;
  lastDigestFailCount: number;
};
export function adminGetEmailNotificationSettings(): Promise<EmailNotificationSettings> {
  return request<EmailNotificationSettings>('/admin/settings/email-notifications');
}
export function adminSaveEmailNotificationSettings(data: Partial<EmailNotificationSettings>): Promise<EmailNotificationSettings> {
  return request<EmailNotificationSettings>('/admin/settings/email-notifications', { method: 'PUT', body: JSON.stringify(data) });
}
export function adminGetDigestStats(): Promise<{ globalEnabled: boolean; userDigestEnabled: boolean; lastRun: string | null; lastSentCount: number; lastFailCount: number }> {
  return request('/admin/digest/stats');
}
export function adminTestDigest(): Promise<{ sent: number; to: string }> {
  return request('/admin/digest/test', { method: 'POST' });
}
// Plan #169 — V1 admin dashboard presets removed.

// ── Email Previews ────────────────────────────────────────────────────────────

export type EmailPreviewCategory =
  | 'auth' | 'challenge' | 'portfolio' | 'user' | 'congress'
  | 'trial' | 'subscription' | 'admin' | 'support' | 'chat' | 'demo';

export interface EmailPreview {
  id: string;
  category: EmailPreviewCategory;
  name: string;
  subject: string;
  description: string;
  html: string;
}

export interface EmailPreviewCategoryInfo {
  id: EmailPreviewCategory;
  label: string;
  count: number;
}

export function adminGetEmailPreviews(): Promise<{ previews: EmailPreview[]; categories: EmailPreviewCategoryInfo[] }> {
  return request('/admin/email-previews');
}

export function adminSendEmailPreview(id: string, to: string): Promise<{ message: string }> {
  return request(`/admin/email-previews/${encodeURIComponent(id)}/send`, {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
}

// ── Claude validation log viewer (plan #128) ─────────────────────────────
export interface ClaudeValidationLogEntry {
  id: string;
  portfolioId: string | null;
  portfolioName: string | null;
  autoTradeEventId: string | null;
  side: 'buy' | 'sell';
  /** 'signal' = pattern validator (first-layer); 'stage' = timing/peak-chase
   *  validator (second-layer, buy-only). Plan #137. */
  kind: 'signal' | 'stage';
  ticker: string;
  model: string;
  approved: boolean;
  confidence: number | null;
  outcome: string;
  reason: string | null;
  latencyMs: number | null;
  occurredAt: string;
  /** Truncated response text (up to 500 chars) for the happy path. For
   *  parse_error / api_error / timeout this is what Claude actually
   *  returned — the fastest way to debug why the parser rejected it. */
  responseText: string | null;
  /** Set on api_error / timeout rows instead of responseText. */
  responseError: string | null;
  /** Issue #181 — workflow attribution + confidence-gate fields. NULL on
   *  pre-#181 rows and on the legacy spike-validator path until that
   *  caller is migrated. */
  workflow: string | null;
  confidenceThreshold: number | null;
  rejectedBy: string | null;
}

export interface ClaudeValidationLogPage {
  items: ClaudeValidationLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

// Issue #181 — workflow + rejected_by filter values.
export type AiWorkflowFilter =
  | 'longterm_buy' | 'longterm_sell'
  | 'technical_buy' | 'technical_sell'
  | 'spike_validator';

export type RejectedByFilter =
  | 'low_confidence' | 'cost_cap' | 'circuit_breaker'
  | 'parse_error' | 'timeout' | 'api_error';

export async function adminGetClaudeValidations(filters: {
  ticker?: string; side?: 'buy' | 'sell'; outcome?: string; approved?: boolean; since?: string;
  workflow?: AiWorkflowFilter; rejectedBy?: RejectedByFilter;
  limit?: number; offset?: number;
} = {}): Promise<ClaudeValidationLogPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<ClaudeValidationLogPage>(`/admin/validator/claude/validations${suffix}`);
}

// ── Event-to-validation map (for the per-event badges in AutoTradeEventsModal) ──
export interface EventValidation {
  approved: boolean;
  outcome: string;
  reason: string | null;
  confidence: number | null;
  model: string;
  occurredAt: string;
}

export async function getAutoTradeEventValidations(
  portfolioId: string,
  eventIds: string[],
): Promise<Record<string, EventValidation>> {
  if (eventIds.length === 0) return {};
  const qs = new URLSearchParams({ eventIds: eventIds.join(',') });
  return request<Record<string, EventValidation>>(`/portfolios/${portfolioId}/auto-trade-events/validations?${qs.toString()}`);
}

// ── AI Setup (issue #150) ───────────────────────────────────────────────

export type PromptKey =
  | 'signal' | 'stage' | 'longterm' | 'news_daily_top'
  | 'longterm_picker' | 'technical_ai_tick' | 'news_web_search'
  | 'near_term_catalysts';

export interface AiPromptRow {
  key: PromptKey;
  description: string;
  whereUsed: string[];
  defaultBody: string;
  overrideBody: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export async function adminListAiPrompts(): Promise<{ prompts: AiPromptRow[] }> {
  return request<{ prompts: AiPromptRow[] }>(`/admin/ai-setup/prompts`);
}

export async function adminSavePromptOverride(key: PromptKey, body: string): Promise<{ prompts: AiPromptRow[] }> {
  return request<{ prompts: AiPromptRow[] }>(`/admin/ai-setup/prompts/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ body }),
  });
}

export async function adminRevertPromptOverride(key: PromptKey): Promise<{ prompts: AiPromptRow[] }> {
  return request<{ prompts: AiPromptRow[] }>(`/admin/ai-setup/prompts/${key}`, {
    method: 'DELETE',
  });
}

export type AiPromptTestResult =
  | { ok: true;  reply: string; latencyMs: number; costUsd: number; model: string }
  | { ok: false; error: string };

export async function adminTestPrompt(key: PromptKey, body: string): Promise<AiPromptTestResult> {
  return request<AiPromptTestResult>(`/admin/ai-setup/prompts/${key}/test`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export interface AiNewsPick {
  rank: number;
  headline: string;
  source: string;
  url: string;
  tickers: string[];
  note: string;
}

export interface AiNewsDailySummary {
  date: string;
  digest: string;
  picks: AiNewsPick[];
  marketTone: 'bullish' | 'neutral' | 'bearish' | null;
  model: string;
  latencyMs: number;
  createdAt: string;
  inputArticleCount: number;
}

export async function adminGetNewsSummary(): Promise<{ summary: AiNewsDailySummary | null }> {
  return request<{ summary: AiNewsDailySummary | null }>(`/admin/ai-setup/news/current`);
}

export async function adminRegenerateNewsSummary(): Promise<
  { ok: true; summary: AiNewsDailySummary } | { ok: false; error: string }
> {
  return request<{ ok: true; summary: AiNewsDailySummary } | { ok: false; error: string }>(
    `/admin/ai-setup/news/regenerate`,
    { method: 'POST' },
  );
}

export interface AiNewsSchedule {
  enabled: boolean;
  fireAtHourET: number;
  lastRunAt: string | null;
}

export async function adminGetNewsSchedule(): Promise<{ schedule: AiNewsSchedule }> {
  return request<{ schedule: AiNewsSchedule }>(`/admin/ai-setup/news/schedule`);
}

export async function adminSaveNewsSchedule(patch: Partial<Pick<AiNewsSchedule, 'enabled' | 'fireAtHourET'>>): Promise<{ schedule: AiNewsSchedule }> {
  return request<{ schedule: AiNewsSchedule }>(`/admin/ai-setup/news/schedule`, {
    method: 'POST',
    body: JSON.stringify(patch),
  });
}

export interface AiPricingRow {
  model: string;
  defaultInputPer1M: number;
  defaultOutputPer1M: number;
  overrideInputPer1M: number | null;
  overrideOutputPer1M: number | null;
}

export async function adminGetAiPricing(): Promise<{ rows: AiPricingRow[] }> {
  return request<{ rows: AiPricingRow[] }>(`/admin/ai-setup/pricing`);
}

// Issue #181 — per-ticker news debug
export interface AvNewsItem {
  publishedAt: string;
  title: string;
  url: string;
  source: string;
  overallSentiment: string;
  overallSentimentScore: number;
  relevance: number;
  tickerSentimentScore: number;
  tickerSentimentLabel: string;
  summary?: string;
  topics?: string[];
}

export interface AvNewsResult {
  ticker: string;
  fetchedAt: string;
  total: number;
  items: AvNewsItem[];
  error?: string;
}

export async function adminFetchAvNews(ticker: string, opts: { limit?: number; maxAgeHours?: number; minRelevance?: number } = {}): Promise<AvNewsResult> {
  const qs = new URLSearchParams();
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  if (opts.maxAgeHours != null) qs.set('maxAgeHours', String(opts.maxAgeHours));
  if (opts.minRelevance != null) qs.set('minRelevance', String(opts.minRelevance));
  qs.set('noCache', '1');
  return request<AvNewsResult>(`/admin/debug/news/av/${encodeURIComponent(ticker)}?${qs.toString()}`);
}

export interface WebSearchNewsItem {
  publishedAt: string | null;
  title: string;
  url: string;
  source: string;
  sentimentLabel: string;
  sentimentScore: number;
  summary?: string;
}

export interface WebSearchNewsResult {
  ticker: string;
  fetchedAt: string;
  total: number;
  items: WebSearchNewsItem[];
  model: string;
  inputTokens: number | null;
  outputTokens: number | null;
  error?: string;
}

export async function adminFetchWebSearchNews(ticker: string, opts: { maxAgeHours?: number; topN?: number; maxUses?: number } = {}): Promise<WebSearchNewsResult> {
  const qs = new URLSearchParams();
  if (opts.maxAgeHours != null) qs.set('maxAgeHours', String(opts.maxAgeHours));
  if (opts.topN != null) qs.set('topN', String(opts.topN));
  if (opts.maxUses != null) qs.set('maxUses', String(opts.maxUses));
  return request<WebSearchNewsResult>(`/admin/debug/news/websearch/${encodeURIComponent(ticker)}?${qs.toString()}`);
}

// Issue #181 — admin settings + run endpoints for the new AI workflows
export interface AiThresholdSettings {
  technical: { buyMinConfidence: number; sellMinConfidence: number };
}

export async function adminGetAiThresholds(): Promise<AiThresholdSettings> {
  return request<AiThresholdSettings>('/admin/settings/ai-thresholds');
}

export async function adminSaveAiThresholds(patch: Partial<AiThresholdSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/ai-thresholds', { method: 'PUT', body: JSON.stringify(patch) });
}

export interface AiNewsSettings {
  avEnabled: boolean;
  webSearchEnabled: boolean;
  webSearchModel?: string;
  technicalMaxAgeHours: number;
}

export async function adminGetAiNewsSettings(): Promise<AiNewsSettings> {
  return request<AiNewsSettings>('/admin/settings/ai-news');
}

export async function adminSaveAiNewsSettings(patch: Partial<AiNewsSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/ai-news', { method: 'PUT', body: JSON.stringify(patch) });
}

export interface TechnicalAiWorkflowSettings {
  enabled: boolean;
  firstFireEtMinutes: number;
  lastFireEtMinutes: number;
  intervalMinutes: number;
  candidatePoolSize: number;
  pickCount: number;
  claudeModel?: string;
}

export async function adminGetTechnicalAiWorkflow(): Promise<TechnicalAiWorkflowSettings> {
  return request<TechnicalAiWorkflowSettings>('/admin/settings/technical-ai-workflow');
}

export async function adminSaveTechnicalAiWorkflow(patch: Partial<TechnicalAiWorkflowSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/technical-ai-workflow', { method: 'PUT', body: JSON.stringify(patch) });
}

export async function adminRunTechnicalAiWorkflow(): Promise<unknown> {
  return request<unknown>('/admin/technical-ai-workflow/run', { method: 'POST' });
}

export interface TechnicalSellEvaluatorSettings {
  enabled: boolean;
  intervalMinutes: number;
}

export async function adminGetTechnicalSellEvaluator(): Promise<TechnicalSellEvaluatorSettings> {
  return request<TechnicalSellEvaluatorSettings>('/admin/settings/technical-sell-evaluator');
}

export async function adminSaveTechnicalSellEvaluator(patch: Partial<TechnicalSellEvaluatorSettings>): Promise<{ message: string }> {
  return request<{ message: string }>('/admin/settings/technical-sell-evaluator', { method: 'PUT', body: JSON.stringify(patch) });
}

export async function adminRunTechnicalSellEvaluator(): Promise<unknown> {
  return request<unknown>('/admin/technical-sell-evaluator/run', { method: 'POST' });
}

export async function adminSaveAiPricingOverride(model: string, inputPer1M: number, outputPer1M: number): Promise<{ overrides: Record<string, { inputPer1M: number; outputPer1M: number }> }> {
  return request<{ overrides: Record<string, { inputPer1M: number; outputPer1M: number }> }>(`/admin/ai-setup/pricing`, {
    method: 'POST',
    body: JSON.stringify({ model, inputPer1M, outputPer1M }),
  });
}

export async function adminDeleteAiPricingOverride(model: string): Promise<{ overrides: Record<string, { inputPer1M: number; outputPer1M: number }> }> {
  return request<{ overrides: Record<string, { inputPer1M: number; outputPer1M: number }> }>(`/admin/ai-setup/pricing/${encodeURIComponent(model)}`, {
    method: 'DELETE',
  });
}

export interface AiUsageRollup {
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface AiUsageSummary {
  since: string;
  until: string | null;
  totals: { calls: number; inputTokens: number; outputTokens: number; costUsd: number };
  byModel: AiUsageRollup[];
}

export async function adminAiUsageSummary(since?: string, until?: string): Promise<AiUsageSummary> {
  const qs = new URLSearchParams();
  if (since) qs.set('since', since);
  if (until) qs.set('until', until);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<AiUsageSummary>(`/admin/ai-setup/usage/summary${suffix}`);
}

export interface AiUsageRow {
  id: string;
  ts: string;
  model: string;
  caller: string;
  promptKey: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  inputCostUsd: number;
  outputCostUsd: number;
  costUsd: number;
  latencyMs: number | null;
  ticker: string | null;
  outcome: string;
  errorMessage: string | null;
}

export async function adminAiUsageRecent(opts: {
  limit?: number; caller?: string; outcome?: string;
} = {}): Promise<{ rows: AiUsageRow[] }> {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.caller) qs.set('caller', opts.caller);
  if (opts.outcome) qs.set('outcome', opts.outcome);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<{ rows: AiUsageRow[] }>(`/admin/ai-setup/usage/recent${suffix}`);
}

// ── Strategy Performance (issue #152) ─────────────────────────────────────────

export interface StrategyPerformanceDay {
  date: string;
  wins: number;
  losses: number;
  breakeven: number;
  pnl: number;
}

export interface StrategyPerformanceRow {
  id: string;
  label: string;
  totalWins: number;
  totalLosses: number;
  totalBreakeven: number;
  winRatePct: number;
  totalPnl: number;
  byDay: StrategyPerformanceDay[];
}

export interface StrategyPerformanceResponse {
  rangeStart: string;
  rangeEnd: string;
  days: number;
  strategies: StrategyPerformanceRow[];
}

export async function adminGetStrategyPerformance(days: number): Promise<StrategyPerformanceResponse> {
  return request<StrategyPerformanceResponse>(`/admin/strategy-performance?days=${days}`);
}

// ── P/S history (stock detail) ───────────────────────────────────────────────
// Quarterly P/S series — TTM revenue per quarter × CURRENT shares
// outstanding ÷ historical close. Approximation flagged in the chart
// tooltip (see PsHistoryChart). Free for all callers — same access tier
// as the rest of the stock detail page.
export interface PsHistoryPoint {
  date: string;
  psRatio: number;
  /** Diluted P/E using TTM EPS. Null when trailing year unprofitable
   *  (negative TTM EPS) or when EPS data is missing for a quarter. */
  peRatio: number | null;
  price: number;
  ttmRevenue: number;
  ttmEps: number | null;
}
export interface PsHistoryResponse {
  ticker: string;
  years: number;
  points: PsHistoryPoint[];
}
export async function getPsHistory(ticker: string, years = 10): Promise<PsHistoryResponse> {
  return request<PsHistoryResponse>(`/stocks/${encodeURIComponent(ticker)}/ps-history?years=${years}`);
}

// ── ETF Setups ───────────────────────────────────────────────────────────────
// Daily-bar pattern matches across the ETF universe. End-of-day driven —
// no live/intraday quotes feed this; the page surfaces close-based signals
// for swing-trade entries (Connors 2-Day Pullback in v1).
export type EtfSetupStrategy = 'connors_pullback' | 'rsi2_oversold' | 'donchian_breakout';
export interface EtfSetupSignal {
  ticker: string;
  name: string;
  asOfDate: string;
  strategy: EtfSetupStrategy;
  /** Suggested entry price (last bar close). */
  entryPrice: number;
  /** Per-strategy exit reference price. Connors: prev day's high.
   *  RSI(2): 5-day SMA. Donchian: prior 10-day low. */
  exitReference: number;
  /** Plain-English exit rule for this signal. */
  exitRule: string;
  recentBars: OHLCV[];
  // Connors-only — optional now that other strategies coexist.
  downStreak?: number;
  upStreak?: number;
  prevDayHigh?: number;
  // Uptrend filter context (Connors + RSI(2)).
  sma200?: number;
  pctAbove200Sma?: number;
  // RSI(2)-specific.
  rsi2?: number;
  fiveSma?: number;
  // Donchian-specific.
  donchianHigh?: number;
  donchianLow?: number;
  donchianPeriod?: number;
}
export interface EtfSetupsParams {
  strategy?: EtfSetupStrategy;
  /** Connors only — minimum consecutive down closes (default 2). */
  minDownDays?: number;
  /** Connors + RSI(2) — toggle the uptrend filter (default true). */
  requireAbove200Sma?: boolean;
  /** RSI(2) only — RSI ceiling for the buy signal (default 5). */
  rsi2Threshold?: number;
  /** Donchian only — lookback for the breakout high (default 20). */
  donchianPeriod?: number;
}
export async function getEtfSetups(params: EtfSetupsParams = {}): Promise<EtfSetupSignal[]> {
  const qs = new URLSearchParams();
  if (params.strategy) qs.set('strategy', params.strategy);
  if (params.minDownDays != null) qs.set('minDownDays', String(params.minDownDays));
  if (params.requireAbove200Sma === false) qs.set('requireAbove200Sma', 'false');
  if (params.rsi2Threshold != null) qs.set('rsi2Threshold', String(params.rsi2Threshold));
  if (params.donchianPeriod != null) qs.set('donchianPeriod', String(params.donchianPeriod));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<EtfSetupSignal[]>(`/etf/setups${suffix}`);
}

export async function getEtfSetupBars(ticker: string, days = 90): Promise<{ ticker: string; bars: OHLCV[] }> {
  return request<{ ticker: string; bars: OHLCV[] }>(`/etf/setups/bars/${encodeURIComponent(ticker)}?days=${days}`);
}

// Per-ETF backtest — historical signals + win/loss stats for one ticker.
export interface EtfSetupTrade {
  signalDate: string;
  entryPrice: number;
  exitDate: string | null;
  exitPrice: number | null;
  exitReason: 'target_hit' | 'max_hold' | 'still_open';
  pnlPct: number | null;
  holdDays: number | null;
  outcome: 'win' | 'loss' | 'flat' | 'pending';
  // Connors-only fields — populated to 0 for other strategies. Kept on
  // every trade for backwards compatibility with the existing track-
  // record schema and renderer.
  downStreak: number;
  upStreak: number;
  sma200: number;
  pctAbove200Sma: number;
  prevDayHigh: number;
}
export interface EtfBacktestStats {
  count: number;
  wins: number;
  losses: number;
  flats: number;
  pending: number;
  winRate: number;
  avgPnlPct: number;
  avgHoldDays: number;
  totalReturnPct: number;
}
export interface EtfBacktestResult {
  ticker: string;
  trades: EtfSetupTrade[];
  stats: EtfBacktestStats;
  bars: OHLCV[];
}
export interface EtfBacktestParams {
  strategy?: EtfSetupStrategy;
  days?: number;
  minDownDays?: number;
  maxHoldDays?: number;
  requireAbove200Sma?: boolean;
  rsi2Threshold?: number;
  donchianPeriod?: number;
}
export async function getEtfSetupBacktest(ticker: string, params: EtfBacktestParams = {}): Promise<EtfBacktestResult> {
  const qs = new URLSearchParams();
  if (params.strategy) qs.set('strategy', params.strategy);
  if (params.days != null) qs.set('days', String(params.days));
  if (params.minDownDays != null) qs.set('minDownDays', String(params.minDownDays));
  if (params.maxHoldDays != null) qs.set('maxHoldDays', String(params.maxHoldDays));
  if (params.requireAbove200Sma === false) qs.set('requireAbove200Sma', 'false');
  if (params.rsi2Threshold != null) qs.set('rsi2Threshold', String(params.rsi2Threshold));
  if (params.donchianPeriod != null) qs.set('donchianPeriod', String(params.donchianPeriod));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<EtfBacktestResult>(`/etf/setups/backtest/${encodeURIComponent(ticker)}${suffix}`);
}

// Track Record — list + aggregate stats of recent predictions captured
// by the daily cron. Outcomes are graded as bars roll in; pending = still
// inside the hold window with no target hit yet.
export interface EtfPrediction {
  id: string;
  ticker: string;
  signalDate: string;
  entryPrice: number;
  sma200: number | null;
  downStreak: number | null;
  upStreak: number | null;
  pctAbove200Sma: number | null;
  prevDayHigh: number | null;
  maxHoldDays: number;
  outcome: 'pending' | 'win' | 'loss' | 'flat';
  exitDate: string | null;
  exitPrice: number | null;
  exitReason: 'target_hit' | 'max_hold' | 'still_open' | null;
  pnlPct: number | null;
  holdDays: number | null;
}
export interface EtfPredictionStats {
  count: number;
  wins: number;
  losses: number;
  flats: number;
  pending: number;
  winRate: number;
  avgPnlPct: number;
  avgHoldDays: number;
  totalReturnPct: number;
}
export interface EtfPredictionsParams {
  strategy?: EtfSetupStrategy;
  days?: number;
  ticker?: string;
}
export async function getEtfPredictions(params: EtfPredictionsParams = {}): Promise<{ predictions: EtfPrediction[]; stats: EtfPredictionStats }> {
  const qs = new URLSearchParams();
  if (params.strategy) qs.set('strategy', params.strategy);
  if (params.days != null) qs.set('days', String(params.days));
  if (params.ticker) qs.set('ticker', params.ticker);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<{ predictions: EtfPrediction[]; stats: EtfPredictionStats }>(`/etf/setups/predictions${suffix}`);
}

// ── Admin: ETF setups status + seed (manual cache warm + backfill) ───────────
export interface EtfSetupsStatus {
  dbReady: boolean;
  etfCount?: number;
  etfWithDailyCache?: number;
  predictionCount?: number;
  earliestSignalDate?: string | null;
  latestSignalDate?: string | null;
}
export interface EtfSetupsSeedStatus {
  inFlight: boolean;
  last: {
    startedAt: string;
    completedAt: string | null;
    warmed: number;
    warmFailed: number;
    predictionsInserted: number;
    error: string | null;
  } | null;
}
export async function getEtfSetupsAdminStatus(): Promise<EtfSetupsStatus> {
  return request<EtfSetupsStatus>('/etf/setups/admin/status');
}
export async function getEtfSetupsAdminSeedStatus(): Promise<EtfSetupsSeedStatus> {
  return request<EtfSetupsSeedStatus>('/etf/setups/admin/seed-status');
}
export async function startEtfSetupsAdminSeed(): Promise<{ started: boolean; message: string }> {
  return request<{ started: boolean; message: string }>('/etf/setups/admin/seed', { method: 'POST' });
}

// ── Manual review queue (plan #239) ───────────────────────────────────────
export interface PendingReviewCandidate {
  ticker: string;
  severity: string;
  rvol: number;
  changePct: number;
  price: number;
  qualificationReason: string;
  bucket: string | null;
  sector: string | null;
  dollarVolume: number;
  firstDetectedAt: string;
  phase: 'regular' | 'premarket' | 'afterhours';
  score: number;
  validationId?: string | null;
  shares?: number;
  entryPrice?: number;
  peakUnrealizedPct?: number;
  exitReason?: string;
}
export interface PendingReview {
  id: string;
  portfolioId: string;
  portfolioName: string | null;
  action: 'buy' | 'sell';
  candidates: PendingReviewCandidate[];
  spikeKey: string | null;
  positionId: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  userReason: string | null;
  aiVerdict: unknown | null;
  resolutionAction: 'next_candidate' | 'snooze' | 'skip' | 'manual_hold' | null;
  snoozeMinutes: number | null;
  validationId: string | null;
  expiresAt: string;
  createdAt: string;
  resolvedAt: string | null;
  /** Live slot accounting for the buy-review's portfolio. Populated
   *  only by the list endpoint; null on sell reviews and on the
   *  approve/reject responses (those don't refresh slot counts). */
  maxConcurrent?: number | null;
  openPositions?: number | null;
  slotsAvailable?: number | null;
}
export interface PendingReviewApproveResult {
  executed: Array<{ ticker: string; eventId: string; price: number; shares: number }>;
  skipped: Array<{ ticker: string; reason: string }>;
  /** Candidates remaining in the review after this partial approve.
   *  Zero means the row transitioned to 'approved' (terminal). */
  remaining: number;
  /** Review status after the partial action. 'pending' when remaining > 0. */
  status: 'pending' | 'approved' | 'rejected' | 'expired';
}
export interface PendingReviewSkipResult {
  review: PendingReview;
  /** Tickers actually removed from the row. */
  skipped: string[];
  /** Candidates left after the partial skip. */
  remaining: number;
}
export type PendingReviewRejectResolution = 'skip' | 'snooze' | 'manual_hold' | 'next_candidate';

export async function getPendingReviews(): Promise<PendingReview[]> {
  return request<PendingReview[]>('/portfolio/pending-reviews');
}
/** Partial approve — fires the fill for the listed tickers only.
 *  Tickers that drop from the review's candidates regardless of fill
 *  outcome (success or in-tx skip). When the review's candidates
 *  empty out, status flips to 'approved'. `tickers` is REQUIRED. */
export async function approvePendingReview(
  id: string,
  input: { tickers: string[]; reason?: string },
): Promise<PendingReviewApproveResult> {
  return request<PendingReviewApproveResult>(`/portfolio/pending-reviews/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
/** Reject — three shapes via `resolution`:
 *  • skip + tickers: partial skip (drop those tickers from the row)
 *  • skip without tickers: whole-row skip (terminate)
 *  • snooze: portfolio-wide buy snooze + terminate the review
 *  • manual_hold: sell-only — sets positions.manual_hold + terminates
 *  Returns the partial-skip variant separately so the caller can read
 *  `remaining` to decide whether to keep the panel open. */
export async function rejectPendingReview(
  id: string,
  input: {
    resolution: PendingReviewRejectResolution;
    tickers?: string[];
    snoozeMinutes?: number;
    reason?: string;
  },
): Promise<PendingReview | PendingReviewSkipResult> {
  return request<PendingReview | PendingReviewSkipResult>(`/portfolio/pending-reviews/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function cancelPendingReviewSnooze(id: string): Promise<{ portfolioId: string; cleared: boolean }> {
  return request<{ portfolioId: string; cleared: boolean }>(`/portfolio/pending-reviews/${id}/snooze-cancel`, {
    method: 'POST',
  });
}

// ── Manual-review debug controls (admin-only) ─────────────────────────────
export interface ManualReviewSeedBuyInput {
  ticker?: string;
  /** Multi-ticker variant. When non-empty, overrides the single
   *  `ticker` field and seeds one candidate per element (up to 5),
   *  each carrying a different synthetic factor profile. */
  tickers?: string[];
  price?: number;
  changePct?: number;
  rvol?: number;
  expiryMinutes?: number;
}
export interface ManualReviewSeedSellInput {
  positionId?: string;
  exitReason?: string;
}
export interface ManualReviewOpenPosition {
  id: string;
  ticker: string;
  shares: number;
  entry_price: number;
  purchase_date: string | null;
  current_price: number | null;
}

export async function adminManualReviewSeedBuy(portfolioId: string, input: ManualReviewSeedBuyInput = {}): Promise<{ id: string; candidates: PendingReviewCandidate[]; expiryMinutes: number }> {
  return request<{ id: string; candidates: PendingReviewCandidate[]; expiryMinutes: number }>(
    `/admin/manual-review/seed-buy/${portfolioId}`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}
export async function adminManualReviewSeedSell(portfolioId: string, input: ManualReviewSeedSellInput = {}): Promise<{ id: string; candidate: PendingReviewCandidate; positionId: string }> {
  return request<{ id: string; candidate: PendingReviewCandidate; positionId: string }>(
    `/admin/manual-review/seed-sell/${portfolioId}`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}
export async function adminManualReviewCleanup(): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(`/admin/manual-review/debug-cleanup`, { method: 'DELETE' });
}
export async function adminManualReviewOpenPositions(portfolioId: string): Promise<ManualReviewOpenPosition[]> {
  return request<ManualReviewOpenPosition[]>(`/admin/manual-review/open-positions/${portfolioId}`);
}

// ── Admin AI assistant config (issue #370) ──────────────────────────────────
export type AgentChatStatus = {
  agentChatEnabled: boolean;
  sqlEnabled: boolean;
  authMode: 'subscription' | 'apikey' | 'none';
  configured: boolean;
  model: string;
  effort: string;
  modelOptions: string[];
  effortOptions: string[];
  diag?: {
    tokenLength: number;
    tokenPrefix: string;
    looksLikeOauthToken: boolean;
    hasWhitespace: boolean;
    hasApiKeyVar: boolean;
    hasAuthTokenVar: boolean;
  };
  /** Codex CLI route credential state (no token material). */
  codex?: {
    configured: boolean;
    ready: boolean;
    source: 'db' | 'env' | 'apikey' | 'none';
    authMode: string | null;
    accountId: string | null;
    lastRefresh: string | null;
    hasRefreshToken: boolean;
    error: string | null;
    /** No encryption key → token rotations can't be stored durably. */
    persistenceDisabled: boolean;
    home: string;
  };
};
export async function adminAgentChatStatus(): Promise<AgentChatStatus> {
  return request<AgentChatStatus>(`/admin/agent-chat/status`);
}
export async function adminAgentChatEnable(): Promise<{ agentChatEnabled: boolean }> {
  return request<{ agentChatEnabled: boolean }>(`/admin/agent-chat/enable`, { method: 'POST' });
}
export async function adminAgentChatDisable(): Promise<{ agentChatEnabled: boolean }> {
  return request<{ agentChatEnabled: boolean }>(`/admin/agent-chat/disable`, { method: 'POST' });
}
export async function adminAgentChatConfig(model: string, effort: string): Promise<{ model?: string; effort?: string }> {
  return request<{ model?: string; effort?: string }>(`/admin/agent-chat/config`, {
    method: 'PUT',
    body: JSON.stringify({ model, effort }),
  });
}
// ── In-app AI assistant chat (VIP + admin popup) ────────────────────────────
export type AssistantStatus = { enabled: boolean; configured: boolean; isAdmin: boolean };
export async function assistantStatus(): Promise<AssistantStatus> {
  return request<AssistantStatus>(`/assistant/status`);
}
/** Start a turn — returns an id IMMEDIATELY (agent runs in the background). Poll
 *  assistantChunk for status + the answer. adminMode is honored server-side only
 *  for real admins. */
export async function assistantMessage(
  message: string,
  sessionId?: string,
  adminMode?: boolean,
  context?: { ticker: string; timeframe: string; bars?: Array<{ t: string; o: number; h: number; l: number; c: number; v: number }> },
): Promise<{ id: string }> {
  return request<{ id: string }>(`/assistant/message`, {
    method: 'POST',
    body: JSON.stringify({ message, sessionId, adminMode, context }),
  });
}
export type AssistantChunk = {
  status: 'running' | 'done' | 'error';
  chunk?: string;
  next?: number;
  done?: boolean;
  total?: number;
  sessionId?: string;
  tools?: string[];
  error?: string | null;
};
/** Poll job status / pull one small slice. Every response is small+fast → plain
 *  Content-Length, never chunked. */
export async function assistantChunk(id: string, offset: number): Promise<AssistantChunk> {
  return request<AssistantChunk>(`/assistant/chunk?id=${encodeURIComponent(id)}&offset=${offset}`);
}
export async function assistantCancel(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/assistant/cancel`, { method: 'POST', body: JSON.stringify({ id }) });
}
